import AutoVaCard from '../components/AutoVaCard'
import { useState, useEffect, useRef, useMemo } from 'react'
import StudioOnboarding, { FREE_STEPS } from '../components/StudioOnboarding'
import { createPortal } from 'react-dom'
import { useAuth } from '../context/AuthContext'
import { sb, API_URL } from '../utils/config'
import { loadAllVideoUrls, getVideoMp4, getVideoThumb, getSupabaseThumb } from '../utils/airtableVideo'
import { cleanScriptTemplate } from '../utils/scriptText'
import AdminBacklinks from '../components/AdminBacklinks'
import merlinImg from '../assets/merlin.png'
import merlinSearching from '../assets/Merlin_sort-removebg-preview.png'
import merlinFound from '../assets/Merlin_succes-removebg-preview.png'
import { STUDIO_MOCK_SESSION } from './studioMockData'
import StudioPro, { StudioAdminPanel, InsightsBubble, StudioProfileModal } from './StudioPro'
import StudioAnalysisTab, { TourVideoAnalysisDemo, VaGiftFiche } from './StudioAnalysisTab'
import { trackLead, trackInitiateCheckout, studioEvents, getMetaClickIds } from '../utils/metaPixel'
import { initUiExperiments, subscribeUiExperiments, getSlot, getVariantKey, isUiExpLoaded } from '../utils/uiExperiments'
import { TrameContent } from '../utils/beatGuide'
import FormatFiche from '../components/FormatFiche'
import MagicLinkConfirm, { readMagicConfirm, buildMagicRedirect } from '../components/MagicLinkConfirm'
import AvisBanner from '../components/AvisBanner'
import VERTICALES from '../config/verticales.json'

// Durée de l'essai gratuit Studio (jours). DOIT rester alignée avec
// admin-server.js `trial_period_days`. Tout ce qui est daté en dérive : fin
// d'essai = J+TRIAL_DAYS (1er débit), rappel mail = J+(TRIAL_DAYS−3) via le
// webhook Stripe trial_will_end. Prochaine bascule = ce seul chiffre.
// Bascule 14j→7j le 30/07 (97% des résiliations d'essai en <7j).
const TRIAL_DAYS = 7

/**
 * StudioPage — Charte Merlin (fond clair, mascotte, classes merlin-*) +
 * qualité produit AI moderne (hiérarchie, animations, layout 3 col, tabs).
 *
 * Inspiration Be Viral pour la STRUCTURE (hiérarchie, density, motion, paywall)
 * mais charte 100% Merlin (palette, mascotte, univers magicien).
 *
 * Vidéos : iframe Instagram embed via façade poster + click → iframe.
 */

function isPreviewMode() {
  try { return new URLSearchParams(window.location.search).get('studio') === 'preview' }
  catch { return false }
}

// MODE INVITÉ : bypass complet de l'auth gate, scan en POST /api/studio/init-guest
// avec guest_session_id local. Activable par 3 voies :
//   1. URL ?guest=1 (utilisé par l'ad-set Meta variant C "direct outil")
//   2. localStorage studio_guest_mode=1 (admin toggle pour tester en logged-in)
//   3. La 1ere visite via ?guest=1 set le localStorage pour persister le mode
//      sur les navigations (sinon refresh = perd le mode).
function isGuestMode() {
  if (typeof window === 'undefined') return false
  try {
    const params = new URLSearchParams(window.location.search)
    // ?guest=0 : PORTE DE SORTIE (14/07 : Antonin piégé — le toggle admin
    // déconnecte pour simuler un visiteur, mais une fois déconnecté la barre
    // admin n'existe plus, donc plus aucun moyen de quitter le mode invité).
    if (params.get('guest') === '0') {
      localStorage.removeItem('studio_guest_mode')
      return false
    }
    if (params.get('guest') === '1') {
      localStorage.setItem('studio_guest_mode', '1')
      return true
    }
    return localStorage.getItem('studio_guest_mode') === '1'
  } catch { return false }
}

function exitGuestMode() {
  try { localStorage.removeItem('studio_guest_mode') } catch {}
}

// VERTICALES (proto 13/07) : ?v=sport habille l'intake aux couleurs et au
// wording de la verticale et force le matching backend sur son secteur.
// Corps commun front+back, enveloppe 100% config (verticales.json).
// Persisté en localStorage (même logique que le mode invité), validé contre
// la whitelist du fichier de config.
export function getVertical() {
  if (typeof window === 'undefined') return null
  try {
    // DÉFAUT = GÉNÉRIQUE (retour Antonin 21/07 : le signup sport s'affichait
    // par défaut). La verticale entrée par URL ne vaut que pour la VISITE
    // (sessionStorage) ; seule la verticale du COMPTE (persona serveur,
    // clé studio_vertical_persona) persiste entre les visites.
    localStorage.removeItem('studio_vertical') // migration : purge l'ancienne clé collante
    const v = new URLSearchParams(window.location.search).get('v')
    if (v === '0') {
      sessionStorage.removeItem('studio_vertical')
      localStorage.removeItem('studio_vertical_persona')
      applyVerticalTheme(null)
      return null
    }
    if (v && VERTICALES[v]) {
      sessionStorage.setItem('studio_vertical', v)
      applyVerticalTheme(v)
      return v
    }
    const stored = sessionStorage.getItem('studio_vertical') || localStorage.getItem('studio_vertical_persona')
    if (VERTICALES[stored]) { applyVerticalTheme(stored); return stored }
    return null
  } catch { return null }
}

// Thème verticale global : TOUT le parcours (chargement, score, cards, CTA,
// paywall, popups) lit ses couleurs via var(--merlin-accent*) avec l'orange
// Merlin en fallback. On pose les variables sur <html> une seule fois ; la
// classe .studio-vertical permet aussi d'écraser les fonds violets Tailwind
// (règle dans index.css). Idempotent, appelé par getVertical().
function applyVerticalTheme(verticalId) {
  if (typeof document === 'undefined') return
  try {
    const root = document.documentElement
    const vt = verticalId ? VERTICALES[verticalId] : null
    if (vt) {
      // Décision Antonin 13/07 : on remplace le VIOLET par la couleur de la
      // verticale (fonds + tab Analyse), l'orange Merlin reste l'accent
      // d'action partout. Les var(--merlin-accent*) gardent leur fallback
      // orange (non posées), prêtes si on veut un jour thématiser à fond.
      if (vt.bg_gradient) root.style.setProperty('--merlin-bg', vt.bg_gradient)
      root.style.setProperty('--merlin-tab-accent', vt.accent)
      root.style.setProperty('--merlin-tab-accent-2', vt.accent2)
      if (vt.accent_rgb) root.style.setProperty('--merlin-tab-accent-rgb', vt.accent_rgb)
      // Le VIOLET Merlin (boutons améliorer, badges analyse, vignettes type
      // de contenu, sparkles) prend la couleur de la verticale — c'est la
      // doctrine « on remplace le violet, pas l'orange » appliquée aux
      // accents dispersés, pas seulement aux fonds.
      root.style.setProperty('--merlin-violet', vt.accent)
      root.style.setProperty('--merlin-violet-2', vt.accent2)
      root.style.setProperty('--merlin-violet-soft', `color-mix(in srgb, ${vt.accent} 55%, white)`)
      if (vt.accent_rgb) root.style.setProperty('--merlin-violet-rgb', vt.accent_rgb)
      root.classList.add('studio-vertical')
    } else {
      ;['--merlin-accent', '--merlin-accent-2', '--merlin-accent-rgb', '--merlin-bg', '--merlin-tab-accent', '--merlin-tab-accent-2', '--merlin-tab-accent-rgb', '--merlin-accent-soft', '--merlin-violet', '--merlin-violet-2', '--merlin-violet-soft', '--merlin-violet-rgb'].forEach(k => root.style.removeProperty(k))
      root.classList.remove('studio-vertical')
    }
  } catch { /* noop */ }
}

// Aperçu de script replié : coupe sur des LIGNES ENTIÈRES (jamais au milieu
// d'un mot ou d'une phrase). Les scripts sont des verbatims ligne par ligne :
// tronquer à N caractères donnait des "ÇA A COMMENCÉ À SE…" moches.
function scriptPreview(txt, maxChars = 180) {
  const lines = (txt || '').split('\n')
  const out = []
  let len = 0
  for (const line of lines) {
    if (out.length > 0 && len + line.length > maxChars) break
    out.push(line)
    len += line.length + 1
    if (len >= maxChars) break
  }
  let s = out.join('\n')
  // Cas pathologique : première ligne géante sans retour (script en bloc).
  // On coupe alors au dernier espace avant la limite, jamais en plein mot.
  if (s.length > maxChars + 60) {
    s = s.slice(0, maxChars)
    const lastSpace = s.lastIndexOf(' ')
    if (lastSpace > 80) s = s.slice(0, lastSpace)
  }
  return s
}

// guest_session_id : on réutilise l'anon_id du tracking (cohérence funnel).
// Permet de JOIN les events lp_loaded → scan_started → scan_completed du
// même invité dans l'admin dashboard avec ses studio_sessions.
function getGuestSessionId() {
  if (typeof window === 'undefined') return null
  try {
    let id = localStorage.getItem('studio_anon_id')
    if (!id) {
      id = `guest_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`
      localStorage.setItem('studio_anon_id', id)
    }
    return id
  } catch { return null }
}

// Helper paywall : OUVRE la modal d'upgrade (qui explique les benefits +
// urgence béta + 2 boutons monthly/annual) au lieu de rediriger direct sur
// Stripe. Custom event simple → tout composant intéressé écoute.
// A/B/C structure de facturation (Antonin 06/08), piloté par le lien d'adset :
// A = normal, essai 7 jours (défaut) · B = offre unique deal annuel -80%
// (portée par studio_deal_offer, machinerie deal existante) · C = tarifs
// normaux SANS essai, satisfait ou remboursé 14j. Objectif : filtrer les
// cartes sans fonds (57% d'impayés sur 30j) + signal Meta réel (PurchaseCash).
export function getTrialArm() {
  try { return localStorage.getItem('studio_offer_arm') || 'A' } catch { return 'A' }
}

// Lien test « Créateur annuel -75% sans essai » (13/08, session DATA).
// ?offer=creator75 → l'offre unique du visiteur devient Créateur 117€/an payé
// d'avance (pas d'essai). Isolé : sans le drapeau, aucun changement de parcours.
export function isCreator75Visitor() {
  try {
    if (new URLSearchParams(window.location.search).get('offer') === 'creator75') return true
    return localStorage.getItem('studio_offer_creator75') === '1'
  } catch { return false }
}

// Offre « merci » (compensation bugs semaine du 13/08) : Pro+ AU PRIX CRÉATEUR,
// à vie, prélèvement direct. Annuel -40% vs mensuel recalculé. Valable 24h.
// ?offer=merci → envoyée aux users sans plan en cours. Isolé derrière le drapeau.
export function isMerciVisitor() {
  try {
    if (new URLSearchParams(window.location.search).get('offer') === 'merci') return true
    return localStorage.getItem('studio_offer_merci') === '1'
  } catch { return false }
}

// La copy « essai 7 jours » ne doit s'afficher QUE si le checkout offrira
// vraiment un essai : bras A sans drapeau deal/membre. Adrian (06/08) voyait
// « essai 7 jours » sur la page puis un paywall membre sans essai → confusion
// « soucis de redirection », prospect perdu.
export function showsTrialUx() {
  try { return getTrialArm() === 'A' && !isDealVisitor() && !isMemberVisitor() } catch { return true }
}

export function goToStudioCheckout(_period, trigger = 'direct_cta') {
  try {
    // Mode invité : on n'a pas de user_id → impossible de créer un checkout
    // Stripe direct (les webhooks linkent par stripe_customer_id → users.id).
    // Donc on ouvre la modal signup d'abord. Le claim de la session guest
    // se fera côté backend dans /api/studio/claim-guest-session après login.
    // Pas de compte connecté (invité ou pas encore loggé) → on ne peut pas
    // créer de checkout Stripe (lié à users.id). On ouvre le signup d'abord
    // (modal montée dans toutes les vues invité). Ne dépend plus de
    // studio_guest_mode (supprimé par le flow LP → détection cassée avant).
    if (typeof window !== 'undefined' && window.__STUDIO_HAS_USER !== true) {
      window.dispatchEvent(new CustomEvent('studio:open-guest-signup', {
        detail: { trigger: 'paywall' }
      }))
      return
    }
    // trigger : identifie le CTA d'origine dans les stats du popup (110
    // ouvertures "unknown" en 21j = le tunnel principal était invisible).
    window.dispatchEvent(new CustomEvent('studio:open-upgrade', { detail: { trigger } }))
  } catch {}
}

// Helper utilisé par la modal : démarre vraiment le checkout Stripe.
// plan : 'studio_creator' (39€/mo) | 'studio_pro_plus' (89€/mo)
// bypassTrial : true → skip les 7 jours offerts, prélèvement direct
export async function triggerStudioCheckout(period = 'annual', plan = 'studio_creator', bypassTrial = false) {
  try {
    // ── VERROU D'OFFRE (Antonin 06/08) : QUEL QUE SOIT le CTA d'origine
    // (paywall, downsell, trame, récompense...), l'offre du visiteur s'impose
    // ici, au point de passage unique vers Stripe. Fin des essais qui fuient
    // par les portes latérales sur les campagnes annuelles.
    const _arm = getTrialArm()
    if (isMerciVisitor()) {
      // offre merci : Pro+ au prix Créateur, à vie, prélèvement direct.
      // Annuel (loyalty_annual, -40%) mis en avant ; mensuel (loyalty_monthly, 39€).
      plan = 'studio_pro_plus'; bypassTrial = true
      period = period === 'monthly' ? 'loyalty_monthly' : 'loyalty_annual'
    } else if (isCreator75Visitor() && plan !== 'studio_pro_plus') {
      // lien test creator75 : offre Créateur, prélèvement direct (jamais d'essai).
      // Annuel → deal -75% (117€) ; mensuel → 39€ direct. Le switch du popup pilote
      // la période. Garde `plan !== 'studio_pro_plus'` : un upgrade Pro+ EXPLICITE
      // (onglet analyse) contourne l'offre → on ne casse jamais le passage au Pro+.
      plan = 'studio_creator'; bypassTrial = true
      period = period === 'monthly' ? 'monthly' : 'annual_deal'
    } else if (isDealVisitor()) {
      // campagne deal -80% : offre unique = Pro+ annuel payé d'avance
      period = 'annual_deal'; plan = 'studio_pro_plus'; bypassTrial = true
    } else if (isMemberVisitor() && (period === 'monthly' || period === 'annual')) {
      // offre membre -45% : mêmes périodes mais tarif membre, jamais d'essai
      period = period === 'annual' ? 'annual_member' : 'monthly_member'
      plan = 'studio_pro_plus'; bypassTrial = true
    } else if (_arm === 'C') {
      // bras C : structure normale (plan/période du CTA) mais jamais d'essai
      bypassTrial = true
    }
    // Track avant la redirection — si Stripe charge, l'event est déjà parti
    trackInitiateCheckout({ plan, billingPeriod: period })
    studioEvents.checkoutStarted({ plan, period, bypass_trial: bypassTrial })
    const { data } = await sb.auth.getSession()
    const token = data?.session?.access_token
    if (!token) {
      // En webview Instagram, la session est souvent perdue (stockage cloisonné)
      // -> avant on renvoyait à l'accueil EN SILENCE = checkout fantôme. Maintenant
      // on l'explique et on propose le vrai navigateur (Android : ouverture directe).
      const wv = getWebviewInfo()
      try { studioEvents.checkoutFailed?.({ plan, period, error: 'no_token' + (wv.isMetaWebview ? '_webview' : '') }) } catch {}
      if (wv.isMetaWebview) {
        if (wv.isAndroid) {
          alert('Le navigateur d\'Instagram bloque le paiement. On ouvre Merlin dans Chrome : reconnecte-toi et finalise ta souscription (rien n\'a été débité).')
          escapeWebviewToChrome()
        } else {
          alert('Le navigateur d\'Instagram bloque le paiement.\n\nOuvre cette page dans Safari : menu ••• en haut à droite → « Ouvrir dans le navigateur », reconnecte-toi, puis finalise ta souscription.')
        }
        return
      }
      window.location.href = '/'
      return
    }
    // fbp/fbc du clic pub : transmis au backend → metadata Stripe → CAPI,
    // pour que Meta attribue la conversion à la campagne (fix 23/07)
    const { fbp, fbc } = getMetaClickIds()
    const res = await fetch(`${API_URL}/api/stripe/checkout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ plan, billingPeriod: period, offer_arm: getTrialArm(), bypassTrial, fbp, fbc }),
    })
    const out = await res.json()
    // GARDE-FOU DOUBLE ABONNEMENT (22/07) : si un abonnement actif existe
    // déjà, le backend fait un upgrade EN PLACE (jamais un 2e abonnement).
    if (out?.upgraded) {
      studioEvents.checkoutRedirected({ plan, period, upgraded_in_place: true })
      alert('C\'est fait : ton abonnement a été mis à jour vers le nouveau plan (pas de nouvel abonnement, ton essai en cours est conservé).')
      window.location.reload()
      return
    }
    if (out?.already_subscribed) {
      alert('Tu es déjà sur ce plan.')
      return
    }
    if (!res.ok || !out?.url) throw new Error(out?.error || `HTTP ${res.status}`)
    // Event AVANT la redirection : en data, "redirected sans trial derrière"
    // = abandon sur la page Stripe ; "started sans redirected" = échec API.
    studioEvents.checkoutRedirected({ plan, period })
    window.location.href = out.url
  } catch (e) {
    console.error('[Studio checkout]', e)
    studioEvents.checkoutFailed({ plan, period, error: (e?.message || '').substring(0, 120) })
    alert(`Impossible de démarrer le paiement : ${e.message}\n\nRéessaie dans quelques secondes ou écris-nous : support@merlin-content-ai.com`)
  }
}

// Poster d'un format : on PRÉFÈRE le vrai beat-frame stocké (image_cle_url),
// qui peut pointer vers le Storage dev OU prod selon le sync. getSupabaseThumb
// reconstruit une URL sur le Supabase COURANT (prod) -> 404 si le beat-frame
// vit dans l'autre Storage. D'où des miniatures vides. On utilise donc l'URL
// réelle d'abord, la reconstruite en fallback.
function formatPosterUrl(f) {
  const beats = f?.beats_raw || f?.structure || []
  const real = Array.isArray(beats) ? beats.find(b => b && b.image_cle_url)?.image_cle_url : null
  // Ordre : vrai beat-frame stocké > poster_url explicite (ex: asset bundlé des
  // fixtures LP, toujours fiable) > URL Supabase reconstruite (souvent 404 :
  // dette Storage dev). Sans le poster_url, la card "Ce que tu reçois" de la LP
  // tombait sur une URL prod /beat-frames/<shortcode> inexistante → miniature vide.
  return real || f?.source?.poster_url || getSupabaseThumb({ lien: f?.source?.lien }) || null
}

function isPaidPreviewMode() {
  try { return new URLSearchParams(window.location.search).get('studio') === 'paid-preview' }
  catch { return false }
}

function extractShortcode(lien) {
  if (!lien) return null
  const m = lien.match(/\/(reel|p|tv)\/([A-Za-z0-9_-]+)/)
  return m ? m[2] : null
}

export default function StudioPage() {

  const auth = useAuth()
  const { user, signUpWithEmail, signInWithEmail, signInWithGoogle } = auth
  const preview = isPreviewMode()
  const paidPreview = isPaidPreviewMode()
  const guestMode = isGuestMode()

  // Drapeau global lu par goToStudioCheckout() / AccountScore pour router les
  // CTA : connecté → modal upgrade ; non connecté (invité) → signup. Sans ça
  // les CTA paywall d'un invité dispatchaient un event sans modal montée
  // (rien ne se passait au clic). Posé en synchrone ET en effet (1er paint).
  if (typeof window !== 'undefined') window.__STUDIO_HAS_USER = !!user
  useEffect(() => { try { window.__STUDIO_HAS_USER = !!user } catch {} }, [user])
  // Visiteur deal : on MAINTIENT deal=1 dans la barre d'adresse. Le menu
  // natif iOS « Ouvrir dans le navigateur » copie l'URL telle quelle : si la
  // navigation SPA a vidé la query, le drapeau se perdrait dans Safari.
  useEffect(() => {
    try {
      if (localStorage.getItem('studio_deal_offer') === '1') {
        const u = new URL(window.location.href)
        if (u.searchParams.get('deal') !== '1') {
          u.searchParams.set('deal', '1')
          window.history.replaceState({}, '', u.href)
        }
      }
    } catch { /* best effort */ }
  })
  // Lien test creator75 : on grave le drapeau et on le maintient dans l'URL
  // (comme le deal) pour survivre aux sauts de navigateur (webview → Safari).
  useEffect(() => {
    try {
      if (new URLSearchParams(window.location.search).get('offer') === 'creator75') {
        localStorage.setItem('studio_offer_creator75', '1')
      }
      if (localStorage.getItem('studio_offer_creator75') === '1') {
        const u = new URL(window.location.href)
        if (u.searchParams.get('offer') !== 'creator75') {
          u.searchParams.set('offer', 'creator75')
          window.history.replaceState({}, '', u.href)
        }
      }
      // offre merci : même persistance (survit aux sauts de navigateur / magic link)
      if (new URLSearchParams(window.location.search).get('offer') === 'merci') {
        localStorage.setItem('studio_offer_merci', '1')
      }
      if (localStorage.getItem('studio_offer_merci') === '1') {
        const u = new URL(window.location.href)
        if (u.searchParams.get('offer') !== 'merci') {
          u.searchParams.set('offer', 'merci')
          window.history.replaceState({}, '', u.href)
        }
      }
    } catch { /* best effort */ }
  })
  // Réhydrate le drapeau deal depuis le SERVEUR au login : le localStorage ne
  // survit pas aux sauts de navigateur (webview → Safari via magic link),
  // le compte oui (users.deal_offer posé au claim/init/checkout).
  useEffect(() => {
    if (!user?.id) return
    ;(async () => {
      try {
        const h = await authHeaders()
        const r = await fetch(`${API_URL}/api/stripe/sub-info`, { headers: h })
        const j = await r.json().catch(() => null)
        if (j?.deal_offer) localStorage.setItem('studio_deal_offer', '1')
        if (j?.offer_arm === 'C') localStorage.setItem('studio_offer_arm', 'C')
      } catch { /* best effort */ }
    })()
  }, [user?.id])
  // Plan Studio exposé pour le gate du tuto (14/07, demande Antonin par
  // Telegram : tuto réservé aux essais/abonnés, désactivé pour les free).
  const _planFlag = (() => { try { return localStorage.getItem('studio_plan_dev_override') } catch { return null } })() || auth.studioPlan || auth.user?.user_metadata?.studio_plan || null
  if (typeof window !== 'undefined') window.__STUDIO_PLAN = _planFlag
  useEffect(() => { try { window.__STUDIO_PLAN = _planFlag } catch {} }, [_planFlag])

  // ── CLAIM GLOBAL DE SESSION INVITÉE (fix 2026-06-11) ─────────
  // Avant, le claim post-OAuth vivait dans GuestSignupModal : au retour de
  // Google, l'user connecté sans session rendait l'INTAKE, la modal n'était
  // plus montée → claim jamais exécuté → scan invité orphelin, user devant
  // un écran vide (3/3 tentatives Google perdues le 11/06, 2 users ont tout
  // refait). Cet effet racine tourne dès que l'user est connecté, claim par
  // anon_id PUIS fallback par handle côté backend (cas cross-navigateur :
  // webview Instagram → escape Chrome = localStorage différent).
  // Retour de MAGIC LINK : applique le consentement CGU + l'opt-in daily
  // stashés avant l'envoi du lien (le compte a pu être créé par l'OTP,
  // sans passer par signUpWithEmail qui fait normalement ce PATCH).
  useEffect(() => {
    if (!user?.id) return
    let meta = null
    try { meta = JSON.parse(localStorage.getItem('studio_magic_meta') || 'null') } catch { /* ignore */ }
    // Retour d'OAuth Google : la mention "En continuant, tu acceptes les CGU"
    // est affichée sous le bouton AVANT le clic → continuer = accepter.
    // On enregistre ce consentement à la 1re connexion (RGPD : traçabilité).
    let oauthConsent = null
    try { oauthConsent = localStorage.getItem('studio_oauth_consent_shown') } catch { /* ignore */ }
    // Cross-navigateur (magic link demandé en webview, ouvert dans Safari) :
    // le stash localStorage est absent, mais le consentement voyage dans
    // user_metadata (posé par signInWithMagicLink au moment de l'envoi).
    if (!meta) {
      const um = user?.user_metadata || {}
      if (um.studio_magic_consent_at || um.studio_daily_optin !== undefined) {
        meta = { consent: !!um.studio_magic_consent_at, dailyOptin: um.studio_daily_optin !== false, at: um.studio_magic_consent_at || null }
      }
    }
    if (!meta && !oauthConsent) return
    ;(async () => {
      try {
        const patch = {}
        if (meta) {
          patch.daily_email_optin = !!meta.dailyOptin
          if (meta.consent) patch.consent_given_at = meta.at || new Date().toISOString()
        } else if (oauthConsent) {
          patch.consent_given_at = oauthConsent
        }
        await sb.from('users').update(patch).eq('id', user.id)
        localStorage.removeItem('studio_magic_meta')
        localStorage.removeItem('studio_oauth_consent_shown')
      } catch { /* retenté au prochain load, la clé reste */ }
    })()
  }, [user?.id])

  useEffect(() => {
    if (!user?.id) return
    let pendingOauth = false, pendingMagic = false, hasGuestArtifacts = false, urlClaim = ''
    try {
      // ?claim=<anon_id> : posé dans le redirect du magic link pour rattacher
      // le scan invité quand le lien s'ouvre dans un AUTRE navigateur que
      // celui du scan (webview Instagram → Safari : localStorage cloisonné,
      // les clés pending/anon_id ci-dessous sont vides dans ce cas).
      urlClaim = (new URLSearchParams(window.location.search).get('claim') || '').trim()
    } catch { /* ignore */ }
    try {
      pendingOauth = localStorage.getItem('studio_guest_pending_claim') === '1'
      pendingMagic = localStorage.getItem('studio_magic_pending') === '1'
      hasGuestArtifacts = !!localStorage.getItem('studio_guest_session_id') || !!localStorage.getItem('studio_anon_id')
      if (sessionStorage.getItem('studio_global_claim_done') === '1') return
    } catch { if (!urlClaim) return }
    if (!pendingOauth && !pendingMagic && !hasGuestArtifacts && !urlClaim) return
    ;(async () => {
      try {
        const { data: sess } = await sb.auth.getSession()
        const token = sess?.session?.access_token
        if (!token) return
        const guestId = urlClaim || localStorage.getItem('studio_anon_id') || ''
        const prefHandle = (localStorage.getItem('studio_prefill_handle') || '').trim()
        const res = await fetch(`${API_URL}/api/studio/claim-guest-session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ guest_session_id: guestId, ig_username: prefHandle, deal: isDealVisitor(), offer_arm: getTrialArm() }),
        })
        const d = await res.json().catch(() => null)
        const claimed = !!(d && d.claimed)
        studioEvents.guestSessionClaimed({ ok: claimed, status: res.status, via: urlClaim ? 'url_claim' : 'global_effect' })
        // Le completed Google/magic n'est jamais tracké côté client (le
        // redirect part avant) : on le rattrape ici. urlClaim sans clé
        // pending = retour magic link dans un navigateur vierge.
        if (pendingMagic || urlClaim) studioEvents.signupCompleted({ method: 'magic_link', mode: 'guest_paywall' })
        else if (pendingOauth) studioEvents.signupCompleted({ method: 'google', mode: 'guest_paywall' })
        try {
          sessionStorage.setItem('studio_global_claim_done', '1')
          localStorage.removeItem('studio_guest_pending_claim')
          localStorage.removeItem('studio_magic_pending')
          localStorage.removeItem('studio_guest_session_id')
          localStorage.removeItem('studio_guest_mode')
        } catch {}
        // Nettoie ?claim= de l'URL : évite un re-claim au reload et un
        // partage d'URL contenant l'id de session invitée.
        if (urlClaim) {
          try {
            const u = new URL(window.location.href)
            u.searchParams.delete('claim')
            window.history.replaceState({}, '', u.toString())
          } catch { /* ignore */ }
        }
        if (claimed) window.location.reload()
      } catch { /* le fallback autolaunch reste le filet */ }
    })()
  }, [user?.id])

  // Détecte si on revient de Stripe Checkout après un paiement réussi —
  // affiche une modal de confirmation + bouton refresh pour activer le mode Pro
  // (le webhook a besoin d'une poignée de secondes pour update studio_plan)
  const paymentSuccess = (() => {
    try { return new URLSearchParams(window.location.search).get('payment') === 'success' }
    catch { return false }
  })()

  // Le Studio est un produit SÉPARÉ de Merlin : son propre abonnement
  // dans users.studio_plan (pas users.plan qui gère Merlin classique).
  // Résolution :
  //   1. localStorage override (admin testing, instantané)
  //   2. auth.studioPlan (users table)
  //   3. user_metadata fallback (set via supabase.auth.updateUser)
  const localOverride = (() => {
    try { return localStorage.getItem('studio_plan_dev_override') } catch { return null }
  })()
  const studioPlan = localOverride || auth.studioPlan || auth.user?.user_metadata?.studio_plan || null
  const isPaid = paidPreview || (studioPlan && ['pro', 'pro_plus', 'creator', 'agency'].includes(studioPlan))

  // ── Deep-link "Adapter ce format à mon compte" (/format/:id → ?format_id=X) ──
  // 1) On capture l'id en localStorage (il survit au signup) et on nettoie l'URL.
  useEffect(() => {
    try {
      const p = new URLSearchParams(window.location.search)
      const fid = p.get('format_id')
      if (!fid) return
      localStorage.setItem('studio_pending_adapt', fid)
      p.delete('format_id'); p.delete('utm_source'); p.delete('utm_medium')
      const qs = p.toString()
      window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''))
    } catch {}
  }, [])
  // 2) Cas gratuit / déconnecté : on ouvre le checkout (déconnecté → modal
  //    signup ; connecté gratuit → modal upgrade = essai Créateur). Le format
  //    adapté est la carotte. Une fois payant, StudioPro adapte + favorise.
  const pendingAdaptFiredRef = useRef(false)
  useEffect(() => {
    if (auth.loading || isPaid || pendingAdaptFiredRef.current) return
    let pid = null
    try { pid = localStorage.getItem('studio_pending_adapt') } catch {}
    if (!pid) return
    pendingAdaptFiredRef.current = true
    const t = setTimeout(() => { try { goToStudioCheckout() } catch {} }, 700)
    return () => clearTimeout(t)
  }, [auth.loading, isPaid])

  // NOTE : les early returns StudioPro / PaymentSuccess sont déplacés APRÈS
  // tous les hooks du composant (fin des déclarations) : placés ici, un flip
  // de studioPlan (free -> pro au chargement auth) faisait rendre MOINS de
  // hooks qu'au render précédent -> crash React #300 (prod, 12/06).
  // Pré-rempli depuis l'input @ du hero LP (localStorage) : l'user qui a
  // tapé son handle sur la LP ne le retape pas après le signup.
  const [handle, setHandle] = useState(() => {
    try { return localStorage.getItem('studio_prefill_handle') || '' } catch { return '' }
  })
  const [launching, setLaunching] = useState(false)
  const [launchError, setLaunchError] = useState(null)
  const [session, setSession] = useState(preview ? STUDIO_MOCK_SESSION : null)
  const [loadingSession, setLoadingSession] = useState(!preview)
  const [copyState, setCopyState] = useState({})
  // FICHE-CADEAU (?va_fiche=<id>&k=<kind>) : atterrissage du mail analyse
  // offerte. Les cibles sont free/past → ils rendent StudioPage (PAS
  // StudioPro), donc le handler DOIT être ici (bug vague 2 : il n'était que
  // dans StudioPro → 0 gratuit n'atteignait la fiche). Early-return plus bas.
  const [vaFiche, setVaFiche] = useState(() => {
    try { const q = new URLSearchParams(window.location.search); return q.get('va_fiche') ? { id: q.get('va_fiche'), kind: q.get('k') || 'free' } : null } catch { return null }
  })
  function closeVaFiche() {
    setVaFiche(null)
    try { const u = new URL(window.location.href); u.searchParams.delete('va_fiche'); u.searchParams.delete('k'); window.history.replaceState({}, '', u) } catch { /* noop */ }
  }
  const [airtableReady, setAirtableReady] = useState(false)
  const pollRef = useRef(null)
  const airtableLoadedRef = useRef(false)

  // Charge les URLs MP4 d'Airtable au mount (pattern identique à StrategyPage).
  // Une fois prêt, getVideoMp4(video) retourne l'URL MP4 directement playable.
  useEffect(() => {
    if (!user && !preview) return
    if (airtableLoadedRef.current) return
    airtableLoadedRef.current = true
    const safetyTimer = setTimeout(() => {
      console.warn('[Studio] Airtable load timeout (25s), unblock UI')
      setAirtableReady(true)
    }, 25000)
    loadAllVideoUrls().then(() => {
      clearTimeout(safetyTimer)
      setAirtableReady(true)
      console.log('[Studio] ✅ Airtable MP4 URLs loaded')
    }).catch(e => {
      clearTimeout(safetyTimer)
      console.error('[Studio] Airtable load failed:', e)
      setAirtableReady(true)
    })
  }, [user, preview])

  async function authHeaders() {
    const { data } = await sb.auth.getSession()
    const token = data?.session?.access_token
    return token ? { 'Authorization': `Bearer ${token}` } : {}
  }

  useEffect(() => {
    if (preview) return
    // Pas de user : restaure une éventuelle session invité en cours
    // (refresh pendant le scan, flow @ d'abord OU mode ?guest=1).
    const hasGuestSession = (() => { try { return !!localStorage.getItem('studio_guest_session_id') } catch { return false } })()
    if (!user?.id && !guestMode && !hasGuestSession) return
    if (!user?.id && (guestMode || hasGuestSession)) {
      // Reload une éventuelle session guest en cours pour reprendre où on
      // s'est arrêté (refresh page pendant un scan). On stocke session.id
      // dans localStorage à la création.
      try {
        const cached = localStorage.getItem('studio_guest_session_id')
        if (!cached) { setLoadingSession(false); return }
        let cancelled = false
        ;(async () => {
          try {
            const guestId = getGuestSessionId()
            const res = await fetch(`${API_URL}/api/studio/guest-session/${cached}?guest_id=${encodeURIComponent(guestId)}`)
            const data = await res.json()
            if (!cancelled) {
              if (data && data.id) setSession({ ...data, _is_guest: true })
              else { try { localStorage.removeItem('studio_guest_session_id') } catch {} }
              setLoadingSession(false)
            }
          } catch { if (!cancelled) setLoadingSession(false) }
        })()
        return () => { cancelled = true }
      } catch { setLoadingSession(false); return }
    }
    let cancelled = false
    ;(async () => {
      try {
        const h = await authHeaders()
        const res = await fetch(`${API_URL}/api/studio/session`, { headers: h })
        const data = await res.json()
        if (!cancelled) {
          if (data && data.id) setSession(data)
          setLoadingSession(false)
        }
      } catch {
        if (!cancelled) setLoadingSession(false)
      }
    })()
    return () => { cancelled = true }
  }, [user?.id, preview, guestMode])

  // Persiste l'id de session guest pour reload (refresh page pendant scan)
  useEffect(() => {
    if (!session?.id || !session?._is_guest) return
    try { localStorage.setItem('studio_guest_session_id', session.id) } catch {}
  }, [session?.id, session?._is_guest])

  useEffect(() => {
    if (pollRef.current) { clearTimeout(pollRef.current); pollRef.current = null }
    if (!session?.id || preview) return
    if (session.status !== 'pending' && session.status !== 'running') return
    let cancelled = false
    // Retourne false quand il faut arrêter de poller (done/error).
    async function fetchOne() {
      try {
        // Polling : route guest endpoint si session invité (pas de JWT à envoyer)
        let res
        if (session._is_guest) {
          const guestId = getGuestSessionId()
          res = await fetch(`${API_URL}/api/studio/guest-session/${session.id}?guest_id=${encodeURIComponent(guestId)}`)
        } else {
          const h = await authHeaders()
          res = await fetch(`${API_URL}/api/studio/session/${session.id}`, { headers: h })
        }
        const data = await res.json()
        if (cancelled) return
        if (res.ok) {
          const prevStatus = session.status
          // PRÉSERVER _is_guest : la row serveur ne porte pas ce flag client.
          // Sans ça, le 1er poll écrasait la session invité → le render ne
          // la reconnaissait plus → retour au gate (bug flow invité 06-11).
          setSession(session._is_guest ? { ...data, _is_guest: true } : data)
          if (data.status === 'done' && prevStatus !== 'done') {
            studioEvents.scanCompleted({
              session_id: session.id,
              ig_username: data.ig_username,
              duration_ms: data.duration_ms || null,
              format_count: data.formats?.length || 0,
            })
          }
          if (data.status === 'error' && prevStatus !== 'error') {
            studioEvents.scanFailed({
              session_id: session.id,
              ig_username: data.ig_username,
              reason: 'backend_error',
              error: (data.error || '').substring(0, 150),
              current_step: data.current_step || null,
            })
          }
          if (data.status === 'done' || data.status === 'error') return false
        }
      } catch {}
      return true
    }
    // Poll rampé : rapide au début (1200ms) pour afficher le score + les 3
    // formats au plus vite, puis throttle à 2500ms. Détecte "done" jusqu'à
    // ~1.3s plus tôt que l'ancien interval fixe à 2500ms.
    let polls = 0
    async function loop() {
      if (cancelled) return
      const cont = await fetchOne()
      if (cancelled || !cont) return
      polls++
      const delay = polls <= 10 ? 1200 : 2500
      pollRef.current = setTimeout(loop, delay)
    }
    loop()
    return () => { cancelled = true; if (pollRef.current) clearTimeout(pollRef.current) }
  }, [session?.id, session?.status, preview])

  // VERTICALE CÔTÉ COMPTE : si le scan a été fait en verticale (persona.vertical
  // persisté serveur), on repose le flag local. L'enveloppe suit donc le COMPTE,
  // pas seulement le navigateur : retour depuis un autre appareil, webview
  // Insta → Safari, localStorage vidé… l'user Sport retombe sur Sport.
  const personaVertical = session?.persona?.vertical
  useEffect(() => {
    if (!personaVertical || !VERTICALES[personaVertical]) return
    try { localStorage.setItem('studio_vertical_persona', personaVertical) } catch {}
    applyVerticalTheme(personaVertical)
  }, [personaVertical])

  // Applique le thème verticale (variables CSS + fonds) dès le mount, quelle
  // que soit la page du parcours : chargement de scan, score, formats, paywall.
  useEffect(() => { getVertical() }, [])

  async function launchGeneric(nicheMacro, nicheFine) {
    if (!nicheMacro) { setLaunchError('Choisis une niche'); return }
    setLaunchError(null)
    setLaunching(true)
    studioEvents.genericSessionCreated({ niche_macro: nicheMacro, has_niche_fine: !!nicheFine })
    try {
      const h = await authHeaders()
      const res = await fetch(`${API_URL}/api/studio/init-generic`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...h },
        body: JSON.stringify({ niche_macro: nicheMacro, niche_fine: nicheFine || null, ...(getVertical() ? { vertical: getVertical() } : {}) }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
      setSession({ id: data.id, status: 'pending', progress_pct: 0, ig_username: '_generic' })
    } catch (e) {
      setLaunchError(e.message)
    } finally {
      setLaunching(false)
    }
  }

  async function launch(handleOverride) {
    // handleOverride : utilisé par l'auto-lancement post-signup (le state
    // `handle` peut être vide si le @ a été saisi dans le gate AVANT le
    // mount du state). Garde : onClick passe un SyntheticEvent, on ne
    // garde que les strings.
    const override = typeof handleOverride === 'string' ? handleOverride : null
    // Auto-clean agressif : URL Insta complète, @, espaces, slash de fin.
    // L'audit a montré que la validation antérieure DÉTECTAIT mais ne
    // CORRIGEAIT pas, créant une friction punitive. Maintenant on tolère
    // tout copier-coller raisonnable.
    let clean = (override ?? handle ?? '').trim()
    // Strip protocole + domain instagram.com (mobile share copie l'URL)
    clean = clean.replace(/^https?:\/\//i, '')
                 .replace(/^(www\.)?instagram\.com\//i, '')
                 .replace(/[\/?#].*$/, '')
                 .replace(/^@/, '')
                 .replace(/\s+/g, '')
    // Reflète la version nettoyée dans l'input pour que l'user voie le résultat
    if (clean !== handle) setHandle(clean)
    if (!clean) { setLaunchError('Tape ton pseudo Instagram'); return }
    // Email collé par erreur (auto-clean n'attrape pas le @gmail.com sans http) : on bloque proprement
    const hasAt = clean.includes('@')
    const hasDot = /\.(com|fr|net|org|io|app)/i.test(clean)
    if (hasAt || hasDot) {
      studioEvents.scanFailed({
        reason: hasAt ? 'has_at_symbol' : 'looks_like_email_or_url',
        handle: clean.substring(0, 50),
      })
      setLaunchError('Tape juste ton pseudo Instagram (sans @ ni email)')
      return
    }
    setLaunchError(null)
    setLaunching(true)
    studioEvents.scanStarted({ handle_length: clean.length, guest: guestMode })
    try {
      // Mode invité : POST /api/studio/init-guest (pas d'auth), avec
      // guest_session_id pour ownership. Sinon mode normal avec JWT.
      const isGuest = guestMode && !user
      const endpoint = isGuest ? '/api/studio/init-guest' : '/api/studio/init'
      const headers = { 'Content-Type': 'application/json' }
      if (!isGuest) Object.assign(headers, await authHeaders())
      const vertical = getVertical()
      const payload = isGuest
        ? { ig_username: clean, guest_session_id: getGuestSessionId(), ...(vertical ? { vertical } : {}), deal: isDealVisitor() }
        : { ig_username: clean, ...(vertical ? { vertical } : {}), deal: isDealVisitor() }
      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
      setSession({ id: data.id, status: 'pending', progress_pct: 0, ig_username: clean, _is_guest: isGuest })
    } catch (e) {
      setLaunchError(e.message)
      studioEvents.scanFailed({ reason: 'init_api_error', error: e.message?.substring(0, 100), guest: guestMode })
    } finally {
      setLaunching(false)
    }
  }

  // ── AUTO-LANCEMENT POST-SIGNUP ──────────────────────────────
  // Si l'user a saisi son @ (ou choisi sa niche) AVANT de créer son compte
  // (hero LP ou étape 1 du gate), le scan part tout seul dès que la session
  // Supabase est active : zéro re-saisie, zéro clic en plus. La promesse
  // "crée ton compte, Merlin lance l'analyse juste après" est tenue
  // littéralement. Le flag studio_autolaunch évite de déclencher pour les
  // users qui reviennent avec un vieux prefill en localStorage.
  const autoLaunchedRef = useRef(false)
  // autoLaunching : écran de chargement optimiste pendant le claim /
  // fallback post-signup. Sans ça, l'intake flashait 3-8 secondes entre
  // la création du compte et le setSession et l'user croyait être revenu
  // en arrière (bug rapporté + reproduit Playwright le 2026-06-10).
  const [autoLaunching, setAutoLaunching] = useState(false)
  const [autoLaunchLabel, setAutoLaunchLabel] = useState('')
  useEffect(() => {
    if (autoLaunchedRef.current || preview) return
    if (!user?.id || loadingSession || session || launching) return
    let wantsLaunch = false, prefHandle = null, prefNicheMacro = null, prefNicheFine = null
    try {
      wantsLaunch = localStorage.getItem('studio_autolaunch') === '1'
      prefHandle = localStorage.getItem('studio_prefill_handle')
      prefNicheMacro = localStorage.getItem('studio_prefill_niche_macro')
      prefNicheFine = localStorage.getItem('studio_prefill_niche_fine')
    } catch {}
    if (!wantsLaunch) return
    autoLaunchedRef.current = true
    let claimPending = false, anonId = null
    try {
      claimPending = localStorage.getItem('studio_claim_after_signup') === '1'
      anonId = localStorage.getItem('studio_anon_id')
      localStorage.removeItem('studio_autolaunch')
      localStorage.removeItem('studio_prefill_niche_macro')
      localStorage.removeItem('studio_prefill_niche_fine')
      localStorage.removeItem('studio_claim_after_signup')
      localStorage.removeItem('studio_guest_session_id')
    } catch {}

    setAutoLaunching(true)
    setAutoLaunchLabel(prefNicheMacro ? '_generic' : (prefHandle || ''))

    // ── CLAIM DU SCAN PRÉ-LANCÉ ──────────────────────────────────
    // Si un scan invité a démarré pendant le signup (étape @ du gate ou
    // hero LP), on le rattache au compte fraîchement créé et on reprend
    // là où il en est. Fallback : si le claim ne donne rien (init invité
    // raté, rate limit IP), on lance un scan normal avec le @ pré-rempli.
    ;(async () => {
      try {
        if (claimPending && anonId) {
          try {
            const h = await authHeaders()
            await fetch(`${API_URL}/api/studio/claim-guest-session`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', ...h },
              body: JSON.stringify({ guest_session_id: anonId, deal: isDealVisitor() }),
            })
            const res = await fetch(`${API_URL}/api/studio/session`, { headers: h })
            const data = await res.json()
            if (data?.id) { setSession(data); return }
          } catch {}
        }
        // Lancement normal (pas de pré-scan, ou claim sans résultat)
        if (prefNicheMacro) {
          await launchGeneric(prefNicheMacro, prefNicheFine || null)
        } else if (prefHandle) {
          setHandle(prefHandle)
          await launch(prefHandle)
        }
      } finally {
        // Une session posée = le render l'affiche. Sinon (échec total),
        // on relâche vers l'intake avec l'erreur de lancement visible.
        setAutoLaunching(false)
      }
    })()
  }, [user?.id, loadingSession, session, launching, preview])

  // ── AUTO-RESCAN WIN-BACK (?rescan=1) ─────────────────────────
  // Le mail win-back promet « reviens voir ta note » : le lien relance
  // AUTOMATIQUEMENT un scan frais avec son @ précédent (zéro re-saisie),
  // au lieu de laisser le résilié sur sa vieille note. L'endpoint /init
  // n'a pas de restriction de plan : un ex-abonné redevenu free re-scanne.
  const autoRescanRef = useRef(false)
  useEffect(() => {
    if (autoRescanRef.current || preview) return
    if (!user?.id || loadingSession || launching) return
    let wants = false
    try { wants = new URLSearchParams(window.location.search).get('rescan') === '1' } catch { /* URL illisible */ }
    if (!wants) return
    const prevHandle = session?.ig_username || session?.persona?.ig_username
    if (!session || session.status !== 'done' || !prevHandle || session.persona?.is_generic) return
    autoRescanRef.current = true
    try {
      const p = new URLSearchParams(window.location.search)
      p.delete('rescan')
      const q = p.toString()
      window.history.replaceState({}, '', window.location.pathname + (q ? `?${q}` : '') + window.location.hash)
    } catch { /* nettoyage best effort */ }
    ;(async () => {
      try {
        studioEvents.scanStarted({ handle_length: prevHandle.length, guest: false, trigger: 'winback_rescan' })
        const res = await fetch(`${API_URL}/api/studio/init`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
          body: JSON.stringify({ ig_username: prevHandle }),
        })
        const data = await res.json()
        if (res.ok && data?.id) setSession({ id: data.id, status: 'pending', progress_pct: 0, ig_username: prevHandle })
      } catch { /* échec : la vieille note reste affichée, pas pire qu'avant */ }
    })()
  }, [user?.id, loadingSession, session, launching, preview])

  function resetSession() {
    if (preview) { window.location.href = '/?studio=preview'; return }
    if (!confirm('Recommencer avec un autre compte ?')) return
    setSession(null)
    setHandle('')
  }

  function copyToClipboard(text, key) {
    navigator.clipboard.writeText(text || '').then(() => {
      setCopyState({ [key]: true })
      setTimeout(() => setCopyState({}), 2000)
    })
  }

  // Magic link en flux token_hash : le lien du mail atterrit ici AVANT tout
  // (l'user n'est pas encore loggué). On montre une confirmation 1-clic
  // anti-préchargement, qui connecte puis rattache le scan invité depuis
  // l'URL (gs/h) — indispensable en webview Insta → Safari.
  const _magicConfirm = readMagicConfirm()
  if (_magicConfirm) {
    return <MagicLinkConfirm params={_magicConfirm} />
  }

  // Tant que l'auth context résout l'état de session Supabase → spinner.
  // Sans ça on flasherait brièvement l'auth gate à un user déjà loggué.
  if (auth.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg">
        <img src={merlinImg} alt="" className="w-12 h-12 object-contain animate-pulse"
          style={{ filter: 'drop-shadow(0 4px 16px rgba(var(--merlin-accent-rgb, 254,130,4),0.30))' }} />
      </div>
    )
  }

  // ── EARLY RETURNS (après TOUS les hooks : règle des hooks React) ──
  // FICHE-CADEAU AVANT le routage payant : un free/past connecté qui arrive
  // du mail analyse offerte voit la fiche plein écran + le paywall v8 câblé
  // (StudioAnalysisPaywallModal écoute l'event du CTA). Les payants ont déjà
  // tout, mais on la sert aussi si le lien leur arrive (cohérent).
  if (user && vaFiche) {
    return (
      <>
        <VaGiftFiche analysisId={vaFiche.id} kind={vaFiche.kind} onClose={closeVaFiche} />
        <StudioAnalysisPaywallModal />
      </>
    )
  }
  if (isPaid && (user || paidPreview)) {
    return <StudioPro onBack={() => { window.location.href = '/' }} />
  }
  // Modal post-paiement (l'user revient de Stripe, son studio_plan n'est
  // probablement pas encore lu).
  if (paymentSuccess && user) {
    return <PaymentSuccessModal />
  }

  // ── SESSION INVITÉ (flow @ d'abord, compte à la fin) ─────────
  // Le scan tourne SANS compte : bibliothèque + progression pendant le
  // scan, puis écran "tes formats sont prêts" qui demande la création de
  // compte (pic de motivation). Rendu AVANT le gate.
  if (!user && session?._is_guest && !preview) {
    const hasFormats = (session.formats || []).some(f => !f.locked)
    // Le score (radar) arrive AVANT les formats (~11s) : dès qu'il est là, on
    // affiche le résultat (le wow), les formats se remplissent juste après.
    const hasScore = !!(session.persona?.score && Array.isArray(session.persona.score.dimensions) && Number.isFinite(session.persona.score.global))
    if ((session.status === 'pending' || session.status === 'running') && !hasFormats && !hasScore) {
      return <StudioLoading session={session} />
    }
    if (hasFormats || hasScore || session.status === 'done') {
      // Le score + les 3 formats TROUVÉS s'affichent sans compte (preuve).
      // La LECTURE des formats (hook/structure/script) = essai. Pas de modal auto.
      return <>
        <GuestResults session={session} />
        <GuestSignupModal signUp={signUpWithEmail} signIn={signInWithEmail} google={signInWithGoogle} />
      </>
    }
    // status error (ex: @ introuvable sur Instagram) : nettoie TOUT, y
    // compris le prefill (sinon le gate s'ouvrait à l'étape compte avec le
    // mauvais @), et passe le message d'erreur au champ @.
    if (session.status === 'error') {
      try {
        localStorage.removeItem('studio_guest_session_id')
        localStorage.removeItem('studio_prefill_handle')
        localStorage.setItem('studio_gate_error', session.error_message || `Impossible d'analyser ce compte. Vérifie l'orthographe du @ et réessaie.`)
      } catch {}
      setSession(null)
      return null
    }
  }

  // Anti-flash : une session invité est en cours de restauration (arrivée
  // depuis la LP ou refresh pendant le scan) → loading optimiste, PAS le
  // gate (sinon flash "crée ton compte" d'1s avant la bibliothèque).
  if (!user && !session && !preview && loadingSession && (() => { try { return !!localStorage.getItem('studio_guest_session_id') } catch { return false } })()) {
    const optimisticHandle = (() => { try { return localStorage.getItem('studio_prefill_handle') || '...' } catch { return '...' } })()
    return <StudioLoading session={{ ig_username: optimisticHandle, status: 'pending', progress_pct: 5, current_step: 'Connexion à ton analyse' }} />
  }

  // Non-auth → auth gate AVANT loadingSession (loadingSession ne se résout
  // jamais sans user, sinon spinner infini sur la première visite).
  // EXCEPTION mode invité : pas d'auth gate, l'user arrive direct sur intake.
  if (!user && !preview && !guestMode) {
    return <StudioAuthGate signUp={signUpWithEmail} signIn={signInWithEmail} google={signInWithGoogle}
      onGuestScanStarted={(id, cleanHandle) => setSession({ id, status: 'pending', progress_pct: 0, ig_username: cleanHandle, _is_guest: true })} />
  }

  if (loadingSession) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg">
        <img src={merlinImg} alt="" className="w-12 h-12 object-contain animate-pulse"
          style={{ filter: 'drop-shadow(0 4px 16px rgba(var(--merlin-accent-rgb, 254,130,4),0.30))' }} />
      </div>
    )
  }

  // Wrap les renders qui peuvent ouvrir le paywall avec <StudioUpgradeModal />
  if (!session) {
    // Auto-lancement en cours (claim du pré-scan ou lancement fallback) :
    // écran de chargement OPTIMISTE au lieu de laisser flasher l'intake.
    // Session factice juste pour l'affichage, remplacée par la vraie au
    // setSession quelques secondes plus tard.
    if (autoLaunching) {
      return <StudioLoading session={{
        ig_username: autoLaunchLabel || '...',
        status: 'pending',
        progress_pct: 5,
        current_step: 'Connexion à ton analyse',
      }} />
    }
    // Mode invité (?guest=1) : land directement sur la page Découvrir
    // (grid de 42 reels viraux). Click sur un reel ouvre la modal signup
    // "Crée ton compte pour personnaliser". C'est la stratégie browse-first
    // : démontre le volume + qualité de la library AVANT de demander le
    // signup. Pattern Lovable/Replit (case studies +200-240% activation).
    if (guestMode && !user) {
      return <>
        <StudioGuestDiscover handle={handle} setHandle={setHandle} launch={launch} launching={launching} error={launchError} />
        <GuestSignupModal signUp={signUpWithEmail} signIn={signInWithEmail} google={signInWithGoogle} />
      </>
    }
    return <>
      <StudioIntake handle={handle} setHandle={setHandle} launch={launch} launchGeneric={launchGeneric} launching={launching} error={launchError} />
      <StudioUpgradeModal />
      <StudioAnalysisPaywallModal />
      <GuestSignupModal signUp={signUpWithEmail} signIn={signInWithEmail} google={signInWithGoogle} />
      <PasswordRecoveryModal />
    </>
  }

  if ((session.status === 'pending' || session.status === 'running') && !(session.formats || []).some(f => !f.locked)) {
    return <StudioLoading session={session} />
  }

  if (session.status === 'error') {
    return <StudioError session={session} onReset={resetSession} />
  }

  return <>
    <StudioReady session={session} copyToClipboard={copyToClipboard} copyState={copyState} resetSession={resetSession} airtableReady={airtableReady} />
    <StudioUpgradeModal />
    <StudioAnalysisPaywallModal />
    <GuestSignupModal signUp={signUpWithEmail} signIn={signInWithEmail} google={signInWithGoogle} />
    <PasswordRecoveryModal />
  </>
}

// ═══════════════════════════════════════════════════════════════
// INTAKE
// ═══════════════════════════════════════════════════════════════
// LEGAL FOOTER — petit, discret, présent partout (auth, intake, ready)
// ═══════════════════════════════════════════════════════════════
// MODE INVITÉ → modal signup affichée quand l'invité clique un paywall.
// Après signup réussi, on POST /api/studio/claim-guest-session pour link
// l'ancienne session guest au nouveau user_id (récup du scan). Puis on
// exit guest mode et la page re-render avec auth normale → upgrade modal.
// ═══════════════════════════════════════════════════════════════
// GUEST RESULTS — les 3 formats DÉBLOQUÉS sans compte (2026-06-12).
// Mobile = instantané : la preuve d'abord (vidéo, hook, pourquoi), le
// compte ensuite (scripts + trame + sauvegarde). S'affiche dès la
// sélection (~50s, preview) pendant que les scripts s'écrivent.
// Plus AUCUNE modal auto : la data a montré qu'elle fait fuir (2/3 des
// pertes voyaient la modal sans même tenter).
// ═══════════════════════════════════════════════════════════════
// Résume la structure narrative en lignes ultra-courtes "Fonction · Seau"
// (les Items consécutifs d'une liste sont regroupés en "Items ×6").
const SEAU_FR_LABELS = {
    curiosite_gap: 'Curiosité', peur_profonde: 'Peur profonde', douleur_quotidienne: 'Douleur du quotidien',
    desir_tangible: 'Désir concret', desir_identitaire: 'Désir d\'identité', honte_secrete: 'Honte secrète',
    vehicule_solution: 'Solution', preuve_credibilite: 'Crédibilité', preuve_sociale: 'Preuve sociale',
    tribu_identitaire: 'Appartenance', tribu_aspirationnelle: 'Aspiration', ennemi_visible: 'Ennemi désigné',
    ennemi_systemique: 'Ennemi système', ennemi_interieur: 'Doute intérieur', emotion_dominante: 'Émotion',
    urgence_rarete: 'Urgence', identite_visee: 'Identité visée', levier_autorite: 'Autorité', nostalgie: 'Nostalgie',
}
const prettySeau = (v) => SEAU_FR_LABELS[v] || (v || '').replace(/_/g, ' ').replace(/^./, c => c.toUpperCase())

function summarizeBeats(beats) {
  const pretty = prettySeau
  const groups = []
  for (const b of (beats || [])) {
    if (!b.fonction) continue
    const last = groups[groups.length - 1]
    if (last && last.fonction === b.fonction) {
      last.count++
      if (b.seau_active && !last.seaux.includes(b.seau_active)) last.seaux.push(b.seau_active)
    } else {
      groups.push({ fonction: b.fonction, count: 1, seaux: b.seau_active ? [b.seau_active] : [] })
    }
  }
  const rows = groups.map(g => ({
    label: g.count > 1 ? `${g.fonction} ×${g.count}` : g.fonction,
    seau: pretty(g.seaux[0] || ''),
  }))
  // ≤4 lignes : début, 1-2 du milieu, fin
  if (rows.length <= 4) return rows
  return [rows[0], ...rows.slice(1, -1).slice(0, 2), rows[rows.length - 1]]
}

function GuestFormatCard({ format, idx, onUnlock, sessionId }) {
  const src = format.source || {}
  const thumb = getSupabaseThumb({ lien: src.lien })
  const isPreview = !format.why_for_you && !format.hook_adapted
  const [videoUrl, setVideoUrl] = useState(null)
  const [videoLoading, setVideoLoading] = useState(false)
  const [paused, setPaused] = useState(false)
  const [muted, setMuted] = useState(false) // son ON par défaut (clic explicite)
  const videoRef = useRef(null)

  // Tap sur la miniature → vraie vidéo (URL CDN fraîche résolue côté
  // backend, restreinte aux formats de cette session invitée)
  async function playVideo() {
    if (videoUrl || videoLoading) return
    setVideoLoading(true)
    try {
      const sc = extractShortcode(src.lien)
      const guestId = getGuestSessionId()
      const res = await fetch(`${API_URL}/api/studio/guest-video?session=${sessionId}&guest_id=${encodeURIComponent(guestId)}&shortcode=${sc}`)
      const d = await res.json()
      if (d?.url) {
        setVideoUrl(d.url)
        try { studioEvents.formatVideoPlayed({ guest: true, format_idx: idx }) } catch {}
      }
    } catch {}
    setVideoLoading(false)
  }

  return (
    <article className="rounded-2xl bg-white border border-border overflow-hidden shadow-sm">
      <div className="relative" style={{ aspectRatio: '16/10', background: 'linear-gradient(135deg, var(--fmt-ph-1, #1e1145), var(--fmt-ph-2, #2d1b69))' }}
        onClick={!videoUrl ? playVideo : undefined} role={!videoUrl ? 'button' : undefined}>
        {videoUrl ? (
          <>
            <video ref={videoRef} src={videoUrl} autoPlay playsInline loop
              className="absolute inset-0 w-full h-full object-cover bg-black"
              onClick={(e) => {
                e.stopPropagation()
                const v = e.currentTarget
                if (v.paused) { v.play().catch(() => {}); setPaused(false) } else { v.pause(); setPaused(true) }
              }} />
            {paused && (
              <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <span className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.92)' }}>
                  <span className="text-purple-900 ml-0.5">▶</span>
                </span>
              </span>
            )}
            <button onClick={(e) => {
              e.stopPropagation()
              const v = videoRef.current
              if (v) { v.muted = !v.muted; setMuted(v.muted) }
            }}
              className="absolute bottom-2 right-2 w-8 h-8 rounded-full flex items-center justify-center text-sm"
              style={{ background: 'rgba(15,10,26,0.7)', backdropFilter: 'blur(6px)' }}>
              {muted ? '🔇' : '🔊'}
            </button>
          </>
        ) : (
          <>
        {thumb && <img src={thumb} alt="" className="absolute inset-0 w-full h-full object-cover"
          onError={(e) => { e.currentTarget.style.display = 'none' }} />}
        <span className="absolute inset-0 flex items-center justify-center">
          <span className="w-12 h-12 rounded-full flex items-center justify-center"
            style={{ background: 'rgba(255,255,255,0.92)', boxShadow: '0 4px 14px rgba(0,0,0,0.25)' }}>
            <span className="text-purple-900 ml-0.5" style={{ fontSize: videoLoading ? '12px' : '16px' }}>{videoLoading ? '⏳' : '▶'}</span>
          </span>
        </span>
          </>
        )}
        {!videoUrl && <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, rgba(15,10,26,0.15), rgba(15,10,26,0.55))' }} />}
        <div className="absolute top-2 left-2 flex items-center gap-1 pointer-events-none">
          <span className="px-1.5 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-wider text-white" style={{ background: 'rgba(var(--merlin-accent-rgb, 254,130,4),0.9)' }}>#{idx + 1}</span>
          {src.style_production && (
            <span className="px-1.5 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-wider text-white" style={{ background: 'rgba(var(--merlin-violet-rgb, 124,58,237),0.88)' }}>
              {src.style_production === 'Face caméra' ? '🎤' : src.style_production === 'Voix off' ? '🎙' : '✨'} {src.style_production}
            </span>
          )}
        </div>
        {src.vues > 0 && (
          <span className="absolute top-2 right-2 px-1.5 py-0.5 rounded-md text-[10px] font-bold text-white font-mono" style={{ background: 'rgba(15,10,26,0.75)' }}>
            {src.vues >= 1e6 ? `${(src.vues / 1e6).toFixed(1)}M` : `${Math.round(src.vues / 1000)}k`} vues
          </span>
        )}
        {src.facile_a_refaire && (
          <span className="absolute bottom-2 left-2 px-1.5 py-0.5 rounded-md text-[9px] font-bold text-white" style={{ background: 'rgba(16,185,129,0.92)' }}>✓ Facile à refaire</span>
        )}
        {!videoUrl && <div className="absolute bottom-2 right-2 text-[10px] text-white/80 font-medium">{src.format_label || ''}</div>}
      </div>
      <div className="p-4">
        {(() => {
          // Meilleur hook affichable : adapté (perso) → verbatim si substantiel
          // → template À TROUS rendu en chips variables (jamais de crochets
          // bruts ni de "décrocheté" illisible : analyse résiliations 12/07,
          // un squelette présenté comme adapté = « pas du tout ciblé »).
          const adapted = String(format.hook_adapted || '').trim()
          if (adapted.length > 8 && !/\[[^\]]+\]/.test(adapted)) {
            return (<>
              <p className="text-[10px] uppercase tracking-wider font-bold text-ink-faint mb-1">Hook adapté à ton compte</p>
              <p className="text-sm font-bold text-ink leading-snug">« {adapted.slice(0, 120)} »</p>
            </>)
          }
          const verbatim = String(src.hook_original || '').trim()
          const template = String(format.hook_template || '').trim()
          if (verbatim.length >= 12 && !template) {
            return (<>
              <p className="text-[10px] uppercase tracking-wider font-bold text-ink-faint mb-1">Hook</p>
              <p className="text-sm font-bold text-ink leading-snug">« {verbatim.slice(0, 120)} »</p>
            </>)
          }
          const base = template || verbatim
          return (<>
            <p className="text-[10px] uppercase tracking-wider font-bold text-ink-faint mb-1">Hook à adapter à ton compte</p>
            <p className="text-sm font-bold text-ink leading-snug">« {String(base).slice(0, 150).split(/(\[[^\]]+\])/g).map((part, k) =>
              part.startsWith('[') && part.endsWith(']')
                ? <span key={k} className="inline-block px-1.5 py-0.5 mx-0.5 rounded-md text-[0.9em] font-bold" style={{ background: 'rgba(var(--merlin-accent-rgb, 254,130,4),0.14)', color: '#d97206' }}>{part.slice(1, -1)}</span>
                : <span key={k}>{part}</span>
            )} »</p>
          </>)
        })()}
        {/* L'ANALYSE en 3 chips : la valeur de Merlin, format mobile */}
        <div className="flex flex-wrap gap-1.5 mt-2.5">
          {src.mecanisme_viral && (
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: 'rgba(var(--merlin-violet-rgb, 124,58,237),0.10)', color: 'var(--merlin-violet-2, #6d28d9)' }}>
              🧠 {String(src.mecanisme_viral).slice(0, 30)}
            </span>
          )}
          {src.emotion_primaire && (
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: 'rgba(var(--merlin-accent-rgb, 254,130,4),0.10)', color: '#b45309' }}>
              ⚡ {String(src.emotion_primaire).slice(0, 20)}
            </span>
          )}
          {src.engagement > 0 && (
            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ background: 'rgba(16,185,129,0.10)', color: '#047857' }}>
              📈 {Number(src.engagement).toFixed(1)}% d'engagement
            </span>
          )}
        </div>
        {/* Structure narrative synthétique : Fonction · levier psycho */}
        {(format.beats_raw || []).length > 0 && (
          <div className="mt-2.5 px-3 py-2.5 rounded-lg" style={{ background: 'rgba(30,17,69,0.04)', border: '1px solid rgba(30,17,69,0.08)' }}>
            <p className="text-[9.5px] uppercase tracking-wider font-bold text-ink-faint mb-1.5">📐 Structure du format</p>
            <div className="space-y-1">
              {summarizeBeats(format.beats_raw).map((r, j) => (
                <div key={j} className="flex items-center gap-2 text-[11px]">
                  <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: 'var(--merlin-violet, #7c3aed)' }} />
                  <span className="font-bold text-ink">{r.label}</span>
                  {r.seau && <span className="text-ink-muted">· {r.seau}</span>}
                </div>
              ))}
            </div>
          </div>
        )}
        {format.why_for_you ? (
          <p className="text-[11.5px] text-ink-muted mt-2">✨ {format.why_for_you}</p>
        ) : isPreview ? (
          <p className="text-[11px] mt-2 flex items-center gap-1.5" style={{ color: '#d97206' }}>
            <span className="inline-block w-3 h-3 border-2 border-orange/30 border-t-orange rounded-full animate-spin" />
            Merlin écrit ton script personnalisé…
          </p>
        ) : null}
        <div className="grid grid-cols-2 gap-2 mt-3">
          <button onClick={() => onUnlock('script', idx)}
            className="py-2.5 rounded-lg text-[11px] font-bold text-white flex items-center justify-center gap-1.5"
            style={{ background: 'linear-gradient(135deg, var(--merlin-violet, #7c3aed), var(--merlin-violet-2, #6d28d9))' }}>
            🔒 Script complet
          </button>
          <button onClick={() => onUnlock('trame', idx)}
            className="py-2.5 rounded-lg text-[11px] font-bold flex items-center justify-center gap-1.5"
            style={{ background: 'rgba(var(--merlin-violet-rgb, 124,58,237),0.08)', color: 'var(--merlin-violet-2, #6d28d9)', border: '1px solid rgba(var(--merlin-violet-rgb, 124,58,237),0.25)' }}>
            🔒 Trame à filmer
          </button>
        </div>
      </div>
    </article>
  )
}

// ═══ PROJECTION — « Avec Merlin et les bons formats » (maquette Antonin
// 24/07) : courbe ORANGE des abonnés, point de départ valorisé, flèche du
// temps « 3 mois » en vert, synthèse « D'ici 3 mois » en 3 lignes. Abonnés
// gagnés = vues projetées × ~1 abonné pour 100 vues. Partagé entre le mur
// invité (bras A) et l'écran connecté (au-dessus du CTA améliorer).
// `formats` accepte les formats de session ({vues}) comme les drops ({source.vues}).
export function AccountProjection({ persona, formats, className = '' }) {
  const fmts = Array.isArray(formats) ? formats : []
  const vueOf = (f) => Number(f?.vues || f?.views || f?.source?.vues || 0)
  const top = fmts.filter(f => !f.locked).slice(0, 3)
  const vuesArr = top.map(vueOf).filter(v => v > 0).sort((a, b) => a - b)
  const nicheMedian = vuesArr.length ? vuesArr[Math.floor(vuesArr.length / 2)] : null
  if (nicheMedian == null) return null
  const VIEW_TO_FOLLOW = 0.01
  const WEEKLY_POSTS = 3
  const followers = Number(persona?.followers || persona?.profile?.followers || persona?.nombre_abonnes || 0) || null
  const userAvg = Number(persona?.avg_views || persona?.median_views || persona?.reels_median_views || 0) || null
  // PLAFOND DE CRÉDIBILITÉ (Antonin 11/08 : « 1,4M de vues en moyenne pour
  // mon compte, abusé ») : la promesse chiffrée est bornée par les métriques
  // DU compte (10x ses vues actuelles max, repli sur les followers), jamais
  // la médiane brute des formats sources qui se compte en millions. Prudence
  // L121-2 en plus de la crédibilité.
  const base = userAvg || (followers ? Math.round(followers * 0.5) : null) || 2000
  const roundNice = (n) => n >= 200000 ? Math.round(n / 50000) * 50000 : n >= 20000 ? Math.round(n / 5000) * 5000 : Math.max(500, Math.round(n / 500) * 500)
  const target = roundNice(Math.min(nicheMedian, base * 10))
  const gained3m = Math.max(100, Math.round((target * WEEKLY_POSTS * 12 * VIEW_TO_FOLLOW) / 100) * 100)
  const followersIn3m = followers ? followers + gained3m : null
  // Gros comptes / formats sources plus faibles que le compte : une projection
  // qui rétrograde (2,6M d'abonnés → « +7 200 ») dessert la crédibilité.
  // Pas d'ambition à montrer = pas de projection du tout.
  if (userAvg && target <= userAvg * 1.2) return null
  if (followers && followers > gained3m * 20) return null
  const fmtK = (n) => n >= 1000 ? `${Math.round(n / 1000)}k` : String(n)
  return (
    <div className={`rounded-2xl p-3.5 ${className}`} style={{ border: '1px solid rgba(124,58,237,0.25)', background: 'linear-gradient(160deg, rgba(124,58,237,0.06), rgba(254,130,4,0.04))' }}>
      <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: '#7c3aed' }}>Avec Merlin et les bons formats</p>
      <p className="text-[14px] font-extrabold text-ink mb-2">Ton compte peut aller :</p>
      {/* Courbe ABONNÉS orange + point de départ valorisé + flèche du temps */}
      <div className="relative">
        <svg width="100%" height="104" viewBox="0 0 320 104" preserveAspectRatio="none">
          <path d="M8,80 C26,79 38,80 50,77 C68,73 76,74 94,72 C116,69 126,64 144,60 C160,56 172,57 188,52 C208,46 218,39 236,34 C252,30 264,31 280,26 C298,20 308,16 316,13"
            fill="none" stroke="var(--merlin-accent, #fe8204)" strokeWidth="3" strokeLinecap="round" />
          <circle cx="8" cy="80" r="4.5" fill="var(--merlin-accent, #fe8204)" />
          <circle cx="316" cy="13" r="4.5" fill="var(--merlin-accent, #fe8204)" />
          {/* Flèche du temps */}
          <line x1="8" y1="97" x2="306" y2="97" stroke="rgba(15,16,64,0.30)" strokeWidth="1.5" />
          <path d="M306,93 L314,97 L306,101 Z" fill="rgba(15,16,64,0.30)" />
        </svg>
        {followers && <span className="absolute left-[14px] top-[62px] text-[11px] font-extrabold text-ink">{followers.toLocaleString('fr-FR')}</span>}
        <span className="absolute right-0 top-0 text-[12px] font-extrabold" style={{ color: 'var(--merlin-accent, #fe8204)' }}>{followersIn3m ? `~${followersIn3m.toLocaleString('fr-FR')} abonnés` : `+${gained3m.toLocaleString('fr-FR')} abonnés`}</span>
        <span className="absolute right-0 bottom-[-16px] text-[11px] font-extrabold" style={{ color: '#16a34a' }}>3 mois</span>
      </div>
      <div className="h-3" />
      {/* Le texte synthétique */}
      <p className="text-[13px] font-bold text-ink mt-3">D'ici 3 mois :</p>
      <div className="text-[13px] leading-relaxed text-ink mt-0.5">
        <p className="my-0.5">1 vidéo à <strong style={{ color: '#16a34a' }}>+{fmtK(target)}</strong> par semaine</p>
        {userAvg ? <p className="my-0.5">De {userAvg.toLocaleString('fr-FR')} à <strong style={{ color: '#16a34a' }}>{target.toLocaleString('fr-FR')} vues</strong> en moyenne</p>
          : <p className="my-0.5"><strong style={{ color: '#16a34a' }}>{target.toLocaleString('fr-FR')} vues</strong> en moyenne par vidéo</p>}
        {followersIn3m ? <p className="my-0.5">De {followers.toLocaleString('fr-FR')} à <strong style={{ color: 'var(--merlin-accent, #fe8204)' }}>~{followersIn3m.toLocaleString('fr-FR')} abonnés</strong> <span className="text-ink-muted">(≈1 abonné pour 100 vues)</span></p>
          : <p className="my-0.5"><strong style={{ color: 'var(--merlin-accent, #fe8204)' }}>+{gained3m.toLocaleString('fr-FR')} abonnés</strong> <span className="text-ink-muted">(≈1 abonné pour 100 vues)</span></p>}
      </div>
      <p className="text-[9.5px] text-ink-faint mt-2">Vues réelles des comptes analysés dans notre base · estimation, résultats non garantis</p>
    </div>
  )
}

// ═══ ANALYSE COMPTE — "noté sur 100" + radar (le wow GRATUIT) ═══
// Données : session.persona.score (calculé au scan, 100% métadonnées, 0 vidéo).
// gated (bras A note_wall) : note globale EN CLAIR, radar + détail des
// dimensions FLOUTÉS derrière « Tes 2 points faibles sont identifiés »,
// clic n'importe où sur le flouté = création de compte. Même carte, même
// place, mêmes tailles que le contrôle : seule la lisibilité du détail change.
export function AccountScore({ score, username, sessionId, compact = false, hideCta = false, onCta = null, gated = false }) {
  const valid = score && Array.isArray(score.dimensions) && score.dimensions.length >= 3 && Number.isFinite(score.global)
  function openGate() {
    try { studioEvents.unlockCtaClicked({ trigger: 'note_wall_locked_dims' }) } catch { /* tracking only */ }
    try { window.dispatchEvent(new CustomEvent('studio:open-guest-signup', { detail: { trigger: 'note_wall_locked_dims' } })) } catch { /* no-op */ }
  }
  // Tracking : le radar /100 affiché = le wow gratuit. Mesure combien de
  // gens voient vraiment leur score (vs combien bouncent avant).
  useEffect(() => { if (valid) { try { studioEvents.scoreViewed({ global: score?.global, username }) } catch {} } }, [valid])
  // Pendant le scan le score n'est pas encore prêt (il arrive en fin
  // d'analyse) : on montre un état "en cours" au lieu de rien.
  if (!valid) {
    return (
      <div className="mb-4 rounded-2xl p-4 flex items-center gap-3" style={{ background: 'linear-gradient(135deg, #ffffff, #fbf8ff)', border: '1px solid rgba(var(--merlin-violet-rgb, 124,58,237),0.18)' }}>
        <span className="w-5 h-5 border-2 border-purple-300 border-t-purple-600 rounded-full animate-spin shrink-0" />
        <div className="min-w-0">
          <p className="text-[13px] font-bold text-ink">On note {username ? `@${username}` : 'ton compte'} sur 100…</p>
          <p className="text-[11px] text-ink-muted leading-snug">Engagement, niche, offre, monétisation. Encore quelques secondes.</p>
        </div>
      </div>
    )
  }
  const dims = score.dimensions
  const N = dims.length
  const size = 230, cx = size / 2, cy = size / 2, R = 76
  const ang = (i) => (Math.PI * 2 * i) / N - Math.PI / 2
  const pt = (i, r) => [cx + Math.cos(ang(i)) * r, cy + Math.sin(ang(i)) * r]
  const ring = (frac) => dims.map((_, i) => pt(i, R * frac).join(',')).join(' ')
  const valPoly = dims.map((d, i) => pt(i, R * (Math.max(0, Math.min(100, d.value)) / 100)).join(',')).join(' ')
  const g = score.global
  const gColor = g >= 65 ? '#16a34a' : g >= 50 ? 'var(--merlin-accent, #fe8204)' : '#dc2626'
  return (
    <div className={compact ? 'rounded-2xl p-3' : 'mb-4 rounded-2xl p-4 sm:p-5'} style={{ background: 'linear-gradient(135deg, #ffffff, #fbf8ff)', border: '1px solid rgba(var(--merlin-violet-rgb, 124,58,237),0.18)', boxShadow: '0 4px 20px rgba(var(--merlin-violet-rgb, 124,58,237),0.07)' }}>
      <div className="flex items-center gap-2 mb-1">
        <span className="text-[9px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full text-white" style={{ background: 'linear-gradient(135deg, var(--merlin-violet, #7c3aed), var(--merlin-violet-2, #5b21b6))' }}>✨ Analyse</span>
        <h2 className="text-[14px] font-bold text-ink">{username ? `@${username}` : 'Ton compte'} noté sur 100</h2>
      </div>
      <div className={gated ? 'flex items-center gap-1 sm:gap-3 cursor-pointer' : 'flex items-center gap-1 sm:gap-3'} onClick={gated ? openGate : undefined}>
        <svg viewBox={`-26 0 ${size + 52} ${size}`} className={compact ? 'w-[42%] max-w-[150px] shrink-0' : 'w-[58%] max-w-[230px] shrink-0'}
          style={gated ? { filter: 'blur(7px)', opacity: 0.6, pointerEvents: 'none', userSelect: 'none' } : undefined} aria-hidden={gated || undefined}>
          {[0.25, 0.5, 0.75, 1].map(f => <polygon key={f} points={ring(f)} fill="none" stroke="rgba(var(--merlin-violet-rgb, 124,58,237),0.13)" strokeWidth="1" />)}
          {dims.map((_, i) => { const [x, y] = pt(i, R); return <line key={i} x1={cx} y1={cy} x2={x} y2={y} stroke="rgba(var(--merlin-violet-rgb, 124,58,237),0.13)" strokeWidth="1" /> })}
          <polygon points={valPoly} fill="rgba(var(--merlin-accent-rgb, 254,130,4),0.22)" stroke="var(--merlin-accent, #fe8204)" strokeWidth="2" strokeLinejoin="round" />
          {dims.map((d, i) => { const [x, y] = pt(i, R * (Math.max(0, Math.min(100, d.value)) / 100)); return <circle key={i} cx={x} cy={y} r="3" fill="var(--merlin-accent, #fe8204)" /> })}
          {dims.map((d, i) => {
            const [x, y] = pt(i, R + 16)
            const anchor = Math.abs(x - cx) < 8 ? 'middle' : x > cx ? 'start' : 'end'
            return <text key={i} x={x} y={y} fontSize="9.5" fontWeight="700" textAnchor={anchor} dominantBaseline="middle" fill="#4a4458">{d.label}</text>
          })}
        </svg>
        <div className="flex-1 text-center">
          <div className={compact ? 'text-[34px] font-extrabold leading-none' : 'text-[44px] sm:text-[52px] font-extrabold leading-none'} style={{ color: gColor }}>{g}</div>
          <div className="text-[12px] font-bold text-ink-faint -mt-1">/ 100</div>
        </div>
      </div>
      {/* Barres par dimension (repliées en mode compact) */}
      {compact && (
        <details className="mt-2">
          <summary className="cursor-pointer list-none text-[11px] font-bold text-ink-muted flex items-center gap-1">
            <span>Le détail des 6 dimensions</span><span className="text-ink-faint">⌄</span>
          </summary>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 mt-2 pt-2 border-t border-purple-100">
            {dims.map(d => (
              <div key={d.key}>
                <div className="flex justify-between text-[10.5px] mb-0.5">
                  <span className="font-bold text-ink">{d.label}</span>
                  <span className="font-mono font-bold" style={{ color: d.value >= 65 ? '#16a34a' : d.value >= 45 ? '#b45309' : '#dc2626' }}>{d.value}</span>
                </div>
                <div className="h-1.5 rounded-full bg-ink/8 overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${d.value}%`, background: d.value >= 65 ? '#16a34a' : d.value >= 45 ? 'var(--merlin-accent, #fe8204)' : '#dc2626' }} />
                </div>
              </div>
            ))}
          </div>
        </details>
      )}
      {!compact && <div className={gated ? 'relative cursor-pointer' : undefined} role={gated ? 'button' : undefined} tabIndex={gated ? 0 : undefined}
        onClick={gated ? openGate : undefined}
        onKeyDown={gated ? (e) => { if (e.key === 'Enter' || e.key === ' ') openGate() } : undefined}>
        <div className="grid grid-cols-2 gap-x-4 gap-y-2 mt-3 pt-3 border-t border-purple-100"
          style={gated ? { filter: 'blur(7px)', opacity: 0.6, pointerEvents: 'none', userSelect: 'none' } : undefined} aria-hidden={gated || undefined}>
          {dims.map(d => (
            <div key={d.key}>
              <div className="flex justify-between text-[10.5px] mb-0.5">
                <span className="font-bold text-ink">{d.label}</span>
                <span className="font-mono font-bold" style={{ color: d.value >= 65 ? '#16a34a' : d.value >= 45 ? '#b45309' : '#dc2626' }}>{d.value}</span>
              </div>
              <div className="h-1.5 rounded-full bg-ink/8 overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${d.value}%`, background: d.value >= 65 ? '#16a34a' : d.value >= 45 ? 'var(--merlin-accent, #fe8204)' : '#dc2626' }} />
              </div>
            </div>
          ))}
        </div>
        {gated && (
          <div className="absolute inset-0 flex flex-col items-center justify-center pt-2">
            <span className="text-xl">🔒</span>
            <p className="text-[12px] font-bold text-ink mt-0.5">Tes 2 points faibles sont identifiés</p>
          </div>
        )}
      </div>}
      {/* CTA : l'analyse compte détaillée (plan par axe + créateurs + bios) est
          le livrable Pro+ (89€, essai 7j). Invité -> signup d'abord ;
          connecté -> paywall Pro+. Masqué dans l'analyse même. */}
      {!hideCta && (
        <button type="button" data-tour="score-improve" onClick={() => {
          try { studioEvents.scoreImproveClicked({ username, has_cta: !!onCta }) } catch {}
          // Handler custom fourni (ex: StudioPro -> aller à l'onglet Analyse).
          if (onCta) { onCta(); return }
          // Invité (pas de compte) : il doit d'abord créer un compte.
          if (typeof window !== 'undefined' && window.__STUDIO_HAS_USER !== true) {
            goToStudioCheckout(undefined, 'score_improve')
            return
          }
          // Connecté : l'analyse de compte est Pro+, pas Créateur.
          try { window.dispatchEvent(new CustomEvent('studio:open-analysis-paywall', { detail: { upgrade_to: 'pro_plus', code: 'free_no_analysis' } })) } catch {}
        }}
          className={compact ? 'w-full mt-2.5 py-2 rounded-xl font-bold text-[12px] flex items-center justify-center gap-1.5 transition-transform active:scale-[0.99]' : 'w-full mt-3.5 py-2.5 rounded-xl font-bold text-[12.5px] flex items-center justify-center gap-1.5 transition-transform active:scale-[0.99]'}
          style={{ background: 'linear-gradient(135deg, var(--merlin-violet, #7c3aed), var(--merlin-violet-2, #5b21b6))', color: '#fff', boxShadow: '0 4px 14px rgba(var(--merlin-violet-rgb, 124,58,237),0.28)' }}>
          📈 Découvre comment améliorer ton compte
        </button>
      )}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// FORMAT TEASER CARD — miniature EN CLAIR + métadonnées. NON jouable.
// Au clic : ouvre l'analyse complète du format (hook, script, structure,
// pourquoi ça marche) via `studio:open-format-detail`. Pour un INVITÉ, cette
// analyse s'ouvre FLOUTÉE avec une demande de création de compte (le mur =
// compte gratuit). Pour un compte connecté, elle s'ouvre en clair.
// ═══════════════════════════════════════════════════════════════
function FormatTeaserCard({ format, idx, trigger = 'format_teaser' }) {
  const src = format.source || {}
  // Poster avec retry sur la chaîne de fallback + spinner (beaucoup de
  // beat-frames PROD 404 sur le Storage dev → on retente le fallback).
  const candidates = useMemo(() => {
    const seen = new Set()
    return [formatPosterUrl(format), src.poster_url].filter(u => u && !seen.has(u) && seen.add(u))
  }, [format]) // eslint-disable-line react-hooks/exhaustive-deps
  const [pIdx, setPIdx] = useState(0)
  const [loaded, setLoaded] = useState(false)
  useEffect(() => { setPIdx(0); setLoaded(false) }, [candidates.join('|')])
  const poster = candidates[pIdx] || null
  const loadingThumb = !!poster && !loaded

  const fmtNum = (n) => !n ? '0' : n >= 1_000_000 ? `${(n / 1_000_000).toFixed(1)}M` : n >= 1_000 ? `${(n / 1_000).toFixed(0)}k` : String(n)
  const niche = src.niche || src.niche_nom || (Array.isArray(src.categorie_format) ? src.categorie_format[0] : '') || 'Format viral'
  const typeLabel = format.format_label || src.format_label || src.format_nom || 'Format viral'

  function open(e) {
    if (e) e.preventDefault()
    try { studioEvents.unlockCtaClicked({ trigger, format_idx: idx }) } catch {}
    try { window.dispatchEvent(new CustomEvent('studio:open-format-detail', { detail: { format, idx } })) } catch {}
  }

  return (
    <article className="merlin-format-card flex flex-col overflow-hidden">
      <button type="button" onClick={open}
        className="relative block w-full text-left overflow-hidden aspect-[4/5] sm:aspect-[9/16] max-h-[440px] group active:scale-[0.99] transition-transform studio-thumbnail"
        style={{ background: 'linear-gradient(135deg, var(--fmt-ph-1, #1e1145) 0%, var(--fmt-ph-2, #2d1b69) 100%)' }}>
        {/* Miniature EN CLAIR (non jouable) — fondu à l'apparition + spinner. */}
        {poster && (
          <img key={poster} src={poster} alt="" loading="lazy"
            onLoad={() => setLoaded(true)}
            onError={() => setPIdx(i => i + 1)}
            className="absolute inset-0 w-full h-full object-cover transition-opacity duration-300"
            style={{ opacity: loaded ? 1 : 0 }} />
        )}
        {/* Voile bas : lisibilité des badges */}
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: 'linear-gradient(180deg, rgba(15,10,26,0.28) 0%, rgba(15,10,26,0) 30%, rgba(15,10,26,0) 55%, rgba(15,10,26,0.85) 100%)' }} />

        {/* Spinner pendant le chargement de la miniature */}
        {loadingThumb && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="w-7 h-7 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          </div>
        )}

        {/* #idx + style de production — haut gauche */}
        <div className="absolute top-2 left-2 z-10 flex items-center gap-1">
          <span className="px-1.5 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-wider text-white" style={{ background: 'rgba(var(--merlin-accent-rgb, 254,130,4),0.92)', backdropFilter: 'blur(8px)' }}>#{String(idx + 1).padStart(2, '0')}</span>
          {src.style_production && (
            <span className="px-1.5 py-0.5 rounded-md text-[9px] font-bold text-white" style={{ background: 'rgba(var(--merlin-violet-rgb, 124,58,237),0.9)', backdropFilter: 'blur(8px)' }}>
              {src.style_production === 'Face caméra' ? '🎤' : src.style_production === 'Voix off' ? '🎙' : '✨'} {src.style_production}
            </span>
          )}
        </div>
        {/* Vues — haut droite */}
        {src.vues > 0 && (
          <div className="absolute top-2 right-2 z-10">
            <span className="px-1.5 py-0.5 rounded-md text-[10px] font-bold text-white font-mono" style={{ background: 'rgba(15,10,26,0.78)', backdropFilter: 'blur(8px)' }}>{fmtNum(src.vues)} vues</span>
          </div>
        )}

        {/* Affordance "voir l'analyse" — apparaît au survol/tap */}
        {!loadingThumb && (
          <div className="absolute inset-0 flex items-center justify-center z-10 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
            <span className="px-3 py-1.5 rounded-full text-[11px] font-bold text-white flex items-center gap-1.5" style={{ background: 'rgba(var(--merlin-violet-rgb, 124,58,237),0.95)', boxShadow: '0 4px 14px rgba(var(--merlin-violet-rgb, 124,58,237),0.5)' }}>
              🔍 Voir l'analyse
            </span>
          </div>
        )}

        {/* Bas : type + niche + badges de preuve */}
        <div className="absolute inset-x-0 bottom-0 p-2.5 z-10">
          {/* Badge matching honnête (Antonin 27/07) : « dans ta niche » vs
              « autre niche à adapter » — visible dès la vignette, pas seulement
              dans la fiche. C'est le moment « je me reconnais ». */}
          {typeof format?.cross === 'boolean' && (
            <span className="inline-block px-1.5 py-0.5 rounded text-[9px] font-extrabold text-white mb-1"
              style={{ background: format.cross ? 'rgba(254,130,4,0.95)' : 'rgba(16,159,110,0.95)', backdropFilter: 'blur(8px)' }}>
              {format.cross ? '↔ Autre niche, à adapter' : '✓ Dans ta niche'}
            </span>
          )}
          <span className="block text-[8.5px] uppercase tracking-wider font-bold text-white/75 mb-0.5 truncate">{niche}</span>
          <span className="block text-[12px] font-bold text-white leading-snug mb-1.5 line-clamp-2">{typeLabel}</span>
          <div className="flex items-center gap-1 flex-wrap">
            {src.mecanisme_viral && <span className="px-1.5 py-0.5 rounded text-[8.5px] font-bold text-white" style={{ background: 'rgba(var(--merlin-violet-rgb, 124,58,237),0.6)' }}>{src.mecanisme_viral}</span>}
            {src.arc_narratif && <span className="px-1.5 py-0.5 rounded text-[8.5px] font-bold text-white" style={{ background: 'rgba(var(--merlin-accent-rgb, 254,130,4),0.55)' }}>▣ {src.arc_narratif}</span>}
            {src.engagement > 0 && <span className="px-1.5 py-0.5 rounded text-[8.5px] font-bold text-white" style={{ background: 'rgba(16,185,129,0.6)' }}>◆ {src.engagement}% eng</span>}
          </div>
        </div>
      </button>
    </article>
  )
}

// ═══════════════════════════════════════════════════════════════
// FORMAT DETAIL MODAL — ouverte au clic sur une miniature.
// Invité : le détail complet (FormatCardFree) s'affiche FLOUTÉ derrière une
// demande de création de compte (gratuit). Connecté : détail en clair.
// Écoute `studio:open-format-detail` (payload { format, idx }).
// ═══════════════════════════════════════════════════════════════
export function FormatDetailModal() {
  const [data, setData] = useState(null)
  const auth = useAuth()
  // À l'OUVERTURE seulement (clic = intention, zéro coût au paint de la grille),
  // on charge les URLs Airtable pour disposer de la miniature Instagram en
  // repli quand le beat-frame Supabase manque (dette Storage dev) → plus de
  // fond violet figé avant le play.
  const [atReady, setAtReady] = useState(false)
  useEffect(() => {
    function handler(e) {
      const nd = e?.detail || null
      // Ré-ouverture du même format (tuto qui revient en arrière) : no-op,
      // on garde l'état chargé (analyse, exemples) au lieu de remonter.
      setData(prev => {
        const prevKey = prev?.format?.source?.airtable_id
        return (prev && nd && prevKey && prevKey === nd.format?.source?.airtable_id) ? prev : nd
      })
    }
    function closer() { setData(null) }
    window.addEventListener('studio:open-format-detail', handler)
    window.addEventListener('studio:close-format-detail', closer)
    return () => { window.removeEventListener('studio:open-format-detail', handler); window.removeEventListener('studio:close-format-detail', closer) }
  }, [])
  useEffect(() => {
    if (!data) { setAtReady(false); return }
    let alive = true
    loadAllVideoUrls().then(() => { if (alive) setAtReady(true) }).catch(() => {})
    return () => { alive = false }
  }, [data])
  // Ferme le détail quand le signup s'ouvre (évite l'empilement modal sur modal)
  useEffect(() => {
    function onSignup() { setData(null) }
    window.addEventListener('studio:open-guest-signup', onSignup)
    return () => window.removeEventListener('studio:open-guest-signup', onSignup)
  }, [])
  if (!data) return null
  const isGuest = typeof window !== 'undefined' && window.__STUDIO_HAS_USER !== true
  const { format, idx, preload } = data
  function close() { setData(null) }
  function signup() {
    try { studioEvents.unlockCtaClicked({ trigger: 'format_detail_signup', format_idx: idx }) } catch {}
    try { window.dispatchEvent(new CustomEvent('studio:open-guest-signup', { detail: { trigger: 'format_detail' } })) } catch {}
  }

  // Nouvelle fiche (doctrine viralité) pour les connectés. Le player vidéo
  // reste celui de l'app (poster Supabase instantané + MP4 paresseux).
  const src = format?.source || {}
  const localOverride = (() => { try { return localStorage.getItem('studio_plan_dev_override') } catch { return null } })()
  const plan = localOverride || auth.studioPlan || auth.user?.user_metadata?.studio_plan || null
  const entitled = ['creator', 'pro', 'pro_plus', 'agency'].includes(plan)
  const mp4Url = atReady ? getVideoMp4({ lien: src.lien, airtable_id: src.airtable_id }) : null
  const supabaseThumb = formatPosterUrl(format)
  const airtableThumb = atReady ? getVideoThumb({ lien: src.lien, airtable_id: src.airtable_id }) : null
  const videoSlot = (
    <div className="relative overflow-hidden bg-black/95 w-full aspect-[4/5] sm:aspect-[9/16] max-h-[380px] sm:max-h-[480px] studio-thumbnail"
      style={{ background: 'linear-gradient(135deg, var(--fmt-ph-1, #1e1145) 0%, var(--fmt-ph-2, #2d1b69) 100%)' }}>
      {/* Priorité au poster_url fourni par la recherche (celui qui s'affiche
          déjà dans la grille) : l'URL reconstruite passe en fallback. */}
      <FreeVideoPlayer mp4Url={mp4Url} posterUrl={src.poster_url || supabaseThumb || airtableThumb || null}
        posterFallbacks={[src.poster_url, supabaseThumb, airtableThumb].filter(Boolean)}
        lien={src.lien} airtable_id={src.airtable_id} />
    </div>
  )

  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center sm:p-4"
      style={{ background: 'rgba(15,10,26,0.72)', backdropFilter: 'blur(3px)' }} onClick={close}>
      <div className="relative w-full sm:max-w-sm max-h-[92vh] overflow-y-auto bg-bg rounded-t-2xl sm:rounded-2xl shadow-2xl"
        onClick={e => e.stopPropagation()}>
        <button onClick={close} aria-label="Fermer"
          className="absolute top-2 right-2 z-30 w-8 h-8 rounded-full flex items-center justify-center"
          style={{ background: 'rgba(15,10,26,0.6)', backdropFilter: 'blur(6px)' }}>
          <span className="text-white text-sm">✕</span>
        </button>
        <div className="relative">
          {/* Détail réel du format. Flouté + non interactif pour un invité
              (l'invité garde l'ancienne carte : la fiche demande un fetch
              authentifié, inutile derrière un flou). */}
          <div style={isGuest ? { filter: 'blur(7px)', pointerEvents: 'none', userSelect: 'none' } : undefined} aria-hidden={isGuest}>
            {isGuest
              ? <FormatCardFree format={format} idx={idx} airtableReady={atReady} unlocked={false} />
              : <FormatFiche format={format} idx={idx} videoSlot={videoSlot} entitled={entitled} preload={!!preload} />}
          </div>
          {isGuest && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 z-20"
              style={{ background: 'rgba(255,251,245,0.32)' }}>
              <img src={merlinFound} alt="" className="w-16 h-16 object-contain mb-3"
                style={{ filter: 'drop-shadow(0 8px 24px rgba(var(--merlin-accent-rgb, 254,130,4),0.4))' }} />
              <h3 className="text-lg font-bold text-ink leading-tight mb-1.5">Crée ton compte pour voir l'analyse complète</h3>
              <p className="text-[13px] text-ink-muted mb-4 max-w-xs leading-snug">Hook, script, structure et ce qui fait que ce format marche : tout se débloque quand tu crées ton compte.</p>
              <button onClick={signup}
                className="px-7 py-3 rounded-xl font-bold text-sm text-white transition-transform hover:scale-[1.02]"
                style={{ background: 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))', boxShadow: '0 8px 24px rgba(var(--merlin-accent-rgb, 254,130,4),0.35)' }}>
                Créer mon compte →
              </button>
              <p className="text-[11px] text-ink-faint mt-2.5">{showsTrialUx() ? 'Essai 7 jours · sans engagement · annulable en 1 clic' : 'Sans engagement · satisfait ou remboursé 14 jours'}</p>
            </div>
          )}
        </div>
      </div>
    </div>, document.body)
}

function FormatsLockedTeaser({ formats = [], score, loadingMore = 0 }) {
  const teasers = (formats || []).slice(0, 3)
  // Le score (radar) s'affiche avant les formats (~11s vs ~17s). Tant que les
  // formats ne sont pas là, on montre un chargement clair (3 cases + barre)
  // au lieu d'un vide.
  if (teasers.length === 0) {
    return (
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-2.5">
          <span className="inline-block w-3.5 h-3.5 border-2 border-orange/30 border-t-orange rounded-full animate-spin shrink-0" />
          <h3 className="text-[14px] font-bold text-ink leading-tight">On te trouve tes 3 meilleurs formats…</h3>
        </div>
        <div className="grid grid-cols-3 gap-2 sm:gap-3">
          {[0, 1, 2].map(i => (
            <div key={i} className="rounded-2xl overflow-hidden border border-border animate-pulse"
              style={{ aspectRatio: '4/5', background: 'linear-gradient(135deg, var(--fmt-ph-1, #2a1c52), var(--fmt-ph-2, #3a2a6e))' }} />
          ))}
        </div>
      </div>
    )
  }
  return (
    <div className="mb-4">
      <div className="flex items-baseline justify-between gap-2 mb-1">
        <h3 className="text-[14px] font-bold text-ink leading-tight">{VERTICALES[getVertical()]?.teasers_title || <>🎬 Les 3 meilleurs formats à poster pour <span style={{ color: 'var(--merlin-accent, #fe8204)', textShadow: '0 0 14px rgba(254,130,4,0.45)' }}>TON compte</span></>}</h3>
      </div>
      <p className="text-[12px] text-ink-muted leading-snug mb-3">Sélectionnés pour ton compte. Clique sur un format pour voir son <strong className="text-ink">analyse complète</strong> (hook, script, structure, pourquoi ça marche).</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-4">
        {teasers.map((f, i) => (
          <FormatTeaserCard key={i} format={f} idx={i} trigger="guest_format_teaser" />
        ))}
        {/* Scan encore en cours : squelettes pour les slots pas encore
            arrivés (les formats peuvent apparaître au fil de l'eau depuis
            que l'écran s'affiche dès la note, retour Antonin 11/08). */}
        {Array.from({ length: Math.max(0, loadingMore) }).map((_, i) => (
          <div key={`skel-${i}`} className="rounded-2xl overflow-hidden border border-border animate-pulse"
            style={{ aspectRatio: '4/5', background: 'linear-gradient(135deg, var(--fmt-ph-1, #2a1c52), var(--fmt-ph-2, #3a2a6e))' }} />
        ))}
      </div>
      <button type="button" onClick={() => { try { studioEvents.unlockCtaClicked({ trigger: 'guest_formats_band' }) } catch {}; window.dispatchEvent(new CustomEvent('studio:open-guest-signup', { detail: { trigger: 'guest_formats_band' } })) }}
        className="w-full py-3.5 rounded-xl text-white font-bold text-[14px]"
        style={{ background: 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))', boxShadow: '0 6px 18px rgba(var(--merlin-accent-rgb, 254,130,4),0.30)' }}>
        Créer mon compte pour voir mes 3 formats →
      </button>
      <p className="text-[10.5px] text-ink-faint text-center mt-2">{showsTrialUx() ? 'Essai 7 jours · sans engagement · annulable en 1 clic' : 'Sans engagement · satisfait ou remboursé 14 jours'}</p>
      <FormatDetailModal />
    </div>
  )
}


// ═══════════════════════════════════════════════════════════════
// MUR DE LA NOTE — ABANDONNÉ (Antonin 11/08) : le bras A n'est plus un
// écran séparé mais le MÊME écran que le contrôle avec la note gatée
// (AccountScore gated) + la projection. Composant conservé (exporté,
// référencé par des maquettes/l'app) mais plus rendu nulle part.
// Ancienne spec : data/spec-ab-mur-note-2026-07-23.md.
// ═══════════════════════════════════════════════════════════════
export function GuestNoteWallA({ session }) {
  const { signInWithMagicLink, verifyEmailOtp } = useAuth()
  const webview = useMemo(() => getWebviewInfo(), [])
  const username = session.ig_username === '_generic' ? '' : session.ig_username
  const score = session.persona?.score
  const g = Number.isFinite(score?.global) ? score.global : null
  const dims = Array.isArray(score?.dimensions) ? score.dimensions : []
  const formats = (session.formats || []).filter(f => !f.locked).slice(0, 3)

  const [step, setStep] = useState('email') // email | code
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [resendIn, setResendIn] = useState(0)
  const focusedOnce = useRef(false)
  const emailFormRef = useRef(null)
  const codeRef = useRef(null)

  useEffect(() => {
    try { studioEvents.noteWallViewed({ variant: 'A', in_meta_webview: !!webview.isMetaWebview, username }) } catch {}
  }, [])
  // Drapeau lu par GuestSignupModal : tant que le mur est monté, le signup
  // vit inline ici, la modal legacy ne doit pas s'ouvrir par-dessus.
  useEffect(() => {
    window.__NOTE_WALL_A_MOUNTED = true
    return () => { window.__NOTE_WALL_A_MOUNTED = false }
  }, [])
  useEffect(() => {
    if (resendIn <= 0) return
    const t = setInterval(() => setResendIn(x => Math.max(0, x - 1)), 1000)
    return () => clearInterval(t)
  }, [resendIn > 0])

  async function sendCode(e) {
    e?.preventDefault?.()
    setError(null)
    if (!/.+@.+\..+/.test(email)) { setError('Entre ton email.'); return }
    setLoading(true)
    try { studioEvents.signupMethodChosen({ method: 'magic_link', mode: 'note_wall_a', variant: 'A' }) } catch {}
    try {
      let anonId = ''
      try { anonId = localStorage.getItem('studio_anon_id') || ''; localStorage.setItem('studio_guest_pending_claim', '1') } catch {}
      const redirectTo = buildMagicRedirect()
      await signInWithMagicLink(email, { consent: true, dailyOptin: true, redirectTo })
      setStep('code'); setResendIn(25)
      setTimeout(() => { try { codeRef.current?.focus() } catch {} }, 250)
    } catch (err) {
      setError(err?.message || 'Envoi impossible, réessaie.')
    } finally { setLoading(false) }
  }

  async function submitCode(raw) {
    const digits = String(raw ?? code).replace(/\D/g, '')
    if (digits.length < 6 || loading) return
    setLoading(true); setError(null)
    try {
      await verifyEmailOtp(email, digits)
      try { trackLead({ method: 'email_otp' }) } catch {}
      try { studioEvents.signupCompleted({ method: 'email_otp', mode: 'note_wall_a', variant: 'A' }) } catch {}
      try { studioEvents.noteRevealViewed({ variant: 'A' }) } catch {}
      await claimGuestSessionShared()
      exitGuestMode()
      try {
        localStorage.removeItem('studio_guest_pending_claim')
        localStorage.removeItem('studio_guest_session_id')
      } catch {}
      window.location.reload()
    } catch (err) {
      setLoading(false)
      try { studioEvents.signupFailed({ method: 'email_otp', error: (err?.message || '').substring(0, 100), mode: 'note_wall_a' }) } catch {}
      setError(/expired|invalid|token/i.test(err?.message || '') ? "Ce code a expiré. On t'en renvoie un ?" : (err?.message || 'Code refusé, réessaie.'))
    }
  }

  // La carte analyse vidéo dispatch studio:open-guest-signup : ici on
  // scrolle vers le formulaire email (c'est LE chemin de signup du mur).
  useEffect(() => {
    const onSignup = () => {
      try { emailFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }) } catch { /* no-op */ }
      try { emailFormRef.current?.querySelector('input')?.focus({ preventScroll: true }) } catch { /* no-op */ }
    }
    window.addEventListener('studio:open-guest-signup', onSignup)
    return () => window.removeEventListener('studio:open-guest-signup', onSignup)
  }, [])

  const gColor = g == null ? '#7c3aed' : g >= 65 ? '#16a34a' : g >= 45 ? 'var(--merlin-accent, #fe8204)' : '#dc2626'
  const R = 44, C = 2 * Math.PI * R

  return (
    <div className="min-h-screen bg-bg">
      <div className="max-w-md mx-auto px-4 py-6 pb-10">
        {/* 1. LA NOTE — en clair */}
        <p className="text-center text-[11px] font-bold uppercase tracking-wider" style={{ color: '#7c3aed' }}>
          {username ? `@${username}` : 'Ton compte'} · scan terminé
        </p>
        <div className="relative w-[120px] h-[120px] mx-auto mt-3">
          <svg width="120" height="120" viewBox="0 0 120 120">
            <circle cx="60" cy="60" r={R} fill="none" stroke="rgba(15,16,64,0.08)" strokeWidth="10" />
            {g != null && (
              <circle cx="60" cy="60" r={R} fill="none" stroke={gColor} strokeWidth="10" strokeLinecap="round"
                strokeDasharray={`${(g / 100) * C} ${C}`} transform="rotate(-90 60 60)" />
            )}
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-[34px] font-extrabold leading-none text-ink">{g ?? '…'}</span>
            <span className="text-[11px] text-ink-faint">/100</span>
          </div>
        </div>

        {/* 2. LE DÉTAIL — flouté (la valeur retenue). Cliquable : tout le
            bloc verrouillé mène à l'email inline (demande Antonin 11/08). */}
        {dims.length > 0 && (
          <div className="relative mt-4 rounded-2xl border border-border bg-white p-3.5 overflow-hidden cursor-pointer active:scale-[0.99] transition-transform"
            role="button" tabIndex={0}
            onClick={() => {
              try { studioEvents.unlockCtaClicked({ trigger: 'note_wall_locked_dims' }) } catch {}
              try { window.dispatchEvent(new CustomEvent('studio:open-guest-signup', { detail: { trigger: 'note_wall_locked_dims' } })) } catch {}
            }}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') e.currentTarget.click() }}>
            <p className="text-[10px] uppercase tracking-wider font-bold text-ink-faint mb-2">Ta note en détail — {dims.length} dimensions</p>
            <div style={{ filter: 'blur(7px)', opacity: 0.6, pointerEvents: 'none', userSelect: 'none' }} aria-hidden="true">
              {dims.map(d => (
                <div key={d.key || d.label} className="flex items-center gap-2.5 my-1.5">
                  <span className="text-[12px] w-24 shrink-0">{d.label}</span>
                  <div className="flex-1 h-[7px] rounded-full" style={{ background: 'rgba(15,16,64,0.07)' }}>
                    <div className="h-full rounded-full" style={{ width: `${d.value}%`, background: d.value >= 60 ? '#16a34a' : d.value >= 45 ? '#fe8204' : '#dc2626' }} />
                  </div>
                  <span className="text-[11px] font-bold w-6 text-right">{d.value}</span>
                </div>
              ))}
            </div>
            <div className="absolute inset-0 flex flex-col items-center justify-center pt-5">
              <span className="text-xl">🔒</span>
              <p className="text-[12px] font-bold text-ink mt-1">Tes 2 points faibles sont identifiés</p>
            </div>
          </div>
        )}

        {/* 3. LA PROJECTION — composant partagé (aussi affiché dans l'écran
            connecté au-dessus du CTA améliorer, demande Antonin 24/07). */}
        <AccountProjection persona={session.persona} formats={session.formats} className="mt-3.5" />

        {/* 3bis. ANALYSE VIDÉO AUTO (A/B note_va_auto, bras A) : la carte
            vit AUSSI ici car le mur note_wall A est servi à 100% des invités
            (GuestResults ne rend plus cette section pour eux). Le clic
            détourne vers le formulaire email juste en dessous. */}
        {getVariantKey('note_va_auto') === 'A' && !session?.persona?.is_generic && (
          <AutoVaCard sessionId={session.id} isGuest className="mt-3.5" />
        )}

        {/* 4. LE PLAN — dotation. Mêmes vignettes vidéo cliquables que
            l'écran contrôle (FormatTeaserCard + FormatDetailModal floutée
            invité) : les lignes texte façon menus déroulants ne montraient
            pas la vidéo. Le CTA de la modal dispatch open-guest-signup, que
            le mur écoute déjà pour scroller vers l'email inline. */}
        {formats.length > 0 && (
          <div className="mt-3.5">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] uppercase tracking-wider font-bold text-ink-faint">Ton plan est prêt</p>
              <span className="text-[10px] font-bold" style={{ color: '#fe8204' }}>scan fait ✓</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {formats.map((f, i) => (
                <FormatTeaserCard key={i} format={f} idx={i} trigger="note_wall_teaser" />
              ))}
            </div>
          </div>
        )}

        {/* 5. EMAIL / CODE — inline, wording spec §6bis */}
        <div className="mt-5" ref={emailFormRef}>
          {step === 'email' ? (
            <form onSubmit={sendCode}>
              <p className="text-[16px] font-extrabold text-ink text-center mb-2.5">Voir mon analyse complète</p>
              <input
                type="email" inputMode="email" autoComplete="email" placeholder="email@gmail.com"
                value={email} onChange={e => setEmail(e.target.value)}
                onFocus={() => { if (!focusedOnce.current) { focusedOnce.current = true; try { studioEvents.noteWallEmailFocus({ variant: 'A' }) } catch {} } }}
                className="w-full px-4 py-3.5 rounded-xl border border-border bg-white text-[15px] text-ink outline-none focus:border-orange"
              />
              {error && <p className="text-[12px] text-rose-600 mt-1.5">{error}</p>}
              <button type="submit" disabled={loading}
                className="w-full mt-2.5 py-3.5 rounded-xl font-extrabold text-[15px] text-white disabled:opacity-60"
                style={{ background: 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))', boxShadow: '0 6px 18px rgba(var(--merlin-accent-rgb, 254,130,4),0.32)' }}>
                {loading ? 'Un instant…' : 'Débloquer mon analyse →'}
              </button>
              <p className="text-center text-[11px] text-ink-muted mt-2 leading-snug">
                Gratuit, sans carte. Ton analyse est liée à ton email : tu la retrouves quand tu veux.
              </p>
            </form>
          ) : (
            <div>
              <p className="text-[16px] font-extrabold text-ink text-center mb-1">Dernière étape avant ton analyse</p>
              <p className="text-[12.5px] text-ink-muted text-center mb-2.5 leading-snug">
                Tape le code à 6 chiffres envoyé à {email}. Pas besoin d'ouvrir le mail : le code est dans la notification.
              </p>
              <input
                ref={codeRef} type="text" inputMode="numeric" autoComplete="one-time-code" placeholder="······"
                value={code}
                onChange={e => {
                  const v = e.target.value.replace(/\D/g, '').slice(0, 8)
                  setCode(v)
                  if (v.length >= 6) submitCode(v)
                }}
                className="w-full px-4 py-3.5 rounded-xl border border-border bg-white text-[22px] tracking-[8px] text-center font-bold text-ink outline-none focus:border-orange"
              />
              {error && <p className="text-[12px] text-rose-600 mt-1.5 text-center">{error}</p>}
              {loading && <p className="text-[12px] text-ink-muted mt-1.5 text-center">Vérification…</p>}
              <div className="flex items-center justify-center gap-4 mt-3">
                <button type="button" disabled={resendIn > 0 || loading} onClick={() => sendCode()}
                  className="text-[12px] font-bold text-ink-muted underline underline-offset-2 disabled:opacity-50">
                  {resendIn > 0 ? `Renvoyer le code (${resendIn}s)` : 'Renvoyer le code'}
                </button>
                <button type="button" onClick={() => { setStep('email'); setCode(''); setError(null) }}
                  className="text-[12px] font-bold text-ink-muted underline underline-offset-2">
                  Changer d'email
                </button>
              </div>
            </div>
          )}
        </div>
        {/* Modal détail des vignettes (fiche floutée + CTA compte pour
            l'invité) : montée ici car le mur remplace tout l'écran. */}
        <FormatDetailModal />
      </div>
    </div>
  )
}

function GuestResults({ session }) {
  const formats = (session.formats || []).filter(f => !f.locked).slice(0, 3)
  const isPreview = session.status === 'running'
  // A/B note_wall : bras A = mur note claire + détail flouté + email inline ;
  // bras B = cet écran, inchangé (contrôle). Sticky par anon_id via
  // ui-experiments. Override dev/screenshots : ?wall=A|B.
  const [, forceExp] = useState(0)
  useEffect(() => { initUiExperiments(); return subscribeUiExperiments(() => forceExp(x => x + 1)) }, [])
  const wallVariant = (() => {
    try {
      const o = new URLSearchParams(window.location.search).get('wall')
      if (o === 'A' || o === 'B') return o
    } catch { /* no-op */ }
    return getVariantKey('note_wall') || 'B'
  })()
  // A/B note_graph : la projection d'abonnés s'affiche entre le CTA « améliorer »
  // et les formats (variante A) ou pas (variante B). Override écran : ?graph=A|B.
  const graphVariant = (() => {
    try { const o = new URLSearchParams(window.location.search).get('graph'); if (o === 'A' || o === 'B') return o } catch { /* no-op */ }
    return getVariantKey('note_graph') || 'B'
  })()
  useEffect(() => {
    studioEvents.formatsViewed({ guest: true, preview: isPreview, count: formats.length })
  }, [])
  // Handoff webview → navigateur : le scan tourne aussi bien en webview mais
  // le compte y meurt. On déporte la note + le compte dans le vrai navigateur,
  // pile quand la note est prête. Override écran : ?handoff=1 (forcer) | 0 (couper).
  const wv = useMemo(() => getWebviewInfo(), [])
  const [handoffStay, setHandoffStay] = useState(false)
  const handoffOverride = (() => { try { return new URLSearchParams(window.location.search).get('handoff') } catch { return null } })()
  function unlock(what, idx) {
    try { studioEvents.discoverSignupCtaClicked({ trigger: `guest_results_${what}`, format_idx: idx }) } catch {}
    try { window.dispatchEvent(new CustomEvent('studio:open-guest-signup', { detail: { trigger: `guest_results_${what}` } })) } catch {}
  }
  const isGeneric = session.ig_username === '_generic'
  const hasScore = !!(session.persona?.score && Number.isFinite(session.persona.score.global))
  // Handoff : webview in-app (Meta/TikTok/…) DÈS que la note existe (hasScore),
  // pas quand le scan est 100% fini : avec le lazy-adapt le statut reste
  // 'running' longtemps après la note prête → sinon on intercepte trop tard
  // (l'user a déjà vu note+formats et entamé le signup dans le webview).
  // ?handoff=1 force (screenshots dev), ?handoff=0 coupe.
  const showHandoff = handoffOverride === '1' ||
    (handoffOverride !== '0' && wv.isInAppWebview && hasScore && !handoffStay)
  if (showHandoff) {
    return <HandoffToNavigator handle={isGeneric ? '' : session.ig_username} score={session.persona?.score} onStay={() => setHandoffStay(true)} />
  }
  // Bras A (redéfini par Antonin 11/08) : EXACTEMENT le même écran que le
  // contrôle (mêmes vignettes, mêmes tailles, même modal de compte), avec
  // seulement 2 différences : la note est GATÉE (globale en clair, radar +
  // dimensions floutés, clic = création de compte) et la projection
  // d'abonnés s'affiche. L'écran mur dédié (GuestNoteWallA) est abandonné.
  // Anti-flash : tant que l'assignation A/B n'est pas connue (fetch des
  // expériences ~0,5s au 1er chargement), on rend GATÉ par défaut : un
  // bras B voit le détail se déverrouiller, jamais l'inverse (zéro fuite).
  const wallKnown = (() => {
    try { const o = new URLSearchParams(window.location.search).get('wall'); if (o === 'A' || o === 'B') return true } catch { /* no-op */ }
    return isUiExpLoaded()
  })()
  const noteGated = !isGeneric && (wallVariant === 'A' || !wallKnown)
  return (
    <div className="min-h-screen bg-bg flex flex-col">
      <div className="sticky top-0 z-30 backdrop-blur-xl border-b border-border" style={{ background: 'rgba(255,251,245,0.94)' }}>
        <div className="max-w-3xl mx-auto px-4 py-2.5 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <img src={merlinFound} alt="" className="w-7 h-7 object-contain shrink-0" />
            <span className="text-sm font-bold text-ink truncate">Tes formats {isGeneric ? '' : `· @${session.ig_username}`}</span>
          </div>
          <button onClick={() => unlock('header', -1)}
            className="shrink-0 px-3 py-1.5 rounded-lg text-[11px] font-bold text-white"
            style={{ background: 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))' }}>
            Créer mon compte
          </button>
        </div>
      </div>
      <div className="flex-1 max-w-3xl mx-auto w-full px-4 py-5 pb-10">
        <h1 className="text-xl sm:text-2xl font-bold text-ink leading-tight mb-3">
          Ton analyse {isGeneric ? '' : <>de <span style={{ color: 'var(--merlin-accent, #fe8204)' }}>@{session.ig_username}</span></>} est prête ✨
        </h1>
        <AccountScore score={session.persona?.score} username={isGeneric ? '' : session.ig_username} sessionId={session.id} gated={noteGated}
          hideCta={isCreator75Visitor()}
          onCta={() => { try { window.dispatchEvent(new CustomEvent('studio:open-guest-signup', { detail: { trigger: 'score_improve' } })) } catch {} }} />
        <WebviewBanner handle={isGeneric ? '' : session.ig_username} context="results" />
        {((wallVariant === 'A' && wallKnown) || graphVariant === 'A') && !isGeneric && (
          <AccountProjection persona={session.persona} formats={session.formats} className="mb-4" />
        )}
        {/* Analyse vidéo AUTO (A/B note_va_auto, bras A) : AVANT les 3 formats.
            Le gating (paywall Pro) et le contenu vivent côté serveur. */}
        {(() => { try { if (new URLSearchParams(window.location.search).get('vad')) return true } catch { /* no-op */ } return getVariantKey('note_va_auto') === 'A' })() && !isGeneric && (
          <AutoVaCard sessionId={session.id} isGuest className="mb-4" previewReel={session.persona?.last_reel || null} />
        )}
        <FormatsLockedTeaser formats={formats} score={session.persona?.score}
          loadingMore={isPreview ? Math.max(0, 3 - formats.length) : 0} />
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// GUEST READY GATE — fin du scan invité : les formats sont prêts, la
// création de compte est demandée MAINTENANT (pic de motivation : l'user
// a investi 90s, exploré la bibliothèque, et SES résultats l'attendent).
// ═══════════════════════════════════════════════════════════════
function GuestReadyGate({ session }) {
  // Expériences UI (A/B agents) : init + re-render à l'arrivée des configs
  const [, forceExp] = useState(0)
  useEffect(() => { initUiExperiments(); return subscribeUiExperiments(() => forceExp(x => x + 1)) }, [])
  useEffect(() => {
    studioEvents.signupPageLoaded({ mode: 'guest_scan_done', gate_step: 'ready' })
    // Laisse le "moment de victoire" respirer AVANT d'ouvrir la modal :
    // à 600ms l'écran "tes formats sont prêts" était invisible (audit
    // 2026-06-11, screenshot = modal déjà ouverte sur fond sombre). 2.8s
    // = le temps de lire le titre, puis la modal arrive comme étape logique.
    const t = setTimeout(() => {
      // Bras A redéfini 11/08 : même comportement que le contrôle (la modal
      // s'ouvre aussi), seule la note gatée + la projection diffèrent.
      try { window.dispatchEvent(new CustomEvent('studio:open-guest-signup', { detail: { trigger: 'guest_scan_done' } })) } catch {}
    }, 2800)
    return () => clearTimeout(t)
  }, [])

  const isGeneric = session.ig_username === '_generic'
  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-950 via-purple-900 to-indigo-950 text-white flex flex-col">
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 max-w-xl mx-auto w-full text-center">
        <img src={merlinFound} alt="Merlin" className="w-28 h-28 mb-6 object-contain"
          style={{ filter: 'drop-shadow(0 10px 40px rgba(var(--merlin-accent-rgb, 254,130,4),0.45))' }} />
        {/* SLOTS signup_ready_* (A/B agents) : le mur d'inscription est la
            plus grosse fuite mesurée du funnel (83 arrivent, 17 cliquent). */}
        <h1 className="text-3xl sm:text-4xl font-bold leading-tight mb-3">
          {getSlot('signup_ready_title_prefix', 'Tes 3 formats ')}{isGeneric ? 'pour ta niche' : <>pour <span style={{ color: 'var(--merlin-accent, #fe8204)' }}>@{session.ig_username}</span></>}{getSlot('signup_ready_title_suffix', ' sont prêts ✨')}
        </h1>
        <p className="text-sm text-white/70 max-w-md mb-8 leading-relaxed">
          {VERTICALES[getVertical()]?.signup_sub || getSlot('signup_ready_sub', 'Hook, structure et plans à filmer, adaptés à ton positionnement. Crée ton compte gratuit pour les découvrir : ils restent attachés à ton compte.')}
        </p>
        <button
          onClick={() => { try { window.dispatchEvent(new CustomEvent('studio:open-guest-signup', { detail: { trigger: 'guest_ready_cta' } })) } catch {} }}
          className="px-7 py-3.5 rounded-xl font-bold text-sm text-white transition-all hover:scale-[1.02]"
          style={{ background: 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))', boxShadow: '0 8px 24px rgba(var(--merlin-accent-rgb, 254,130,4),0.30)' }}>
          {getSlot('signup_ready_cta', 'Découvrir mes 3 formats →')}
        </button>
        <p className="text-[11px] text-white/40 mt-4">{getSlot('signup_ready_reassure', 'Gratuit · Sans carte bancaire')}</p>
      </div>
      <LegalFooter tone="dark" />
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// PASSWORD RECOVERY — s'ouvre au retour du lien "mot de passe oublié"
// (Supabase émet l'event PASSWORD_RECOVERY après le redirect). L'user
// choisit son nouveau mot de passe, la session est déjà posée.
// ═══════════════════════════════════════════════════════════════
export function PasswordRecoveryModal() {
  const [open, setOpen] = useState(false)
  const [pwd, setPwd] = useState('')
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)
  const [err, setErr] = useState(null)
  useEffect(() => {
    const { data: sub } = sb.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') setOpen(true)
    })
    return () => { try { sub?.subscription?.unsubscribe() } catch { /* no-op */ } }
  }, [])
  if (!open) return null
  async function save(e) {
    e?.preventDefault?.()
    if (pwd.length < 8) { setErr('8 caractères minimum.'); return }
    setBusy(true); setErr(null)
    const { error } = await sb.auth.updateUser({ password: pwd })
    setBusy(false)
    if (error) { setErr(error.message); return }
    setDone(true)
    setTimeout(() => setOpen(false), 2500)
  }
  return createPortal(
    <div className="fixed inset-0 z-[220] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="w-full max-w-sm rounded-2xl bg-gradient-to-b from-purple-950 to-indigo-950 border border-white/15 shadow-2xl p-5 text-white">
        {done ? (
          <p className="text-sm font-bold text-center py-3">✅ Mot de passe changé. Tu es connecté.</p>
        ) : (
          <>
            <h2 className="text-base font-bold mb-1">Choisis ton nouveau mot de passe</h2>
            <p className="text-[11.5px] text-white/60 mb-3">Tu es bien identifié, il ne reste qu&rsquo;à le définir.</p>
            <form onSubmit={save} className="space-y-2.5">
              <input type="password" value={pwd} onChange={e => setPwd(e.target.value)} autoFocus
                placeholder="Nouveau mot de passe (8+ caractères)" autoComplete="new-password" minLength={8} required
                className="w-full bg-white/10 rounded-lg px-3.5 py-2.5 outline-none text-sm text-white placeholder-white/40 focus:bg-white/15 transition-colors" />
              <button type="submit" disabled={busy}
                className="w-full py-3 rounded-lg font-bold text-sm text-white transition-all disabled:opacity-40"
                style={{ background: 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))', boxShadow: '0 8px 24px rgba(var(--merlin-accent-rgb, 254,130,4),0.30)' }}>
                {busy ? 'Patiente…' : 'Enregistrer et continuer →'}
              </button>
              {err && <p className="text-xs text-red-300 text-center">⚠ {err}</p>}
            </form>
          </>
        )}
      </div>
    </div>, document.body)
}

// Rattachement de la session invitée au compte fraîchement créé — partagé
// entre la modal signup et le mur de la note (bras A de l'A/B note_wall).
async function claimGuestSessionShared() {
  try {
    const guestId = (typeof window !== 'undefined') ? localStorage.getItem('studio_anon_id') : null
    if (!guestId) return
    const { data: sess } = await sb.auth.getSession()
    const token = sess?.session?.access_token
    if (!token) return
    const prefHandle = (() => { try { return (localStorage.getItem('studio_prefill_handle') || '').trim() } catch { return '' } })()
    const res = await fetch(`${API_URL}/api/studio/claim-guest-session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ guest_session_id: guestId, ig_username: prefHandle, deal: isDealVisitor(), offer_arm: getTrialArm() }),
    })
    const d = await res.json().catch(() => null)
    studioEvents.guestSessionClaimed({ ok: !!(res.ok && d?.claimed !== false), status: res.status })
  } catch {}
}

function GuestSignupModal({ signUp, signIn, google }) {
  const webview = useMemo(() => getWebviewInfo(), [])
  const [open, setOpen] = useState(false)
  const [mode, setMode] = useState('signup')
  const [trigger, setTrigger] = useState(null)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const [consent] = useState(true) // notice-based : la ligne légale sous le CTA vaut acceptation
  const [dailyOptin, setDailyOptin] = useState(true)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [resetSent, setResetSent] = useState(false)
  // Webview Meta : magic/OTP par défaut (pas de mot de passe à créer ; le
  // lien transplante l'user vers son vrai navigateur avec ?claim=<anon_id>,
  // le code à 6 chiffres le garde dans le webview). Hors webview : mot de
  // passe + Google, inchangé.
  // PRÉREQUIS avant push prod (dashboard Supabase) : SMTP custom + template
  // FR contenant le lien ET {{ .Token }} + allowlist redirect avec query
  // params (https://studio.merlin-content-ai.com/**).
  const [authMode, setAuthMode] = useState(webview.isMetaWebview ? 'magic' : 'password') // 'magic' | 'password'
  const [magicSent, setMagicSent] = useState(false)
  const [otpCode, setOtpCode] = useState('')
  const [otpLoading, setOtpLoading] = useState(false)
  const { signInWithMagicLink, verifyEmailOtp } = useAuth()

  async function sendMagic(e) {
    e?.preventDefault?.()
    setError(null)
    if (!email) { setError('Entre ton email.'); return }
    setLoading(true)
    studioEvents.signupMethodChosen({ method: 'magic_link', mode: 'guest_paywall' })
    try {
      // Le claim de la session invitée se fera au RETOUR du lien (effet
      // global sur user.id) : on pose le contexte avant l'envoi, ET on met
      // l'anon_id dans l'URL de retour (?claim=) pour le cas cross-navigateur
      // (webview Instagram → Safari : le localStorage ne suit pas).
      let anonId = ''
      try {
        anonId = localStorage.getItem('studio_anon_id') || ''
        localStorage.setItem('studio_guest_pending_claim', '1')
      } catch { /* best effort */ }
      const redirectTo = buildMagicRedirect()
      await signInWithMagicLink(email, { displayName: name, consent, dailyOptin, redirectTo })
      setMagicSent(true)
    } catch (err) {
      setError(err?.message || 'Envoi impossible, réessaie.')
    } finally {
      setLoading(false)
    }
  }

  // Code à 6 chiffres du même envoi : l'user reste dans le navigateur
  // courant (webview comprise), pas de saut d'app. Même chemin post-succès
  // que le signup mot de passe : claim + exit guest + reload.
  async function submitOtp(e) {
    e?.preventDefault?.()
    setError(null)
    const code = otpCode.replace(/\D/g, '')
    if (code.length < 6) { setError('Le code du mail fait au moins 6 chiffres.'); return }
    setOtpLoading(true)
    studioEvents.signupMethodChosen({ method: 'email_otp', mode: 'guest_paywall' })
    try {
      await verifyEmailOtp(email, code)
      trackLead({ method: 'email_otp' })
      studioEvents.signupCompleted({ method: 'email_otp', mode: 'guest_paywall' })
      await claimGuestSession()
      exitGuestMode()
      try {
        localStorage.removeItem('studio_guest_pending_claim')
        localStorage.removeItem('studio_magic_pending')
        localStorage.removeItem('studio_guest_session_id')
      } catch {}
      window.location.reload()
    } catch (err) {
      setOtpLoading(false)
      studioEvents.signupFailed({ method: 'email_otp', error: (err?.message || '').substring(0, 100), mode: 'guest_paywall' })
      setError(/expired|invalid|token/i.test(err?.message || '')
        ? 'Code invalide ou expiré. Clique « Renvoyer » pour en recevoir un nouveau.'
        : (err?.message || 'Code refusé, réessaie.'))
    }
  }

  // Copy cohérente avec le gating : les formats (lecture hook/structure/script)
  // se débloquent à l'essai, pas au compte. Le compte gratuit GARDE la note +
  // les 3 formats trouvés. Pas de promesse de lecture gratuite.
  const headerTitle = VERTICALES[getVertical()]?.signup_modal_title || 'Crée ton compte pour voir l\'analyse de tes 3 formats'
  const headerSub = null

  useEffect(() => {
    function handler(e) {
      setOpen(true)
      const t = e?.detail?.trigger || 'paywall'
      setTrigger(t)
      try { studioEvents.signupPageLoaded({ mode: 'guest_paywall', trigger: t }) } catch {}
    }
    window.addEventListener('studio:open-guest-signup', handler)
    return () => window.removeEventListener('studio:open-guest-signup', handler)
  }, [])

  async function claimGuestSession() { return claimGuestSessionShared() }

  async function handleGoogle() {
    setError(null)
    // La mention CGU est affichée sous le bouton : continuer = accepter.
    try { localStorage.setItem('studio_oauth_consent_shown', new Date().toISOString()) } catch { /* ignore */ }
    // Webview Android : évasion vers Chrome AVANT l'OAuth (Google renvoie
    // 403 disallowed_useragent dans le webview Meta : 3/3 clics perdus le
    // 11/06). Le claim par handle rattachera le scan dans Chrome.
    if (webview.isMetaWebview && webview.isAndroid) {
      studioEvents.signupMethodChosen({ method: 'google_webview_escape', mode: 'guest_paywall' })
      escapeWebviewToChrome()
      return
    }
    setLoading(true)
    studioEvents.signupMethodChosen({ method: 'google', mode: 'guest_paywall' })
    try {
      // Persiste le guest_session_id avant le redirect OAuth — au retour
      // on est loggé et on peut claim depuis localStorage.
      try { localStorage.setItem('studio_guest_pending_claim', '1') } catch {}
      await google()
    } catch (e) {
      setLoading(false)
      setError(e?.message || 'Erreur Google')
    }
  }

  async function submit(e) {
    e?.preventDefault?.()
    setError(null)
    if (!email || !password) { setError('Email et mot de passe requis'); return }
    setLoading(true)
    studioEvents.signupMethodChosen({ method: 'email', mode: 'guest_paywall' })
    try {
      if (mode === 'signup') {
        const res = await signUp(email, password, name || email.split('@')[0], { consent, dailyOptin })
        if (res?.error) throw new Error(res.error.message || 'Inscription échouée')
        trackLead({ method: 'email' })
        studioEvents.signupCompleted({ method: 'email', mode: 'guest_paywall' })
      } else {
        const res = await signIn(email, password)
        if (res?.error) throw new Error(res.error.message || 'Connexion échouée')
      }
      // Claim la session guest → exit mode → reload pour récup la session
      // côté loggé et ouvrir la modal upgrade.
      await claimGuestSession()
      exitGuestMode()
      try {
        localStorage.removeItem('studio_guest_pending_claim')
        localStorage.removeItem('studio_guest_session_id')
      } catch {}
      window.location.reload()
    } catch (e) {
      setLoading(false)
      // Email déjà inscrit → on bascule en connexion au lieu d'une impasse
      // (22 échecs "User already registered" sur 30j : des users de retour).
      if (mode === 'signup' && /already registered/i.test(e.message || '')) {
        setMode('signin')
        setError('Tu as déjà un compte avec cet email. Entre ton mot de passe pour te connecter.')
      } else if (/invalid login credentials/i.test(e.message || '')) {
        // Mot de passe oublié = l'impasse n°1 des users de retour (15 échecs
        // sur 30j) : on bascule sur le lien magique au lieu de laisser mourir.
        setAuthMode('magic')
        setMagicSent(false)
        setError('Mot de passe incorrect. Pas grave : reçois un lien de connexion par email, clique juste sur Envoyer.')
      } else {
        setError(e.message)
      }
      studioEvents.signupFailed({ method: 'email', error: (e.message || '').substring(0, 100), mode: 'guest_paywall' })
    }
  }

  async function forgotPassword() {
    setError(null)
    if (!email) { setError('Entre ton email au-dessus, puis reclique.'); return }
    try {
      const { error: err } = await sb.auth.resetPasswordForEmail(email, { redirectTo: `${window.location.origin}/?studio=1` })
      if (err) throw err
      setResetSent(true)
    } catch (e) { setError(e.message) }
  }

  if (!open) return null
  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => !loading && setOpen(false)}>
      <div className="w-full max-w-md rounded-2xl bg-gradient-to-b from-purple-950 to-indigo-950 border border-white/15 shadow-2xl overflow-hidden text-white" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-white/10 flex items-start justify-between">
          <div>
            <h2 className="text-base font-bold leading-tight">{headerTitle}</h2>
            {headerSub && <p className="text-[11px] text-white/55 mt-1">{headerSub}</p>}
          </div>
          <button onClick={() => !loading && setOpen(false)} className="text-white/40 hover:text-white/80 text-xl leading-none w-11 h-11 flex items-center justify-center" aria-label="Fermer">×</button>
        </div>

        <div className="px-5 pt-4 pb-4 space-y-3">
          <WebviewBanner handle={(() => { try { return localStorage.getItem('studio_prefill_handle') || '' } catch { return '' } })()} context="signup" />
          {!webview.isMetaWebview && (
          <button onClick={handleGoogle} disabled={loading}
            className="w-full py-3 rounded-lg font-semibold text-sm bg-white text-gray-900 hover:bg-gray-100 transition-colors disabled:opacity-40 flex items-center justify-center gap-2">
            <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09 0-.73.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Continuer avec Google
          </button>)}

          {!webview.isMetaWebview && (
          <div className="flex items-center gap-2">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-[10px] uppercase tracking-wider text-white/40">ou</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>)}

          {authMode === 'magic' ? (
            magicSent ? (
              <div className="rounded-lg px-3 py-3 space-y-2" style={{ background: 'rgba(16,185,129,0.15)' }}>
                <p className="text-[13px] font-bold" style={{ color: '#6ee7b7' }}>📩 C&rsquo;est envoyé !</p>
                {webview.isMetaWebview ? (
                  <p className="text-[12px] text-white/80 leading-snug">Tape le code à 6 chiffres reçu à <b>{email}</b> juste en dessous. Pas besoin d&rsquo;ouvrir le mail : le code est dans la notification.</p>
                ) : (
                  <p className="text-[12px] text-white/80 leading-snug">Ouvre l&rsquo;email envoyé à <b>{email}</b> et clique le lien : tu seras connecté avec ton scan (pense aux spams).</p>
                )}
                <form onSubmit={submitOtp} className="space-y-1.5 pt-0.5">
                  {!webview.isMetaWebview && <p className="text-[11px] text-white/60">Ou tape le code reçu dans le mail pour rester ici :</p>}
                  <div className="flex gap-2">
                    <input inputMode="numeric" autoComplete="one-time-code" maxLength={10} value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                      placeholder="123456"
                      className="flex-1 min-w-0 bg-white/10 rounded-lg px-3 py-2 outline-none text-sm text-white tracking-[0.3em] placeholder-white/25 focus:bg-white/15 transition-colors" />
                    <button type="submit" disabled={otpLoading || otpCode.length < 6}
                      className="px-3.5 py-2 rounded-lg font-bold text-[12px] text-white disabled:opacity-40 shrink-0"
                      style={{ background: 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))' }}>
                      {otpLoading ? 'Patiente…' : 'Valider'}
                    </button>
                  </div>
                </form>
                {error && <p className="text-[11px] text-red-300">⚠ {error}</p>}
                <button type="button" onClick={() => { setMagicSent(false); setOtpCode(''); setError(null) }} className="text-[11px] text-white/55 hover:text-white underline">Renvoyer</button>
              </div>
            ) : (
              <form onSubmit={sendMagic} className="space-y-2">
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="email@gmail.com" autoComplete="email"
                  className="w-full bg-white/10 rounded-lg px-3 py-2.5 outline-none text-sm text-white placeholder-white/30 focus:bg-white/15 transition-colors" />
                <label className="flex items-center gap-2 cursor-pointer text-[11px] text-white/60 leading-snug">
                  <input type="checkbox" checked={dailyOptin} onChange={(e) => setDailyOptin(e.target.checked)} className="accent-orange shrink-0" />
                  <span>🔥 Le format viral du jour par mail</span>
                </label>
                <button type="submit" disabled={loading}
                  className="w-full py-3 rounded-lg font-bold text-sm text-white transition-all disabled:opacity-40 hover:scale-[1.01]"
                  style={{ background: 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))', boxShadow: '0 8px 24px rgba(var(--merlin-accent-rgb, 254,130,4),0.30)' }}>
                  {loading ? 'Patiente…' : (webview.isMetaWebview ? 'Recevoir mon code →' : 'Recevoir mon lien de connexion →')}
                </button>
                {error && <p className="text-xs text-red-300 text-center">⚠ {error}</p>}
              </form>
            )
          ) : (
          <form onSubmit={submit} className="space-y-2">
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="email@gmail.com" autoComplete="email"
              className="w-full bg-white/10 rounded-lg px-3 py-2.5 outline-none text-sm text-white placeholder-white/30 focus:bg-white/15 transition-colors" />
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="Mot de passe (8+ caractères)" autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
              className="w-full bg-white/10 rounded-lg px-3 py-2.5 outline-none text-sm text-white placeholder-white/30 focus:bg-white/15 transition-colors" />
            {mode === 'signin' && !resetSent && (
              <button type="button" onClick={forgotPassword}
                className="block text-[11px] text-white/55 hover:text-white underline">
                Mot de passe oublié ?
              </button>
            )}
            {resetSent && (
              <p className="text-[11px] rounded-lg px-2.5 py-2" style={{ background: 'rgba(16,185,129,0.15)', color: '#6ee7b7' }}>
                📩 Email envoyé à {email}. Clique le lien pour choisir un nouveau mot de passe.
              </p>
            )}
            {mode === 'signup' && (
              <label className="flex items-center gap-2 cursor-pointer text-[11px] text-white/60 leading-snug">
                <input type="checkbox" checked={dailyOptin} onChange={(e) => setDailyOptin(e.target.checked)} className="accent-orange shrink-0" />
                <span>🔥 Le format viral du jour par mail</span>
              </label>
            )}
            <button type="submit" disabled={loading}
              className="w-full py-3 rounded-lg font-bold text-sm text-white transition-all disabled:opacity-40 hover:scale-[1.01]"
              style={{ background: 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))', boxShadow: '0 8px 24px rgba(var(--merlin-accent-rgb, 254,130,4),0.30)' }}>
              {loading ? 'Patiente…' : mode === 'signup' ? 'Créer mon compte →' : 'Se connecter →'}
            </button>
            {error && <p className="text-xs text-red-300 text-center">⚠ {error}</p>}
          </form>
          )}

          {authMode === 'magic' ? (
            <button onClick={() => { setAuthMode('password'); setError(null) }}
              className="w-full text-[11px] text-white/55 hover:text-white underline text-center">
              Utiliser un mot de passe à la place
            </button>
          ) : (
            <>
              <button onClick={() => { setAuthMode('magic'); setError(null) }}
                className="w-full text-[11px] text-white/55 hover:text-white underline text-center">
                ← Recevoir un lien de connexion par email (sans mot de passe)
              </button>
              <button onClick={() => { setMode(mode === 'signup' ? 'signin' : 'signup'); setError(null) }}
                className="w-full text-[11px] text-white/55 hover:text-white underline text-center">
                {mode === 'signup' ? 'Déjà un compte ? Se connecter' : 'Pas encore de compte ? S\'inscrire'}
              </button>
            </>
          )}
          {/* Ligne légale UNIQUE (notice-based, couvre Google + email + magic) */}
          <p className="text-[10px] text-white/35 leading-snug text-center pt-1">
            En continuant, tu acceptes les <a href="/cgu" target="_blank" className="underline">CGU</a> et la <a href="/confidentialite" target="_blank" className="underline">politique de confidentialité</a>.
          </p>
        </div>
      </div>
    </div>,
    document.body
  )
}

// ═══════════════════════════════════════════════════════════════
// STUDIO GUEST DISCOVER — page d'arrivée pour adset C "browse-first"
// ═══════════════════════════════════════════════════════════════
// Land direct sur grid de 42 reels viraux random. Démontre le volume +
// qualité de la library avant de demander signup. Click sur n'importe
// quel reel = modal signup "crée ton compte pour personnaliser".
// Pattern Lovable/Replit : value-first puis engagement.
// Hero du flow invité, mutualisé avec l'habillage verticale : hors verticale
// il rend exactement l'ancien hero (mêmes textes, mêmes couleurs Merlin).
function GuestHeroVertical({ handle, setHandle, launch, launching, error }) {
  const vt = VERTICALES[getVertical()] || null
  // L'orange Merlin reste l'accent d'action même en verticale (le fond, lui, change)
  const accent = '#fe8204'
  const accent2 = '#f59e0b'
  return (
    <>
      {vt && (
        <div className="inline-block mb-3 px-3 py-1 rounded-full text-[10px] font-extrabold tracking-[0.12em] text-white"
          style={{ background: vt.accent }}>
          {vt.chip}
        </div>
      )}
      <h1 className="text-2xl sm:text-3xl font-bold text-ink leading-tight mb-2">
        {vt
          ? <>{vt.h1_guest_prefix} <span style={{ color: accent }}>{vt.h1_guest_accent}</span></>
          : <>Ton compte Instagram <span style={{ color: 'var(--merlin-accent, #fe8204)' }}>noté sur 100</span></>}
      </h1>
      <p className="text-sm text-ink-muted mb-4">
        {vt ? vt.sous_titre : 'Entre ton @, on analyse ton compte et on te trouve tes formats du jour.'}
      </p>
      {launch ? (
        <>
          <form onSubmit={(e) => { e.preventDefault(); launch() }} className="flex gap-2 max-w-md mx-auto">
            <div className="flex-1 flex items-center rounded-xl border-2 px-3" style={{ borderColor: vt ? `${accent}59` : 'rgba(254,130,4,0.35)', background: '#fff' }}>
              <span className="text-ink-faint font-bold">@</span>
              <input value={handle || ''} onChange={e => setHandle && setHandle(e.target.value)}
                placeholder="ton_compte" autoCapitalize="none" autoCorrect="off" spellCheck="false"
                className="flex-1 px-2 py-3 bg-transparent outline-none text-ink font-medium text-[15px]" />
            </div>
            <button type="submit" disabled={launching}
              className="px-5 py-3 rounded-xl text-white font-bold text-sm disabled:opacity-60 shrink-0"
              style={{ background: `linear-gradient(135deg, ${accent}, ${accent2})` }}>
              {launching ? '…' : 'Analyser'}
            </button>
          </form>
          {error && <p className="text-[12px] text-red-500 mt-2">{error}</p>}
        </>
      ) : null}
    </>
  )
}

function StudioGuestDiscover({ handle, setHandle, launch, launching, error } = {}) {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    try { studioEvents.discoverViewed({}) } catch {}
    // Verticale : le grid ne montre que les vidéos du secteur (backend filtre
    // par macro-niche). Sans verticale, comportement inchangé.
    const v = getVertical()
    fetch(`${API_URL}/api/studio/library/preview?count=60${v ? `&vertical=${encodeURIComponent(v)}` : ''}`)
      .then(r => r.json())
      .then(d => {
        // Shuffle pour donner du random et éviter que 2 visiteurs voient
        // le même grid (perception de fraîcheur + couvre + de niches).
        const arr = [...(d.items || [])]
        for (let i = arr.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1))
          ;[arr[i], arr[j]] = [arr[j], arr[i]]
        }
        setItems(arr)
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [])

  function promptSignup(trigger) {
    try { window.dispatchEvent(new CustomEvent('studio:open-guest-signup', { detail: { trigger } })) } catch {}
  }

  function onClickReel(item) {
    try {
      studioEvents.discoverReelClicked({
        video_id: item.id,
        niche: item.niche || null,
        format: item.format_nom || null,
        vues: item.vues || 0,
      })
    } catch {}
    // Click reel → modal signup (sans redirect tab spécifique)
    try { localStorage.setItem('studio_post_signup_redirect', 'home') } catch {}
    promptSignup('discover_reel_click')
  }

  function onClickTab(tab) {
    if (tab === 'library') return // déjà sur Découvrir
    // Persiste la cible pour que StudioReady setView(tab) après signup
    try { localStorage.setItem('studio_post_signup_redirect', tab) } catch {}
    try { studioEvents.discoverSignupCtaClicked({ trigger: 'tab', target_tab: tab }) } catch {}
    promptSignup(`discover_tab_${tab}`)
  }

  return (
    <div className="min-h-screen bg-bg flex flex-col">
      {/* Sticky top bar : logo + tabs (signup-gated sauf Découvrir) */}
      <div className="sticky top-0 z-30 backdrop-blur-xl border-b border-border" style={{ background: 'rgba(255,251,245,0.92)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
          <div className="flex items-center justify-between gap-3">
            <img src={merlinFound} alt="Merlin" className="w-8 h-8 sm:w-9 sm:h-9 object-contain shrink-0"
              style={{ filter: 'drop-shadow(0 2px 8px rgba(var(--merlin-accent-rgb, 254,130,4),0.25))' }} />
            <div className="hidden md:flex items-center gap-1">
              <NavTabLocal active={false} onClick={() => onClickTab('home')}>Pour toi</NavTabLocal>
              <NavTabLocal active={true} onClick={() => onClickTab('library')}>Découvrir</NavTabLocal>
              <NavTabLocal active={false} onClick={() => onClickTab('vault')}>Mes formats</NavTabLocal>
              <NavTabLocal active={false} onClick={() => onClickTab('analyse')} highlight>Analyse</NavTabLocal>
            </div>
            <div className="shrink-0 w-9 sm:w-9" />
          </div>
          {/* Tabs mobile : 2e ligne scrollable */}
          <div className="md:hidden flex items-center gap-1 mt-2 -mx-1 px-1 overflow-x-auto">
            <NavTabLocal active={false} onClick={() => onClickTab('home')}>Pour toi</NavTabLocal>
            <NavTabLocal active={true} onClick={() => onClickTab('library')}>Découvrir</NavTabLocal>
            <NavTabLocal active={false} onClick={() => onClickTab('vault')}>Mes formats</NavTabLocal>
            <NavTabLocal active={false} onClick={() => onClickTab('analyse')} highlight>Analyse</NavTabLocal>
          </div>
        </div>
      </div>

      {/* Hero : @ d'abord — analyse du compte notée /100 + formats du jour.
          Verticale (?v=sport) : accent + wording du secteur depuis verticales.json. */}
      <div className="max-w-xl mx-auto px-4 sm:px-6 py-6 sm:py-9 text-center w-full">
        <GuestHeroVertical handle={handle} setHandle={setHandle} launch={launch} launching={launching} error={error} />
      </div>

      {/* Grid reels */}
      <div className="flex-1 max-w-7xl mx-auto w-full px-3 sm:px-6 pb-12">
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 sm:gap-3">
            {Array.from({ length: 18 }).map((_, i) => (
              <div key={i} className="rounded-xl bg-ink/5 animate-pulse w-full" style={{ aspectRatio: '9/16' }} />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-12 text-ink-muted text-sm">
            Connecte-toi pour explorer la bibliothèque.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 sm:gap-3">
              {items.map(item => (
                <GuestReelCard key={item.id} item={item} onClick={() => onClickReel(item)} />
              ))}
            </div>
            {/* CTA "Charger plus" → signup gate (pas de pagination réelle
                pour les invités, tout passe par la création de compte). */}
            <div className="flex justify-center mt-8 sm:mt-10">
              <button onClick={() => {
                try { studioEvents.discoverSignupCtaClicked({ trigger: 'load_more' }) } catch {}
                try { localStorage.setItem('studio_post_signup_redirect', 'library') } catch {}
                try { window.dispatchEvent(new CustomEvent('studio:open-guest-signup', { detail: { trigger: 'discover_load_more' } })) } catch {}
              }}
                className="text-sm font-bold px-6 py-3 rounded-xl bg-white border-2 border-ink/10 hover:border-orange/40 hover:shadow-md text-ink transition-all hover:scale-[1.02] active:scale-[0.98]">
                Charger plus de formats →
              </button>
            </div>
          </>
        )}
      </div>

      <LegalFooter tone="light" />
    </div>
  )
}

// Panneau d'analyse PLEIN FORMAT au survol d'une vignette (toutes les
// grilles, quel que soit le tunnel) : mécanisme, émotion, engagement,
// structure étape par étape + CTA. La preuve du décorticage Merlin.
function ThumbAnalysisOverlay({ item, cta }) {
  if (!(item.mecanisme_viral || item.structure)) return null
  return (
    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none flex flex-col p-3"
      style={{ background: 'rgba(15,10,26,0.93)', backdropFilter: 'blur(2px)' }}>
      {item.mecanisme_viral && (
        <div className="text-left">
          <p className="text-[8.5px] uppercase tracking-wider font-bold mb-0.5" style={{ color: 'var(--merlin-accent, #fe8204)' }}>Pourquoi ça marche</p>
          <p className="text-[11.5px] text-white font-bold leading-snug">🧠 {String(item.mecanisme_viral).slice(0, 60)}</p>
        </div>
      )}
      <div className="flex items-center gap-1.5 mt-2 flex-wrap">
        {item.emotion && (
          <span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold text-white" style={{ background: 'rgba(var(--merlin-accent-rgb, 254,130,4),0.25)' }}>⚡ {String(item.emotion).slice(0, 16)}</span>
        )}
        {item.engagement > 0 && (
          <span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold text-white" style={{ background: 'rgba(16,185,129,0.25)' }}>📈 {Number(item.engagement).toFixed(1)}% d'engagement</span>
        )}
      </div>
      {Array.isArray(item.structure) && item.structure.length > 0 && (
        <div className="flex-1 mt-2.5 text-left">
          <p className="text-[8.5px] uppercase tracking-wider font-bold mb-1" style={{ color: 'var(--merlin-violet-soft, #a78bfa)' }}>📐 Structure</p>
          <div className="space-y-1.5">
            {item.structure.map((g, j) => (
              <div key={j} className="flex items-start gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full mt-1 shrink-0" style={{ background: 'var(--merlin-violet, #7c3aed)' }} />
                <div className="leading-tight">
                  <span className="text-[10px] text-white font-bold">{g.n > 1 ? `${g.f} ×${g.n}` : g.f}</span>
                  {g.seau && <span className="text-[9px] text-white/65 block">{prettySeau(g.seau)}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function GuestReelCard({ item, onClick, badgeLabel = 'Personnaliser ✨' }) {
  const supaThumb = item.thumb_url || getSupabaseThumb({ lien: item.lien })
  const formatVues = (n) => {
    if (!n) return null
    if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace('.0', '') + 'M'
    if (n >= 1_000) return Math.round(n / 1_000) + 'k'
    return String(n)
  }
  return (
    <button onClick={onClick}
      className="aspect-[9/16] rounded-xl overflow-hidden bg-ink/5 hover:scale-[1.02] active:scale-[0.98] transition-transform relative group ring-1 ring-border">
      {supaThumb ? (
        <img src={supaThumb} alt={item.format_nom || 'reel viral'}
          className="w-full h-full object-cover"
          loading="lazy"
          onError={(e) => { e.currentTarget.style.display = 'none' }} />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-ink-faint text-[10px] p-2 text-center bg-gradient-to-br from-purple-100 to-orange-100">
          {item.format_nom || 'Format viral'}
        </div>
      )}
      {/* Lien source : admin uniquement */}
      <AdminSourceLink lien={item.lien} />
      <ThumbAnalysisOverlay item={item} cta={badgeLabel} />
      {/* Overlay gradient bas pour lisibilité des badges */}
      <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/75 to-transparent pointer-events-none" />
      <div className="absolute bottom-7 sm:bottom-1 left-1 right-1 flex justify-between items-end gap-1 text-[9px] font-bold text-white drop-shadow">
        {item.vues > 0 && <span className="bg-black/40 px-1.5 py-0.5 rounded backdrop-blur-sm">{formatVues(item.vues)} vues</span>}
        {item.format_nom && <span className="truncate opacity-90">{item.format_nom}</span>}
      </div>
    </button>
  )
}

function LegalFooter({ tone = 'dark' }) {
  // tone='dark' (texte clair sur fond foncé) | 'light' (texte foncé sur clair)
  const text = tone === 'light' ? 'text-ink-muted hover:text-ink' : 'text-white/40 hover:text-white/70'
  const sep = tone === 'light' ? 'text-ink-faint' : 'text-white/20'
  return (
    <div className={`text-[10px] sm:text-[11px] text-center py-3 px-4 flex flex-wrap items-center justify-center gap-x-2 gap-y-1`}>
      <a href="/cgu" className={`${text} transition-colors underline`}>CGU</a>
      <span className={sep}>·</span>
      <a href="/cgv" className={`${text} transition-colors underline`}>CGV</a>
      <span className={sep}>·</span>
      <a href="/confidentialite" className={`${text} transition-colors underline`}>Confidentialité</a>
      <span className={sep}>·</span>
      <a href="/mentions-legales" className={`${text} transition-colors underline`}>Mentions légales</a>
      <span className={sep}>·</span>
      <a href="mailto:hello@merlin-content-ai.com" className={`${text} transition-colors underline`}>Contact</a>
      <span className={sep}>·</span>
      <span className={tone === 'light' ? 'text-ink-faint' : 'text-white/30'}>© 2026 AUDAX</span>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
const NICHE_MACRO_OPTIONS = [
  { value: 'Business & Finance', emoji: '💼', hint: 'Entrepreneurs, freelances, business solo, vente en ligne' },
  { value: 'Créativité & Contenu', emoji: '🎨', hint: 'Créateurs de contenu, vidéastes, écrivains, designers' },
  { value: 'Développement personnel', emoji: '🧠', hint: 'Productivité, mindset, confiance, motivation' },
  { value: 'Éducation', emoji: '📚', hint: 'Langues, apprentissage, vulgarisation, étudiants' },
  { value: 'Lifestyle & Divertissement', emoji: '✨', hint: 'Food, voyage, mode, déco, humour' },
  { value: 'Santé & Bien-être', emoji: '🧘', hint: 'Fitness, sommeil, nutrition, stress, mobilité' },
  { value: 'Technologie', emoji: '⚡', hint: 'Tech, IA, no-code, outils, devs' },
]

function StudioIntake({ handle, setHandle, launch, launchGeneric, launching, error }) {
  // Verticale : habillage (accent, chip, wording) depuis verticales.json.
  // vt=null → intake Merlin classique, strictement identique à avant.
  const vt = VERTICALES[getVertical()] || null
  // L'orange Merlin reste l'accent d'action même en verticale (le fond, lui, change)
  const accent = '#fe8204'
  const accent2 = '#f59e0b'
  const accentShadow = 'rgba(254,130,4,0.30)'
  useEffect(() => { studioEvents.intakeShown() }, [])
  const [genericOpen, setGenericOpen] = useState(false)
  const [nicheMacro, setNicheMacro] = useState('')
  const [nicheFine, setNicheFine] = useState('')

  function openGeneric() {
    studioEvents.genericModeChosen({})
    setGenericOpen(true)
  }
  function closeGeneric() {
    studioEvents.genericModeDismissed({ had_niche: !!nicheMacro })
    setGenericOpen(false)
    setNicheMacro('')
    setNicheFine('')
  }
  function submitGeneric() {
    if (!nicheMacro) return
    studioEvents.genericNicheSelected({ niche_macro: nicheMacro, has_niche_fine: !!nicheFine.trim() })
    setGenericOpen(false)
    launchGeneric(nicheMacro, nicheFine.trim() || null)
  }

  return (
    <div className={`min-h-screen text-white flex flex-col ${vt?.bg_gradient ? '' : 'bg-gradient-to-b from-purple-950 via-purple-900 to-indigo-950'}`}
      style={vt?.bg_gradient ? { background: vt.bg_gradient } : undefined}>
      {/* Barre admin (invisible pour les users) : l'intake est l'écran
          d'atterrissage post-login, sans elle l'admin n'avait AUCUN accès
          aux outils ni au panel (constat 2026-06-11) */}
      <div className="absolute top-3 right-3 z-20 flex items-center gap-1.5">
        <StudioAdminLogsButton />
        <StudioAdminTopFormatsButton />
              <StudioAdminAdsButton />
              <StudioAdminSeoButton />
        <StudioAdminHubLink />
        <StudioAdminVerticalToggle />
        <StudioAdminSimulateSportButton />
              <StudioAdminGuestToggle />
      </div>
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 max-w-2xl mx-auto w-full">
        <img src={merlinImg} alt="Merlin" className="w-28 h-28 mb-6 object-contain"
          style={{ filter: 'drop-shadow(0 10px 40px rgba(254,130,4,0.45))' }} />

        {vt && (
          <div className="mb-4 px-3.5 py-1.5 rounded-full text-[11px] font-extrabold tracking-[0.12em]"
            style={{ background: vt.accent, color: '#fff' }}>
            {vt.chip}
          </div>
        )}
        <h1 className="text-3xl sm:text-4xl font-bold text-center mb-3 leading-tight">
          {vt
            ? <>{vt.h1_prefix} <span style={{ color: accent }}>{vt.h1_accent}</span> {vt.h1_suffix}</>
            : <>Ton compte noté sur 100, et tes 3 formats adaptés à <span style={{ color: 'var(--merlin-accent, #fe8204)' }}>TON</span> compte</>}
        </h1>
        <p className="text-sm text-white/70 text-center max-w-md mb-10 leading-relaxed">
          {vt?.sous_titre || 'Colle ton Instagram. Merlin analyse ton compte, le note sur 100, et te trouve 3 formats viraux adaptés à ton positionnement.'}
        </p>

        <div className="w-full max-w-md rounded-2xl bg-white/[0.06] backdrop-blur-sm border border-white/15 p-5">
          <label className="text-[10px] uppercase tracking-wider font-bold text-white/50 mb-2 block">
            Ton compte Instagram
          </label>
          <div className="flex items-center gap-1 bg-white/10 rounded-lg px-3.5 py-3.5 mb-3 transition-colors focus-within:bg-white/15">
            <span className="text-white/50 text-base">@</span>
            <input
              type="text"
              value={handle}
              onChange={(e) => setHandle(e.target.value)}
              placeholder="ton_compte_instagram"
              autoComplete="off" autoCapitalize="none" autoCorrect="off" spellCheck={false}
              className="bg-transparent flex-1 outline-none text-white placeholder-white/30 text-base"
              onKeyDown={(e) => { if (e.key === 'Enter' && !launching) launch() }}
            />
          </div>
          <button
            onClick={launch}
            disabled={launching || !handle.trim()}
            className="w-full py-3.5 rounded-lg font-bold text-sm text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:scale-[1.01]"
            style={{
              background: launching || !handle.trim() ? (vt ? `${accent}80` : 'rgba(254,130,4,0.5)') : `linear-gradient(135deg, ${accent}, ${accent2})`,
              boxShadow: launching || !handle.trim() ? 'none' : `0 8px 24px ${accentShadow}`,
            }}>
            {launching ? 'Analyse en cours…' : (vt?.cta || 'Voir mon score + mes 3 formats →')}
          </button>
          {error && <p className="text-xs text-red-300 mt-3 text-center">⚠ {error}</p>}

          <div className="flex items-center gap-3 my-4">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-[10px] uppercase tracking-wider text-white/40">ou</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <button
            onClick={openGeneric}
            disabled={launching}
            className="w-full py-3 rounded-lg text-sm font-semibold text-white/90 bg-white/[0.04] hover:bg-white/[0.08] border border-white/15 transition-all disabled:opacity-40">
            Je n'ai pas encore de compte Instagram
          </button>
        </div>

        <p className="text-[11px] text-white/40 text-center mt-6 max-w-md">
          ~20 secondes · Lecture seule de ton compte public · Aucun post automatique
        </p>
      </div>
      <LegalFooter tone="dark" />

      {genericOpen && <GenericNicheModal
        nicheMacro={nicheMacro}
        setNicheMacro={setNicheMacro}
        nicheFine={nicheFine}
        setNicheFine={setNicheFine}
        onSubmit={submitGeneric}
        onClose={closeGeneric}
      />}
    </div>
  )
}

function GenericNicheModal({ nicheMacro, setNicheMacro, nicheFine, setNicheFine, onSubmit, onClose }) {
  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-in fade-in" onClick={onClose}>
      <div
        className="w-full max-w-md rounded-2xl bg-gradient-to-b from-purple-950 to-indigo-950 border border-white/15 shadow-2xl overflow-hidden text-white"
        onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-white/10 flex items-start justify-between">
          <div>
            <h2 className="text-base font-bold leading-tight">Quel type de contenu tu veux faire ?</h2>
            <p className="text-[11px] text-white/50 mt-1">Choisis une niche, on te propose 3 formats viraux qui marchent dedans.</p>
          </div>
          <button
            onClick={onClose}
            className="text-white/40 hover:text-white/80 text-lg leading-none w-6 h-6 flex items-center justify-center"
            aria-label="Fermer">×</button>
        </div>

        <div className="px-5 py-4 space-y-2 max-h-[55vh] overflow-y-auto">
          {NICHE_MACRO_OPTIONS.map(opt => {
            const isSel = nicheMacro === opt.value
            return (
              <button
                key={opt.value}
                onClick={() => setNicheMacro(opt.value)}
                className={`w-full text-left p-3 rounded-lg border transition-all flex items-start gap-3 ${
                  isSel
                    ? 'bg-orange-500/15 border-orange-400/50'
                    : 'bg-white/[0.04] border-white/10 hover:bg-white/[0.08] hover:border-white/20'
                }`}>
                <span className="text-xl leading-none mt-0.5">{opt.emoji}</span>
                <div className="flex-1 min-w-0">
                  <div className={`text-sm font-semibold ${isSel ? 'text-orange-200' : 'text-white'}`}>{opt.value}</div>
                  <div className="text-[11px] text-white/50 leading-snug mt-0.5">{opt.hint}</div>
                </div>
                {isSel && <div className="text-orange-400 text-base">✓</div>}
              </button>
            )
          })}
        </div>

        <div className="px-5 pt-3 pb-4 border-t border-white/10">
          <label className="text-[10px] uppercase tracking-wider font-bold text-white/50 mb-1.5 block">
            Plus précisément <span className="text-white/30 normal-case font-normal">(optionnel)</span>
          </label>
          <input
            type="text"
            value={nicheFine}
            onChange={(e) => setNicheFine(e.target.value)}
            placeholder="ex : Coach yoga femmes 35-50 ans"
            maxLength={120}
            className="w-full bg-white/10 rounded-lg px-3 py-2.5 outline-none text-sm text-white placeholder-white/30 focus:bg-white/15 transition-colors"
          />
          <button
            onClick={onSubmit}
            disabled={!nicheMacro}
            className="w-full mt-3 py-3 rounded-lg font-bold text-sm text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:scale-[1.01]"
            style={{
              background: !nicheMacro ? 'rgba(var(--merlin-accent-rgb, 254,130,4),0.5)' : 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))',
              boxShadow: !nicheMacro ? 'none' : '0 8px 24px rgba(var(--merlin-accent-rgb, 254,130,4),0.30)',
            }}>
            Trouver mes 3 formats viraux →
          </button>
          <p className="text-[10px] text-white/40 text-center mt-2">
            ~60 secondes · Pas de compte requis
          </p>
        </div>
      </div>
    </div>,
    document.body
  )
}

// ═══════════════════════════════════════════════════════════════
// AUTH GATE — signup/login inline avant intake
// ═══════════════════════════════════════════════════════════════
// Détection du webview in-app Meta (Instagram/Facebook). Google OAuth y
// échoue souvent (403 disallowed_useragent côté Google) : on adapte le CTA.
//   Android : deep link intent:// qui force l'ouverture dans Chrome,
//             l'user retombe sur cette page en vrai navigateur et Google marche.
//   iOS     : aucun échappement programmatique possible (bloqué par Apple),
//             on masque Google, email-first + hint "menu ⋯ → Ouvrir dans le navigateur".
function getWebviewInfo() {
  try {
    const ua = navigator.userAgent || ''
    const isMetaWebview = /Instagram|FBAN|FBAV|FB_IAB/i.test(ua)
    // Webview in-app élargi : toutes les apps qui ouvrent les liens de pub dans
    // leur navigateur intégré cassé (Google OAuth mort, session fragmentée).
    //  - Meta (Insta/FB)  - TikTok (BytedanceWebview / musical_ly / Trill)
    //  - Snapchat  - LinkedIn  - Android WebView générique (« ; wv »).
    // Sert au handoff « ouvre dans ton navigateur » ; isMetaWebview reste
    // inchangé pour la logique Google existante.
    const isTikTok = /BytedanceWebview|musical_ly|Trill|TikTok|ByteFull/i.test(ua)
    const isOtherIab = /Snapchat|LinkedInApp|\bwv\b/i.test(ua)
    return {
      isMetaWebview,
      isTikTok,
      isInAppWebview: isMetaWebview || isTikTok || isOtherIab,
      isAndroid: /Android/i.test(ua),
      isIOS: /iPhone|iPad|iPod/i.test(ua),
    }
  } catch { return { isMetaWebview: false, isTikTok: false, isInAppWebview: false, isAndroid: false, isIOS: false } }
}

// Construit l'URL à ouvrir dans le vrai navigateur. On cible la racine et on
// emporte le @ via ?ig=<handle> : dans le nouveau navigateur, le scan repart
// TOUT SEUL (cf. auto-ig), donc la bascule est transparente. Les UTM sont gardés.
function buildBrowserUrl(handle) {
  try {
    const u = new URL(window.location.href)
    const h = String(handle || '').replace(/^@/, '').trim()
    if (h && /^[a-zA-Z0-9._]{1,30}$/.test(h)) u.searchParams.set('ig', h)
    // Visiteur deal : le flag localStorage ne suit pas dans le vrai
    // navigateur, on le remet dans l'URL même si la navigation SPA l'a
    // fait disparaître de la barre d'adresse.
    try { if (localStorage.getItem('studio_deal_offer') === '1') u.searchParams.set('deal', '1') } catch { /* best effort */ }
    u.pathname = '/'
    u.hash = ''
    return u.href
  } catch { return window.location.href }
}

function escapeWebviewToChrome(handle) {
  // intent:// ouvre l'URL dans Chrome (fallback https si Chrome absent).
  try {
    const u = new URL(buildBrowserUrl(handle))
    const fallback = encodeURIComponent(u.href)
    window.location.href =
      `intent://${u.host}${u.pathname}${u.search}#Intent;scheme=https;package=com.android.chrome;S.browser_fallback_url=${fallback};end`
  } catch {}
}

// ═══════════════════════════════════════════════════════════════
// BANNIÈRE "OUVRE DANS TON NAVIGATEUR" — webview in-app Meta.
// Le navigateur d'Instagram casse Google OAuth, fragmente la session (-> le
// checkout renvoie à l'accueil sans payer) et empêche de revenir (pas de
// favori / écran d'accueil). On propose de basculer vers le vrai navigateur
// en emportant le @ (le scan reprend seul). Ne s'affiche QUE en webview Meta.
// context: 'results' (doux, post-score) | 'signup' | 'checkout' (fort).
// ═══════════════════════════════════════════════════════════════
function WebviewBanner({ handle, context = 'signup' }) {
  const wv = useMemo(() => getWebviewInfo(), [])
  const [copied, setCopied] = useState(false)
  useEffect(() => {
    if (wv.isMetaWebview) { try { studioEvents.webviewBannerShown?.({ context, platform: wv.isIOS ? 'ios' : wv.isAndroid ? 'android' : 'other' }) } catch {} }
  }, []) // eslint-disable-line
  if (!wv.isMetaWebview) return null
  const url = buildBrowserUrl(handle)
  const msg = context === 'checkout'
    ? 'Pour payer sans bug, ouvre Merlin dans ton navigateur.'
    : context === 'results'
      ? 'Ouvre Merlin dans ton navigateur pour créer ton compte sans bug et le retrouver après.'
      : 'Tu es dans le navigateur d\'Instagram. Ouvre dans ton vrai navigateur pour t\'inscrire et payer sans accroc.'
  function copy() {
    try { navigator.clipboard.writeText(url) } catch {}
    setCopied(true); setTimeout(() => setCopied(false), 2200)
    try { studioEvents.webviewOpenClicked?.({ context, method: 'copy' }) } catch {}
  }
  function openChrome() {
    try { studioEvents.webviewOpenClicked?.({ context, method: 'android_intent' }) } catch {}
    escapeWebviewToChrome(handle)
  }
  return (
    <div className="rounded-xl p-3 mb-3" style={{ background: 'rgba(var(--merlin-accent-rgb, 254,130,4),0.12)', border: '1px solid rgba(var(--merlin-accent-rgb, 254,130,4),0.35)' }}>
      <p className="text-[12.5px] font-bold leading-snug flex items-start gap-1.5" style={{ color: 'var(--merlin-accent-soft, #fdba74)' }}>
        <span>📲</span><span>{msg}</span>
      </p>
      {wv.isAndroid ? (
        <button type="button" onClick={openChrome}
          className="mt-2 w-full py-2.5 rounded-lg font-bold text-[13px] text-white"
          style={{ background: 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))' }}>
          Ouvrir dans Chrome →
        </button>
      ) : (
        <div className="mt-2">
          <p className="text-[11.5px] text-white/65 leading-snug">
            Touche le menu <span className="font-bold text-white/90">•••</span> en haut à droite → <span className="font-bold text-white/90">« Ouvrir dans le navigateur »</span>.
          </p>
          <button type="button" onClick={copy}
            className="mt-2 w-full py-2 rounded-lg font-bold text-[12.5px]"
            style={{ background: 'rgba(255,255,255,0.12)', color: '#fff', border: '1px solid rgba(255,255,255,0.18)' }}>
            {copied ? 'Lien copié ✓ (colle-le dans Safari)' : 'Ou copier le lien (reprend ton analyse)'}
          </button>
        </div>
      )}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// HANDOFF « OUVRE DANS TON NAVIGATEUR » — écran plein post-scan.
// Le scan tourne à 83% en webview mais le compte y meurt (1% vs 15% en
// navigateur) : créer un compte OBLIGE à sortir du webview (round-trip
// mail). Donc on garde le wow (scan) in-app, et on déporte la note + le
// compte dans le VRAI navigateur, PILE au pic de motivation (note prête).
//  - Android : intent:// ouvre Chrome, le scan repart seul (?ig=handle).
//  - iOS     : Apple bloque l'ouverture auto → geste ••• + lien copié.
// S'affiche pour TOUT webview in-app (Meta + TikTok + Snap + LinkedIn).
// onStay = repli « voir ma note ici » (garde l'OTP comme filet).
// ═══════════════════════════════════════════════════════════════
function HandoffToNavigator({ handle, score, onStay }) {
  const wv = useMemo(() => getWebviewInfo(), [])
  const [copied, setCopied] = useState(false)
  const platform = wv.isIOS ? 'ios' : wv.isAndroid ? 'android' : 'other'
  useEffect(() => {
    try { studioEvents.webviewBannerShown?.({ context: 'note_handoff', platform }) } catch {}
  }, []) // eslint-disable-line
  const g = Number.isFinite(score?.global) ? score.global : null
  const scoreLabel = g == null ? '7?' : `${g}`
  const url = buildBrowserUrl(handle)
  function openBrowser() {
    if (wv.isAndroid) {
      try { studioEvents.webviewOpenClicked?.({ context: 'note_handoff', method: 'android_intent' }) } catch {}
      escapeWebviewToChrome(handle)
    } else {
      try { navigator.clipboard.writeText(url) } catch {}
      setCopied(true); setTimeout(() => setCopied(false), 2600)
      try { studioEvents.webviewOpenClicked?.({ context: 'note_handoff', method: 'copy' }) } catch {}
    }
  }
  function stay() {
    try { studioEvents.noteHandoffStay?.({ platform }) } catch {}
    onStay?.()
  }
  return (
    <div className="min-h-screen w-full text-white" style={{ background: 'radial-gradient(120% 60% at 50% 0%, #2d1b69 0%, #1a1040 45%, #0f0a1a 100%)' }}>
      <div className="max-w-md mx-auto px-6 pt-6 pb-8 min-h-screen flex flex-col">
        <span className="self-center inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-wide rounded-full px-3 py-1.5"
          style={{ color: '#fdba74', background: 'rgba(254,130,4,0.12)', border: '1px solid rgba(254,130,4,0.32)' }}>
          ✨ Analyse terminée
        </span>

        <div className="relative flex justify-center mt-3">
          <div className="absolute" style={{ width: 260, height: 260, top: -18, left: '50%', transform: 'translateX(-50%)', background: 'radial-gradient(circle, rgba(254,130,4,0.40) 0%, rgba(254,130,4,0) 66%)', filter: 'blur(4px)' }} />
          <img src={merlinFound} alt="" className="relative w-[180px] h-auto" style={{ filter: 'drop-shadow(0 12px 24px rgba(0,0,0,0.45))' }} />
        </div>

        <h1 className="text-center font-extrabold mt-3.5 leading-tight" style={{ fontSize: 27, letterSpacing: '-0.01em' }}>
          Ta note est prête <span style={{ color: '#fdba74' }}>🔮</span>
        </h1>
        <p className="text-center mt-3 leading-relaxed" style={{ fontSize: 14.5, color: 'rgba(255,255,255,0.72)' }}>
          On a passé {handle ? <>@{handle}</> : 'ton compte'} au crible. Ta note et tes formats t'attendent, mais <b className="text-white">Instagram bloque l'affichage ici.</b>
        </p>

        <div className="mt-5 rounded-[20px] p-[18px] relative overflow-hidden"
          style={{ background: 'linear-gradient(160deg, rgba(124,58,237,0.28), rgba(45,27,105,0.32))', border: '1px solid rgba(255,255,255,0.12)', boxShadow: '0 10px 30px rgba(0,0,0,0.32)' }}>
          <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)' }}>Ta note globale</div>
          <div className="flex items-center justify-between mt-1.5">
            <div style={{ fontSize: 56, fontWeight: 900, lineHeight: 1, filter: 'blur(9px)', userSelect: 'none', background: 'linear-gradient(180deg,#fff,#fdba74)', WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }}>
              {scoreLabel}<span style={{ fontSize: 24 }}>/100</span>
            </div>
            <div className="flex items-center justify-center" style={{ width: 46, height: 46, borderRadius: 14, background: 'rgba(255,255,255,0.10)', border: '1px solid rgba(255,255,255,0.18)', fontSize: 22 }}>🔒</div>
          </div>
          <div className="flex gap-1.5 mt-3.5">
            <span className="flex-1 rounded" style={{ height: 7, filter: 'blur(2px)', background: 'linear-gradient(90deg,#fe8204,#f59e0b)' }} />
            <span className="flex-1 rounded" style={{ height: 7, filter: 'blur(2px)', background: 'linear-gradient(90deg,#7c3aed,#a855f7)' }} />
            <span className="flex-1 rounded" style={{ height: 7, filter: 'blur(2px)', background: 'linear-gradient(90deg,#16a34a,#4ade80)' }} />
          </div>
        </div>

        <ul className="list-none mt-[18px] flex flex-col gap-2.5">
          {[['📊', 'Ta note détaillée', 'les points forts et les trous de ton compte'],
            ['🎯', '3 formats viraux', 'taillés pour ton compte, à refaire direct'],
            ['🪄', 'Ta trame à refaire', "l'accroche et les étapes, prêtes à filmer"]].map(([ic, t, d], i) => (
            <li key={i} className="flex items-center gap-3 rounded-[14px] px-3 py-[11px]" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <span className="flex items-center justify-center shrink-0" style={{ width: 34, height: 34, borderRadius: 10, fontSize: 17, background: 'rgba(254,130,4,0.14)', border: '1px solid rgba(254,130,4,0.28)' }}>{ic}</span>
              <div>
                <b className="block text-white" style={{ fontSize: 14, lineHeight: 1.2 }}>{t}</b>
                <span className="block" style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.58)', marginTop: 2, lineHeight: 1.3 }}>{d}</span>
              </div>
            </li>
          ))}
        </ul>

        <div className="flex-1 min-h-[12px]" />

        <button type="button" onClick={openBrowser} className="w-full text-center font-extrabold text-white"
          style={{ padding: '17px 18px', borderRadius: 16, fontSize: 16.5, background: 'linear-gradient(135deg,#fe8204,#f59e0b)', boxShadow: '0 12px 26px rgba(254,130,4,0.42), inset 0 1px 0 rgba(255,255,255,0.25)' }}>
          {copied ? 'Lien copié ✓ colle-le dans Safari' : <>Découvrir ma note dans mon navigateur <span style={{ marginLeft: 4 }}>→</span></>}
        </button>
        <div className="flex items-center justify-center gap-2 flex-wrap mt-3" style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.6)' }}>
          <b style={{ color: 'rgba(255,255,255,0.85)' }}>Gratuit</b><span style={{ width: 3, height: 3, borderRadius: '50%', background: 'rgba(255,255,255,0.4)' }} />Rien à installer<span style={{ width: 3, height: 3, borderRadius: '50%', background: 'rgba(255,255,255,0.4)' }} /><b style={{ color: 'rgba(255,255,255,0.85)' }}>Ton analyse te suit</b>
        </div>

        {!wv.isAndroid && (
          <div className="mt-4 text-center rounded-xl px-3 py-2.5" style={{ fontSize: 12, lineHeight: 1.5, color: 'rgba(255,255,255,0.52)', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.09)' }}>
            Sur iPhone : touche le menu <b style={{ color: 'rgba(255,255,255,0.82)' }}>•••</b> en haut à droite → <b style={{ color: 'rgba(255,255,255,0.82)' }}>« Ouvrir dans le navigateur »</b>
          </div>
        )}

        <button type="button" onClick={stay} className="mt-4 mx-auto block" style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.45)', textDecoration: 'underline' }}>
          Voir ma note ici quand même
        </button>
      </div>
    </div>
  )
}

function StudioAuthGate({ signUp, signIn, google, onGuestScanStarted }) {
  const [mode, setMode] = useState('signup') // 'signup' | 'signin'
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  // setConsent est référencé par les checkbox CGU des deux formulaires : le
  // setter manquait (ReferenceError au tap sur la case, bug préexistant
  // devenu visible avec le magic par défaut en webview).
  const [consent, setConsent] = useState(true) // notice-based : la ligne légale sous le CTA vaut acceptation
  const [dailyOptin, setDailyOptin] = useState(true)
  const [loading, setLoading] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  const [error, setError] = useState(null)
  const [info, setInfo] = useState(null)
  const webview = useMemo(() => getWebviewInfo(), [])
  // Webview Meta : magic/OTP par défaut (mêmes raisons que GuestSignupModal :
  // pas de mot de passe à créer, lien → vrai navigateur avec ?claim=,
  // code 6 chiffres → reste dans le webview). Hors webview : mot de passe.
  const [authMode, setAuthMode] = useState(webview.isMetaWebview ? 'magic' : 'password') // 'magic' | 'password'
  const [magicSent, setMagicSent] = useState(false)
  const [otpCode, setOtpCode] = useState('')
  const [otpLoading, setOtpLoading] = useState(false)
  const { signInWithMagicLink, verifyEmailOtp } = useAuth()

  async function sendMagic(e) {
    e?.preventDefault?.()
    setError(null)
    if (!email) { setError('Entre ton email.'); return }
    setLoading(true)
    studioEvents.signupMethodChosen({ method: 'magic_link', mode })
    try {
      // ?claim=<anon_id> dans l'URL de retour : si un scan invité existe et
      // que le lien s'ouvre dans un autre navigateur (webview → Safari), le
      // claim global le rattache quand même.
      let anonId = ''
      try { anonId = localStorage.getItem('studio_anon_id') || '' } catch { /* ignore */ }
      const redirectTo = buildMagicRedirect()
      await signInWithMagicLink(email, { displayName: name, consent, dailyOptin, redirectTo })
      setMagicSent(true)
    } catch (err) {
      setError(err?.message || 'Envoi impossible, réessaie.')
    } finally {
      setLoading(false)
    }
  }

  // Code à 6 chiffres du même envoi : verifyOtp pose la session dans CE
  // navigateur (webview comprise), le context re-render vers le scan
  // (autolaunch + claim gérés par les effets globaux sur user.id).
  async function submitOtp(e) {
    e?.preventDefault?.()
    setError(null)
    const code = otpCode.replace(/\D/g, '')
    if (code.length < 6) { setError('Le code du mail fait au moins 6 chiffres.'); return }
    setOtpLoading(true)
    studioEvents.signupMethodChosen({ method: 'email_otp', mode })
    try {
      await verifyEmailOtp(email, code)
      trackLead({ method: 'email_otp' })
      studioEvents.signupCompleted({ method: 'email_otp' })
    } catch (err) {
      setOtpLoading(false)
      studioEvents.signupFailed({ method: 'email_otp', error: (err?.message || '').substring(0, 100) })
      setError(/expired|invalid|token/i.test(err?.message || '')
        ? 'Code invalide ou expiré. Clique « Renvoyer » pour en recevoir un nouveau.'
        : (err?.message || 'Code refusé, réessaie.'))
    }
  }

  // ── ÉTAPE 1 : LE @ D'ABORD, LE COMPTE ENSUITE ───────────────
  // On demande la valeur (ton @ Instagram) avant l'engagement (le compte).
  // Le @ est stocké + flag autolaunch : après le signup, le scan part tout
  // seul. Si l'user arrive de la LP avec un @ déjà saisi, on saute
  // directement à l'étape compte.
  const [gateStep, setGateStep] = useState(() => {
    try { return localStorage.getItem('studio_prefill_handle') || localStorage.getItem('studio_prefill_niche_macro') ? 'account' : 'handle' } catch { return 'handle' }
  })
  const [gateHandle, setGateHandle] = useState(() => {
    try { return localStorage.getItem('studio_prefill_handle') || '' } catch { return '' }
  })
  const [gateGenericNiche, setGateGenericNiche] = useState(() => {
    try { return localStorage.getItem('studio_prefill_niche_macro') || '' } catch { return '' }
  })
  const [genericOpen, setGenericOpen] = useState(false)
  const [nicheMacro, setNicheMacro] = useState('')
  const [nicheFine, setNicheFine] = useState('')

  function cleanHandle(raw) {
    return (raw || '').trim()
      .replace(/^https?:\/\//i, '')
      .replace(/^(www\.)?instagram\.com\//i, '')
      .replace(/[\/?#].*$/, '')
      .replace(/^@/, '')
      .replace(/\s+/g, '')
  }

  const [gateLaunching, setGateLaunching] = useState(false)
  const gateTypedRef = useRef(false)
  const autoIgRef = useRef(false)

  // Erreur d'un scan invité précédent (@ introuvable...) : affichée une fois
  useEffect(() => {
    try {
      const msg = localStorage.getItem('studio_gate_error')
      if (msg) {
        setError(msg)
        localStorage.removeItem('studio_gate_error')
      }
    } catch {}
  }, [])

  // ?ig=username : l'@ arrive dans l'URL (automatisation ManyChat qui
  // connaît l'identité du DM) → le scan démarre TOUT SEUL, zéro saisie.
  // Impossible pour le trafic ads (Instagram ne révèle jamais le visiteur),
  // mais parfait pour les DM : l'analyse est déjà en route au clic.
  useEffect(() => {
    if (autoIgRef.current) return
    try {
      const ig = new URLSearchParams(window.location.search).get('ig')
      const clean = cleanHandle(ig || '')
      if (clean && /^[a-zA-Z0-9._]{1,30}$/.test(clean)) {
        autoIgRef.current = true
        setGateHandle(clean)
        studioEvents.gateHandleSubmitted({ handle_length: clean.length, auto_ig: true })
        // Nettoie l'URL (pas de re-scan au refresh) puis lance
        try { window.history.replaceState({}, '', window.location.pathname) } catch {}
        setTimeout(() => submitGateHandle(clean), 250)
      }
    } catch {}
  }, [])

  async function submitGateHandle(handleArg) {
    const clean = cleanHandle(typeof handleArg === 'string' ? handleArg : gateHandle)
    if (!clean) { setError('Tape ton pseudo Instagram'); return }
    // Les visiteurs tapent parfois leur email ici (vu en prod : sessions en
    // erreur sur "aaron...@gmail.com"). Un handle IG = lettres/chiffres/._
    if (!/^[a-zA-Z0-9._]{1,30}$/.test(clean)) {
      setError(clean.includes('@') ? "Ça ressemble à un email : entre ton pseudo Instagram (celui après le @ sur ton profil)" : 'Pseudo Instagram invalide (lettres, chiffres, points et _ uniquement)')
      return
    }
    if (gateLaunching) return
    setError(null)
    setGateLaunching(true)
    studioEvents.gateHandleSubmitted({ handle_length: clean.length })
    try {
      localStorage.setItem('studio_prefill_handle', clean)
      localStorage.removeItem('studio_prefill_niche_macro')
      localStorage.removeItem('studio_prefill_niche_fine')
      localStorage.removeItem('studio_guest_mode')
    } catch {}
    setGateHandle(clean)
    setGateGenericNiche('')

    // ── FLOW INVITÉ-D'ABORD (2026-06-11) ─────────────────────────
    // Le scan démarre EN INVITÉ et l'user va DIRECT sur la bibliothèque
    // avec la progression, SANS créer de compte. La création de compte
    // n'est demandée qu'à la fin, pour accéder à SES formats (pic de
    // motivation : il a investi 90s et ses résultats l'attendent).
    try {
      const guestId = getGuestSessionId()
      const res = await fetch(`${API_URL}/api/studio/init-guest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ig_username: clean, guest_session_id: guestId, deal: isDealVisitor(), offer_arm: getTrialArm() }),
      })
      const d = await res.json()
      if (res.ok && d?.id) {
        try { localStorage.setItem('studio_guest_session_id', d.id) } catch {}
        studioEvents.scanStarted({ pre_signup: true, guest: true, handle_length: clean.length })
        setGateLaunching(false)
        onGuestScanStarted?.(d.id, clean)
        return
      }
      throw new Error(d?.error || `HTTP ${res.status}`)
    } catch (e) {
      // FALLBACK (rate limit IP, RocketAPI down...) : flow classique,
      // compte d'abord puis scan auto-lancé après le signup.
      try {
        localStorage.setItem('studio_autolaunch', '1')
        localStorage.removeItem('studio_claim_after_signup')
      } catch {}
      setGateLaunching(false)
      studioEvents.scanFailed({ reason: 'guest_init_failed', source_page: 'signup', error: (e?.message || '').substring(0, 80), guest: true, pre_signup: true })
      studioEvents.intakeShown({ pre_signup: true, has_handle: true, guest_init_failed: true })
      setGateStep('account')
    }
  }

  function submitGateGeneric() {
    if (!nicheMacro) return
    try {
      localStorage.setItem('studio_prefill_niche_macro', nicheMacro)
      if (nicheFine.trim()) localStorage.setItem('studio_prefill_niche_fine', nicheFine.trim())
      else localStorage.removeItem('studio_prefill_niche_fine')
      localStorage.setItem('studio_autolaunch', '1')
      localStorage.removeItem('studio_prefill_handle')
      localStorage.removeItem('studio_guest_mode')
    } catch {}
    setGateGenericNiche(nicheMacro)
    setGateHandle('')
    studioEvents.genericNicheSelected({ niche_macro: nicheMacro, has_niche_fine: !!nicheFine.trim(), pre_signup: true })
    setGenericOpen(false)
    setGateStep('account')
  }

  useEffect(() => {
    studioEvents.signupPageLoaded({
      mode,
      gate_step: gateStep,
      in_meta_webview: webview.isMetaWebview,
      webview_os: webview.isMetaWebview ? (webview.isAndroid ? 'android' : webview.isIOS ? 'ios' : 'other') : null,
    })
  }, [])

  async function handleGoogle() {
    setError(null)
    // La mention CGU est affichée sous le bouton : continuer = accepter.
    try { localStorage.setItem('studio_oauth_consent_shown', new Date().toISOString()) } catch { /* ignore */ }; setInfo(null)
    // Webview Android : on s'évade vers Chrome AVANT de tenter OAuth
    // (Google renvoie 403 disallowed_useragent dans le webview Meta).
    if (webview.isMetaWebview && webview.isAndroid) {
      studioEvents.signupMethodChosen({ method: 'google_webview_escape', mode })
      escapeWebviewToChrome()
      return
    }
    setGoogleLoading(true)
    studioEvents.signupMethodChosen({ method: 'google', mode })
    try {
      await google()
      // OAuth redirige hors-page, le retour set le user dans le context
    } catch (e) {
      setError(e?.message || 'Erreur Google')
      setGoogleLoading(false)
      studioEvents.signupFailed({ method: 'google', error: (e?.message || '').substring(0, 100) })
    }
  }

  async function submit(e) {
    e?.preventDefault?.()
    setError(null); setInfo(null)
    if (!email || !password) { setError('Email et mot de passe requis'); return }
    setLoading(true)
    studioEvents.signupMethodChosen({ method: 'email', mode })
    try {
      if (mode === 'signup') {
        const res = await signUp(email, password, name || email.split('@')[0], { consent, dailyOptin })
        if (res?.error) throw new Error(res.error.message || 'Inscription échouée')
        trackLead({ method: 'email' })
        studioEvents.signupCompleted({ method: 'email' })
        // Avec l'autoconfirm Supabase activé, signUp retourne directement
        // une session : le user est loggé, la page re-render vers le scan
        // auto-lancé, aucun message à afficher. Le message boîte mail ne
        // s'affiche QUE si la confirmation email est encore exigée.
        const gotSession = !!(res?.session)
        if (!gotSession) {
          setInfo('Vérifie ta boîte mail pour confirmer ton compte (puis reviens ici).')
        }
      } else {
        const res = await signIn(email, password)
        if (res?.error) throw new Error(res.error.message || 'Connexion échouée')
        // La page va se re-render automatiquement (user dans context)
      }
    } catch (e) {
      // Email déjà inscrit → bascule en connexion (pas d'impasse pour les
      // users de retour, cf. 22 échecs "already registered" sur 30j).
      if (mode === 'signup' && /already registered/i.test(e.message || '')) {
        setMode('signin')
        setError('Tu as déjà un compte avec cet email. Entre ton mot de passe pour te connecter.')
      } else if (/invalid login credentials/i.test(e.message || '')) {
        // Même filet que la modale paywall : mot de passe oublié → lien
        // magique proposé d'office au lieu d'une erreur sèche.
        setAuthMode('magic')
        setMagicSent(false)
        setError('Mot de passe incorrect. Pas grave : reçois un lien de connexion par email, clique juste sur Envoyer.')
      } else {
        setError(e.message)
      }
    } finally {
      setLoading(false)
    }
  }

  async function forgotPassword() {
    setError(null)
    if (!email) { setError('Entre ton email au-dessus, puis reclique.'); return }
    try {
      const { error: err } = await sb.auth.resetPasswordForEmail(email, { redirectTo: `${window.location.origin}/?studio=1` })
      if (err) throw err
      setInfo(`📩 Email envoyé à ${email}. Clique le lien pour choisir un nouveau mot de passe.`)
    } catch (e) { setError(e.message) }
  }

  const vtIntake = VERTICALES[getVertical()] || null
  const tools = [
    { icon: '📜', label: 'Formats adaptés à ton compte', desc: 'Plus jamais de page blanche. Hook, déroulé, CTA de chaque format, adaptés à ton compte.', accent: '#fe8204' },
    { icon: '🎬', label: 'Analyse vidéo', desc: 'Tu sais ce qui marche dans tes vidéos. Et ce qui freine la suivante.', accent: vtIntake?.accent || '#a855f7' },
    { icon: '🎯', label: 'Analyse compte', desc: 'Le diagnostic complet : bio, niche, positionnement, audience.', accent: '#10b981' },
    { icon: '🔍', label: vtIntake?.tools_formats_label || 'Des milliers de formats', desc: vtIntake?.tools_formats_desc || 'Des vidéos à plusieurs millions de vues qui fonctionneront pour ton compte.', accent: '#fbbf24' },
  ]

  return (
    <div className="min-h-screen text-white flex flex-col"
      style={{ background: 'radial-gradient(ellipse at 30% 0%, var(--fmt-ph-radial-1, #2e1065) 0%, var(--fmt-ph-radial-2, #1a1040) 40%, #0f0a1a 100%)' }}>
      {/* Keyframes pour marquee */}
      <style>{`
        @keyframes studio-marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .studio-marquee-track { animation: studio-marquee 22s linear infinite; }
      `}</style>

      {/* Noise + glow overlays */}
      <div className="fixed inset-0 pointer-events-none opacity-[0.04]"
        style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3' /%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' /%3E%3C/svg%3E")` }} />
      <div className="fixed inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(circle at 80% 30%, rgba(var(--merlin-accent-rgb, 254,130,4),0.12) 0%, transparent 50%)' }} />

      {/* ALLÈGEMENT MOBILE (retour Antonin 21/07 : « bien trop remplie ») :
          avant, flex-1 min-h-0 ÉCRASAIT tout (punchline + form + outils +
          footer CGU) dans un seul écran de 390px. Maintenant le 1er écran =
          UNE action (titre + promesse + champ + CTA, centrés, aérés) et les
          outils/footer suivent en scroll naturel. Data : 552 tapent → 591
          soumettent, la fuite est AVANT la 1re touche : l'écran, pas le form. */}
      <div className="relative max-w-md sm:max-w-4xl mx-auto w-full px-4 sm:px-6 pt-5 sm:pt-8 pb-3 flex flex-col">

        <div className="min-h-[82svh] flex flex-col justify-center">
        {/* ─── PUNCHLINE en haut ─── */}
        <h1 className="text-[28px] sm:text-6xl font-bold text-center leading-[1.08] tracking-tight mb-3">
          {vtIntake?.punchline_prefix || 'Ton prochain reel mérite mieux'}<br />
          <span style={{
            background: 'linear-gradient(135deg, var(--merlin-accent, #fe8204), #fbbf24)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
          }}>qu'un prompt ChatGPT.</span>
        </h1>
        <p className="text-[13px] sm:text-sm text-white/70 text-center max-w-md mx-auto leading-relaxed mb-5 sm:mb-6">
          {vtIntake?.punchline_sub || <>Colle ton <span className="font-bold text-white">@instagram</span> : ta note sur 100 et 3 formats viraux adaptés à <span className="font-bold" style={{ color: 'var(--merlin-accent-soft, #fdba74)' }}>TON compte</span>, en 20 secondes.</>}
        </p>

        {/* ─── FORM CARD ─── */}
        <div id="studio-auth-form" className="rounded-2xl bg-white/[0.07] backdrop-blur-sm border border-white/15 p-4 sm:p-5 shadow-2xl w-full max-w-md mx-auto"
          style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.40), 0 0 0 1px rgba(255,255,255,0.04) inset' }}>

          {/* ─── ÉTAPE 1 : LE @ (avant le compte) ─── */}
          {gateStep === 'handle' && mode === 'signup' ? (
            <>
              <label className="text-[10px] uppercase tracking-wider font-bold text-white/50 mb-2 block">
                Ton compte Instagram
              </label>
              <div className="flex items-center gap-1 bg-white/10 rounded-lg px-3.5 py-3.5 mb-3 transition-colors focus-within:bg-white/15">
                <span className="text-white/50 text-base">@</span>
                <input
                  type="text"
                  value={gateHandle}
                  onChange={(e) => {
                    if (!gateTypedRef.current && e.target.value.trim()) {
                      gateTypedRef.current = true
                      studioEvents.intakeHandleTyping({ pre_signup: true, source_page: 'signup' })
                    }
                    setGateHandle(e.target.value)
                  }}
                  onKeyDown={(e) => { if (e.key === 'Enter') submitGateHandle() }}
                  placeholder="ton_compte_instagram"
                  autoComplete="off" autoCapitalize="none" autoCorrect="off" spellCheck={false}
                  className="bg-transparent flex-1 outline-none text-white placeholder-white/30 text-base"
                />
              </div>
              <button onClick={submitGateHandle} disabled={!gateHandle.trim() || gateLaunching}
                className="w-full py-3.5 rounded-lg font-bold text-sm text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:scale-[1.01]"
                style={{
                  background: (!gateHandle.trim() || gateLaunching) ? 'rgba(var(--merlin-accent-rgb, 254,130,4),0.5)' : 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))',
                  boxShadow: (!gateHandle.trim() || gateLaunching) ? 'none' : '0 8px 24px rgba(var(--merlin-accent-rgb, 254,130,4),0.30)',
                }}>
                {gateLaunching ? 'Lancement de l\'analyse…' : 'Voir mon score + mes 3 formats →'}
              </button>
              {error && <p className="text-xs text-red-300 mt-3 text-center">⚠ {error}</p>}

              <div className="flex items-center gap-3 my-4">
                <div className="flex-1 h-px bg-white/10" />
                <span className="text-[10px] uppercase tracking-wider text-white/40">ou</span>
                <div className="flex-1 h-px bg-white/10" />
              </div>
              <button onClick={() => { studioEvents.genericModeChosen({ pre_signup: true }); setGenericOpen(true) }}
                className="w-full text-[12px] text-white/55 hover:text-white/85 underline underline-offset-2 transition-colors">
                Je n'ai pas encore de compte Instagram
              </button>
              <p className="text-[10.5px] text-white/40 text-center mt-4">
                Gratuit · Sans carte bancaire · Lecture seule de ton compte public
              </p>
            </>
          ) : (
          <>
          {/* ─── ÉTAPE 2 : LE COMPTE ─── */}
          {/* Rappel du contexte : ce que l'user obtient juste après */}
          {mode === 'signup' && (gateHandle || gateGenericNiche) && (
            <div className="flex items-center justify-between gap-2 mb-3.5 px-3 py-2.5 rounded-lg"
              style={{ background: 'rgba(var(--merlin-accent-rgb, 254,130,4),0.10)', border: '1px solid rgba(var(--merlin-accent-rgb, 254,130,4),0.25)' }}>
              <p className="text-[12px] text-white/85 leading-snug min-w-0">
                {gateHandle
                  ? <>Analyse prête pour <span className="font-bold" style={{ color: 'var(--merlin-accent-soft, #fdba74)' }}>@{gateHandle}</span> : elle démarre dès ton compte créé.</>
                  : <>Niche <span className="font-bold" style={{ color: 'var(--merlin-accent-soft, #fdba74)' }}>{gateGenericNiche}</span> : tes scripts arrivent dès ton compte créé.</>}
              </p>
              <button onClick={() => { setGateStep('handle'); setError(null); setInfo(null) }}
                className="text-[11px] text-white/50 hover:text-white underline shrink-0">
                modifier
              </button>
            </div>
          )}

          {/* Google OAuth : masqué dans le webview iOS Meta (OAuth y échoue
              et iOS n'a pas d'échappement programmatique). Android webview :
              le clic s'évade vers Chrome via intent:// (cf. handleGoogle). */}
          {!webview.isMetaWebview && (
            <>
              <button type="button" onClick={handleGoogle} disabled={googleLoading || loading}
                className="w-full flex items-center justify-center gap-2.5 py-2.5 rounded-lg text-sm font-medium transition-all disabled:opacity-50 hover:bg-white/15"
                style={{ border: '1.5px solid rgba(255,255,255,0.18)', color: 'rgba(255,255,255,0.92)', background: 'rgba(255,255,255,0.06)' }}>
                <svg className="w-4 h-4" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                {googleLoading ? 'Redirection…' : 'Continuer avec Google'}
              </button>
              <p className="text-[10px] text-white/40 leading-snug mt-1">
                En continuant, tu acceptes les <a href="/cgu" target="_blank" className="underline">CGU</a> et la <a href="/confidentialite" target="_blank" className="underline">politique de confidentialité</a>.
              </p>

              <div className="flex items-center gap-3 my-3">
                <div className="flex-1 h-px bg-white/10" />
                <span className="text-[10px] uppercase tracking-wider text-white/40 font-bold">ou email</span>
                <div className="flex-1 h-px bg-white/10" />
              </div>
            </>
          )}
          {webview.isMetaWebview && webview.isIOS && (
            <p className="text-[10.5px] text-white/45 text-center mb-3 leading-snug">
              💡 Tu préfères Google ? Ouvre cette page dans Safari : menu <span className="font-bold text-white/70">⋯</span> en haut à droite → <span className="font-bold text-white/70">Ouvrir dans le navigateur</span>.
            </p>
          )}

          {authMode === 'magic' ? (
            magicSent ? (
              <div className="rounded-lg px-3 py-3 space-y-2" style={{ background: 'rgba(16,185,129,0.15)' }}>
                <p className="text-[13px] font-bold" style={{ color: '#6ee7b7' }}>📩 C&rsquo;est envoyé !</p>
                {webview.isMetaWebview ? (
                  <p className="text-[12px] text-white/80 leading-snug">Tape le code à 6 chiffres reçu à <b>{email}</b> juste en dessous. Pas besoin d&rsquo;ouvrir le mail : le code est dans la notification.</p>
                ) : (
                  <p className="text-[12px] text-white/80 leading-snug">Ouvre l&rsquo;email envoyé à <b>{email}</b> et clique le lien : tu seras connecté et ton analyse se lancera (pense aux spams).</p>
                )}
                <form onSubmit={submitOtp} className="space-y-1.5 pt-0.5">
                  {!webview.isMetaWebview && <p className="text-[11px] text-white/60">Ou tape le code reçu dans le mail pour rester ici :</p>}
                  <div className="flex gap-2">
                    <input inputMode="numeric" autoComplete="one-time-code" maxLength={10} value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                      placeholder="123456"
                      className="flex-1 min-w-0 bg-white/10 rounded-lg px-3 py-2 outline-none text-sm text-white tracking-[0.3em] placeholder-white/25 focus:bg-white/15 transition-colors" />
                    <button type="submit" disabled={otpLoading || otpCode.length < 6}
                      className="px-3.5 py-2 rounded-lg font-bold text-[12px] text-white disabled:opacity-40 shrink-0"
                      style={{ background: 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))' }}>
                      {otpLoading ? 'Patiente…' : 'Valider'}
                    </button>
                  </div>
                </form>
                {error && <p className="text-[11px] text-red-300">⚠ {error}</p>}
                <button type="button" onClick={() => { setMagicSent(false); setOtpCode(''); setError(null) }} className="text-[11px] text-white/55 hover:text-white underline">Renvoyer</button>
              </div>
            ) : (
            <form onSubmit={sendMagic} className="space-y-2.5">
              <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="Email" autoComplete="email" required
                className="w-full bg-white/10 rounded-lg px-3.5 py-2.5 outline-none text-white placeholder-white/40 focus:bg-white/15 transition-colors text-sm" />
              <label className="flex items-start gap-2 cursor-pointer text-[10.5px] text-white/65 leading-snug">
                <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)}
                  className="mt-0.5 accent-orange shrink-0" />
                <span>
                  J'accepte les <a href="/cgu" target="_blank" rel="noreferrer" className="underline text-white/85 hover:text-white">CGU</a>,
                  les <a href="/cgv" target="_blank" rel="noreferrer" className="underline text-white/85 hover:text-white">CGV</a> et
                  la <a href="/confidentialite" target="_blank" rel="noreferrer" className="underline text-white/85 hover:text-white">politique de confidentialité</a>.
                  Lecture seule de mon compte Instagram public.
                </span>
              </label>
              <label className="flex items-start gap-2 cursor-pointer text-[10.5px] leading-snug rounded-lg px-2.5 py-2"
                style={{ background: 'rgba(var(--merlin-accent-rgb, 254,130,4),0.10)', border: '1px solid rgba(var(--merlin-accent-rgb, 254,130,4),0.30)' }}>
                <input type="checkbox" checked={dailyOptin} onChange={e => setDailyOptin(e.target.checked)} className="mt-0.5 accent-orange shrink-0" />
                <span className="text-white/90">🔥 <strong>Reçois le format viral du jour de ta niche, chaque matin par mail.</strong> Désinscription en 1 clic.</span>
              </label>
              <button type="submit" disabled={loading}
                className="w-full py-3 rounded-lg font-bold text-sm text-white transition-all disabled:opacity-40 hover:scale-[1.01]"
                style={{
                  background: loading ? 'rgba(var(--merlin-accent-rgb, 254,130,4),0.5)' : 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))',
                  boxShadow: loading ? 'none' : '0 8px 24px rgba(var(--merlin-accent-rgb, 254,130,4),0.30)',
                }}>
                {loading ? 'Patiente…' : (webview.isMetaWebview ? 'Recevoir mon code →' : 'Recevoir mon lien de connexion →')}
              </button>
              <p className="text-[10.5px] text-white/45 text-center">Sans mot de passe : un lien par email, tu cliques, tu es dedans.</p>
              {error && <p className="text-xs text-red-300 text-center">⚠ {error}</p>}
            </form>
            )
          ) : (
          <form onSubmit={submit} className="space-y-2.5">
            <input type="email" value={email} onChange={e => setEmail(e.target.value)}
              placeholder="Email" autoComplete="email" required
              className="w-full bg-white/10 rounded-lg px-3.5 py-2.5 outline-none text-white placeholder-white/40 focus:bg-white/15 transition-colors text-sm" />
            <input type="password" value={password} onChange={e => setPassword(e.target.value)}
              placeholder="Mot de passe" autoComplete={mode === 'signup' ? 'new-password' : 'current-password'} required minLength={6}
              className="w-full bg-white/10 rounded-lg px-3.5 py-2.5 outline-none text-white placeholder-white/40 focus:bg-white/15 transition-colors text-sm" />
            {mode === 'signin' && (
              <button type="button" onClick={forgotPassword}
                className="block text-[11px] text-white/55 hover:text-white underline">
                Mot de passe oublié ?
              </button>
            )}

            {mode === 'signup' && (
              <label className="flex items-start gap-2 cursor-pointer text-[10.5px] text-white/65 leading-snug">
                <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)}
                  className="mt-0.5 accent-orange shrink-0" />
                <span>
                  J'accepte les <a href="/cgu" target="_blank" rel="noreferrer" className="underline text-white/85 hover:text-white">CGU</a>,
                  les <a href="/cgv" target="_blank" rel="noreferrer" className="underline text-white/85 hover:text-white">CGV</a> et
                  la <a href="/confidentialite" target="_blank" rel="noreferrer" className="underline text-white/85 hover:text-white">politique de confidentialité</a>.
                  Lecture seule de mon compte Instagram public.
                </span>
              </label>
            )}

            {mode === 'signup' && (
              <label className="flex items-start gap-2 cursor-pointer text-[10.5px] leading-snug rounded-lg px-2.5 py-2"
                style={{ background: 'rgba(var(--merlin-accent-rgb, 254,130,4),0.10)', border: '1px solid rgba(var(--merlin-accent-rgb, 254,130,4),0.30)' }}>
                <input type="checkbox" checked={dailyOptin} onChange={e => setDailyOptin(e.target.checked)} className="mt-0.5 accent-orange shrink-0" />
                <span className="text-white/90">🔥 <strong>Reçois le format viral du jour de ta niche, chaque matin par mail.</strong> Désinscription en 1 clic.</span>
              </label>
            )}

            <button type="submit" disabled={loading}
              className="w-full py-3 rounded-lg font-bold text-sm text-white transition-all disabled:opacity-40 hover:scale-[1.01]"
              style={{
                background: loading ? 'rgba(var(--merlin-accent-rgb, 254,130,4),0.5)' : 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))',
                boxShadow: loading ? 'none' : '0 8px 24px rgba(var(--merlin-accent-rgb, 254,130,4),0.30)',
              }}>
              {loading ? 'Patiente…' : mode === 'signup' ? 'Créer mon compte et lancer l\'analyse →' : 'Se connecter →'}
            </button>

            {error && <p className="text-xs text-red-300 text-center">⚠ {error}</p>}
            {info && <p className="text-xs text-green-300 text-center">✓ {info}</p>}
          </form>
          )}

          <button onClick={() => { setAuthMode(authMode === 'magic' ? 'password' : 'magic'); setError(null); setMagicSent(false) }}
            className="mt-2.5 w-full text-[11px] text-white/50 hover:text-white underline text-center">
            {authMode === 'magic' ? 'Utiliser un mot de passe à la place' : '← Recevoir un lien de connexion par email (sans mot de passe)'}
          </button>
          </>
          )}
        </div>

        {/* Modal niche (option sans Instagram, accessible dès l'étape 1) */}
        {genericOpen && <GenericNicheModal
          nicheMacro={nicheMacro}
          setNicheMacro={setNicheMacro}
          nicheFine={nicheFine}
          setNicheFine={setNicheFine}
          onSubmit={submitGateGeneric}
          onClose={() => setGenericOpen(false)}
        />}

        {/* Sous-form : juste le toggle mode (CB mention retirée 2026-06-08 :
            le signup ne demande PAS la CB, elle vient au checkout Stripe.
            L'annoncer ici = anxiogène + mensonger). */}
        <div className="mt-3 flex flex-col items-center gap-2">
          <button onClick={() => {
            const next = mode === 'signup' ? 'signin' : 'signup'
            setMode(next); setError(null); setInfo(null)
            // Se connecter ne demande pas de @ : on saute l'étape handle.
            // Retour en signup : étape handle seulement si rien de pré-rempli.
            if (next === 'signin') setGateStep('account')
            else if (!gateHandle && !gateGenericNiche) setGateStep('handle')
          }}
            className="text-[11px] text-white/60 hover:text-white underline">
            {mode === 'signup' ? 'Déjà un compte ? Se connecter' : 'Pas encore de compte ? S\'inscrire'}
          </button>
        </div>
        </div>

        {/* ─── 4 OUTILS sous le form ─── */}
        {/* Mobile : défilement cards avec texte */}
        <div className="sm:hidden mt-5 overflow-hidden -mx-4"
          style={{
            maskImage: 'linear-gradient(90deg, transparent 0%, black 6%, black 94%, transparent 100%)',
            WebkitMaskImage: 'linear-gradient(90deg, transparent 0%, black 6%, black 94%, transparent 100%)',
          }}>
          <div className="studio-marquee-track flex gap-3 w-max">
            {[...tools, ...tools, ...tools].map((t, i) => (
              <div key={i} className="w-[240px] rounded-xl p-3 flex flex-col gap-1 shrink-0"
                style={{ background: 'rgba(255,255,255,0.05)', border: `1px solid ${t.accent}40` }}>
                <div className="flex items-center gap-2">
                  <span className="text-base leading-none">{t.icon}</span>
                  <span className="text-[11px] uppercase tracking-wider font-bold" style={{ color: t.accent }}>{t.label}</span>
                </div>
                <p className="text-[11.5px] text-white/70 leading-snug">{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
        {/* Desktop : 4 cards en row avec text */}
        <div className="hidden sm:grid grid-cols-4 gap-3 mt-8">
          {tools.map((t, i) => (
            <div key={i} className="rounded-xl p-3.5 flex flex-col gap-1.5"
              style={{ background: 'rgba(255,255,255,0.04)', border: `1px solid ${t.accent}33` }}>
              <div className="flex items-center gap-2">
                <span className="text-lg leading-none">{t.icon}</span>
                <span className="text-[11px] uppercase tracking-wider font-bold" style={{ color: t.accent }}>{t.label}</span>
              </div>
              <p className="text-[12px] text-white/70 leading-snug">{t.desc}</p>
            </div>
          ))}
        </div>

        {/* Légal footer — petit, en bas */}
        <LegalFooter tone="dark" />
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// LOADING
// ═══════════════════════════════════════════════════════════════
const LOADING_TIPS = [
  "Un hook a 1,7 seconde pour capter l'attention.",
  "Les top 5 % des reels ont un arc narratif clair.",
  "La variable la plus puissante d'un hook est temporelle.",
  "Les ennemis communs accélèrent l'identification audience.",
  "Un format viral est portable, un créateur viral ne l'est pas.",
]

function StudioLoading({ session }) {
  // Progression LISSÉE pilotée par le temps écoulé, avec le vrai % backend
  // comme plancher. Corrige : (1) l'affichage > 100 % (le progress_pct backend
  // n'était pas borné → on a vu "387 %"), (2) l'effet "barre figée puis saut"
  // qui faisait paraître l'analyse plus longue qu'elle ne l'est. La courbe
  // atteint ~92 % vers ~18-20 s (le rythme réel : score ~11 s, formats ~17 s),
  // jamais bloquée, jamais en arrière, cap 99 % jusqu'au démontage (résultat prêt).
  const loadStartRef = useRef(Date.now())
  const realPct = Math.min(100, Math.max(0, session.progress_pct || 0))
  const [displayPct, setDisplayPct] = useState(() => Math.min(99, Math.max(4, realPct)))
  useEffect(() => {
    const id = setInterval(() => {
      setDisplayPct(prev => {
        const elapsed = Date.now() - loadStartRef.current
        const timeEst = 92 * (1 - Math.exp(-elapsed / 11000))
        const floor = Math.max(realPct, timeEst)
        return Math.max(prev, Math.min(99, floor))
      })
    }, 120)
    return () => clearInterval(id)
  }, [realPct])

  // ── TRACKING D'ATTENTE ──────────────────────────────────────
  // Combien de gens atteignent chaque palier (25/50/75/90%) et où ils
  // décrochent. Permet de voir si on perd des gens sur le temps de scan.
  const firedMs = useRef(new Set())
  const firedAbandon = useRef(false)
  useEffect(() => {
    const real = session.progress_pct || 0
    for (const m of [25, 50, 75, 90]) {
      if (displayPct >= m && !firedMs.current.has(m)) {
        firedMs.current.add(m)
        try { studioEvents.scanProgress({ milestone: m, pct: real, elapsed_ms: Date.now() - loadStartRef.current, session_id: session.id, guest: !!session._is_guest, ig_username: session.ig_username }) } catch {}
      }
    }
  }, [displayPct, session.progress_pct, session.id, session._is_guest, session.ig_username])
  useEffect(() => {
    function fireAbandon(reason) {
      if (firedAbandon.current) return
      firedAbandon.current = true
      try { studioEvents.scanAbandoned({ pct: session.progress_pct || 0, elapsed_ms: Date.now() - loadStartRef.current, current_step: session.current_step || null, reason, session_id: session.id, guest: !!session._is_guest }) } catch {}
    }
    function onVis() { if (typeof document !== 'undefined' && document.visibilityState === 'hidden') fireAbandon('backgrounded') }
    function onPageHide() { fireAbandon('pagehide') }
    document.addEventListener('visibilitychange', onVis)
    window.addEventListener('pagehide', onPageHide)
    return () => { document.removeEventListener('visibilitychange', onVis); window.removeEventListener('pagehide', onPageHide) }
  }, [session.progress_pct, session.current_step, session.id, session._is_guest])

  // ── BIBLIOTHÈQUE PENDANT L'ATTENTE ──────────────────────────
  // Le scan dure 90-160s : au lieu d'un écran d'attente statique, on laisse
  // l'user explorer la library pendant que Merlin travaille. Le temps mort
  // devient de la découverte produit (et démontre le volume de la base).
  const [libItems, setLibItems] = useState([])
  useEffect(() => {
    // Verticale : pendant l'attente aussi, on ne montre QUE le secteur
    // (demande Antonin 14/07 : des formats hors sport s'affichaient
    // pendant le scan en mode sport).
    const v = getVertical()
    fetch(`${API_URL}/api/studio/library/preview?count=30${v ? `&vertical=${encodeURIComponent(v)}` : ''}`)
      .then(r => r.json())
      .then(d => {
        const arr = [...(d.items || [])]
        for (let i = arr.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1))
          ;[arr[i], arr[j]] = [arr[j], arr[i]]
        }
        setLibItems(arr)
      })
      .catch(() => {})
  }, [])

  // Toast au clic sur une card : on ne bloque rien, on rappelle juste que
  // SES formats personnalisés arrivent.
  const [toast, setToast] = useState(false)
  const toastTimer = useRef(null)
  function onCardClick() {
    setToast(true)
    if (toastTimer.current) clearTimeout(toastTimer.current)
    toastTimer.current = setTimeout(() => setToast(false), 2500)
  }
  useEffect(() => () => { if (toastTimer.current) clearTimeout(toastTimer.current) }, [])

  const isGenericScan = session.ig_username === '_generic'
  // Garde-fou : si le scan dépasse 90s (ex: process backend redémarré en plein
  // scan, ou hang), on propose de relancer au lieu de poller à l'infini.
  const [tooLong, setTooLong] = useState(false)
  useEffect(() => { const t = setTimeout(() => setTooLong(true), 90000); return () => clearTimeout(t) }, [])
  function retryScan() {
    try {
      localStorage.removeItem('studio_guest_session_id')
      localStorage.setItem('studio_autolaunch', '1')
    } catch {}
    window.location.href = '/'
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-950 via-purple-900 to-indigo-950 text-white flex flex-col">
      {/* ── BANDEAU PROGRESSION STICKY ── */}
      <div className="sticky top-0 z-30 backdrop-blur-xl border-b border-white/10"
        style={{ background: 'rgba(30,17,69,0.92)' }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3">
          <div className="flex items-center gap-3">
            <img src={merlinSearching} alt="" className="w-10 h-10 sm:w-12 sm:h-12 object-contain animate-pulse shrink-0"
              style={{ filter: 'drop-shadow(0 4px 16px rgba(var(--merlin-accent-rgb, 254,130,4),0.45))' }} />
            <div className="min-w-0 flex-1">
              <p className="text-sm sm:text-base font-bold leading-tight">
                {isGenericScan
                  ? <>Merlin analyse <span style={{ color: 'var(--merlin-accent, #fe8204)' }}>ta niche</span> et prépare tes formats…</>
                  : <>Merlin analyse <span style={{ color: 'var(--merlin-accent, #fe8204)' }}>@{session.ig_username}</span> et le note sur 100…</>}
              </p>
              <p key={session.current_step} className="text-[11px] text-white/60 leading-tight truncate">
                {session.current_step || 'Démarrage de l\'analyse…'} · ~20 secondes
              </p>
            </div>
            <span className="text-xs font-mono text-white/50 shrink-0">{Math.round(displayPct)} %</span>
          </div>
          <div className="w-full h-1 bg-white/10 rounded-full overflow-hidden mt-2.5">
            <div className="h-full rounded-full"
              style={{
                width: `${displayPct}%`,
                background: 'linear-gradient(90deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))',
                boxShadow: '0 0 16px rgba(var(--merlin-accent-rgb, 254,130,4),0.4)',
                transition: 'width 350ms linear'
              }} />
          </div>
          {tooLong && (
            <div className="mt-2.5 flex items-center justify-between gap-2 px-3 py-2 rounded-lg"
              style={{ background: 'rgba(var(--merlin-accent-rgb, 254,130,4),0.12)', border: '1px solid rgba(var(--merlin-accent-rgb, 254,130,4),0.35)' }}>
              <span className="text-[11px] text-white/85 leading-snug">Ça prend plus de temps que prévu. Tu peux relancer.</span>
              <button onClick={retryScan}
                className="shrink-0 px-3 py-1.5 rounded-lg text-[11px] font-bold text-white"
                style={{ background: 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))' }}>
                Relancer
              </button>
            </div>
          )}
        </div>
        {/* Toast clic card */}
        {toast && (
          <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 px-3.5 py-2 rounded-lg text-xs font-bold text-white whitespace-nowrap shadow-xl"
            style={{ background: 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))' }}>
            Tes 3 formats personnalisés arrivent dans un instant ✨
          </div>
        )}
      </div>

      {/* ── LIBRARY À EXPLORER PENDANT L'ATTENTE ── */}
      <div className="flex-1 max-w-5xl mx-auto w-full px-3 sm:px-6 py-6">
        <p className="text-center text-sm text-white/60 mb-4">
          En attendant, jette un œil à ce que Merlin a déjà décortiqué :
        </p>
        {libItems.length === 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 sm:gap-3">
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={i} className="rounded-xl bg-white/5 animate-pulse w-full" style={{ aspectRatio: '9/16' }} />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 sm:gap-3">
            {libItems.map(item => (
              <GuestReelCard key={item.id} item={item} onClick={onCardClick} badgeLabel="Dans ta bibliothèque ✨" />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// ERROR
// ═══════════════════════════════════════════════════════════════
function StudioError({ session, onReset }) {
  return (
    <div className="min-h-screen bg-bg flex items-center justify-center px-6">
      <div className="max-w-md text-center">
        <img src={merlinImg} alt="" className="w-20 h-20 mx-auto mb-5 object-contain opacity-70" />
        <h2 className="text-xl font-bold text-ink mb-2">L'analyse a échoué</h2>
        <p className="text-sm text-ink-muted mb-6">{session.error || 'Erreur inconnue.'}</p>
        <button onClick={onReset} className="merlin-cta-primary px-6">Réessayer</button>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// PAYMENT SUCCESS MODAL — après retour de Stripe Checkout
// Le webhook met ~2-5s pour propager studio_plan='pro' en DB. On évite
// le flash "encore free" en montrant cette page avec bouton refresh manuel.
// ═══════════════════════════════════════════════════════════════
function PaymentSuccessModal() {
  const auth = useAuth()
  const [checking, setChecking] = useState(false)

  async function activateNow() {
    setChecking(true)
    // Refetch userProfile depuis Supabase pour récupérer le studio_plan='pro'
    // tout juste écrit par le webhook. Si studio_plan='pro', la prochaine
    // évaluation de StudioPage rend StudioPro automatiquement.
    if (typeof auth.refreshProfile === 'function') await auth.refreshProfile()
    // Vire les params query et reload propre. Si webhook pas encore fired,
    // au pire l'user verra encore le free flow et pourra refresh à nouveau.
    window.location.replace('/')
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-950 via-purple-900 to-indigo-950 text-white flex flex-col items-center justify-center px-6 py-12">
      <div className="max-w-md w-full text-center">
        <div className="inline-block mb-6" style={{ filter: 'drop-shadow(0 10px 40px rgba(var(--merlin-accent-rgb, 254,130,4),0.45))' }}>
          <img src={merlinFound} alt="Merlin" className="w-32 h-32 object-contain mx-auto" />
        </div>
        <div className="inline-flex items-center gap-2 mb-4 px-3 py-1 rounded-full"
          style={{ background: 'rgba(34,197,94,0.18)', border: '1px solid rgba(34,197,94,0.35)' }}>
          <span className="text-green-300">✓</span>
          <span className="text-[11px] uppercase tracking-wider font-bold text-green-200">Paiement réussi</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold leading-tight mb-3">
          Bienvenue dans <span style={{ color: 'var(--merlin-accent, #fe8204)' }}>Studio Pro</span>
        </h1>
        <p className="text-sm text-white/80 leading-relaxed max-w-sm mx-auto mb-8">
          Ton paiement est confirmé. Clique le bouton ci-dessous pour activer
          ton accès Pro maintenant (ça peut prendre quelques secondes le temps
          que Stripe nous notifie).
        </p>
        <button onClick={activateNow} disabled={checking}
          className="w-full py-4 rounded-xl font-bold text-base text-white transition-all hover:scale-[1.01] active:scale-[0.99] disabled:opacity-60 flex items-center justify-center gap-2"
          style={{
            background: 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))',
            boxShadow: '0 8px 24px rgba(var(--merlin-accent-rgb, 254,130,4),0.45)',
          }}>
          {checking ? (
            <>
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              <span>Activation en cours…</span>
            </>
          ) : (
            <>
              <span>Activer mon accès Studio Pro</span>
              <span>→</span>
            </>
          )}
        </button>
        <p className="text-[11px] text-white/45 mt-5 leading-relaxed">
          Si après 2-3 essais ton accès n'est toujours pas activé, contacte<br />
          <a href="mailto:antonin.girardeau.pro@gmail.com" className="underline">antonin.girardeau.pro@gmail.com</a>
        </p>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// ADMIN TOGGLE — switch free/pro pour antonin.girardeau.pro (testing)
// ═══════════════════════════════════════════════════════════════
const ADMIN_EMAIL_STUDIO = 'antonin.girardeau.pro@gmail.com'

export { Chevron }

// ═══════════════════════════════════════════════════════════════
// ADMIN SOURCE LINK — badge 🔗 visible UNIQUEMENT pour l'admin sur les
// thumbnails de la library. Clic = copie l'URL Instagram source (pour
// référencer les vidéos dans des formats type "3 meilleurs formats de
// 2026"), Cmd/Ctrl+clic = ouvre le reel. Span (pas <a>) car souvent
// rendu À L'INTÉRIEUR d'un <button> (nested interactive interdit).
// ═══════════════════════════════════════════════════════════════
export function AdminSourceLink({ lien, className = '' }) {
  const auth = useAuth()
  const [copied, setCopied] = useState(false)
  if (!lien || auth.user?.email !== ADMIN_EMAIL_STUDIO) return null
  return (
    <span
      role="button"
      onClick={(e) => {
        e.stopPropagation()
        e.preventDefault()
        if (e.metaKey || e.ctrlKey) { window.open(lien, '_blank', 'noopener'); return }
        try {
          navigator.clipboard.writeText(lien).then(() => {
            setCopied(true)
            setTimeout(() => setCopied(false), 1500)
          })
        } catch {}
      }}
      className={`absolute top-1.5 left-1.5 z-20 w-7 h-7 rounded-full flex items-center justify-center cursor-pointer transition-all hover:scale-110 ${className}`}
      style={{
        background: copied ? 'rgba(16,185,129,0.95)' : 'rgba(0,0,0,0.55)',
        backdropFilter: 'blur(4px)',
        fontSize: '12px',
      }}
      title={copied ? 'Lien copié !' : `Admin · copier le lien source (Cmd+clic pour ouvrir)\n${lien}`}>
      {copied ? '✓' : '🔗'}
    </span>
  )
}

export function StudioAdminToggle({ currentPlan }) {
  const auth = useAuth()
  const isAdmin = auth.user?.email === ADMIN_EMAIL_STUDIO
  const [busy, setBusy] = useState(false)
  if (!isAdmin) return null

  // Cycle 4-states : Free → Créateur en essai → Créateur payant → Pro+ → Free
  // Le flag 'studio_trial_dev' distingue Créateur trial (no-analyse) vs paid.
  // En prod ces deux infos viendront de Stripe (subscription.status='trialing'
  // vs 'active'), ici on simule en dev via localStorage.
  async function cycle() {
    if (busy) return
    setBusy(true)
    try {
      const currentTrial = (() => {
        try { return localStorage.getItem('studio_trial_dev') === '1' } catch { return false }
      })()
      // Etat suivant : null (free) → pro+trial → pro+paid → pro_plus → null
      let nextPlan, nextTrial
      if (!currentPlan) { nextPlan = 'pro'; nextTrial = true }
      else if (currentPlan === 'pro' && currentTrial)  { nextPlan = 'pro'; nextTrial = false }
      else if (currentPlan === 'pro' && !currentTrial) { nextPlan = 'pro_plus'; nextTrial = false }
      else                                              { nextPlan = null;  nextTrial = false }
      console.log('[admin toggle] switching to:', nextPlan, 'trial=', nextTrial)

      // 1) Override localStorage (source instantanée, pas de race condition)
      try {
        if (nextPlan) localStorage.setItem('studio_plan_dev_override', nextPlan)
        else localStorage.removeItem('studio_plan_dev_override')
        if (nextTrial) localStorage.setItem('studio_trial_dev', '1')
        else localStorage.removeItem('studio_trial_dev')
      } catch {}

      const next = nextPlan  // alias pour la suite (compat avec PATCH backend)

      // 2) Best-effort : user_metadata (persiste cross-device)
      try { await sb.auth.updateUser({ data: { studio_plan: next } }) } catch (e) {
        console.warn('[admin toggle] updateUser warn:', e.message)
      }
      try { await sb.auth.refreshSession() } catch {}

      // 3) Best-effort : patch users.studio_plan (no-op si colonne absente)
      try {
        const { data } = await sb.auth.getSession()
        const token = data?.session?.access_token
        if (token) {
          await fetch(`${API_URL}/api/admin/studio-plan`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ plan: next }),
          })
        }
      } catch (e) { console.warn('[admin toggle] users PATCH warn:', e.message) }

      console.log('[admin toggle] reloading…')
      window.location.replace('/')
    } catch (e) {
      console.error('[admin toggle]', e)
      alert(`Erreur switch : ${e.message}`)
      setBusy(false)
    }
  }

  const inTrial = (() => {
    try { return localStorage.getItem('studio_trial_dev') === '1' } catch { return false }
  })()
  const isFree = !currentPlan || currentPlan === 'free'
  const isCreator = currentPlan === 'pro'
  const isPro = currentPlan === 'pro_plus'
  // Cycle : Free → Créateur trial → Créateur paid → Pro+ → Free
  const nextLabel = isPro
    ? 'Passer Free'
    : (isCreator && !inTrial) ? 'Passer Pro+'
    : (isCreator && inTrial)  ? 'Sortir essai'
    : 'Passer Créateur trial'
  const currentColor = isPro ? 'var(--merlin-accent, #fe8204)' : isCreator ? 'var(--merlin-violet, #7c3aed)' : 'var(--merlin-violet-2, #5b21b6)'
  return (
    <button onClick={cycle} disabled={busy}
      className="text-[10px] font-bold px-2.5 py-1 rounded-md whitespace-nowrap transition-colors flex items-center gap-1.5 disabled:opacity-50"
      style={{
        background: `rgba(${isPro ? '254,130,4' : '124,58,237'},0.10)`,
        color: currentColor,
        border: `1px solid rgba(${isPro ? '254,130,4' : '124,58,237'},0.25)`,
      }}
      title={`Admin : free → Créateur → Pro (cycle). Actuel : ${isPro ? 'Pro' : isCreator ? 'Créateur' : 'Free'}`}>
      <span style={{ fontSize: '9px' }}>🛠</span>
      {busy ? '...' : nextLabel}
    </button>
  )
}

// ═══════════════════════════════════════════════════════════════
// RESET FRESH USER BUTTON — admin only. Simule un parcours de zéro
// en supprimant sessions/analyses/saves de l'admin actuel.
// ═══════════════════════════════════════════════════════════════
export function StudioAdminResetButton() {
  const auth = useAuth()
  const isAdmin = auth.user?.email === ADMIN_EMAIL_STUDIO
  const [busy, setBusy] = useState(false)
  if (!isAdmin) return null

  async function doReset() {
    if (busy) return
    if (!confirm('Reset complet du parcours Studio (sessions + analyses + plan free) ? Tu repartiras vierge sur l\'intake.')) return
    setBusy(true)
    try {
      const { data } = await sb.auth.getSession()
      const token = data?.session?.access_token
      if (!token) throw new Error('no session')

      // 1a) Clear localStorage override (priorité absolue côté front)
      try {
        localStorage.removeItem('studio_plan_dev_override')
        localStorage.removeItem('studio_trial_dev')
      } catch {}

      // 1b) Reset plan à null (free) dans user_metadata
      const { error: metaErr } = await sb.auth.updateUser({ data: { studio_plan: null } })
      if (metaErr) console.warn('[admin reset] updateUser warn:', metaErr.message)

      // 2) Refresh session pour que le JWT contienne le plan null
      await sb.auth.refreshSession().catch(() => {})

      // 3) DELETE sessions + analyses côté backend
      const r = await fetch(`${API_URL}/api/admin/studio-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)

      // 4) Reload → StudioPage routes vers StudioIntake (free + no session)
      window.location.replace('/')
    } catch (e) {
      console.error('[admin reset]', e)
      alert(`Erreur reset : ${e.message}`)
      setBusy(false)
    }
  }

  return (
    <button onClick={doReset} disabled={busy}
      className="text-[10px] font-bold px-2.5 py-1 rounded-md whitespace-nowrap transition-colors flex items-center gap-1.5 disabled:opacity-50"
      style={{
        background: 'rgba(220,38,38,0.08)',
        color: '#dc2626',
        border: '1px solid rgba(220,38,38,0.20)',
      }}
      title="Admin : reset Studio (sessions + analyses) pour simuler un nouvel user">
      <span style={{ fontSize: '9px' }}>↺</span>
      {busy ? '…' : 'Fresh user'}
    </button>
  )
}

// ═══════════════════════════════════════════════════════════════
// ADMIN VERTICALE TOGGLE — bascule sport <-> aucune. Nettoie le localStorage
// ET users.vertical côté serveur (sinon AuthContext repose le flag au login
// suivant : la verticale est rattachée au COMPTE, pas juste au navigateur).
export function StudioAdminVerticalToggle() {
  const auth = useAuth()
  const isAdmin = auth.user?.email === ADMIN_EMAIL_STUDIO
  const current = (() => {
    try { return sessionStorage.getItem('studio_vertical') || localStorage.getItem('studio_vertical_persona') || null } catch { return null }
  })()
  if (!isAdmin) return null

  async function toggle() {
    const next = current ? null : 'sport'
    try {
      const { data } = await sb.auth.getSession()
      const token = data?.session?.access_token
      await fetch(`${API_URL}/api/studio/vertical`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ vertical: next }),
      })
    } catch { /* best-effort : le localStorage reste la bascule immédiate */ }
    try {
      if (next) localStorage.setItem('studio_vertical_persona', next)
      else { localStorage.removeItem('studio_vertical_persona'); sessionStorage.removeItem('studio_vertical') }
    } catch {}
    window.location.replace(next ? `/?v=${next}` : '/?v=0')
  }

  return (
    <button onClick={toggle}
      className="text-[10px] font-bold px-2.5 py-1 rounded-md whitespace-nowrap transition-colors flex items-center gap-1.5"
      style={{ background: current ? 'rgba(29,155,240,0.18)' : 'rgba(255,255,255,0.08)', color: current ? '#1d9bf0' : 'rgba(255,255,255,0.6)', border: '1px solid rgba(255,255,255,0.15)' }}>
      {current ? `🔵 ${current}` : '⚪ verticale'}
    </button>
  )
}

// ADMIN SIMULATE SPORT — simule un COMPTE sport complet en un clic
// (demande Antonin 14/07 : impossible de tester formats du jour +
// bibliothèque sport avec son compte marketing). Fait 3 choses :
// verticale sport posée (localStorage + users.vertical), scan RELANCÉ
// avec un @ fitness (nouvelle session = persona sport → matching,
// formats du jour, weekly et library passent tous en sport), reload en
// /?v=sport. Retour : re-clic verticale pour sortir + re-scan de ton @.
// ═══════════════════════════════════════════════════════════════
export function StudioAdminSimulateSportButton() {
  const auth = useAuth()
  const isAdmin = auth.user?.email === ADMIN_EMAIL_STUDIO
  const [busy, setBusy] = useState(false)
  if (!isAdmin) return null

  async function simulate() {
    if (busy) return
    const handle = prompt(
      'Simuler un compte SPORT : @ Instagram fitness à scanner.\n(Ton persona actuel sera remplacé par ce scan — pour revenir, re-scanne ton @ perso ou Fresh user.)',
      'tiboinshape'
    )
    if (!handle) return
    setBusy(true)
    try {
      const { data } = await sb.auth.getSession()
      const token = data?.session?.access_token
      if (!token) throw new Error('pas de session')
      const H = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
      // 1) Verticale sport rattachée au compte + au navigateur
      await fetch(`${API_URL}/api/studio/vertical`, { method: 'POST', headers: H, body: JSON.stringify({ vertical: 'sport' }) })
      try { localStorage.setItem('studio_vertical_persona', 'sport') } catch { /* bascule serveur déjà posée */ }
      // 2) Nouveau scan avec le @ fitness → persona sport
      const r = await fetch(`${API_URL}/api/studio/init`, {
        method: 'POST', headers: H,
        body: JSON.stringify({ ig_username: handle.replace(/^@/, '').trim() }),
      })
      const d = await r.json().catch(() => null)
      if (!r.ok || !d?.id) throw new Error(d?.error || `HTTP ${r.status}`)
      // 3) Recharge en mode sport → écran de scan puis home sport
      window.location.replace('/?v=sport')
    } catch (e) {
      alert(`Simulation sport : ${e.message}`)
      setBusy(false)
    }
  }

  return (
    <button onClick={simulate} disabled={busy}
      className="text-[10px] font-bold px-2.5 py-1 rounded-md whitespace-nowrap transition-colors flex items-center gap-1.5 disabled:opacity-50"
      style={{ background: 'rgba(29,155,240,0.10)', color: '#1d9bf0', border: '1px solid rgba(29,155,240,0.25)' }}
      title="Admin : verticale sport + re-scan avec un @ fitness (persona, formats du jour, library, weekly en sport)">
      🏋️ {busy ? '…' : 'Simuler compte sport'}
    </button>
  )
}

// ═══════════════════════════════════════════════════════════════
// ADMIN GUEST TOGGLE — bascule le navigateur en mode "invité" pour
// tester l'expérience adset C (?guest=1) sans devoir se déconnecter.
// Clic = set localStorage + reload sur /?guest=1. Re-clic = exit.
// ═══════════════════════════════════════════════════════════════
export function StudioAdminGuestToggle() {
  const auth = useAuth()
  const isAdmin = auth.user?.email === ADMIN_EMAIL_STUDIO
  const isGuest = (() => {
    try { return localStorage.getItem('studio_guest_mode') === '1' } catch { return false }
  })()
  if (!isAdmin) return null

  function toggle() {
    try {
      if (isGuest) {
        localStorage.removeItem('studio_guest_mode')
        localStorage.removeItem('studio_guest_session_id')
        window.location.replace('/')
      } else {
        // Sign out d'abord pour vraiment simuler un visiteur Meta sans compte.
        // Sans ça l'auth context redirect vers StudioPro si l'admin a un plan.
        sb.auth.signOut().finally(() => {
          localStorage.setItem('studio_guest_mode', '1')
          window.location.replace('/?guest=1')
        })
      }
    } catch {
      window.location.replace(isGuest ? '/' : '/?guest=1')
    }
  }

  return (
    <button onClick={toggle}
      className="text-[10px] font-bold px-2.5 py-1 rounded-md whitespace-nowrap transition-colors flex items-center gap-1.5"
      style={{
        background: isGuest ? 'rgba(16,185,129,0.12)' : 'rgba(var(--merlin-violet-rgb, 124,58,237),0.10)',
        color: isGuest ? '#059669' : 'var(--merlin-violet, #7c3aed)',
        border: `1px solid ${isGuest ? 'rgba(16,185,129,0.30)' : 'rgba(var(--merlin-violet-rgb, 124,58,237),0.25)'}`,
      }}
      title={isGuest ? 'Mode invité actif (test adset C). Clic pour repasser admin loggé.' : 'Tester le mode invité (?guest=1 / adset C "direct outil")'}>
      <span style={{ fontSize: '10px' }}>{isGuest ? '✓' : '👤'}</span>
      {isGuest ? 'Quitter invité' : 'Mode invité'}
    </button>
  )
}

// ═══════════════════════════════════════════════════════════════
// ADMIN LOGS BUTTON — accès direct au panel admin depuis n'importe
// quel mode (free ou pro). Ouvre StudioAdminPanel en fullscreen overlay.
// ═══════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════
// ADMIN TOP DUPLICABLES — pour créer les contenus "Top 3 formats de
// [mois]" : les vidéos les plus virales ET vraiment refaisables
// (scores recalibrés), avec brief markdown copiable pour le tournage.
// ═══════════════════════════════════════════════════════════════
// Bloc relance inscrits : lien direct tracké + message prêt à copier.
function RelanceInscritsBlock() {
  const LINK = 'https://studio.merlin-content-ai.com/?utm_source=relance&utm_medium=message&utm_campaign=inscrits'
  const MSG = `Salut ! Merci d'avoir créé ton compte sur Merlin 🎬\n\nLe lancement est tout récent, donc le site est encore difficile à retrouver sur Google. Voici le lien direct pour revenir quand tu veux à tes formats viraux :\n${LINK}\n\nAstuce : ajoute-le à ton écran d'accueil pour le garder sous la main. À très vite !`
  const [c1, setC1] = useState(false)
  const [c2, setC2] = useState(false)
  function copy(txt, set) { try { navigator.clipboard.writeText(txt); set(true); setTimeout(() => set(false), 1500) } catch {} }
  return (
    <div className="mb-4 p-3 rounded-xl" style={{ background: 'rgba(var(--merlin-accent-rgb, 254,130,4),0.06)', border: '1px solid rgba(var(--merlin-accent-rgb, 254,130,4),0.2)' }}>
      <p className="text-[11px] font-bold text-ink mb-1.5">📨 Relancer les inscrits (site mal référencé)</p>
      <p className="text-[10.5px] text-ink-muted mb-2 leading-snug">Lien direct tracké + message tout prêt à envoyer (DM, email) aux gens qui ont créé un compte mais ne retrouvent pas le site.</p>
      <div className="flex gap-2">
        <button onClick={() => copy(LINK, setC1)} className="flex-1 text-[10.5px] font-bold px-2.5 py-2 rounded-lg text-white" style={{ background: c1 ? '#10b981' : 'var(--merlin-accent, #fe8204)' }}>
          {c1 ? '✓ Lien copié' : '🔗 Copier le lien tracké'}
        </button>
        <button onClick={() => copy(MSG, setC2)} className="flex-1 text-[10.5px] font-bold px-2.5 py-2 rounded-lg" style={{ background: c2 ? '#10b981' : 'rgba(var(--merlin-violet-rgb, 124,58,237),0.1)', color: c2 ? '#fff' : 'var(--merlin-violet-2, #6d28d9)', border: '1px solid rgba(var(--merlin-violet-rgb, 124,58,237),0.25)' }}>
          {c2 ? '✓ Message copié' : '📋 Copier le message'}
        </button>
      </div>
    </div>
  )
}

// Panneau SEO : liste des articles (manifeste statique) + trafic vers l'app
export function StudioAdminSeoButton() {
  const auth = useAuth()
  const [open, setOpen] = useState(false)
  const [articles, setArticles] = useState(null)
  const [traffic, setTraffic] = useState({})
  const [copied, setCopied] = useState(null)
  const [seoTab, setSeoTab] = useState('articles') // articles | backlinks
  if (auth.user?.email !== ADMIN_EMAIL_STUDIO) return null

  async function load() {
    setOpen(true)
    if (articles) return
    try {
      const m = await fetch('/blog/articles.json').then(r => r.json())
      setArticles(m.articles || [])
    } catch { setArticles([]) }
    try {
      const { data: sess } = await sb.auth.getSession()
      const t = await fetch(`${API_URL}/api/admin/seo-traffic?days=30`, { headers: { Authorization: `Bearer ${sess?.session?.access_token}` } }).then(r => r.json())
      const map = {}
      for (const c of (t.par_article || [])) map[c.campaign] = c.visiteurs
      setTraffic(map)
    } catch {}
  }
  function copy(url, slug) {
    try { navigator.clipboard.writeText(url); setCopied(slug); setTimeout(() => setCopied(null), 1500) } catch {}
  }

  return (
    <>
      <button onClick={load}
        className="text-[10px] font-bold px-2.5 py-1 rounded-md whitespace-nowrap flex items-center gap-1.5 hover:opacity-80"
        style={{ background: 'rgba(16,185,129,0.10)', color: '#047857', border: '1px solid rgba(16,185,129,0.25)' }}
        title="Articles SEO + trafic">
        <span style={{ fontSize: '9px' }}>📝</span> SEO
      </button>
      {open && typeof document !== 'undefined' && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-start justify-center p-0 sm:p-4 overflow-y-auto"
          onClick={() => setOpen(false)} style={{ background: 'rgba(15,10,26,0.7)', backdropFilter: 'blur(6px)' }}>
          <div className="w-full max-w-2xl bg-bg rounded-t-2xl sm:rounded-2xl shadow-2xl my-0 sm:my-8" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-3 border-b border-border bg-white/95 backdrop-blur rounded-t-2xl">
              <h2 className="text-sm font-bold text-ink">🔎 SEO</h2>
              <div className="flex items-center gap-2">
                <a href="https://search.google.com/search-console" target="_blank" rel="noopener" className="text-[10px] font-bold px-2 py-1 rounded-md" style={{ background: 'rgba(59,130,246,0.1)', color: '#1d4ed8' }}>Search Console ↗</a>
                <a href="https://analytics.google.com" target="_blank" rel="noopener" className="text-[10px] font-bold px-2 py-1 rounded-md" style={{ background: 'rgba(var(--merlin-violet-rgb, 124,58,237),0.1)', color: 'var(--merlin-violet-2, #6d28d9)' }}>GA4 ↗</a>
                <button onClick={() => setOpen(false)} className="text-ink-muted hover:text-ink text-lg leading-none px-1">×</button>
              </div>
            </div>
            <div className="p-4">
              {/* Onglets du tableau de bord SEO unifié : Articles + Backlinks */}
              <div className="flex gap-1 mb-3 bg-ink/5 rounded-lg p-1">
                {[['articles', '📝 Articles'], ['backlinks', '🔗 Backlinks']].map(([k, l]) => (
                  <button key={k} onClick={() => setSeoTab(k)}
                    className={`flex-1 text-xs font-bold py-1.5 rounded-md transition-colors ${seoTab === k ? 'bg-white shadow-sm text-ink' : 'text-ink-muted hover:text-ink'}`}>{l}</button>
                ))}
              </div>

              {seoTab === 'backlinks' && <AdminBacklinks />}

              {seoTab === 'articles' && (<>
              {/* Relance des inscrits : lien direct tracké + message à copier.
                  Le site est mal référencé, les inscrits ne le retrouvent pas. */}
              <RelanceInscritsBlock />
              <p className="text-[11px] text-ink-muted mb-3">
                {articles ? `${articles.length} article(s) en ligne.` : 'Chargement...'} Le trafic ci-dessous = visiteurs de l'app venus d'un article (30 j). Les vues de page détaillées sont dans GA4.
              </p>
              {!articles ? (
                <div className="flex justify-center py-10"><div className="w-6 h-6 border-2 border-orange/30 border-t-orange rounded-full animate-spin" /></div>
              ) : !articles.length ? (
                <p className="text-xs text-ink-muted">Aucun article généré.</p>
              ) : articles.map(a => (
                <div key={a.slug} className="p-3 mb-2 rounded-xl bg-white border border-border">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 rounded-full" style={{ background: a.type === 'pilier' ? 'rgba(var(--merlin-accent-rgb, 254,130,4),0.12)' : 'rgba(var(--merlin-violet-rgb, 124,58,237),0.12)', color: a.type === 'pilier' ? '#b45309' : 'var(--merlin-violet-2, #6d28d9)' }}>{a.type}</span>
                        {traffic[a.slug] > 0 && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(16,185,129,0.12)', color: '#047857' }}>{traffic[a.slug]} visiteurs ↗ app</span>}
                      </div>
                      <p className="text-[12px] font-bold text-ink mt-1 leading-snug">{a.title}</p>
                      <p className="text-[10px] text-ink-faint truncate mt-0.5">{a.url}</p>
                    </div>
                    <div className="flex flex-col gap-1 shrink-0">
                      <a href={a.url} target="_blank" rel="noopener" className="text-[10px] font-bold px-2 py-1.5 rounded-md text-center" style={{ background: 'rgba(15,10,26,0.06)', border: '1px solid rgba(15,10,26,0.12)' }}>Ouvrir ↗</a>
                      <button onClick={() => copy(a.url, a.slug)} className="text-[10px] font-bold px-2 py-1.5 rounded-md" style={{ background: copied === a.slug ? 'rgba(16,185,129,0.95)' : 'rgba(15,10,26,0.06)', color: copied === a.slug ? '#fff' : 'inherit', border: '1px solid rgba(15,10,26,0.12)' }}>{copied === a.slug ? '✓' : '🔗 Lien'}</button>
                    </div>
                  </div>
                </div>
              ))}
              <div className="mt-3 p-3 rounded-xl text-[11px] leading-relaxed" style={{ background: 'rgba(59,130,246,0.05)', border: '1px solid rgba(59,130,246,0.15)' }}>
                <b>Sitemap :</b> <a href="/sitemap.xml" target="_blank" className="underline">studio.merlin-content-ai.com/sitemap.xml</a> (à soumettre dans Search Console pour l'indexation)
              </div>
              </>)}
            </div>
          </div>
        </div>, document.body)}
    </>
  )
}

// Analyses Meta Ads (benchmark concurrents) : lecture depuis docs/ads-benchmarks
export function StudioAdminAdsButton() {
  const auth = useAuth()
  const [open, setOpen] = useState(false)
  const [docs, setDocs] = useState(null)
  if (auth.user?.email !== ADMIN_EMAIL_STUDIO) return null

  async function load() {
    setOpen(true)
    if (docs) return
    try {
      const { data: sess } = await sb.auth.getSession()
      const res = await fetch(`${API_URL}/api/admin/ads-benchmarks`, {
        headers: { Authorization: `Bearer ${sess?.session?.access_token}` },
      })
      const d = await res.json()
      setDocs(d.docs || [])
    } catch { setDocs([]) }
  }

  return (
    <>
      <button onClick={load}
        className="text-[10px] font-bold px-2.5 py-1 rounded-md whitespace-nowrap flex items-center gap-1.5 hover:opacity-80"
        style={{ background: 'rgba(59,130,246,0.10)', color: '#1d4ed8', border: '1px solid rgba(59,130,246,0.25)' }}
        title="Benchmarks Meta Ads concurrents">
        <span style={{ fontSize: '9px' }}>📣</span> Ads
      </button>
      {open && typeof document !== 'undefined' && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-start justify-center p-0 sm:p-4 overflow-y-auto"
          onClick={() => setOpen(false)}
          style={{ background: 'rgba(15,10,26,0.7)', backdropFilter: 'blur(6px)' }}>
          <div className="w-full max-w-3xl bg-bg rounded-t-2xl sm:rounded-2xl shadow-2xl my-0 sm:my-8"
            onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-3 border-b border-border bg-white/95 backdrop-blur rounded-t-2xl">
              <h2 className="text-sm font-bold text-ink">📣 Benchmarks Meta Ads</h2>
              <button onClick={() => setOpen(false)} className="text-ink-muted hover:text-ink text-lg leading-none px-1">×</button>
            </div>
            <div className="p-5">
              {!docs ? (
                <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-orange/30 border-t-orange rounded-full animate-spin" /></div>
              ) : !docs.length ? (
                <p className="text-xs text-ink-muted">Aucun benchmark (docs/ads-benchmarks vide).</p>
              ) : docs.map((d) => (
                <div key={d.name} className="mb-6">
                  <div className="text-xs leading-relaxed text-ink whitespace-pre-wrap"
                    style={{ fontFamily: 'inherit' }}>
                    {d.content.split('\n').map((line, i) => {
                      if (line.startsWith('# ')) return <h2 key={i} className="text-base font-bold text-ink mt-1 mb-2">{line.slice(2)}</h2>
                      if (line.startsWith('## ')) return <h3 key={i} className="text-sm font-bold text-ink mt-4 mb-1.5">{line.slice(3)}</h3>
                      if (line.startsWith('### ')) return <h4 key={i} className="text-[12.5px] font-bold mt-3 mb-1" style={{ color: 'var(--merlin-violet-2, #6d28d9)' }}>{line.slice(4)}</h4>
                      return <p key={i} className="mb-1">{line.replace(/\*\*/g, '')}</p>
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>, document.body)}
    </>
  )
}

// Lien vers le panel admin complet (/admin marche aussi sur studio.*)// Lien vers le panel admin complet (/admin marche aussi sur studio.*)
export function StudioAdminHubLink() {
  const auth = useAuth()
  if (auth.user?.email !== ADMIN_EMAIL_STUDIO) return null
  return (
    <a href="/admin"
      className="text-[10px] font-bold px-2.5 py-1 rounded-md whitespace-nowrap flex items-center gap-1.5 hover:opacity-80"
      style={{ background: 'rgba(var(--merlin-violet-rgb, 124,58,237),0.10)', color: 'var(--merlin-violet-2, #6d28d9)', border: '1px solid rgba(var(--merlin-violet-rgb, 124,58,237),0.25)' }}
      title="Panel admin complet (analyses, funnel, clients)">
      <span style={{ fontSize: '9px' }}>🛠</span> Admin
    </a>
  )
}

export function StudioAdminTopFormatsButton() {
  const auth = useAuth()
  const isAdmin = auth.user?.email === ADMIN_EMAIL_STUDIO
  const [open, setOpen] = useState(false)
  const [month, setMonth] = useState('') // '' = tout le corpus
  const [curateQuery, setCurateQuery] = useState('')
  const [curating, setCurating] = useState(false)
  const [curatedFor, setCuratedFor] = useState(null) // requête active (mode curaté)
  const [items, setItems] = useState(null)
  const [loading, setLoading] = useState(false)
  const [copied, setCopied] = useState(false)
  const [downloading, setDownloading] = useState(null)
  const [analyses, setAnalyses] = useState({}) // video_id → analysis
  const [analyzing, setAnalyzing] = useState(null)
  const [openAnalysis, setOpenAnalysis] = useState(null) // video_id du panneau ouvert
  const [player, setPlayer] = useState(null) // {url, titre} → lightbox
  const [playerLoading, setPlayerLoading] = useState(null)
  const blobCacheRef = useRef(new Map()) // id → objectURL (évite les re-fetch)

  // Récupère le MP4 (URL fraîche RocketAPI via le backend) en blob local.
  // Mutualisé entre la lecture (lightbox) et le téléchargement.
  async function fetchVideoBlobUrl(v) {
    const cached = blobCacheRef.current.get(v.id)
    if (cached) return cached
    const shortcode = extractShortcode(v.lien)
    if (!shortcode) throw new Error('Lien sans shortcode')
    const { data: sess } = await sb.auth.getSession()
    const token = sess?.session?.access_token
    const res = await fetch(`${API_URL}/api/admin/video-download?shortcode=${shortcode}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(err.error || `HTTP ${res.status}`)
    }
    const blobUrl = URL.createObjectURL(await res.blob())
    blobCacheRef.current.set(v.id, blobUrl)
    return blobUrl
  }

  async function playVideo(v) {
    setPlayerLoading(v.id)
    try {
      const url = await fetchVideoBlobUrl(v)
      setPlayer({ url, titre: v.format_nom || v.titre || '' })
    } catch (e) { alert(`Lecture impossible : ${e.message}`) }
    setPlayerLoading(null)
  }

  async function analyzeForVideo(v) {
    if (analyses[v.id]) { setOpenAnalysis(openAnalysis === v.id ? null : v.id); return }
    setAnalyzing(v.id)
    try {
      const { data: sess } = await sb.auth.getSession()
      const token = sess?.session?.access_token
      const res = await fetch(`${API_URL}/api/admin/top-format-analysis`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ video_id: v.id }),
      })
      const d = await res.json()
      if (!res.ok || !d?.analysis) throw new Error(d?.error || `HTTP ${res.status}`)
      setAnalyses(prev => ({ ...prev, [v.id]: d.analysis }))
      setOpenAnalysis(v.id)
    } catch (e) { alert(`Analyse impossible : ${e.message}`) }
    setAnalyzing(null)
  }

  function copyAnalysis(v) {
    const a = analyses[v.id]
    if (!a) return
    const txt = [
      `🎬 ${v.format_nom || v.titre} (@${v.createur_nom}, ${(v.vues || 0).toLocaleString('fr-FR')} vues)`,
      `Source : ${v.lien}`,
      '',
      '── PITCH À DIRE (30-40s) ──',
      a.pitch_30s,
      '',
      '── TEXTES ÉCRAN ──',
      ...(a.points_cles || []).map(pt => `• ${pt}`),
      '',
      `Hook à citer : « ${a.hook_a_citer || ''} »`,
      `Pourquoi ça marche : ${a.pourquoi_ca_marche || ''}`,
      `Comment refaire : ${a.comment_refaire || ''}`,
    ].join('\n')
    try { navigator.clipboard.writeText(txt) } catch {}
  }

  async function downloadVideo(v) {
    setDownloading(v.id)
    try {
      const url = await fetchVideoBlobUrl(v)
      const a = document.createElement('a')
      a.href = url
      a.download = `${extractShortcode(v.lien) || 'video'}.mp4`
      document.body.appendChild(a)
      a.click()
      setTimeout(() => a.remove(), 2000)
    } catch (e) {
      alert(`Téléchargement impossible : ${e.message}`)
    }
    setDownloading(null)
  }

  async function load(m = month) {
    setLoading(true)
    try {
      const { data: sess } = await sb.auth.getSession()
      const token = sess?.session?.access_token
      const res = await fetch(`${API_URL}/api/admin/studio-top-duplicables?count=12${m ? `&month=${m}` : ''}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      const d = await res.json()
      setItems(d.items || [])
    } catch { setItems([]) }
    setLoading(false)
  }

  async function curate() {
    const q = curateQuery.trim()
    if (q.length < 5 || curating) return
    setCurating(true)
    try {
      const { data: sess } = await sb.auth.getSession()
      const res = await fetch(`${API_URL}/api/admin/studio-top-curate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${sess?.session?.access_token}` },
        body: JSON.stringify({ query: q, count: 12 }),
      })
      const d = await res.json()
      if (!res.ok) throw new Error(d?.error || `HTTP ${res.status}`)
      setItems(d.items || [])
      setCuratedFor(q)
      setOpenAnalysis(null)
    } catch (e) { alert(`Curation impossible : ${e.message}`) }
    setCurating(false)
  }

  function copyBrief(top) {
    const lines = top.map((v, i) => [
      `## #${i + 1} — ${v.format_nom || v.titre || 'Format'}`,
      `- Source : ${v.lien} (@${v.createur_nom || '?'}, ${(v.vues || 0).toLocaleString('fr-FR')} vues)`,
      `- Hook : « ${v.hook_audio_verbatim || '?'} »`,
      v.hook_audio_template_fr ? `- Template FR : ${v.hook_audio_template_fr}` : null,
      `- Duplicabilité : ${v.score_format}/10 (${v.dependance_execution}) · technique ${v.score_technique}/10 · ${v.duree_secondes || '?'}s`,
      `- Niche : ${v.niche_nom || '?'}`,
    ].filter(Boolean).join('\n')).join('\n\n')
    const head = `# Top ${top.length} formats duplicables${month ? ` — ${month}` : ''}\n\n`
    try {
      navigator.clipboard.writeText(head + lines)
      setCopied(true); setTimeout(() => setCopied(false), 1800)
    } catch {}
  }

  if (!isAdmin) return null
  return (
    <>
      <button onClick={() => { setOpen(true); if (!items) load() }}
        className="text-[10px] font-bold px-2.5 py-1 rounded-md whitespace-nowrap flex items-center gap-1.5 hover:opacity-80"
        style={{ background: 'rgba(var(--merlin-accent-rgb, 254,130,4),0.10)', color: '#b45309', border: '1px solid rgba(var(--merlin-accent-rgb, 254,130,4),0.30)' }}
        title="Admin : top formats duplicables (pour les contenus Top du mois)">
        <span style={{ fontSize: '9px' }}>🏆</span> Top
      </button>
      {open && typeof document !== 'undefined' && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-start justify-center p-0 sm:p-4 overflow-y-auto"
          onClick={() => setOpen(false)}
          style={{ background: 'rgba(15,10,26,0.7)', backdropFilter: 'blur(6px)' }}>
          <div className="w-full max-w-3xl bg-bg rounded-t-2xl sm:rounded-2xl shadow-2xl my-0 sm:my-8"
            onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 z-10 flex items-center justify-between gap-2 px-4 py-3 border-b border-border bg-white/95 backdrop-blur rounded-t-2xl">
              <h2 className="text-sm font-bold text-ink flex items-center gap-2">🏆 Top formats duplicables</h2>
              <div className="flex items-center gap-2">
                <input type="month" value={month}
                  onChange={(e) => { setMonth(e.target.value); load(e.target.value) }}
                  className="text-[11px] px-2 py-1 rounded-md border border-border bg-white" />
                {month && (
                  <button onClick={() => { setMonth(''); load('') }}
                    className="text-[10px] text-ink-muted underline">tout</button>
                )}
                {items?.length > 0 && (
                  <button onClick={() => copyBrief(items.slice(0, 3))}
                    className="text-[10px] font-bold px-2.5 py-1.5 rounded-md text-white"
                    style={{ background: copied ? '#10b981' : 'var(--merlin-accent, #fe8204)' }}>
                    {copied ? '✓ Copié' : '📋 Brief Top 3'}
                  </button>
                )}
                <button onClick={() => setOpen(false)} className="text-ink-muted hover:text-ink text-lg leading-none px-1">×</button>
              </div>
            </div>
            <div className="px-4 pt-3">
              <div className="flex items-center gap-2">
                <input type="text" value={curateQuery}
                  onChange={(e) => setCurateQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') curate() }}
                  placeholder='Demande à Merlin... ex: "Top 3 formats pour un entrepreneur qui veut vendre"'
                  className="flex-1 text-xs px-3 py-2 rounded-lg border border-border bg-white outline-none focus:border-orange/40" />
                <button onClick={curate} disabled={curating || curateQuery.trim().length < 5}
                  className="text-[11px] font-bold px-3 py-2 rounded-lg text-white disabled:opacity-40 whitespace-nowrap"
                  style={{ background: 'linear-gradient(135deg, var(--merlin-violet, #7c3aed), var(--merlin-violet-2, #6d28d9))' }}>
                  {curating ? '🪄 Merlin cherche…' : '🪄 Sélectionner'}
                </button>
              </div>
              {curatedFor && (
                <p className="text-[10.5px] text-ink-muted mt-1.5">
                  Sélection Merlin pour : <span className="font-bold text-ink">« {curatedFor} »</span>
                  <button onClick={() => { setCuratedFor(null); setCurateQuery(''); load(month) }}
                    className="ml-2 underline">retour au top classique</button>
                </p>
              )}
            </div>
            <div className="p-4 space-y-2.5">
              {loading ? (
                <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-orange/30 border-t-orange rounded-full animate-spin" /></div>
              ) : !items?.length ? (
                <p className="text-xs text-ink-muted text-center py-10">Aucun format trouvé{month ? ` pour ${month} (essaie "tout")` : ''}.</p>
              ) : items.map((v, i) => (
                <div key={v.id} className="p-3 rounded-xl bg-white border border-border">
                  <div className="flex gap-3 items-start">
                  <button onClick={() => playVideo(v)} disabled={playerLoading === v.id}
                    className="relative w-12 h-20 shrink-0 rounded-lg overflow-hidden bg-purple-950/10"
                    title="Regarder la vidéo en grand">
                    <img src={getSupabaseThumb({ lien: v.lien }) || ''} alt=""
                      className="w-full h-full object-cover"
                      onError={(e) => { e.currentTarget.style.visibility = 'hidden' }} />
                    <span className="absolute inset-0 flex items-center justify-center text-white text-sm"
                      style={{ background: 'rgba(15,10,26,0.35)' }}>
                      {playerLoading === v.id ? '⏳' : '▶'}
                    </span>
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[11px] font-bold text-ink">#{i + 1} · {(v.format_nom || v.titre || '?').slice(0, 60)}</span>
                      <span className="text-[10px] px-1.5 rounded-full font-bold" style={{ background: 'rgba(16,185,129,0.12)', color: '#047857' }}>
                        {v.score_format}/10
                      </span>
                      <span className="text-[10px] text-ink-faint">{v.dependance_execution}</span>
                    </div>
                    <p className="text-[11px] text-ink-muted truncate mt-0.5">
                      @{v.createur_nom || '?'} · {(v.vues || 0).toLocaleString('fr-FR')} vues · {v.niche_nom || '?'} · {v.duree_secondes || '?'}s
                    </p>
                    {v.hook_audio_verbatim && (
                      <p className="text-[11px] text-ink italic truncate mt-0.5">« {v.hook_audio_verbatim.slice(0, 90)} »</p>
                    )}
                    {v.raison && (
                      <p className="text-[10.5px] mt-0.5 font-medium" style={{ color: 'var(--merlin-violet-2, #6d28d9)' }}>🪄 {v.raison}</p>
                    )}
                  </div>
                  <div className="shrink-0 flex flex-col gap-1">
                    <button onClick={() => { try { navigator.clipboard.writeText(v.lien) } catch {} }}
                      className="text-[10px] font-bold px-2 py-1.5 rounded-md hover:opacity-80"
                      style={{ background: 'rgba(15,10,26,0.06)', border: '1px solid rgba(15,10,26,0.12)' }}
                      title={`Copier ${v.lien}`}>🔗</button>
                    <button onClick={() => downloadVideo(v)}
                      disabled={downloading === v.id}
                      className="text-[10px] font-bold px-2 py-1.5 rounded-md hover:opacity-80 disabled:opacity-40"
                      style={{ background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.30)' }}
                      title="Télécharger le MP4 (pour les montages)">
                      {downloading === v.id ? '⏳' : '⬇️'}
                    </button>
                    <button onClick={() => analyzeForVideo(v)}
                      disabled={analyzing === v.id}
                      className="text-[10px] font-bold px-2 py-1.5 rounded-md hover:opacity-80 disabled:opacity-40"
                      style={{ background: 'rgba(var(--merlin-violet-rgb, 124,58,237),0.10)', border: '1px solid rgba(var(--merlin-violet-rgb, 124,58,237),0.30)' }}
                      title="Analyse présentable : pitch à délivrer en vidéo">
                      {analyzing === v.id ? '⏳' : '🎬'}
                    </button>
                  </div>
                </div>
                {/* Panneau analyse présentable */}
                {openAnalysis === v.id && analyses[v.id] && (
                  <div className="mt-1 p-3.5 rounded-xl text-[11.5px] leading-relaxed"
                    style={{ background: 'rgba(var(--merlin-violet-rgb, 124,58,237),0.05)', border: '1px solid rgba(var(--merlin-violet-rgb, 124,58,237),0.18)' }}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] uppercase tracking-wider font-bold" style={{ color: 'var(--merlin-violet-2, #6d28d9)' }}>🎬 Pitch à délivrer</span>
                      <button onClick={() => copyAnalysis(v)}
                        className="text-[10px] font-bold px-2 py-1 rounded-md text-white" style={{ background: 'var(--merlin-violet, #7c3aed)' }}>
                        📋 Copier tout
                      </button>
                    </div>
                    <p className="text-ink whitespace-pre-wrap mb-2.5">{analyses[v.id].pitch_30s}</p>
                    {(analyses[v.id].points_cles || []).length > 0 && (
                      <div className="mb-2">
                        <span className="text-[10px] font-bold text-ink-faint">TEXTES ÉCRAN : </span>
                        {(analyses[v.id].points_cles || []).map((pt, j) => (
                          <span key={j} className="inline-block mr-1.5 mb-1 px-2 py-0.5 rounded-md text-[10.5px] font-medium"
                            style={{ background: 'rgba(var(--merlin-accent-rgb, 254,130,4),0.10)', color: '#b45309' }}>{pt}</span>
                        ))}
                      </div>
                    )}
                    <p className="text-ink-muted"><b>Pourquoi ça marche :</b> {analyses[v.id].pourquoi_ca_marche}</p>
                    <p className="text-ink-muted"><b>Comment refaire :</b> {analyses[v.id].comment_refaire}</p>
                  </div>
                )}
                </div>
              ))}
            </div>
          </div>
          {/* Lightbox player — clic sur une miniature */}
          {player && (
            <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4"
              onClick={() => setPlayer(null)}
              style={{ background: 'rgba(15,10,26,0.92)' }}>
              <div className="relative" onClick={(e) => e.stopPropagation()}>
                <video src={player.url} controls autoPlay playsInline
                  className="rounded-xl shadow-2xl"
                  style={{ maxHeight: '85vh', maxWidth: '92vw' }} />
                <button onClick={() => setPlayer(null)}
                  className="absolute -top-3 -right-3 w-8 h-8 rounded-full bg-white text-ink font-bold shadow-lg">×</button>
                <p className="text-white/70 text-[11px] mt-2 text-center truncate max-w-[80vw]">{player.titre}</p>
              </div>
            </div>
          )}
        </div>, document.body)}
    </>
  )
}

export function StudioAdminLogsButton() {
  const auth = useAuth()
  const isAdmin = auth.user?.email === ADMIN_EMAIL_STUDIO
  const [open, setOpen] = useState(false)
  if (!isAdmin) return null
  return (
    <>
      <button onClick={() => setOpen(true)}
        className="text-[10px] font-bold px-2.5 py-1 rounded-md whitespace-nowrap flex items-center gap-1.5 hover:opacity-80"
        style={{
          background: 'rgba(15,10,26,0.06)',
          color: '#1e1145',
          border: '1px solid rgba(15,10,26,0.15)',
        }}
        title="Admin : logs users + sessions">
        <span style={{ fontSize: '9px' }}>📊</span>
        Logs
      </button>
      {open && typeof document !== 'undefined' && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-start justify-center p-0 sm:p-4 overflow-y-auto"
          onClick={() => setOpen(false)}
          style={{ background: 'rgba(15,10,26,0.7)', backdropFilter: 'blur(6px)' }}>
          <div className="w-full max-w-3xl bg-bg rounded-t-2xl sm:rounded-2xl shadow-2xl my-0 sm:my-8"
            onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-3 border-b border-border bg-white/95 backdrop-blur rounded-t-2xl">
              <h2 className="text-sm font-bold text-ink flex items-center gap-2">
                <span>🛠</span> Admin · Logs utilisateurs
              </h2>
              <button onClick={() => setOpen(false)}
                className="w-8 h-8 rounded-full flex items-center justify-center text-ink-muted hover:text-ink hover:bg-ink/5">
                ×
              </button>
            </div>
            <div className="p-4">
              <StudioAdminPanel />
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  )
}

// ═══════════════════════════════════════════════════════════════
// NAV TAB (local) — mirroir du NavTab de StudioPro
// ═══════════════════════════════════════════════════════════════
function NavTabLocal({ active, onClick, children, highlight = false }) {
  const base = 'text-xs font-bold px-3 py-1.5 rounded-md transition-all flex items-center gap-1 whitespace-nowrap'
  if (highlight) {
    // Variant "Merlin sparkle" : violet glowing + paillettes orbitales animées
    // pour rendre l'onglet Analyse impossible à manquer (2nd point de conv).
    return (
      <button type="button" onClick={onClick}
        className={`${base} merlin-tab-sparkle relative text-white hover:scale-[1.05] active:scale-[0.97] overflow-visible`}
        style={{
          background: 'linear-gradient(135deg, var(--merlin-tab-accent, var(--merlin-violet, #7c3aed)) 0%, var(--merlin-tab-accent-2, var(--merlin-violet-2, #5b21b6)) 100%)',
          boxShadow: active
            ? '0 0 0 2px rgba(var(--merlin-accent-rgb, 254,130,4),0.45), 0 4px 18px rgba(var(--merlin-tab-accent-rgb, 124,58,237),0.55), 0 0 24px rgba(var(--merlin-accent-rgb, 254,130,4),0.30)'
            : '0 4px 14px rgba(var(--merlin-tab-accent-rgb, 124,58,237),0.45), 0 0 20px rgba(var(--merlin-accent-rgb, 254,130,4),0.18)',
          // Halo orange pulsant via box-shadow animée
        }}>
        {/* Halo glow autour du bouton (sous-couche pulsante) */}
        <span className="absolute inset-[-3px] rounded-md pointer-events-none merlin-tab-halo" aria-hidden="true" />
        {/* Paillettes orbitales (4 étoiles autour du bouton) */}
        <span className="merlin-sparkle merlin-sparkle-1" aria-hidden="true">✦</span>
        <span className="merlin-sparkle merlin-sparkle-2" aria-hidden="true">✧</span>
        <span className="merlin-sparkle merlin-sparkle-3" aria-hidden="true">✦</span>
        <span className="merlin-sparkle merlin-sparkle-4" aria-hidden="true">✧</span>
        <span className="text-[11px] relative z-10">✨</span>
        <span className="relative z-10">{children}</span>
        {/* Pulse dot orange */}
        <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full animate-ping"
          style={{ background: '#fb923c' }} aria-hidden="true" />
        <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full"
          style={{ background: 'var(--merlin-accent-soft, #fdba74)', boxShadow: '0 0 8px rgba(var(--merlin-accent-rgb, 254,130,4),0.8)' }} aria-hidden="true" />
      </button>
    )
  }
  if (active) {
    return (
      <button type="button" onClick={onClick} className={`${base} bg-orange/15 text-orange`}>
        {children}
      </button>
    )
  }
  return (
    <button type="button" onClick={onClick}
      className={`${base} text-ink-muted hover:text-ink hover:bg-ink/5`}>
      {children}
    </button>
  )
}

// ═══════════════════════════════════════════════════════════════
// PUSH POPUPS — séquence library puis analyse pour pousser free → trial
// ═══════════════════════════════════════════════════════════════
// Apparition séquentielle :
//   1. Popup library : 25s après landing sur home OU scroll bas formats
//   2. Popup analyse : 40s après que popup 1 ait été shown
// Chacune flag localStorage pour ne plus jamais re-apparaître.
function PushPopupBase({ icon, title, description, ctaLabel, ctaSubtext, onCta, onDismiss, accent = null }) {
  // Hex BRUT obligatoire ici (concaténations alpha `${accent}22` plus bas).
  accent = accent || '#fe8204'
  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onDismiss}
      style={{ background: 'rgba(15,10,26,0.65)', backdropFilter: 'blur(6px)' }}>
      <div className="w-full max-w-md rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden bg-white"
        onClick={(e) => e.stopPropagation()}
        style={{ boxShadow: '0 30px 80px rgba(0,0,0,0.40)' }}>
        <div className="px-5 pt-5 pb-3 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full mb-3"
            style={{ background: `linear-gradient(135deg, ${accent}22, ${accent}11)`, border: `1px solid ${accent}30` }}>
            <span style={{ fontSize: '28px' }}>{icon}</span>
          </div>
          <h2 className="text-lg sm:text-xl font-bold text-ink leading-tight mb-1">{title}</h2>
          <p className="text-[13px] text-ink-muted leading-snug">{description}</p>
        </div>
        <div className="px-5 pb-5">
          <button onClick={onCta}
            className="w-full py-3.5 rounded-xl font-bold text-sm text-white transition-all hover:scale-[1.01]"
            style={{ background: `linear-gradient(135deg, ${accent}, var(--merlin-accent-2, #f59e0b))`, boxShadow: `0 8px 24px ${accent}55` }}>
            {ctaLabel} →
          </button>
          {ctaSubtext && <p className="text-[10.5px] text-ink-faint text-center mt-2">{ctaSubtext}</p>}
          <button onClick={onDismiss}
            className="w-full text-[11px] text-ink-faint hover:text-ink-muted underline text-center mt-3">
            Plus tard
          </button>
        </div>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// READY — fond clair, charte Merlin
// ═══════════════════════════════════════════════════════════════
function StudioReady({ session, copyToClipboard, copyState, resetSession, airtableReady }) {
  const persona = session.persona || {}
  const formats = session.formats || []
  const [view, setView] = useState('home') // home | library | vault
  // Avatar + modal profil (parité avec la home Pro, 14/07)
  const auth = useAuth()
  const user = auth.user
  const [profileOpen, setProfileOpen] = useState(false)
  // "Revenir aux reels" depuis l'analyse de compte : retour à "Pour toi"
  // (l'analyse reste montée en fond et continue de poller).
  useEffect(() => {
    function goHome() { setView('home') }
    function goAnalyse() { setView('analyse') }
    window.addEventListener('studio:goto-home', goHome)
    window.addEventListener('studio:goto-video-analysis', goAnalyse)
    // Deep-link du mail « analyse vidéo offerte » (?va=1) : ouvre direct
    // l'onglet Analyse où le rapport pré-généré attend l'utilisateur
    const _sp = new URLSearchParams(window.location.search)
    if (_sp.get('va') === '1') goAnalyse()
    // Deep-link de la fiche publique /video/:id (CTA « Analyser ma vidéo ») :
    // ?goto=video ouvre l'onglet Analyse. Le gating par plan (free → paywall,
    // Créateur → quota hebdo, Pro+ → illimité) est géré dans l'onglet lui-même.
    if (_sp.get('goto') === 'video') goAnalyse()
    return () => {
      window.removeEventListener('studio:goto-home', goHome)
      window.removeEventListener('studio:goto-video-analysis', goAnalyse)
    }
  }, [])
  const [showLibraryPopup, setShowLibraryPopup] = useState(false)
  const [showAnalysePopup, setShowAnalysePopup] = useState(false)
  // Impressions des popups d'upsell (pour calculer leur CTR).
  useEffect(() => { if (showLibraryPopup) { try { studioEvents.popupLibraryShown() } catch {} } }, [showLibraryPopup])
  useEffect(() => { if (showAnalysePopup) { try { studioEvents.popupAnalyseShown() } catch {} } }, [showAnalysePopup])
  const userPlan = auth?.studioPlan || null

  // Trigger sequencer : montre popup 1 (library) après 25s sur home,
  // puis popup 2 (analyse) 40s après que la 1 ait été dismissée/actionnée.
  // localStorage : skip si déjà vu.
  useEffect(() => {
    if (view !== 'home') return
    // Event : l'user a atteint l'écran des 3 formats
    studioEvents.formatsViewed({ plan: userPlan || 'free' })

    // POPUPS DÉSACTIVÉES 2026-06-08 : 100% dismissal en prod sur 24h (7
    // shown → 0 clicked → 7 dismissed pour library, idem analyse). Trigger
    // time-based interrompait la lecture des 3 cards = pure friction sans
    // conversion. Re-activer uniquement si on a un signal d'intention
    // (scroll bottom, exit intent) ET un wording qui ne dégrade pas la
    // perception "ils ont fait ça pour moi" des 3 formats persona.
    return () => {}
  }, [view, userPlan])

  function dismissLibraryPopup(actioned = false) {
    setShowLibraryPopup(false)
    try { localStorage.setItem('studio_popup_library_seen', '1') } catch {}
    if (actioned) studioEvents.popupLibraryClicked({ plan: userPlan || 'free' })
    else studioEvents.popupLibraryDismissed({ plan: userPlan || 'free' })
    // Popup analyse également désactivée (cf. ci-dessus)
  }

  function dismissAnalysePopup(actioned = false) {
    setShowAnalysePopup(false)
    try { localStorage.setItem('studio_popup_analyse_seen', '1') } catch {}
    if (actioned) studioEvents.popupAnalyseClicked({ plan: userPlan || 'free' })
    else studioEvents.popupAnalyseDismissed({ plan: userPlan || 'free' })
  }

  function handleLibraryCta() {
    dismissLibraryPopup(true)
    // Pour un user free : navigue vers Découvrir (la bibliothèque déclenchera
    // son propre paywall si tentative d'action gated). Pour un Créateur/Pro+ :
    // navigue direct, l'usage est autorisé.
    setView('library')
    studioEvents.libraryVisited({ plan: userPlan || 'free', source: 'popup' })
  }

  function handleAnalyseCta() {
    dismissAnalysePopup(true)
    // Free : navigation vers Analyse déclenchera le paywall Créateur (trial 7j).
    // Créateur/Pro+ : accès direct à la section vidéos.
    setView('analyse')
    studioEvents.videoAnalysisStarted({ plan: userPlan || 'free', source: 'popup' })
  }

  // Filtre strict : ne garde QUE les formats avec média visible (MP4 OU
  // thumbnail). Crucial pour l'UX : on ne propose jamais un format sans
  // vidéo regardable, sinon le visiteur ne peut pas comprendre le contexte
  // du script. Si airtableReady=false, on garde tout (loading state, ils
  // afficheront leur spinner). On trie aussi : MP4 d'abord, thumbnail
  // sinon — les vrais lecteurs vidéo en premier.
  const formatsWithMp4 = useMemo(() => {
    if (!airtableReady) return formats
    const scored = formats.map(f => {
      const mp4 = getVideoMp4({ lien: f.source?.lien, airtable_id: f.source?.airtable_id })
      const airtableThumb = getVideoThumb({ lien: f.source?.lien, airtable_id: f.source?.airtable_id })
      const supabaseThumb = formatPosterUrl(f)
      // 2 = MP4 (vidéo jouable), 1 = thumbnail dispo, 0 = rien (shortcode invalide)
      const score = mp4 ? 2 : (supabaseThumb || airtableThumb) ? 1 : 0
      return { f, score }
    })
    const usable = scored.filter(({ score }) => score > 0)
    usable.sort((a, b) => b.score - a.score)
    const result = usable.map(({ f }) => f)
    const freeCount = result.filter(f => !f.locked).length
    console.log(`[Studio] ${result.length}/${formats.length} formats avec média (${freeCount} free)`)
    return result
  }, [formats, airtableReady])

  const free = formatsWithMp4.filter(f => !f.locked)
  // Tuto en contexte : ouvre la VRAIE fiche du 1er format offert (préchargée
  // côté serveur au scan) pour montrer l'analyse et les exemples perso.
  const tourFormatsRef = useRef(null)
  tourFormatsRef.current = (free.length ? free : (session.formats || [])).filter(f => !f.locked)
  useEffect(() => {
    function onTourOpen() {
      const f = (tourFormatsRef.current || [])[0]
      if (f) { try { window.dispatchEvent(new CustomEvent('studio:open-format-detail', { detail: { format: f, idx: 0 } })) } catch { /* no-op */ } }
    }
    window.addEventListener('studio:tour-open-first-format', onTourOpen)
    return () => window.removeEventListener('studio:tour-open-first-format', onTourOpen)
  }, [])
  const locked = formatsWithMp4.filter(f => f.locked)

  return (
    <div className="min-h-screen bg-bg">
      {/* ═══ Top nav Studio (style Pro, mais 2 onglets lockés en free) ═══ */}
      {/* Mirroir exact du nav de StudioPro pour que le free user sente l'app
          full — pas une landing isolée. Les 2 onglets lockés cliquent vers
          la modal d'upgrade / Stripe. */}
      <div className="sticky top-0 z-30 backdrop-blur-xl border-b border-border"
        style={{ background: 'rgba(255,251,245,0.92)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
          {/* Layout 2-row sur mobile, 1-row sur desktop pour éviter overflow */}
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 sm:gap-2.5 min-w-0">
              <img src={merlinFound} alt="Merlin" className="w-8 h-8 sm:w-9 sm:h-9 object-contain shrink-0"
                style={{ filter: 'drop-shadow(0 2px 8px rgba(var(--merlin-accent-rgb, 254,130,4),0.25))' }} />
              <span className="text-sm font-bold text-ink">Studio</span>
              <span className="hidden sm:inline-block text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full"
                style={{ background: 'rgba(var(--merlin-accent-rgb, 254,130,4),0.12)', color: '#d97206' }}>
                Aperçu
              </span>
            </div>
            {/* Tabs desktop only (mobile en 2e row) */}
            <div className="hidden md:flex items-center gap-1">
              <NavTabLocal active={view === 'home'} onClick={() => setView('home')}>Pour toi</NavTabLocal>
              <NavTabLocal active={view === 'library'} onClick={() => setView('library')}>Découvrir</NavTabLocal>
              <NavTabLocal active={view === 'vault'} onClick={() => setView('vault')}>Mes formats</NavTabLocal>
              <NavTabLocal active={view === 'analyse'} onClick={() => setView('analyse')} highlight>Analyse</NavTabLocal>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <StudioAdminLogsButton />
              <StudioAdminTopFormatsButton />
              <StudioAdminAdsButton />
              <StudioAdminSeoButton />
              <StudioAdminHubLink />
              <StudioAdminToggle currentPlan={null /* free flow */} />
              <StudioAdminVerticalToggle />
        <StudioAdminSimulateSportButton />
              <StudioAdminGuestToggle />
              <StudioAdminResetButton />
              <button onClick={resetSession}
                className="text-[11px] text-ink-muted hover:text-ink underline underline-offset-2 whitespace-nowrap hidden sm:inline">
                Recommencer
              </button>
              <button onClick={resetSession}
                className="text-base text-ink-muted hover:text-ink sm:hidden" title="Recommencer">
                ↻
              </button>
              {/* Avatar profil : MÊME pastille que la home Pro (14/07 : le
                  portrait n'existait que sur le Pro, incohérent entre les
                  deux accueils). Ouvre la même modal compte/abonnement. */}
              {user && (
                <button onClick={() => setProfileOpen(true)}
                  className="w-9 h-9 rounded-full flex items-center justify-center transition-all hover:scale-105 shrink-0"
                  style={{ background: 'linear-gradient(135deg, var(--merlin-violet, #7c3aed), var(--merlin-violet-2, #5b21b6))', color: 'white', boxShadow: '0 2px 8px rgba(var(--merlin-violet-rgb, 124,58,237),0.30)' }}
                  title="Profil & abonnement">
                  <span className="text-xs font-bold">{(user?.email?.[0] || '?').toUpperCase()}</span>
                </button>
              )}
              {profileOpen && (
                <StudioProfileModal user={user} userProfile={auth.userProfile} studioPlan={auth.userProfile?.studio_plan || null}
                  onClose={() => setProfileOpen(false)}
                  onSignOut={async () => { try { await auth.signOut?.() } catch {} window.location.replace('/') }} />
              )}
            </div>
          </div>
          {/* Tabs mobile : 2e ligne, scrollable si besoin */}
          <div className="md:hidden flex items-center gap-1 mt-2 -mx-1 px-1 overflow-x-auto">
            <NavTabLocal active={view === 'home'} onClick={() => setView('home')}>Pour toi</NavTabLocal>
            <NavTabLocal active={view === 'library'} onClick={() => setView('library')}>Découvrir</NavTabLocal>
            <NavTabLocal active={view === 'vault'} onClick={() => setView('vault')}>Mes formats</NavTabLocal>
            <NavTabLocal active={view === 'analyse'} onClick={() => setView('analyse')} highlight>Analyse</NavTabLocal>
          </div>
        </div>
      </div>

      {/* TUTO post-scan (tous comptes) : monté au niveau RACINE (pas dans la
          vue home) car il navigue entre les onglets pendant le tour. */}
      <StudioOnboarding steps={FREE_STEPS} storageKey="studio_onboarded_free_v1"
        onOpenFirstFormat={() => { try { document.querySelector('[data-tour="free-formats"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' }) } catch { /* no-op */ } }} />
      {/* Démo du tuto : une vraie analyse vidéo (fixture réelle) */}
      <TourVideoAnalysisDemo />

      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-8">

        {/* ═══ Vue LIBRARY (Découvrir) — bibliothèque paywallée ═══ */}
        <div style={{ display: view === 'library' ? 'block' : 'none' }}>
          <StudioFreeLibrary airtableReady={airtableReady} />
        </div>

        {/* ═══ Vue VAULT (Mes formats) — empty state ═══ */}
        <div style={{ display: view === 'vault' ? 'block' : 'none' }}>
          <StudioFreeVault />
        </div>

        {/* ═══ Vue ANALYSE — carotte free : 1 analyse gratuite ═══
            Mounted en permanence (display:none) pour que les analyses en
            cours continuent à poller même quand on switch d'onglet. */}
        <div style={{ display: view === 'analyse' ? 'block' : 'none' }}>
          <StudioAnalysisTab />
        </div>

        {/* ═══ Vue HOME (Pour toi) — profil + 3 cards + CTA ═══ */}
        {view === 'home' && (
        <>
        {/* Hero ultra-compact : @ + titre court */}
        {/* Bandeau avis discret (2e connexion + 5 min), tout en haut */}
        <AvisBanner sourcePage="studio_ready" />
        <div className="flex items-center gap-2.5 mb-3">
          <img src={merlinFound} alt="Merlin" className="w-9 h-9 object-contain shrink-0"
            style={{ filter: 'drop-shadow(0 2px 8px rgba(var(--merlin-accent-rgb, 254,130,4),0.25))' }} />
          <div className="min-w-0">
            <p className="text-[9px] uppercase tracking-wider font-bold text-ink-faint truncate">
              @{session.ig_username}
            </p>
            <h1 className="text-[15px] sm:text-xl font-bold text-ink leading-tight">
              Ton analyse <span className="text-orange">est prête</span>
            </h1>
          </div>
        </div>

        {/* ═══ SCORE /100 — le wow gratuit, l'analyse notée du compte.
            Insights compte dans la bulle (?) (demande Antonin 14/07 : plus
            de gros pavé, même pattern que la carte note de la home Pro) ═══ */}
        <div data-tour="score" className="relative">
          {persona.niche_fine && <InsightsBubble persona={persona} />}
          <AccountScore score={persona.score} username={session.ig_username} sessionId={session.id}
            hideCta={isCreator75Visitor()}
            onCta={() => { try { window.dispatchEvent(new CustomEvent('studio:open-analysis-paywall', { detail: { upgrade_to: 'pro_plus', code: 'free_no_analysis' } })) } catch {} }} />
        </div>

        {/* ═══ TES 3 FORMATS — miniatures EN CLAIR + métadonnées.
            Clic → analyse complète du format (FormatDetailModal). Connecté =
            en clair. La trame « à mettre » + scripts adaptés restent l'essai. ═══ */}
        {/* PONT NOTE → FORMATS (levier n°1, 2026-07-07) */}
        <button type="button"
          onClick={() => { try { studioEvents.tabClicked?.({ tab: 'score_to_formats', source_page: 'studio_ready' }) } catch {}; try { document.querySelector('[data-tour="free-formats"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' }) } catch {} }}
          className="mb-3 w-full flex items-center justify-between gap-2 px-4 py-3 rounded-xl transition-transform hover:scale-[1.005] active:scale-[0.99]"
          style={{ background: 'linear-gradient(90deg, rgba(var(--merlin-accent-rgb, 254,130,4),0.10), rgba(var(--merlin-accent-rgb, 254,130,4),0.04))', border: '1px solid rgba(var(--merlin-accent-rgb, 254,130,4),0.30)' }}>
          <span className="text-[13px] font-bold text-ink text-left">
            Fais monter ta note : <span className="text-orange">3 formats choisis pour ton compte</span> t'attendent en dessous
          </span>
          <span className="text-orange text-lg leading-none">↓</span>
        </button>
        <div className="mb-4" data-tour="free-formats">
          <div className="flex items-baseline justify-between gap-2 mb-1">
            <h3 className="text-[14px] font-bold text-ink leading-tight">{VERTICALES[getVertical()]?.teasers_title || <>🎬 Les 3 meilleurs formats à poster pour <span style={{ color: 'var(--merlin-accent, #fe8204)', textShadow: '0 0 14px rgba(254,130,4,0.45)' }}>TON compte</span></>}</h3>
          </div>
          <p className="text-[12px] text-ink-muted leading-snug mb-3">Sélectionnés pour ton compte. Clique sur un format pour voir son <strong className="text-ink">analyse complète</strong> (hook, script, structure, pourquoi ça marche).</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
            {(free.length ? free : (session.formats || [])).slice(0, 3).map((f, i) => (
              <FormatTeaserCard key={i} format={f} idx={i} trigger="ready_format_teaser" />
            ))}
          </div>
          {/* CTA essai : la TRAME 'à mettre' + les scripts adaptés à la voix */}
          <div className="mt-4 rounded-2xl p-4 sm:p-5 text-center" style={{ background: 'linear-gradient(135deg, rgba(var(--merlin-accent-rgb, 254,130,4),0.10), rgba(var(--merlin-violet-rgb, 124,58,237),0.10))', border: '1px solid rgba(var(--merlin-accent-rgb, 254,130,4),0.28)' }}>
            <p className="text-[13px] text-ink-muted mb-3.5 leading-snug">Tu vois <strong className="text-ink">quels formats</strong> tourner. {showsTrialUx() ? "L'essai" : 'Merlin'} te donne le <strong className="text-ink">comment</strong> : chaque format adapté à ton compte, l'accroche transposée à ta niche, étape par étape.</p>
            <button type="button" onClick={() => { try { studioEvents.unlockCtaClicked({ trigger: 'scripts_band' }) } catch {}; goToStudioCheckout(undefined, 'scripts_band') }}
              className="w-full sm:w-auto sm:px-9 py-3 rounded-xl text-white font-bold text-[14px]"
              style={{ background: 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))', boxShadow: '0 6px 18px rgba(var(--merlin-accent-rgb, 254,130,4),0.30)' }}>
              {showsTrialUx() ? 'Débloquer mes formats adaptés · essai 7 jours →' : 'Débloquer mes formats adaptés →'}
            </button>
            <p className="text-[10.5px] text-ink-faint mt-2.5">{showsTrialUx() ? '7 jours gratuits · annulation en 1 clic · rappel avant facturation' : 'Accès immédiat · satisfait ou remboursé 14 jours'}</p>
          </div>
          <FormatDetailModal />
        </div>

        </>
        )}
      </div>
      <LegalFooter tone="light" />

      {/* ═══ Push popups séquentiels : library puis analyse ═══ */}
      {showLibraryPopup && (
        <PushPopupBase
          icon="📚"
          accent="#fbbf24"
          title="Et si tu explorais la bibliothèque ?"
          description="Des milliers de reels viraux classés par niche, hook et durée. Tu trouves un format adapté à toi en 20 secondes."
          ctaLabel="Découvrir la bibliothèque"
          ctaSubtext={userPlan ? null : 'Aperçu gratuit, débloque tout en passant Créateur'}
          onCta={handleLibraryCta}
          onDismiss={() => dismissLibraryPopup(false)}
        />
      )}
      {showAnalysePopup && (
        <PushPopupBase
          icon="🎬"
          accent="#a855f7"
          title="Analyse ton dernier reel"
          description="Tu sauras exactement ce qui marche, ce qui freine, et quoi améliorer pour le prochain. Note sur 7 dimensions, plan d'actions concret."
          ctaLabel="Analyser un de mes reels"
          ctaSubtext={userPlan ? null : (showsTrialUx() ? 'Active ton essai 7 jours pour débloquer l\'analyse' : 'Débloque l\'analyse complète de ton compte')}
          onCta={handleAnalyseCta}
          onDismiss={() => dismissAnalysePopup(false)}
        />
      )}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// PLAN PICKER — 2 cards Créateur + Pro avec comparatif
// Utilisé dans StudioUpgradeModal ET StudioAnalysisPaywallModal
// ═══════════════════════════════════════════════════════════════
function PlanCard({ tier, isAnnual, currentPlan, busy, onChoose, showTrial = true, deal75 = false, loyalty = false }) {
  const isPro = tier === 'pro_plus'
  const planKey = isPro ? 'studio_pro_plus' : 'studio_creator'
  // Essai 7 jours sur les DEUX tiers (l'analyse compte = l'argument de vente,
  // doit être testable).
  const hasTrial = showTrial

  // loyalty (offre merci) : Pro+ AU PRIX CRÉATEUR à vie. Annuel -40% vs mensuel
  // recalculé (39x12=468) → 280,80€/an (23,40€/mois).
  const monthlyPrice = isPro ? (loyalty ? 39 : 89) : 39
  // deal75 (lien test creator75) : annuel Créateur -75% = 117€/an (9,75€/mois).
  const annualPerMonth = isPro ? (loyalty ? 23.40 : 69) : (deal75 ? 9.75 : 29)
  const annualTotal = isPro ? (loyalty ? 280.80 : 828) : (deal75 ? 117 : 348)
  const displayPrice = isAnnual ? annualPerMonth : monthlyPrice
  const annualSavings = (monthlyPrice * 12) - annualTotal
  const fmt = (n) => (Number.isInteger(n) ? String(n) : n.toFixed(2).replace('.', ','))

  // Liste des features clés (langage CM2, mobile-friendly)
  const features = isPro ? [
    'Tout ce qu\'inclut Créateur',
    '✨ Analyse complète de ton compte',
    '✨ Ta bio décodée + suggestions',
    '✨ 3 créateurs de ta niche à étudier',
    '✨ Analyses vidéo illimitées',
    '✨ Plan d\'actions personnalisé',
  ] : [
    '🔥 Le format viral du jour de ta niche, chaque matin',
    'Le top 1% des formats viraux, adapté à ta voix',
    'Le pas-à-pas pour le refaire (écriture + montage)',
    'Bibliothèque filtrable + sauvegardes illimitées',
    '1 analyse vidéo par semaine',
  ]

  const ctaLabel = hasTrial
    ? 'Démarrer mes 7 jours offerts'
    : ((deal75 || loyalty) && isAnnual ? `Débloquer · ${fmt(annualTotal)}€/an`
      : loyalty ? `Débloquer · ${fmt(displayPrice)}€/mois`
      : `Démarrer · ${fmt(displayPrice)}€/mois`)

  return (
    <div className="rounded-2xl p-4 sm:p-5 relative"
      style={{
        background: isPro ? 'linear-gradient(135deg, var(--fmt-ph-radial-2, #1a1040) 0%, var(--fmt-ph-radial-1, #2e1065) 100%)' : 'white',
        border: isPro ? '2px solid rgba(var(--merlin-accent-rgb, 254,130,4),0.40)' : '1.5px solid #f1e9dc',
        boxShadow: isPro ? '0 12px 32px rgba(var(--merlin-accent-rgb, 254,130,4),0.18)' : '0 4px 12px rgba(15,16,64,0.04)',
      }}>
      {isPro && (
        <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full text-[9px] uppercase tracking-wider font-bold text-white whitespace-nowrap"
          style={{ background: 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))', boxShadow: '0 4px 14px rgba(var(--merlin-accent-rgb, 254,130,4),0.45)' }}>
          ✨ Le plus complet
        </div>
      )}

      {/* Plan name */}
      <p className={`text-[10px] uppercase tracking-wider font-bold mb-1 ${isPro ? 'text-orange-300' : 'text-ink-muted'}`}>
        Studio
      </p>
      <div className="flex items-center gap-2 mb-2">
        <h3 className={`text-lg font-bold leading-tight ${isPro ? 'text-white' : 'text-ink'}`}>
          {isPro ? 'Pro' : 'Créateur'}
        </h3>
        {hasTrial && (
          <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full"
            style={{ background: '#16a34a', color: '#ffffff', boxShadow: '0 2px 8px rgba(22,163,74,0.35)' }}>
            7 jours offerts
          </span>
        )}
      </div>

      {/* Prix : "0 € aujourd'hui" + UNE ligne de prix réel. Refonte
          benchmark paywalls 2026-06-10 : on retire "économise 120 €"
          (3e donnée chiffrée dans une ligne de 11px, et 9 abonnements
          sur 10 se vendent plein tarif). */}
      {hasTrial ? (
        <>
          <div className="flex items-baseline gap-1.5 mb-0.5">
            <span className="text-3xl font-bold" style={{ color: 'var(--merlin-accent, #fe8204)', textShadow: '0 0 18px rgba(var(--merlin-accent-rgb, 254,130,4),0.30)' }}>
              0 €
            </span>
            <span className="text-sm font-bold" style={{ color: 'var(--merlin-accent, #fe8204)' }}>aujourd'hui</span>
          </div>
          <p className={`text-[11px] mb-4 leading-snug ${isPro ? 'text-white/55' : 'text-ink-muted'}`}>
            puis <span className="font-bold">{displayPrice} €/mois</span>{isAnnual ? ` (facturé ${annualTotal} €/an)` : ' · sans engagement'}
          </p>
        </>
      ) : (
        <>
          {((deal75 || loyalty) && isAnnual) && (
            <div className="flex items-center gap-2 mb-1">
              <s className={`text-[13px] ${isPro ? 'text-white/50' : 'text-ink-faint'}`}>468 €/an</s>
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full text-white" style={{ background: '#16a34a' }}>{deal75 ? '-75%' : '-40%'}</span>
            </div>
          )}
          <div className="flex items-baseline gap-1.5 mb-0.5">
            <span className={`text-3xl font-bold ${isPro ? 'text-white' : 'text-ink'}`}>{fmt(displayPrice)}</span>
            <span className={`text-sm ${isPro ? 'text-white/55' : 'text-ink-muted'}`}>€/mois</span>
          </div>
          <p className={`text-[11px] mb-4 leading-snug ${isPro ? 'text-white/55' : 'text-ink-muted'}`}>
            {isAnnual ? <>Facturé annuellement ({fmt(annualTotal)} €/an)</> : <>Sans engagement · annule à tout moment</>}
          </p>
        </>
      )}

      {/* Features */}
      <ul className="space-y-1.5 mb-4">
        {features.map((f, i) => (
          <li key={i} className={`flex items-start gap-1.5 text-xs leading-snug ${isPro ? 'text-white/85' : 'text-ink'}`}>
            <span className={`shrink-0 mt-0.5 ${isPro ? 'text-orange-400' : 'text-green-600'}`}>{f.startsWith('✨') ? '' : '✓'}</span>
            <span>{f.startsWith('✨') ? f : f}</span>
          </li>
        ))}
      </ul>

      {/* Réassurance ENCADRANT le CTA (pattern Superwall/Blinkist) :
          la peur n°1 ("facturé parce que j'ai oublié") est désamorcée au
          moment exact du doute, avec la date réelle (DGCCRF : prix total
          + date avant engagement). Le rappel J-3 existe (trial_will_end). */}
      {hasTrial ? (
        <p className={`text-[11px] font-bold text-center mb-1.5 ${isPro ? 'text-white/70' : 'text-ink'}`}>
          Aucun paiement avant le {new Date(Date.now() + TRIAL_DAYS * 24 * 3600 * 1000).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })}
        </p>
      ) : (
        <p className={`text-[11px] font-bold text-center mb-1.5 ${isPro ? 'text-white/70' : 'text-ink'}`}>
          Accès immédiat · satisfait ou remboursé 14 jours
        </p>
      )}
      <button onClick={() => onChoose(planKey)} disabled={busy}
        className="w-full py-3 rounded-xl font-bold text-sm text-white transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-60 flex items-center justify-center gap-1.5"
        style={{
          background: 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))',
          boxShadow: '0 8px 24px rgba(var(--merlin-accent-rgb, 254,130,4),0.40)',
        }}>
        {busy ? '…' : <><span>{ctaLabel}</span><span>→</span></>}
      </button>
      {hasTrial ? (
        <p className={`text-[10px] text-center mt-2 leading-snug ${isPro ? 'text-white/45' : 'text-ink-faint'}`}>
          Rappel par email 3 jours avant · Annule en 1 clic depuis ton compte
        </p>
      ) : isPro ? (
        <p className={`text-[10px] text-center mt-2 text-white/45`}>
          Prélèvement immédiat. Annule à tout moment.
        </p>
      ) : null}
    </div>
  )
}

function PeriodToggle({ isAnnual, onChange, savingsPct }) {
  return (
    <div className="flex gap-0.5 bg-ink/5 rounded-lg p-0.5">
      <button onClick={() => onChange('annual')}
        className={`flex-1 text-xs font-bold py-2 rounded-md transition-colors flex items-center justify-center gap-1.5 ${isAnnual ? 'bg-white shadow-sm text-ink' : 'text-ink-muted'}`}>
        Annuel
        <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-orange/15 text-orange">-{savingsPct}%</span>
      </button>
      <button onClick={() => onChange('monthly')}
        className={`flex-1 text-xs font-bold py-2 rounded-md transition-colors ${!isAnnual ? 'bg-white shadow-sm text-ink' : 'text-ink-muted'}`}>
        Mensuel
      </button>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// STUDIO UPGRADE MODAL — plan picker Créateur + Pro
// Listens for `studio:open-upgrade` custom event
// ═══════════════════════════════════════════════════════════════
// Visiteur « campagne deal » : arrivé via ?deal=1 (lien de pub), persisté en
// localStorage pour survivre aux redirections (scan → compte → paywall).
// ?deal=0 nettoie. Partagé par TOUS les paywalls : pour ce visiteur, l'offre
// unique 214€/an remplace Créateur ET Pro partout (cohérence du test CAC).
function isDealVisitor() {
  try {
    const p = new URLSearchParams(window.location.search).get('deal')
    if (p === '1') { localStorage.setItem('studio_deal_offer', '1'); return true }
    if (p === '0') { localStorage.removeItem('studio_deal_offer'); return false }
    return localStorage.getItem('studio_deal_offer') === '1'
  } catch { return false }
}

// Visiteur OFFRE MEMBRE -45% (?promo=membre) : réservée aux utilisateurs
// déjà scannés, activée par le lien du mail. Même mécanique de persistance
// que le deal. ?promo=0 nettoie.
function isMemberVisitor() {
  try {
    const p = new URLSearchParams(window.location.search).get('promo')
    if (p === 'membre' || p === 'member') { localStorage.setItem('studio_member_offer', '1'); return true }
    if (p === '0') { localStorage.removeItem('studio_member_offer'); return false }
    return localStorage.getItem('studio_member_offer') === '1'
  } catch { return false }
}

// Corps du paywall pour la variante « annuel -80% sans essai » (A/B).
// Une seule offre, prélèvement direct, pas de toggle ni d'essai.
function DealCardBody({ busy, onChoose }) {
  return (
    <div className="rounded-2xl border p-5 text-center" style={{ borderColor: '#fdba74', background: 'linear-gradient(180deg,#fff7ed,#ffffff)' }}>
      <div className="inline-block px-3 py-1 rounded-full text-[11px] font-extrabold mb-3" style={{ background: '#fe8204', color: '#fff', letterSpacing: '.02em' }}>-80% · offre de lancement</div>
      <div className="flex items-end justify-center gap-2">
        <span className="text-[15px] line-through text-ink-faint">89&nbsp;€/mois</span>
        <span className="text-[44px] font-extrabold leading-none text-ink">18&nbsp;€</span>
        <span className="text-[13px] text-ink-muted mb-1">/ mois</span>
      </div>
      <div className="inline-block px-3 py-1.5 rounded-full text-[12.5px] font-bold mt-2" style={{ background: 'rgba(124,58,237,0.10)', color: '#6d28d9', border: '1px solid rgba(124,58,237,0.25)' }}>
        Moins de 0,60&nbsp;€ par jour
      </div>
      <p className="text-[12.5px] text-ink-muted mt-2">Payé une seule fois : <b className="text-ink">214&nbsp;€ pour 12 mois</b>.</p>
      <ul className="text-left mt-4 space-y-2 max-w-xs mx-auto">
        {['Ta note détaillée, et le plan pour la faire monter',
          'Tes formats viraux prêts à filmer, accroche et trame incluses',
          'Un nouveau format taillé pour ton compte chaque jour',
          'Tes vidéos analysées en illimité pour comprendre ce qui marche'].map((t, i) => (
          <li key={i} className="flex items-start gap-2 text-[13.5px] text-ink"><span style={{ color: '#16a34a' }}>✓</span><span>{t}</span></li>
        ))}
      </ul>
      <button type="button" disabled={busy} onClick={onChoose}
        className="mt-5 w-full py-3.5 rounded-xl text-white font-bold text-[15px]"
        style={{ background: 'linear-gradient(135deg,#fe8204,#f59e0b)', boxShadow: '0 8px 20px rgba(254,130,4,.35)', opacity: busy ? 0.7 : 1 }}>
        {busy ? 'Redirection…' : 'Débloquer mon accès'}
      </button>
      <p className="text-[11.5px] text-ink-faint mt-2.5">Accès immédiat. Satisfait ou remboursé 14 jours.</p>
    </div>
  )
}

// Corps du paywall OFFRE MEMBRE -45% à vie : même squelette que le deal
// validé (promo au-dessus, prix barré, tarif quotidien en pastille, ce que
// ça débloque, réassurance), avec le choix mensuel/annuel.
function MemberCardBody({ busy, onChoose }) {
  const [annual, setAnnual] = useState(true)
  return (
    <div className="rounded-2xl border p-5 text-center" style={{ borderColor: '#fdba74', background: 'linear-gradient(180deg,#fff7ed,#ffffff)' }}>
      <div className="inline-block px-3 py-1 rounded-full text-[11px] font-extrabold mb-3" style={{ background: '#fe8204', color: '#fff', letterSpacing: '.02em' }}>-45% à vie · offre membre</div>
      <div className="flex justify-center gap-1.5 mb-3">
        <button type="button" onClick={() => setAnnual(false)}
          className="px-3.5 py-1.5 rounded-full text-[12.5px] font-bold"
          style={annual ? { background: 'rgba(26,16,64,0.06)', color: '#6b6685' } : { background: '#1a1040', color: '#fff' }}>
          Mensuel · 49,90 €
        </button>
        <button type="button" onClick={() => setAnnual(true)}
          className="px-3.5 py-1.5 rounded-full text-[12.5px] font-bold"
          style={annual ? { background: '#1a1040', color: '#fff' } : { background: 'rgba(26,16,64,0.06)', color: '#6b6685' }}>
          Annuel · 29,90 €/mois
        </button>
      </div>
      <div className="flex items-end justify-center gap-2">
        <span className="text-[15px] line-through text-ink-faint">89&nbsp;€/mois</span>
        <span className="text-[44px] font-extrabold leading-none text-ink">{annual ? '29,90\u00a0€' : '49,90\u00a0€'}</span>
        <span className="text-[13px] text-ink-muted mb-1">/ mois</span>
      </div>
      <div className="inline-block px-3 py-1.5 rounded-full text-[12.5px] font-bold mt-2" style={{ background: 'rgba(124,58,237,0.10)', color: '#6d28d9', border: '1px solid rgba(124,58,237,0.25)' }}>
        {annual ? 'Moins de 1\u00a0€ par jour' : 'Moins de 1,70\u00a0€ par jour'}
      </div>
      <p className="text-[12.5px] text-ink-muted mt-2">
        {annual ? <>Facturé <b className="text-ink">358,80&nbsp;€ par an</b>.</> : <>Sans engagement, <b className="text-ink">49,90&nbsp;€ par mois</b>.</>}
      </p>
      <ul className="text-left mt-4 space-y-2 max-w-xs mx-auto">
        {['Ta note détaillée, et le plan pour la faire monter',
          'Tes formats viraux prêts à filmer, accroche et trame incluses',
          'Un nouveau format taillé pour ton compte chaque jour',
          'Tes vidéos analysées en illimité pour comprendre ce qui marche'].map((t, i) => (
          <li key={i} className="flex items-start gap-2 text-[13.5px] text-ink"><span style={{ color: '#16a34a' }}>✓</span><span>{t}</span></li>
        ))}
      </ul>
      <p className="text-[12.5px] text-ink mt-3 font-semibold">Tarif figé à vie : il ne bougera jamais tant que tu restes abonné.</p>
      <p className="text-[12px] text-ink-muted mt-0.5">Offre exceptionnelle réservée aux membres Merlin.</p>
      <button type="button" disabled={busy} onClick={() => onChoose(annual ? 'annual_member' : 'monthly_member')}
        className="mt-4 w-full py-3.5 rounded-xl text-white font-bold text-[15px]"
        style={{ background: 'linear-gradient(135deg,#fe8204,#f59e0b)', boxShadow: '0 8px 20px rgba(254,130,4,.35)', opacity: busy ? 0.7 : 1 }}>
        {busy ? 'Redirection…' : 'Débloquer mon accès'}
      </button>
      <p className="text-[11.5px] text-ink-faint mt-2.5">Accès immédiat. Satisfait ou remboursé 14 jours.</p>
    </div>
  )
}

export function StudioUpgradeModal() {
  // Variantes du paywall (agent retention-ux) : init + re-render à l'arrivée.
  const [, forceUiExp] = useState(0)
  useEffect(() => { initUiExperiments(); return subscribeUiExperiments(() => forceUiExp(x => x + 1)) }, [])

  const auth = useAuth()
  const studioPlan = auth?.studioPlan
  const [open, setOpen] = useState(false)
  // PRÉSÉLECTION ANNUEL 2026-06-09 : on affiche le prix en équivalent
  // mensuel (29€/mois) + subtext "facturé annuellement" → l'ancrage visuel
  // est bas (29€ vs 39€) ET on capture 12 mois d'engagement d'un coup.
  // Classique SaaS, augmente LTV par 2-3x vs mensuel.
  // creator75 : le deal EST l'annuel -75% → on ouvre sur l'annuel dès le départ
  // (fiable, pas de course d'état vs un setPeriod post-montage).
  const [period, setPeriod] = useState(() => { try { return (isCreator75Visitor() || isMerciVisitor()) ? 'annual' : 'monthly' } catch { return 'monthly' } })
  const [busy, setBusy] = useState(false)
  const [betaInfo, setBetaInfo] = useState(null)
  // Decoy honnête : le plan Pro est accessible via une ligne cliquable sous
  // la card (l'ancien bloc pointillé non cliquable ressemblait à une card
  // morte = friction). showPro affiche la vraie card Pro.
  const [showPro, setShowPro] = useState(false)
  // Engagement carte (réciprocité + commitment, 26/07) : case obligatoire avant
  // checkout. Vise le hedger « carte virtuelle sans fonds » (75% d'échec carte
  // Pro+) — pression psycho, pas une vérif technique (cartes virtuelles indiscernables).
  const [committed, setCommitted] = useState(false)
  const [commitShake, setCommitShake] = useState(false)

  // Décompte 24h de l'offre merci (badge orange), FIXE et commun à tous :
  // fenêtre 24h qui commence le 14/08 18:00 (Paris) → fin le 15/08 18:00 (Paris).
  const [merciLeft, setMerciLeft] = useState('')
  useEffect(() => {
    if (!open || !isMerciVisitor()) return
    const deadline = Date.parse('2026-08-15T18:00:00+02:00')
    const tick = () => {
      const ms = Math.min(deadline - Date.now(), 24 * 3600 * 1000) // jamais > 24h à l'affichage
      if (ms <= 0) { setMerciLeft('00:00:00'); return }
      const h = Math.floor(ms / 3600000), m = Math.floor((ms % 3600000) / 60000), s = Math.floor((ms % 60000) / 1000)
      setMerciLeft(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`)
    }
    tick()
    const iv = setInterval(tick, 1000)
    return () => clearInterval(iv)
  }, [open])

  useEffect(() => {
    const handler = (e) => {
      setOpen(true)
      setShowPro(false)
      setCommitted(false)
      // creator75 / merci : l'offre est l'annuel, on ouvre sur l'annuel.
      try { if (isCreator75Visitor() || isMerciVisitor()) setPeriod('annual') } catch { /* noop */ }
      studioEvents.upgradeModalShown({ trigger: e?.detail?.trigger || 'unknown', modal: 'upgrade' })
    }
    window.addEventListener('studio:open-upgrade', handler)
    return () => window.removeEventListener('studio:open-upgrade', handler)
  }, [])

  // creator75 : auto-ouvre le deal 117€ pour un membre CONNECTÉ free arrivé du
  // lien (post-signup) — sinon il voit le home membre et croit que c'est débloqué
  // sans payer. Géré DANS le modal (setOpen direct) → pas de course au montage.
  // Une fois par session (guard sessionStorage).
  useEffect(() => {
    try {
      if (!auth?.user) return
      if (!isCreator75Visitor() && !isMerciVisitor()) return
      if (studioPlan && studioPlan !== 'free') return
      // Garde-fou PROPRE À CHAQUE OFFRE : sinon un visiteur creator75 puis merci
      // (même onglet) verrait le 2e popup supprimé par le drapeau du 1er.
      const guardKey = isMerciVisitor() ? 'studio_merci_popup_shown' : 'studio_creator75_popup_shown'
      if (sessionStorage.getItem(guardKey) === '1') return
      const t = setTimeout(() => {
        if (sessionStorage.getItem(guardKey) === '1') return
        sessionStorage.setItem(guardKey, '1')
        setPeriod('annual')
        setShowPro(false)
        setOpen(true)
        try { studioEvents.upgradeModalShown({ trigger: isMerciVisitor() ? 'merci_postsignup' : 'creator75_postsignup', modal: 'upgrade' }) } catch { /* noop */ }
      }, 600)
      return () => clearTimeout(t)
    } catch { /* noop */ }
  }, [auth?.user, studioPlan])

  // Deep-link des mails de récupération (?upgrade=1) : le clic atterrit
  // DIRECTEMENT sur le popup de paiement au lieu de la home (l'analyse du
  // 09/07 montrait 86% d'ouverture, des clics, mais 0 récupération : trop
  // d'étapes entre le clic et la carte). Param consommé pour ne pas rouvrir.
  useEffect(() => {
    try {
      const p = new URLSearchParams(window.location.search)
      if (p.get('upgrade') !== '1' || studioPlan) return
      const t = setTimeout(() => {
        setOpen(true)
        setShowPro(false)
        try { if (isCreator75Visitor()) setPeriod('annual') } catch { /* noop */ }
        studioEvents.upgradeModalShown({ trigger: 'email_deeplink', modal: 'upgrade' })
        p.delete('upgrade')
        const q = p.toString()
        window.history.replaceState({}, '', window.location.pathname + (q ? '?' + q : '') + window.location.hash)
      }, 1200)
      return () => clearTimeout(t)
    } catch { /* URL non parsable : pas de deep-link */ }
  }, [studioPlan])

  function handleClose() {
    if (busy) return
    studioEvents.upgradeModalClosed({ modal: 'upgrade' })
    setOpen(false)
  }

  function handlePeriodChange(newPeriod) {
    if (newPeriod !== period) {
      studioEvents.upgradeModalPeriodSwitched({ from: period, to: newPeriod, modal: 'upgrade' })
    }
    setPeriod(newPeriod)
  }

  useEffect(() => {
    if (!open || betaInfo) return
    let cancelled = false
    ;(async () => {
      try {
        const res = await fetch(`${API_URL}/api/studio/beta-spots`)
        const data = await res.json()
        if (!cancelled) setBetaInfo({ taken: data?.taken || 0, total: data?.total || 100 })
      } catch {
        if (!cancelled) setBetaInfo({ taken: 0, total: 100 })
      }
    })()
    return () => { cancelled = true }
  }, [open, betaInfo])

  async function choose(planKey) {
    if (busy) return
    const arm = getTrialArm()
    // Gate engagement (carte virtuelle sans fonds) : n'a de sens qu'avec
    // l'essai gratuit. En paiement direct (B/C, creator75), le débit réel EST le filtre.
    if (arm === 'A' && !committed && !isCreator75Visitor() && !isMerciVisitor()) {
      setCommitShake(true); setTimeout(() => setCommitShake(false), 500)
      try { document.getElementById('commit-gate')?.scrollIntoView({ behavior: 'smooth', block: 'center' }) } catch {}
      try { studioEvents.commitGateBlocked?.({ plan: planKey }) } catch {}
      return
    }
    setBusy(true)
    studioEvents.upgradeModalPlanChosen({ plan: planKey, period, modal: 'upgrade', committed: true, offer_arm: arm })
    await triggerStudioCheckout(period, planKey, arm !== 'A')
    setTimeout(() => setBusy(false), 3000)
  }

  // Offre annuel -80% SANS ESSAI (A/B paywall_annual_deal) : prélèvement direct.
  async function chooseDeal() {
    if (busy) return
    setBusy(true)
    try { studioEvents.upgradeModalPlanChosen({ plan: 'studio_pro_plus', period: 'annual_deal', modal: 'upgrade_deal', committed: true }) } catch {}
    await triggerStudioCheckout('annual_deal', 'studio_pro_plus', true)
    setTimeout(() => setBusy(false), 3000)
  }

  // Offre membre -45% figée à vie : prélèvement direct, période au choix.
  async function chooseMember(memberPeriod) {
    if (busy) return
    setBusy(true)
    try { studioEvents.upgradeModalPlanChosen({ plan: 'studio_pro_plus', period: memberPeriod, modal: 'upgrade_member', committed: true }) } catch {}
    await triggerStudioCheckout(memberPeriod, 'studio_pro_plus', true)
    setTimeout(() => setBusy(false), 3000)
  }

  if (!open) return null
  const remaining = betaInfo ? Math.max(0, (betaInfo.total - betaInfo.taken)) : null
  const isAnnual = period === 'annual'
  const isAlreadyCreator = studioPlan === 'pro'
  // Variante deal — activation par LIEN DE CAMPAGNE (isDealVisitor, persisté)
  // + getVariantKey pour un futur A/B organique (expérience non activée).
  // Lien test creator75 : offre Créateur annuel -75% sans essai. Prioritaire.
  // Offre « merci » (compensation bugs) : Pro+ AU PRIX CRÉATEUR à vie. Prioritaire sur tout.
  const showMerci = !isAlreadyCreator && isMerciVisitor()
  const showCreator75 = !isAlreadyCreator && !showMerci && isCreator75Visitor()
  const showDeal = !isAlreadyCreator && !showMerci && !showCreator75 && (isDealVisitor() || getVariantKey('paywall_annual_deal') === 'B')
  // Offre membre (lien mail ?promo=membre) : jamais par-dessus le deal.
  const showMember = !isAlreadyCreator && !showMerci && !showCreator75 && !showDeal && isMemberVisitor()

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={() => !busy && setOpen(false)}
      style={{ background: 'rgba(15,10,26,0.7)', backdropFilter: 'blur(6px)' }}>
      <div className="w-full max-w-2xl bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[95vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="relative px-5 pt-6 pb-5 text-white sticky top-0 z-10"
          style={{ background: 'linear-gradient(135deg, var(--merlin-violet, #7c3aed) 0%, var(--fmt-ph-1, #1e1145) 100%)' }}>
          <button onClick={handleClose}
            className="absolute top-2 right-2 w-11 h-11 rounded-full flex items-center justify-center text-white/85 hover:text-white text-xl"
            style={{ background: 'rgba(255,255,255,0.10)' }}
            aria-label="Fermer">
            ×
          </button>
          {/* Badge fusionné : béta + tarif à vie (l'ex-badge trust remonte
              ici, ça libère la grille de 4 badges supprimée plus bas) */}
          <div className="inline-flex items-center gap-1.5 mb-3 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider"
            style={{ background: 'rgba(var(--merlin-accent-rgb, 254,130,4),0.20)', color: 'var(--merlin-accent-soft, #fdba74)', border: '1px solid rgba(var(--merlin-accent-rgb, 254,130,4),0.40)' }}>
            {showMerci ? `⏳ Plus que ${merciLeft || '24:00:00'}` : showCreator75 ? '🔥 Offre -75% · sans essai' : showDeal ? '🔥 Offre de lancement' : showMember ? '🎁 Offre membre · -45% à vie' : '🎁 Offre Béta · Tarif bloqué à vie'}
            {!showDeal && !showMerci && remaining !== null && remaining > 0 && remaining < 30 && (
              <span className="ml-1">· {remaining}/{betaInfo.total} places</span>
            )}
          </div>
          <h2 className="text-xl sm:text-2xl font-bold leading-tight mb-1.5">
            {isAlreadyCreator ? 'Passe en Pro' : showMerci ? 'Le Pro+ au prix du Créateur, à vie' : showCreator75 ? 'Un an de Studio Créateur, à -75%' : showDeal ? 'Un an de Studio Pro+' : showMember ? 'Studio Pro+ au tarif membre' : getSlot('paywall_title', 'Débloque des milliers de formats viraux adaptés à TON COMPTE')}
          </h2>
          {/* 1 phrase de valeur, 0 check : la liste unique vit dans la card
              (refonte 2026-06-10, benchmark paywalls : 19 éléments → 9,
              12 checks → 4, pattern Blinkist/Duolingo/RevenueCat) */}
          <p className="text-sm text-white/85 leading-snug">
            {isAlreadyCreator
              ? 'Débloque l\'analyse complète de ton compte et les analyses vidéo illimitées.'
              : showMerci ? 'Merci de rester malgré les bugs. Le plan le plus complet, au prix du Créateur, bloqué à vie. Offre valable 24h.'
              : showCreator75 ? 'Tout débloqué, tout de suite. Sans essai, satisfait ou remboursé 14 jours.'
              : showDeal ? 'Tout débloqué, tout de suite. Un seul paiement pour 12 mois.'
              : showMember ? 'Ton tarif est figé à vie, tant que tu restes abonné.'
              : getSlot('paywall_sub', 'Hook, structure, plans à filmer, tout pour qu\'il fonctionne pour TOI.')}
          </p>
        </div>

        {/* Toggle période (conservé sur demande produit : le choix mensuel
            doit rester visible, pas relégué en lien) + plan card focus */}
        <div className="px-5 pt-5 pb-6 max-w-md mx-auto">
          {showDeal ? (
            <DealCardBody busy={busy} onChoose={chooseDeal} />
          ) : showMember ? (
            <MemberCardBody busy={busy} onChoose={chooseMember} />
          ) : showMerci ? (<>
          <div className="mb-3">
            <PeriodToggle isAnnual={isAnnual} onChange={handlePeriodChange} savingsPct={40} />
          </div>
          <PlanCard tier="pro_plus" isAnnual={isAnnual} currentPlan={studioPlan} busy={busy} onChoose={choose} showTrial={false} loyalty />
          </>) : showCreator75 ? (<>
          <div className="mb-3">
            <PeriodToggle isAnnual={isAnnual} onChange={handlePeriodChange} savingsPct={75} />
          </div>
          <PlanCard tier="creator" isAnnual={isAnnual} currentPlan={studioPlan} busy={busy} onChoose={choose} showTrial={false} deal75 />
          </>) : (<>
          <div className="mb-3">
            <PeriodToggle isAnnual={isAnnual} onChange={handlePeriodChange} savingsPct={isAlreadyCreator ? 22 : 26} />
          </div>
          {(isAlreadyCreator || showPro) ? (
            <PlanCard tier="pro_plus" isAnnual={isAnnual} currentPlan={studioPlan} busy={busy} onChoose={choose} showTrial={getTrialArm() === 'A'} />
          ) : (
            <PlanCard tier="creator" isAnnual={isAnnual} currentPlan={studioPlan} busy={busy} onChoose={choose} showTrial={getTrialArm() === 'A'} />
          )}

          {/* Réciprocité + engagement carte (26/07) — gate le checkout.
              Bras B/C (paiement direct) : pas d'essai donc pas de gate. */}
          {getTrialArm() === 'A' && (<div id="commit-gate" className="mt-3.5 rounded-xl p-3.5 border" style={{ borderColor: commitShake ? '#dc2626' : '#eadfce', background: '#faf8f4', transition: 'border-color .15s' }}>
            <p className="text-[12.5px] leading-snug text-ink">
              <span className="font-bold">On te laisse tester 100% du produit, gratuitement.</span> Ça nous coûte cher : les analyses, les formats, c'est l'IA qui tourne juste pour ton compte. Mais on le fait parce qu'on est sûrs de ce que ça peut t'apporter.
            </p>
            <label className="flex items-start gap-2.5 mt-2.5 cursor-pointer select-none">
              <input type="checkbox" checked={committed}
                onChange={e => { setCommitted(e.target.checked); if (e.target.checked) { try { studioEvents.commitGateChecked?.() } catch {} } }}
                className="mt-0.5 w-4 h-4 shrink-0" style={{ accentColor: 'var(--merlin-accent, #fe8204)' }} />
              <span className={`text-[12.5px] font-semibold ${commitShake ? 'text-red-600' : 'text-ink'}`}>
                Je joue le jeu : je n'utilise pas de carte virtuelle sans fonds.
              </span>
            </label>
          </div>)}

          {/* Decoy Pro cliquable */}
          {!isAlreadyCreator && (
            <div className="mt-3 text-center">
              <button onClick={() => setShowPro(p => !p)}
                className="text-[11px] text-ink-faint hover:text-ink underline underline-offset-2 block w-full">
                {showPro
                  ? '← Revenir à Studio Créateur'
                  : `Studio Pro (analyses illimitées) : ${isAnnual ? '69' : '89'} €/mois →`}
              </button>
            </div>
          )}
          </>)}
        </div>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// STUDIO ANALYSIS PAYWALL MODAL — plan picker contextualisé
// Listens 'studio:open-analysis-paywall' avec detail = { upgrade_to, code }
//   upgrade_to: 'pro'      → résilie ton essai pour débloquer
//   upgrade_to: 'pro_plus' → quota Créateur atteint, passer Pro
// ═══════════════════════════════════════════════════════════════
// ═══ TIMELINE D'ESSAI (paywall v8, étude 24/07) — la peur n°1 devant un
// essai est d'OUBLIER D'ANNULER, pas le prix (A/B Blinkist : timeline +
// garantie de rappel = +23% d'essais, -55% plaintes). On MONTRE le rappel,
// et il est tenu par l'infra réelle : les mails J-3 existent déjà.
function TrialTimeline({ compact = false }) {
  const steps = [
    ['🔓', 'Aujourd\'hui', 'Ton accès'],
    ['✉️', `J+${TRIAL_DAYS - 3}`, 'Rappel pour la fin de l\'essai'],
    ['💳', `J+${TRIAL_DAYS}`, 'Ton accès final'],
  ]
  return (
    <div className={compact ? 'mb-3' : 'mt-1'}>
      <div className="relative flex justify-between">
        <div className="absolute left-[16%] right-[16%] top-[14px] h-[2px]" style={{ background: 'rgba(var(--merlin-violet-rgb, 124,58,237),0.20)' }} />
        {steps.map(([icon, when, what]) => (
          <div key={when} className="relative flex-1 text-center px-1">
            <div className="w-7 h-7 mx-auto rounded-full flex items-center justify-center text-[13px] bg-white" style={{ border: '2px solid rgba(var(--merlin-violet-rgb, 124,58,237),0.35)' }}>{icon}</div>
            <p className="text-[10.5px] font-extrabold text-ink mt-1">{when}</p>
            <p className="text-[10px] text-ink-muted leading-tight">{what}</p>
          </div>
        ))}
      </div>
    </div>
  )
}

// Réassurance SOUS le bouton (v8) : rappel avant prélèvement, résiliation
// 3 clics, date réelle du premier débit. past = pas d'essai, pas de date.
function PaywallReassurance({ past = false }) {
  const end = new Date(Date.now() + TRIAL_DAYS * 86400e3)
  const endStr = end.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })
  return (
    <div className="text-center mt-2.5">
      <p className="text-[10.5px] text-ink-muted leading-snug">
        {past
          ? 'Résiliation en 3 clics · Ton historique et tes analyses sont toujours là'
          : `Rappel par mail avant le prélèvement · Résiliation en 3 clics · Aucun débit avant le ${endStr}`}
      </p>
      <p className="text-[9.5px] text-ink-faint mt-1">Paiement sécurisé Stripe</p>
    </div>
  )
}

export function StudioAnalysisPaywallModal() {
  const auth = useAuth()
  const studioPlan = auth?.studioPlan
  const [open, setOpen] = useState(false)
  const [upgradeTo, setUpgradeTo] = useState('pro_plus')
  const [period, setPeriod] = useState('monthly')
  const [busy, setBusy] = useState(false)
  const [code, setCode] = useState(null)
  const [vaKind, setVaKind] = useState(null) // fiche-cadeau : 'free' | 'past' (past = réactivation sans essai)
  const [vaHandle, setVaHandle] = useState(null) // fiche-cadeau : @handle au-dessus du titre (maquette v8)
  // DOWNSELL CRÉATEUR 39€ (Antonin 25/07) : sur le paywall Pro+ analyse, un
  // bouton blanc « C'est trop pour moi » (contraste avec l'orange pour éviter
  // les clics par défaut) ouvre l'offre Créateur. Mesuré : 85% bouncent sur
  // le mur Pro+, seulement 1,8% trouvent le 39€ ailleurs → filet volontaire.
  const [showDownsell, setShowDownsell] = useState(false)
  const [bypassTrialCreator, setBypassTrialCreator] = useState(true) // par défaut bypass car le user veut débloquer maintenant

  // Expériences UI (A/B) : le modal peut s'ouvrir avant l'init de la page →
  // on s'assure qu'elles sont chargées et on re-render à leur arrivée pour
  // que getVariantKey('paywall_downsell') soit fiable dans tous les parcours.
  const [, forceExp] = useState(0)
  useEffect(() => {
    try { initUiExperiments() } catch { /* noop */ }
    let unsub = () => {}
    try { unsub = subscribeUiExperiments(() => forceExp(x => x + 1)) } catch { /* noop */ }
    return unsub
  }, [])

  useEffect(() => {
    function handler(e) {
      const detail = e.detail || {}
      setUpgradeTo(detail.upgrade_to || 'pro_plus')
      setCode(detail.code || null)
      setVaKind(detail.va_kind || null)
      setVaHandle(detail.va_handle || null)
      setShowDownsell(false)
      // Pour les contextes d'analyse → l'essai est exclu par design,
      // donc on force bypassTrial=true (= souscription immédiate Créateur).
      const analysisCodes = ['free_no_analysis', 'trial_blocks_analysis', 'lifetime_quota_reached', 'weekly_quota_reached']
      if (analysisCodes.includes(detail.code)) {
        setBypassTrialCreator(true)
      } else {
        // Autres contextes : from_free=true (free user qui débloque) →
        // garde l'essai 7j (bypassTrial=false).
        setBypassTrialCreator(detail.from_free ? false : true)
      }
      setOpen(true)
      // Impression du paywall analyse (Pro+) : mesure combien le VOIENT (vs cliquent).
      try { studioEvents.upgradeModalShown({ trigger: detail.trigger || detail.code || 'analysis', modal: 'analysis_paywall', upgrade_to: detail.upgrade_to || 'pro_plus' }) } catch {}
    }
    window.addEventListener('studio:open-analysis-paywall', handler)
    return () => window.removeEventListener('studio:open-analysis-paywall', handler)
  }, [])

  if (!open) return null
  const isAnnual = period === 'annual'
  const isAlreadyCreator = studioPlan === 'pro'

  // Visiteur campagne deal (?deal=1) sans plan : l'offre unique 214€/an
  // remplace AUSSI ce paywall (sinon il verrait « Pro 89€/mois » ici et
  // « 18€/mois » sur le paywall principal — incohérent, tue la confiance).
  if (!studioPlan && isDealVisitor()) {
    const chooseDealHere = async () => {
      if (busy) return
      setBusy(true)
      try { studioEvents.upgradeModalPlanChosen({ plan: 'studio_pro_plus', period: 'annual_deal', modal: 'analysis_paywall_deal', committed: true }) } catch {}
      await triggerStudioCheckout('annual_deal', 'studio_pro_plus', true)
      setTimeout(() => setBusy(false), 3000)
    }
    return (
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
        onClick={() => !busy && setOpen(false)}
        style={{ background: 'rgba(15,10,26,0.7)', backdropFilter: 'blur(6px)' }}>
        <div className="w-full max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[95vh] overflow-y-auto p-5 relative"
          onClick={(e) => e.stopPropagation()}>
          <button onClick={() => !busy && setOpen(false)}
            className="absolute top-2 right-2 w-10 h-10 rounded-full flex items-center justify-center text-ink-faint hover:text-ink text-xl" aria-label="Fermer">×</button>
          <DealCardBody busy={busy} onChoose={chooseDealHere} />
        </div>
      </div>
    )
  }

  // Visiteur offre membre sans plan : cohérence totale, la card membre
  // remplace aussi ce paywall (même logique que le deal).
  if (!studioPlan && isMemberVisitor()) {
    const chooseMemberHere = async (memberPeriod) => {
      if (busy) return
      setBusy(true)
      try { studioEvents.upgradeModalPlanChosen({ plan: 'studio_pro_plus', period: memberPeriod, modal: 'analysis_paywall_member', committed: true }) } catch {}
      await triggerStudioCheckout(memberPeriod, 'studio_pro_plus', true)
      setTimeout(() => setBusy(false), 3000)
    }
    return (
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
        onClick={() => !busy && setOpen(false)}
        style={{ background: 'rgba(15,10,26,0.7)', backdropFilter: 'blur(6px)' }}>
        <div className="w-full max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[95vh] overflow-y-auto p-5 relative"
          onClick={(e) => e.stopPropagation()}>
          <button onClick={() => !busy && setOpen(false)}
            className="absolute top-2 right-2 w-10 h-10 rounded-full flex items-center justify-center text-ink-faint hover:text-ink text-xl" aria-label="Fermer">×</button>
          <MemberCardBody busy={busy} onChoose={chooseMemberHere} />
        </div>
      </div>
    )
  }

  // Cas dédié : Créateur en essai qui veut lancer une analyse.
  // → simple confirmation que lancer = résilier essai (déclenche le
  //   prélèvement immédiat 39 €). Pas de plan picker.
  const isTrialEndConfirm = code === 'trial_blocks_analysis' && isAlreadyCreator

  // Contextes d'analyse : pour ces flows, l'essai 7j n'est PAS une option
  // (analyse exclue de l'essai par design). Donc on cache la checkbox
  // 'résilier essai' (faux choix) et on force le mode immédiat.
  const ANALYSIS_CODES = ['free_no_analysis', 'trial_blocks_analysis', 'lifetime_quota_reached', 'weekly_quota_reached']
  const isAnalysisGate = ANALYSIS_CODES.includes(code)

  // Détermine quel plan afficher (hors cas trial-confirm) :
  const showOnlyPro = isAlreadyCreator || upgradeTo === 'pro_plus' || upgradeTo === 'studio_pro_plus'
  const showOnlyCreator = !showOnlyPro

  // FICHE-CADEAU (v8, maquette validée Antonin 24/07) : le paywall répond à
  // la demande « analyser une autre de mes vidéos ». past = réactivation,
  // jamais « essai gratuit » (doctrine : il l'a déjà utilisé).
  const isVaFiche = code === 'va_fiche'
  const isVaPast = isVaFiche && vaKind === 'past'
  const proWithTrial = showOnlyPro && !isAlreadyCreator && !isVaPast
  // Downsell Créateur possible : un user PAS déjà Créateur, sur un paywall
  // Pro+ analyse (améliorer mon compte = free_no_analysis, ou fiche-cadeau
  // non-past). Le filet 39€ n'a de sens que pour ces gratuits/past.
  // A/B TEST 2 (indépendant du mur de la note = test 1) : surface
  // 'paywall_downsell', bras A = filet visible, B = contrôle (Pro+ seul).
  // Override URL ?downsell=A|B pour tester chaque combinaison à la main.
  // Assignation sticky par anon_id + hash(id_expérience) → dissociable du
  // test 1 quelles que soient les combinaisons, comptes et parcours.
  const downsellVariant = (() => {
    try {
      const o = new URLSearchParams(window.location.search).get('downsell')
      if (o === 'A' || o === 'B') return o
      return getVariantKey('paywall_downsell')
    } catch { return null }
  })()
  const canDownsell = downsellVariant === 'A' && !isAlreadyCreator && studioPlan !== 'pro_plus' && !isVaPast && (isVaFiche || (isAnalysisGate && showOnlyPro))
  // Lance l'essai Créateur (0€ 7j puis 39€), l'aveu volontaire « trop cher »
  async function chooseCreatorTrial() {
    if (busy) return
    setBusy(true)
    await triggerStudioCheckout(period, 'studio_creator', false) // false = garde l'essai 7j
    setTimeout(() => setBusy(false), 3000)
  }

  // Titre contextualisé (l'écran downsell Créateur reprend la main sur l'en-tête)
  const onDownsell = showDownsell && canDownsell
  const title = onDownsell
    ? 'Une version plus accessible'
    : isTrialEndConfirm
    ? 'Débloque ton analyse'
    : isVaFiche
      ? <>Chaque vidéo que tu publies, <span style={{ color: 'var(--merlin-accent-soft, #fdba74)' }}>analysée comme celle-ci</span>. Automatiquement.</>
      : showOnlyPro
        ? 'Passe en Pro'
        : 'Débloque l\'analyse approfondie'
  const subtitle = onDownsell
    ? 'Merlin Créateur, l\'essentiel pour créer, à petit prix.'
    : isTrialEndConfirm
    ? 'L\'analyse vidéo n\'est pas comprise dans ta période d\'essai. Active ton plan Créateur pour débloquer ton analyse.'
    : isVaFiche
      ? 'Rien à faire : tu publies, Merlin analyse, tu reçois le rapport.'
      : showOnlyPro
        ? (showsTrialUx() ? 'L\'audit complet de ton compte + le plan pour le faire grandir. Inclus dans ton essai 7 jours.' : 'L\'audit complet de ton compte + le plan pour le faire grandir.')
        : (showsTrialUx() ? 'L\'audit complet de ton compte + analyses vidéo illimitées. Inclus dans ton essai 7 jours, 0 € avant J+7.' : 'L\'audit complet de ton compte + analyses vidéo illimitées.')

  async function chooseCreator() {
    if (busy) return
    setBusy(true)
    await triggerStudioCheckout(period, 'studio_creator', bypassTrialCreator)
    setTimeout(() => setBusy(false), 3000)
  }
  async function choosePro() {
    if (busy) return
    setBusy(true)
    // past (fiche-cadeau) : réactivation directe, pas de nouvel essai
    await triggerStudioCheckout(period, 'studio_pro_plus', isVaPast)
    setTimeout(() => setBusy(false), 3000)
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={() => !busy && setOpen(false)}
      style={{ background: 'rgba(15,10,26,0.78)', backdropFilter: 'blur(8px)' }}>
      <div className="w-full max-w-2xl bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[95vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="relative px-5 pt-6 pb-5 text-white sticky top-0 z-10"
          style={{ background: 'linear-gradient(135deg, var(--merlin-violet, #7c3aed) 0%, var(--fmt-ph-1, #1e1145) 100%)' }}>
          <button onClick={() => !busy && setOpen(false)}
            className="absolute top-2 right-2 w-11 h-11 rounded-full flex items-center justify-center text-white/80 hover:text-white text-xl"
            style={{ background: 'rgba(255,255,255,0.10)' }}>
            ×
          </button>
          {isVaFiche ? (
            vaHandle ? <p className="text-[12px] font-bold mb-2" style={{ color: '#ddd6fe' }}>@{vaHandle}</p> : null
          ) : (
            <div className="inline-flex items-center gap-1.5 mb-3 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider"
              style={{ background: 'rgba(var(--merlin-accent-rgb, 254,130,4),0.20)', color: 'var(--merlin-accent-soft, #fdba74)', border: '1px solid rgba(var(--merlin-accent-rgb, 254,130,4),0.40)' }}>
              🎁 Offre Béta
            </div>
          )}
          <h2 className="text-xl sm:text-2xl font-bold leading-tight mb-1">{title}</h2>
          <p className="text-sm text-white/85 leading-snug">{subtitle}</p>
        </div>

        {/* Period toggle — masqué en fiche-cadeau (maquette v8) et sur l'écran
            downsell Créateur (prix mensuel unique, simplicité). */}
        {!isVaFiche && !onDownsell && (
          <div className="px-5 pt-5">
            <PeriodToggle isAnnual={isAnnual} onChange={setPeriod} savingsPct={isAlreadyCreator ? 22 : 26} />
          </div>
        )}

        {/* Cas Créateur en essai qui veut lancer une analyse : juste une
            checkbox de confirmation, pas de plan picker (il est déjà
            Créateur, l'unique effet du clic = résilier essai). */}
        {isTrialEndConfirm ? (
          <div className="px-5 py-4 space-y-3">
            <label className="flex items-start gap-2.5 cursor-pointer p-3.5 rounded-xl"
              style={{ background: 'rgba(var(--merlin-accent-rgb, 254,130,4),0.06)', border: '1.5px solid rgba(var(--merlin-accent-rgb, 254,130,4),0.25)' }}>
              <input type="checkbox" checked={bypassTrialCreator}
                onChange={(e) => setBypassTrialCreator(e.target.checked)}
                className="mt-0.5 shrink-0 accent-orange" />
              <span className="text-[13px] text-ink leading-snug">
                <b>J'active mon plan Créateur à {isAnnual ? '29 €/mois (= 348 €/an)' : '39 €/mois'}</b> pour lancer mon analyse.
                <span className="block text-[11px] text-ink-faint mt-1">Mon essai 7j s'arrête, je peux annuler à tout moment.</span>
              </span>
            </label>
            <button
              onClick={chooseCreator}
              disabled={busy || !bypassTrialCreator}
              className="w-full py-3.5 rounded-xl font-bold text-sm text-white transition-all hover:scale-[1.01] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              style={{ background: 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))', boxShadow: '0 8px 24px rgba(var(--merlin-accent-rgb, 254,130,4),0.35)' }}>
              {busy ? '…' : 'Lancer mon analyse'}
            </button>
            <p className="text-[11px] text-ink-faint text-center">
              Tu peux aussi <button onClick={choosePro} className="underline hover:text-ink">passer en Pro+ pour des analyses illimitées</button>.
            </p>
          </div>
        ) : (showDownsell && canDownsell) ? (
          /* ÉCRAN DOWNSELL CRÉATEUR 39€ — ouvert au clic volontaire « trop cher »
             (jamais par défaut : contraste couleur + aveu explicite). Honnête
             sur ce qui est inclus ET ce qui reste en Pro. */
          <div className="px-5 py-4 max-w-md mx-auto">
            <button onClick={() => setShowDownsell(false)} className="text-[12px] text-ink-muted hover:text-ink underline mb-3">← Revenir à l'offre complète</button>
            <p className="text-[17px] font-extrabold text-ink">Merlin Créateur, à 39 €/mois</p>
            <p className="text-[12.5px] text-ink-muted mb-3">Tu gardes l'essentiel pour créer, sans l'analyse de compte.</p>
            <p className="text-[10.5px] font-bold uppercase tracking-wide text-ink-faint mb-1.5">Ce que tu débloques</p>
            <div className="space-y-1.5 mb-3">
              {['Les meilleurs formats adaptés à ton compte', 'La bibliothèque entière de formats viraux', 'Tes sauvegardes illimitées'].map((f, i) => (
                <div key={i} className="flex items-start gap-2 text-[12.5px] text-ink"><span className="shrink-0 font-bold" style={{ color: '#16a34a' }}>✓</span>{f}</div>
              ))}
            </div>
            <p className="text-[10.5px] font-bold uppercase tracking-wide text-ink-faint mb-1.5">Ce qui reste en Pro</p>
            <div className="space-y-1.5 mb-4">
              {["L'analyse complète de ton compte", 'Les analyses vidéo illimitées'].map((f, i) => (
                <div key={i} className="flex items-start gap-2 text-[12.5px] text-ink-muted"><span className="shrink-0" style={{ color: '#dc2626' }}>✗</span>{f}</div>
              ))}
            </div>
            <div className="text-center">
              <p className="leading-none"><span className="text-[34px] font-extrabold text-ink">0 €</span></p>
              <p className="text-[12px] font-bold text-ink mt-0.5">pendant 7 jours</p>
              <p className="text-[11px] text-ink-muted">puis 39 €/mois · annule quand tu veux</p>
            </div>
            <button onClick={chooseCreatorTrial} disabled={busy}
              className="w-full mt-3 py-3.5 rounded-xl font-extrabold text-[14.5px] text-white disabled:opacity-60"
              style={{ background: 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))', boxShadow: '0 8px 24px rgba(var(--merlin-accent-rgb, 254,130,4),0.35)' }}>
              {busy ? '…' : 'Commencer avec Créateur'}
            </button>
            <PaywallReassurance />
          </div>
        ) : (
          <>
            {/* Note explicite pour les analyse-gate où l'essai n'est pas une option */}
            {isAnalysisGate && showOnlyCreator && (
              <div className="px-5 pt-4">
                <div className="p-3 rounded-lg flex items-start gap-2"
                  style={{ background: 'rgba(var(--merlin-violet-rgb, 124,58,237),0.05)', border: '1px solid rgba(var(--merlin-violet-rgb, 124,58,237),0.18)' }}>
                  <span className="text-purple-600 shrink-0 mt-0.5 text-sm">⚡</span>
                  <p className="text-[11.5px] text-ink leading-snug">
                    <b>L'analyse n'est pas comprise dans l'essai gratuit.</b> Tu démarres ton plan Créateur directement pour la débloquer.
                  </p>
                </div>
              </div>
            )}

            {/* Checkbox bypass trial : uniquement hors analyse-gate (vrai choix) */}
            {!isAnalysisGate && showOnlyCreator && (
              <div className="px-5 pt-4">
                <label className="flex items-start gap-2.5 cursor-pointer p-3 rounded-lg"
                  style={{ background: 'rgba(var(--merlin-accent-rgb, 254,130,4),0.06)', border: '1px solid rgba(var(--merlin-accent-rgb, 254,130,4),0.20)' }}>
                  <input type="checkbox" checked={bypassTrialCreator}
                    onChange={(e) => setBypassTrialCreator(e.target.checked)}
                    className="mt-0.5 shrink-0 accent-orange" />
                  <span className="text-xs text-ink leading-snug">
                    <b>Je résilie mes 7 jours offerts</b> et accepte le prélèvement immédiat.
                    <span className="block text-[10px] text-ink-faint mt-1 italic">
                      Sans la case : essai 7j gratuit (CB demandée, prélèvement à J+7).
                    </span>
                  </span>
                </label>
              </div>
            )}

            {/* FICHE-CADEAU v8 (maquette validée) : features menées par les
                analyses, timeline d'essai, 0 € central, réassurance sous le
                bouton. past = réactivation directe sans essai. */}
            {isVaFiche ? (
              <div className="px-5 py-4 max-w-md mx-auto">
                <div className="space-y-2 mb-4">
                  {['Chaque nouveau reel analysé automatiquement sous 24h',
                    'Analyses à la demande, sur toutes tes vidéos',
                    'L\'analyse complète de ton compte et comment l\'améliorer',
                    'Les meilleurs formats adaptés à TON compte'].map((f, i) => (
                    <div key={i} className="flex items-start gap-2 text-[12.5px] text-ink leading-snug">
                      <span className="shrink-0 font-bold" style={{ color: '#16a34a' }}>✓</span>{f}
                    </div>
                  ))}
                </div>
                {!isVaPast && showsTrialUx() && <TrialTimeline />}
                <div className="text-center mt-4">
                  {(isVaPast || !showsTrialUx()) ? (
                    <p className="text-[16px] font-extrabold text-ink">89 €/mois <span className="font-normal text-ink-muted text-[12px]">· ou 69 €/mois en annuel · sans engagement</span></p>
                  ) : (
                    <>
                      <p className="leading-none"><span className="text-[40px] font-extrabold text-ink">0</span><span className="text-[20px] font-extrabold text-ink"> €</span></p>
                      <p className="text-[12.5px] font-bold text-ink mt-0.5">pendant 7 jours</p>
                      <p className="text-[11px] text-ink-muted">puis 89 €/mois · ou 69 €/mois en annuel</p>
                    </>
                  )}
                </div>
                <button onClick={choosePro} disabled={busy}
                  className="w-full mt-3.5 py-3.5 rounded-xl font-extrabold text-[14.5px] text-white disabled:opacity-60"
                  style={{ background: 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))', boxShadow: '0 8px 24px rgba(var(--merlin-accent-rgb, 254,130,4),0.35)' }}>
                  {busy ? '…' : isVaPast ? 'Réactiver mon accès →' : 'Analyser ma prochaine vidéo →'}
                </button>
                <PaywallReassurance past={isVaPast} />
                {canDownsell && (
                  <button onClick={() => { try { studioEvents.creatorDownsellShown({ code, upgrade_to: upgradeTo }) } catch {} ; setShowDownsell(true) }}
                    className="w-full mt-3 py-3 rounded-xl font-bold text-[13px] transition-colors hover:bg-ink/[0.03]"
                    style={{ background: '#fff', color: 'var(--merlin-accent, #fe8204)', border: '1.5px solid rgba(var(--merlin-accent-rgb, 254,130,4),0.4)' }}>
                    Non, c'est trop pour moi…
                  </button>
                )}
              </div>
            ) : (
              /* Plan card unique selon contexte */
              <div className="px-5 py-4 grid grid-cols-1 max-w-md mx-auto">
                {showOnlyCreator && (
                  <PlanCard tier="creator" isAnnual={isAnnual} currentPlan={studioPlan} busy={busy} onChoose={chooseCreator}
                    showTrial={!bypassTrialCreator && getTrialArm() === 'A'} />
                )}
                {showOnlyPro && (
                  <>
                    {proWithTrial && <TrialTimeline compact />}
                    <PlanCard tier="pro_plus" isAnnual={isAnnual} currentPlan={studioPlan} busy={busy} onChoose={choosePro}
                      showTrial={!isAlreadyCreator && getTrialArm() === 'A'} />
                    {proWithTrial && <PaywallReassurance />}
                    {canDownsell && (
                      <button onClick={() => { try { studioEvents.creatorDownsellShown({ code, upgrade_to: upgradeTo }) } catch {} ; setShowDownsell(true) }}
                        className="w-full mt-3 py-3 rounded-xl font-bold text-[13px] transition-colors hover:bg-ink/[0.03]"
                        style={{ background: '#fff', color: 'var(--merlin-accent, #fe8204)', border: '1.5px solid rgba(var(--merlin-accent-rgb, 254,130,4),0.4)' }}>
                        Non, c'est trop pour moi…
                      </button>
                    )}
                  </>
                )}
              </div>
            )}
          </>
        )}

        <div className="pb-5" />
      </div>
    </div>
  )
}


// ═══════════════════════════════════════════════════════════════
// FREE VIDEO PLAYER — custom (sans controls iOS qui foutent le bordel)
// ═══════════════════════════════════════════════════════════════
// Registry global des vidéos Studio actives. Quand une commence à jouer,
// toutes les autres se mettent en pause (UX 1-vidéo-à-la-fois).
const _studioVideos = new Set()
function _pauseOthers(current) {
  _studioVideos.forEach(v => { if (v !== current && !v.paused) { try { v.pause() } catch {} } })
}

function FreeVideoPlayer({ mp4Url: initialMp4Url, posterUrl, posterFallbacks = [], lien, airtable_id }) {
  const ref = useRef(null)
  const [playing, setPlaying] = useState(false)
  // Son ON par défaut. La vidéo ne joue qu'au clic explicite (pas d'autoplay),
  // donc pas de blocage navigateur. Feedback user : le 🔇 bottom-right était
  // pas assez visible, les users croyaient que la vidéo était muette.
  const [muted, setMuted] = useState(false)
  // MP4 résolu PARESSEUSEMENT : zéro dépendance à loadAllVideoUrls (les ~450
  // vidéos Airtable) au 1er paint. Le poster Supabase (CDN) s'affiche tout de
  // suite ; on ne charge l'URL MP4 qu'au 1er tap "play".
  const [mp4Url, setMp4Url] = useState(initialMp4Url || null)
  const [resolving, setResolving] = useState(false)
  useEffect(() => { if (initialMp4Url) setMp4Url(initialMp4Url) }, [initialMp4Url])

  // --- Poster avec retry sur toute la chaîne de fallback + spinner ---
  // Beaucoup de beat-frames PROD pointent encore vers le Storage dev (dette
  // connue) → 404. Avant : le 1er poster échouait, l'img passait en
  // display:none, et la vignette restait VIOLETTE pour toujours (aucun retry
  // quand un meilleur fallback arrivait via airtableReady). Maintenant on
  // gère une chaîne de candidats : on avance sur onError, on relance quand de
  // nouveaux candidats apparaissent, on affiche un rond qui tourne tant que
  // rien n'a chargé, et on fait apparaître la miniature en fondu une fois prête.
  const candidates = useMemo(() => {
    const seen = new Set()
    return [posterUrl, ...(posterFallbacks || [])].filter(u => u && !seen.has(u) && seen.add(u))
  }, [posterUrl, (posterFallbacks || []).join('|')])
  const [posterIdx, setPosterIdx] = useState(0)
  const [posterLoaded, setPosterLoaded] = useState(false)
  const [frameReady, setFrameReady] = useState(false)
  // Nouveaux candidats (ex: thumbnail Airtable fraîche après airtableReady) →
  // on repart du début de la chaîne pour retenter d'afficher une miniature.
  useEffect(() => { setPosterIdx(0); setPosterLoaded(false) }, [candidates.join('|')])
  const currentPoster = candidates[posterIdx] || null
  const postersExhausted = candidates.length === 0 || posterIdx >= candidates.length
  // Tant qu'aucune miniature n'est prête, on tente activement quelque chose :
  // soit un poster en cours, soit la résolution du MP4, soit la 1re frame de la
  // vidéo (repli quand tous les posters ont échoué mais qu'on a un MP4).
  const loadingThumb = !playing && !posterLoaded && !frameReady &&
    (!!currentPoster || resolving || (postersExhausted && !!mp4Url))

  // Enregistre la vidéo dans le registry pour l'auto-pause des autres
  useEffect(() => {
    const v = ref.current; if (!v) return
    _studioVideos.add(v)
    return () => { _studioVideos.delete(v) }
  }, [mp4Url])

  function handleMeta(e) { try { e.currentTarget.currentTime = 0.5 } catch {} }
  async function startPlay() {
    let url = mp4Url
    if (!url) {
      setResolving(true)
      try { await loadAllVideoUrls() } catch {}
      url = getVideoMp4({ lien, airtable_id }) || null
      setResolving(false)
      if (!url) return
      setMp4Url(url)
      await new Promise(r => requestAnimationFrame(() => r())) // laisse monter le <source>
    }
    const v = ref.current; if (!v) return
    try { v.load() } catch {}
    _pauseOthers(v)
    v.play().then(() => setPlaying(true)).catch(() => {})
  }
  function toggle(e) {
    if (e) { e.preventDefault(); e.stopPropagation() }
    const v = ref.current
    if (v && !v.paused && playing) { v.pause(); setPlaying(false); return }
    startPlay()
  }
  function toggleMute(e) {
    e.preventDefault(); e.stopPropagation()
    const v = ref.current; if (!v) return
    v.muted = !v.muted
    setMuted(v.muted)
  }

  return (
    <>
      {/* Poster Supabase (CDN, instantané) + chaîne de fallback avec retry.
          Visible tant qu'on ne joue pas. Fondu à l'apparition une fois chargé
          pour éviter le flash violet → image. */}
      {!playing && currentPoster && (
        <img key={currentPoster} src={currentPoster} alt="" loading="lazy"
          onLoad={() => setPosterLoaded(true)}
          onError={() => setPosterIdx(i => i + 1)}
          className="absolute inset-0 w-full h-full object-cover pointer-events-none transition-opacity duration-300"
          // zIndex 1 : la <video> rendue après peut peindre NOIR par-dessus le
          // poster (vu en headless + certains devices). pointer-events-none
          // laisse les taps atteindre la vidéo en dessous.
          style={{ opacity: posterLoaded ? 1 : 0, zIndex: 1 }} />
      )}
      <video ref={ref}
        muted={muted} playsInline disablePictureInPicture
        // Si tous les posters ont échoué mais qu'on a un MP4 : on précharge la
        // 1re frame pour qu'elle serve de miniature (plus jamais de fond violet
        // nu). Sinon preload="none" pour ne pas tirer les vidéos inutilement.
        preload={postersExhausted && mp4Url ? 'metadata' : 'none'}
        onLoadedMetadata={handleMeta}
        onLoadedData={() => setFrameReady(true)}
        onClick={toggle}
        onPause={() => setPlaying(false)}
        onPlay={() => { _pauseOthers(ref.current); setPlaying(true) }}
        className="absolute inset-0 block cursor-pointer"
        style={{ width: '100%', height: '100%', objectFit: 'cover', background: 'transparent' }}>
        {mp4Url && <source src={mp4Url} type="video/mp4" />}
      </video>

      {/* Play overlay (gros bouton centré quand paused) */}
      {!playing && (
        <button onClick={toggle}
          className="absolute inset-0 flex items-center justify-center z-10 group"
          style={{ background: 'rgba(15,10,26,0.18)' }}
          title="Lire">
          <span className="w-14 h-14 rounded-full flex items-center justify-center transition-transform group-hover:scale-110"
            style={{ background: 'rgba(255,255,255,0.95)', boxShadow: '0 4px 16px rgba(0,0,0,0.30)' }}>
            {(resolving || loadingThumb)
              ? <span className="w-5 h-5 border-2 border-purple-300 border-t-purple-700 rounded-full animate-spin" />
              : <span className="text-purple-900 text-xl ml-0.5">▶</span>}
          </span>
        </button>
      )}

      {/* Pause overlay (centre, visible au hover quand playing) */}
      {playing && (
        <button onClick={toggle}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 opacity-0 hover:opacity-100 focus:opacity-100 transition-opacity"
          style={{ background: 'transparent' }}
          aria-label="Pause">
          <span className="w-12 h-12 rounded-full flex items-center justify-center"
            style={{ background: 'rgba(15,10,26,0.70)', boxShadow: '0 4px 14px rgba(0,0,0,0.45)' }}>
            <span className="text-white text-base">⏸</span>
          </span>
        </button>
      )}

      {/* Contrôles persistants bottom : pause/stop bottom-left, mute bottom-right */}
      {playing && (
        <button onClick={toggle}
          className="absolute bottom-2 left-2 z-20 w-8 h-8 rounded-full flex items-center justify-center transition-all hover:scale-110"
          style={{ background: 'rgba(15,10,26,0.75)', backdropFilter: 'blur(8px)', boxShadow: '0 2px 8px rgba(0,0,0,0.35)' }}
          title="Pause"
          aria-label="Pause">
          <span className="text-white text-sm">⏸</span>
        </button>
      )}

      <button onClick={toggleMute}
        className="absolute bottom-2 right-2 z-20 w-8 h-8 rounded-full flex items-center justify-center transition-all hover:scale-110"
        style={{ background: 'rgba(15,10,26,0.75)', backdropFilter: 'blur(8px)', boxShadow: '0 2px 8px rgba(0,0,0,0.35)' }}
        title={muted ? 'Activer le son' : 'Couper le son'}
        aria-label={muted ? 'Activer le son' : 'Couper le son'}>
        <span className="text-white text-sm">{muted ? '🔇' : '🔊'}</span>
      </button>
    </>
  )
}

// ═══════════════════════════════════════════════════════════════
// CHEVRON — gros indicator cliquable pour les <details>
// ═══════════════════════════════════════════════════════════════
function Chevron({ accent = 'orange' }) {
  const colors = accent === 'orange'
    ? { bg: 'rgba(var(--merlin-accent-rgb, 254,130,4),0.15)', stroke: 'var(--merlin-accent, #fe8204)' }
    : { bg: 'rgba(var(--merlin-violet-rgb, 124,58,237),0.15)', stroke: 'var(--merlin-violet, #7c3aed)' }
  return (
    <span className="w-7 h-7 sm:w-6 sm:h-6 rounded-full flex items-center justify-center shrink-0 transition-transform group-open:rotate-180"
      style={{ background: colors.bg }}>
      <svg viewBox="0 0 12 12" className="w-4 h-4 sm:w-3.5 sm:h-3.5" fill="none" stroke={colors.stroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="3,4.5 6,7.5 9,4.5" />
      </svg>
    </span>
  )
}

// ═══════════════════════════════════════════════════════════════
// CTA Pro réutilisable — compact, 1 ligne sur desktop, stack sur mobile
// ═══════════════════════════════════════════════════════════════
function ProUpgradeCTA({ label = 'Studio Pro', sub }) {
  return (
    <button
      onClick={() => goToStudioCheckout()}
      className="w-full rounded-2xl p-4 text-white transition-all hover:scale-[1.005] active:scale-[0.998] flex items-center justify-between gap-3 group"
      style={{
        background: 'linear-gradient(135deg, var(--merlin-violet, #7c3aed) 0%, var(--fmt-ph-1, #1e1145) 100%)',
        boxShadow: '0 6px 20px rgba(var(--merlin-violet-rgb, 124,58,237),0.25)',
      }}>
      <div className="flex items-center gap-3 min-w-0">
        <span className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 text-sm"
          style={{ background: 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))' }}>
          🔓
        </span>
        <div className="min-w-0 text-left">
          <p className="text-sm sm:text-base font-bold leading-tight truncate">{label}</p>
          {sub && <p className="text-[11px] text-white/70 leading-tight truncate">{sub}</p>}
        </div>
      </div>
      <span className="text-sm font-bold whitespace-nowrap shrink-0 transition-transform group-hover:translate-x-0.5">
        Débloquer →
      </span>
    </button>
  )
}

// ═══════════════════════════════════════════════════════════════
// LIBRARY (free) — titre + grid centré + CTA bas. Pas de filtres lockés.
// ═══════════════════════════════════════════════════════════════
function StudioFreeLibrary({ airtableReady }) {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        // Verticale : la library free aussi ne montre que le secteur.
        const v = getVertical()
        const res = await fetch(`${API_URL}/api/studio/library/preview?count=18${v ? `&vertical=${encodeURIComponent(v)}` : ''}`)
        const data = await res.json()
        if (!cancelled) setItems(data?.items || [])
      } catch (e) {
        console.warn('[Studio Library preview] fetch fail:', e.message)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [])

  const fmtNum = (n) => {
    if (!n) return '0'
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
    if (n >= 1_000) return `${(n / 1_000).toFixed(0)}k`
    return String(n)
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Titre — plus visible sur PC */}
      <div className="mb-4 sm:mb-5 px-0.5">
        <h2 className="text-[15px] sm:text-2xl font-bold text-ink leading-tight">
          Des milliers de reels <span className="text-orange">à explorer</span>
        </h2>
        <p className="text-[10px] sm:text-xs text-ink-faint mt-0.5 sm:mt-1">
          Recherche & filtres déverrouillés en Pro
        </p>
      </div>

      {/* Grid full-width — 2 col mobile, jusqu'à 6 col sur grand écran */}
      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 sm:gap-3">
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className="rounded-xl bg-ink/5 animate-pulse w-full" style={{ aspectRatio: '9/16' }} />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 sm:gap-3">
          {items.map((v) => (
            <ThumbnailCard key={v.id} item={v} fmtNum={fmtNum} airtableReady={airtableReady} />
          ))}
        </div>
      )}

      <div className="mt-5 sm:mt-6 max-w-2xl mx-auto">
        <ProUpgradeCTA label="Toute la library" sub="Recherche + filtres + scripts" />
      </div>
    </div>
  )
}

function ThumbnailCard({ item, fmtNum, airtableReady }) {
  const mp4 = airtableReady ? getVideoMp4({ lien: item.lien, airtable_id: item.airtable_id }) : null
  const [videoError, setVideoError] = useState(false)
  function handleMeta(e) {
    try { e.currentTarget.currentTime = 0.5 } catch {}
  }
  const showVideo = mp4 && !videoError
  return (
    <button
      onClick={() => goToStudioCheckout()}
      className="relative overflow-hidden rounded-xl text-left group cursor-pointer transition-transform hover:scale-[1.02] active:scale-[0.98] studio-thumbnail w-full block"
      style={{
        aspectRatio: '9/16',
        background: 'linear-gradient(135deg, var(--fmt-ph-1, #1e1145) 0%, var(--fmt-ph-2, #2d1b69) 100%)',
        boxShadow: '0 4px 14px rgba(30,17,69,0.18)',
      }}>
      {showVideo ? (
        <video
          preload="metadata" muted playsInline disablePictureInPicture
          onLoadedMetadata={handleMeta}
          onError={() => setVideoError(true)}
          src={mp4}
          className="absolute inset-0 block studio-thumbnail-video"
          style={{
            width: '100%', height: '100%',
            objectFit: 'cover',
            background: 'var(--fmt-ph-1, #1e1145)',
          }} />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center"
          style={{ background: 'linear-gradient(135deg, var(--fmt-ph-1, #1e1145), var(--fmt-ph-2, #2d1b69))' }}>
          <span className="text-white/30 text-2xl">🎬</span>
        </div>
      )}
      {/* Lien source : admin uniquement */}
      <AdminSourceLink lien={item.lien} />
      {/* Analyse plein format au survol (toutes les grilles) */}
      <ThumbAnalysisOverlay item={item} cta="Débloquer en Pro ✨" />
      {/* Overlay gradient bas-haut (pour la lisibilité des badges + dim) */}
      <div className="absolute inset-0 pointer-events-none"
        style={{ background: 'linear-gradient(180deg, rgba(15,10,26,0.10) 0%, rgba(15,10,26,0.0) 30%, rgba(15,10,26,0.0) 65%, rgba(15,10,26,0.78) 100%)' }} />
      {/* Cadenas au centre — visible au hover SEULEMENT si pas de data
          analyse (sinon il recouvrait le panneau ThumbAnalysisOverlay) */}
      {!(item.mecanisme_viral || item.structure) && (
      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-purple-950/40 backdrop-blur-[2px]">
        <div className="w-12 h-12 rounded-full flex items-center justify-center"
          style={{ background: 'rgba(var(--merlin-violet-rgb, 124,58,237),0.95)', boxShadow: '0 4px 14px rgba(var(--merlin-violet-rgb, 124,58,237),0.55)' }}>
          <span className="text-lg">🔒</span>
        </div>
      </div>)}
      {/* Vues en haut à droite */}
      {item.vues > 0 && (
        <div className="absolute top-2 right-2 pointer-events-none">
          <span className="inline-block px-2 py-0.5 rounded-md text-[10px] font-bold text-white font-mono"
            style={{ background: 'rgba(15,10,26,0.7)', backdropFilter: 'blur(8px)' }}>
            {fmtNum(item.vues)}
          </span>
        </div>
      )}
      {/* Niche en bas */}
      <div className="absolute bottom-2 left-2 right-2 pointer-events-none">
        {item.niche && (
          <span className="inline-block px-2 py-0.5 rounded-md text-[10px] font-bold text-white"
            style={{ background: 'rgba(var(--merlin-accent-rgb, 254,130,4),0.85)', backdropFilter: 'blur(8px)' }}>
            {item.niche}
          </span>
        )}
      </div>
    </button>
  )
}

// ═══════════════════════════════════════════════════════════════
// VAULT (free) — empty state
// ═══════════════════════════════════════════════════════════════
function StudioFreeVault() {
  return (
    <div className="max-w-xs mx-auto text-center py-6">
      <div className="inline-flex items-center justify-center w-10 h-10 rounded-full mb-2.5"
        style={{ background: 'rgba(var(--merlin-violet-rgb, 124,58,237),0.10)' }}>
        <span style={{ fontSize: '16px' }}>🗂️</span>
      </div>
      <p className="text-[13px] font-bold text-ink leading-tight mb-3">Coffre vide. Débloque pour sauvegarder tes formats.</p>
      <button
        onClick={() => goToStudioCheckout()}
        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg font-bold text-[12px] text-white transition-all hover:scale-[1.02]"
        style={{ background: 'linear-gradient(135deg, var(--merlin-violet, #7c3aed), var(--merlin-violet-2, #5b21b6))', boxShadow: '0 4px 14px rgba(var(--merlin-violet-rgb, 124,58,237),0.30)' }}>
        <span style={{ fontSize: '10px' }}>🔓</span>
        <span>Débloquer Pro</span>
      </button>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// PERSONA BLOCK
// ═══════════════════════════════════════════════════════════════
function PersonaBlock({ label, items, accent = 'orange', compact }) {
  const dotColor = accent === 'purple' ? 'var(--merlin-violet, #7c3aed)' : 'var(--merlin-accent, #fe8204)'
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider font-bold text-ink-faint mb-2">{label}</p>
      <ul className="space-y-1.5">
        {items.slice(0, 4).map((v, i) => (
          <li key={i} className="flex gap-2 text-xs text-ink leading-relaxed">
            {!compact && (
              <span className="shrink-0 mt-1.5 w-1 h-1 rounded-full" style={{ background: dotColor }} />
            )}
            <span className={compact ? 'italic text-ink-muted' : ''}>
              {compact ? `« ${v} »` : v}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// FORMAT CARD FREE — mobile-first density
// Vidéo + header compact + hook visible + script teaser + details (why/structure)
// Exporté pour réutilisation dans LandingPage (vrais exemples).
// ═══════════════════════════════════════════════════════════════
// ─── Objectif + "à mettre" par fonction de beat (pour la trame) ───
// Map fonction → { objectif: court CM2, aMettre: type de contenu attendu }.
// L'idée : expliquer à un débutant total quoi mettre dans chaque étape
// sans lui écrire le script à sa place.
const BEAT_GUIDE = {
  'Accroche':       { objectif: 'Faire stopper le scroll',                 aMettre: 'Une phrase choc, une question ou un fait surprenant' },
  'Hook':           { objectif: 'Faire stopper le scroll',                 aMettre: 'Une phrase choc, une question ou un fait surprenant' },
  'Agitation':      { objectif: 'Toucher un point sensible',               aMettre: 'Une frustration concrète que vit ton audience' },
  'Contexte':       { objectif: 'Planter le décor en 1 phrase',            aMettre: 'Où, quand, qui, sans rentrer dans les détails' },
  'Buildup':        { objectif: 'Faire monter la tension',                 aMettre: 'Une promesse + un teaser sans tout révéler' },
  'Pivot':          { objectif: 'Retourner le viewer',                     aMettre: 'Le twist, le truc inattendu qui change tout' },
  'Punchline':      { objectif: 'Terminer fort',                           aMettre: 'Une phrase percutante qui reste en tête' },
  'Solution':       { objectif: 'Apporter la valeur',                      aMettre: 'Le truc concret que ton audience peut appliquer' },
  'Preuve':         { objectif: 'Crédibiliser ce que tu dis',              aMettre: 'Un chiffre, un témoignage, un avant/après' },
  'Objection':      { objectif: 'Anticiper la réticence',                  aMettre: 'Le "oui mais..." que pense ton viewer + ta réponse' },
  'CTA':            { objectif: 'Faire passer à l\'action',                aMettre: 'Une instruction claire (commente, follow, partage, DM)' },
  'Boucle':         { objectif: 'Ramener au début pour relancer',          aMettre: 'Reformule ta promesse de départ, sans répéter' },
  'Item':           { objectif: 'Donner un élément de la liste',           aMettre: 'Un seul point clair, court, indépendant des autres' },
  'Verdict':        { objectif: 'Donner ton avis tranché',                 aMettre: 'Ton positionnement clair, sans nuances inutiles' },
  'Recap':          { objectif: 'Récapituler les points clés',             aMettre: 'Les 2-3 idées essentielles en quelques mots' },
  'Reaction':       { objectif: 'Montrer ton émotion',                     aMettre: 'Ta vraie réaction, expressions visage, voix vivante' },
}

function beatGuide(fonction) {
  if (!fonction) return { objectif: 'À toi de jouer', aMettre: 'Ton contenu personnalisé' }
  return BEAT_GUIDE[fonction] || { objectif: fonction, aMettre: 'À toi de remplir' }
}

// ─── Map des variables sémantiques (depuis Claude analyse) → CM2 ─────
// Quand un beat utilise une variable_cle, on traduit en consigne simple.
const VARIABLE_HINT = {
  'chiffre_cle':             'un chiffre marquant (ex : "100 000 vues")',
  'delai_court':             'un délai court (ex : "7 jours")',
  'delai_moyen':             'un délai moyen (ex : "1 mois")',
  'delai_long':              'un délai long (ex : "1 an")',
  'douleur_quotidienne':     'la frustration que vit ton audience',
  'peur_profonde':           'la peur cachée de ton audience',
  'desir_tangible':          'ce que ton audience veut vraiment',
  'desir_identitaire':       'qui ton audience veut devenir',
  'vehicule_solution':       'ta méthode, ce qui te rend unique',
  'levier_autorite':         'ta preuve de crédibilité',
  'ennemi_visible':          'ce qui empêche ton audience d\'avancer',
  'ennemi_systemique':       'le système contre ton audience',
  'ennemi_interieur':        'le doute intérieur de ton audience',
  'tribu_identitaire':       'le groupe auquel ton audience appartient',
  'tribu_aspirationnelle':   'le groupe qu\'ils veulent rejoindre',
  'emotion_dominante':       'l\'émotion à toucher',
  'preuve_credibilite':      'une preuve concrète (chiffre, témoignage)',
  'preuve_sociale':          'une preuve sociale (avis, exemple)',
  'mauvaise_habitude':       'une mauvaise habitude commune',
  'bonne_pratique':          'une bonne pratique à adopter',
  'transformation':          'un avant / après concret',
  'methode_technique':       'une méthode ou technique précise',
  'objet_quotidien':         'un objet du quotidien',
  'celebrite_reference':     'une célébrité ou figure connue',
  'animal':                  'un animal en référence',
  'autorite_scientifique':   'une étude ou autorité scientifique',
  'qualificatif_negatif':    'un mot négatif fort',
  'qualificatif_positif':    'un mot positif fort',
  'superlatif_exclusivite':  'un superlatif (le meilleur, le plus, l\'unique)',
}

function variableHint(varName) {
  if (!varName) return null
  return VARIABLE_HINT[varName] || varName.replace(/_/g, ' ')
}

// ─── Bullets "pourquoi ça a marché" — générés depuis les metadata source ───
// Pas d'IA en live : on synthétise depuis mecanisme_viral, arc_narratif,
// emotion_primaire, categorie_format, vues, engagement, durée. Les libellés
// sont écrits pour être *utiles* (concret, actionnable), pas génériques.
function buildWhyItWorked(src) {
  const bullets = []
  const cats = src.categorie_format || []
  const isEdu = cats.some(c => /educatif|tutorial|tuto|tip|conseil/i.test(c))
  const isDemo = cats.some(c => /demonstrat|how.to|preuve/i.test(c))
  const isStory = cats.some(c => /storytelling|narrat|histoire/i.test(c))

  // 1. Mécanique virale (la fondation)
  if (src.mecanisme_viral) {
    const map = {
      'Valeur':       'Le viewer repart avec un truc concret à appliquer → partage probable ("ça peut servir à un pote").',
      'Surprise':     'Effet "je ne m\'attendais pas à ça" → rétention élevée et envie de revoir.',
      'Identification': 'Le viewer se reconnaît immédiatement → commentaires et tags pour "untel qui fait pareil".',
      'Provocation':  'Avis tranché qui pousse à réagir → commentaires polarisés boost l\'engagement.',
      'Mystère':      'Information retenue jusqu\'à la fin → taux de complétion élevé.',
      'Émotion':      'Réaction émotionnelle forte → enregistrements et partages.',
    }
    bullets.push({ tag: 'Mécanique', text: map[src.mecanisme_viral] || `Levier principal : ${src.mecanisme_viral}.` })
  }

  // 2. Arc narratif
  if (src.arc_narratif) {
    const map = {
      'Problème → Solution': 'Structure tension → résolution : le viewer accroche au problème, reste pour la solution.',
      'Avant → Après':       'Transformation visible avant/après, preuve immédiate et rassurante.',
      'Mythe → Vérité':      'Casse une croyance commune puis remplace par la vraie info → mémorisation forte.',
      'Question → Réponse':  'Curiosité ouverte dès le premier mot, refermée à la fin.',
      'Étapes 1, 2, 3':      'Structure liste : promesse claire de "ce que je vais apprendre", facile à enregistrer.',
      'Story personnelle':   'Anecdote vécue = autorité + identification, le viewer baisse la garde.',
    }
    bullets.push({ tag: 'Arc narratif', text: map[src.arc_narratif] || `Structure : ${src.arc_narratif}.` })
  }

  // 3. Émotion primaire
  if (src.emotion_primaire) {
    const map = {
      'Surprise':     'Émotion surprise. Pic d\'attention dès la première seconde, anti-scroll naturel.',
      'Curiosité':    'Émotion curiosité. Gap d\'info qui pousse à rester jusqu\'au reveal.',
      'Peur':         'Émotion peur. L\'évitement d\'une douleur est plus puissant que la promesse d\'un gain.',
      'Désir':        'Émotion désir. Fait visualiser l\'avant/après transformation, donc enregistrements.',
      'Colère':       'Émotion colère. Pousse aux commentaires polarisés, boost algorithme.',
      'Joie':         'Émotion joie. Partage spontané pour faire sourire un proche.',
      'Admiration':   'Émotion admiration. Déclenche les tags "untel qui devrait voir ça".',
      'Fierté':       'Émotion fierté identitaire. Partage à la tribu pour s\'affirmer.',
    }
    bullets.push({ tag: 'Émotion', text: map[src.emotion_primaire] || `Émotion primaire : ${src.emotion_primaire}.` })
  }

  // 4. Format (educatif / démo / story)
  if (isEdu && isDemo) {
    bullets.push({ tag: 'Format', text: 'Combinaison Éducatif + Démonstration : le viewer apprend ET voit la preuve → rétention doublée.' })
  } else if (isEdu) {
    bullets.push({ tag: 'Format', text: 'Éducatif pur : la promesse "j\'apprends quelque chose en 30s" est anti-scroll.' })
  } else if (isDemo) {
    bullets.push({ tag: 'Format', text: 'Démonstration visuelle : la preuve par l\'image bypasse les objections rationnelles.' })
  } else if (isStory) {
    bullets.push({ tag: 'Format', text: 'Storytelling : l\'histoire active les neurones miroirs, plus mémorable qu\'un message direct.' })
  }

  // 5. Durée optimale
  if (src.duree_secondes) {
    const d = src.duree_secondes
    if (d <= 15) bullets.push({ tag: 'Durée', text: `${d}s. Format ultra-court : taux de complétion proche de 100 % = boost algorithmique max.` })
    else if (d <= 30) bullets.push({ tag: 'Durée', text: `${d}s. Sweet spot Reels : assez long pour livrer la valeur, assez court pour la complétion.` })
    else if (d <= 60) bullets.push({ tag: 'Durée', text: `${d}s. Durée moyenne, fonctionne si le hook tient et que chaque seconde apporte.` })
    else bullets.push({ tag: 'Durée', text: `${d}s. Format long : pari risqué qui paie quand le contenu justifie le temps demandé.` })
  }

  // 6. Performance (vues + engagement)
  if (src.vues || src.engagement) {
    const fmtNum = (n) => n >= 1_000_000 ? `${(n/1_000_000).toFixed(1)}M` : n >= 1_000 ? `${Math.round(n/1_000)}k` : String(n)
    const parts = []
    if (src.vues) parts.push(`${fmtNum(src.vues)} vues`)
    if (src.engagement) parts.push(`${src.engagement}% engagement`)
    bullets.push({ tag: 'Résultats', text: `${parts.join(' · ')}. La performance valide que le format porte plus que le créateur.` })
  }

  return bullets
}

export function FormatCardFree({ format, idx, airtableReady, unlocked = false }) {
  const src = format.source || {}
  // Adaptation pas encore générée (scan en cours) : pas de hook/why perso.
  // On l'indique explicitement au lieu d'afficher la VO ou "Indisponible"
  // en silence — l'user doit savoir que son script arrive.
  const isPreview = !unlocked && !format.why_for_you && !format.hook_adapted
  const PendingNote = () => (
    <span className="inline-flex items-center gap-1.5 text-[11px] font-bold" style={{ color: '#d97206' }}>
      <span className="inline-block w-3 h-3 border-2 border-orange/30 border-t-orange rounded-full animate-spin" />
      Ton script personnalisé s'écrit…
    </span>
  )
  const mp4Url = airtableReady ? getVideoMp4({ lien: src.lien, airtable_id: src.airtable_id }) : null
  // Poster : Supabase Storage en priorité (URL publique stable, jamais
  // d'expiration). Fallback chain géré par img onError ci-dessous.
  const supabaseThumb = formatPosterUrl(format)
  const airtableThumb = airtableReady ? getVideoThumb({ lien: src.lien, airtable_id: src.airtable_id }) : null
  const posterUrl = supabaseThumb || airtableThumb || src.poster_url || null
  const posterFallbacks = [supabaseThumb, airtableThumb, src.poster_url].filter(Boolean)
  // Tabs uniquement en unlocked (LP) — Original / Variables / Ton compte (placeholder)
  const [tab, setTab] = useState('source')

  const fmtNum = (n) => {
    if (!n) return '0'
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
    if (n >= 1_000) return `${(n / 1_000).toFixed(0)}k`
    return String(n)
  }

  function renderText(text) {
    if (!text) return isPreview ? <PendingNote /> : <em className="text-ink-faint">Indisponible</em>
    const parts = text.split(/(\[[^\]]+\])/g)
    return parts.map((p, i) =>
      p.startsWith('[') && p.endsWith(']')
        ? <span key={i} className="inline-block px-1.5 py-0.5 mx-0.5 rounded-md text-[0.95em] font-bold"
            style={{ background: 'rgba(var(--merlin-accent-rgb, 254,130,4),0.14)', color: '#d97206' }}>{p}</span>
        : <span key={i}>{p}</span>
    )
  }

  return (
    <article className="merlin-format-card flex flex-col"
      style={format.is_daily_top ? { border: '2px solid var(--merlin-violet, #7c3aed)', boxShadow: '0 0 0 3px rgba(var(--merlin-violet-rgb, 124,58,237),0.18), 0 14px 38px rgba(var(--merlin-violet-rgb, 124,58,237),0.32)' } : undefined}>

      {/* Ruban FORMAT DU JOUR (slot #1 = viral frais du jour dans la niche) */}
      {format.is_daily_top && (
        <div className="flex items-center gap-1.5 px-3 py-1.5 text-white"
          style={{ background: 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))', fontSize: '11px', fontWeight: 800, letterSpacing: '0.02em' }}>
          🔥 {format.daily_badge || "Viral aujourd'hui dans ta niche"}
        </div>
      )}

      {/* ═══ VIDÉO — cap à 380px sur mobile pour densité ═══
           Priorité MP4 (lecture inline) > poster (statique) > loader.
           Jamais de lien externe vers Instagram (on garde le user). */}
      <div className="relative overflow-hidden bg-black/95 w-full aspect-[4/5] sm:aspect-[9/16] max-h-[380px] sm:max-h-[480px] studio-thumbnail"
        style={{ background: 'linear-gradient(135deg, var(--fmt-ph-1, #1e1145) 0%, var(--fmt-ph-2, #2d1b69) 100%)' }}>
        {/* Toujours le player : poster Supabase (CDN) instantané + MP4
            paresseux (résolu au tap). Plus de gate airtableReady, plus de
            spinner bloquant. */}
        <FreeVideoPlayer mp4Url={mp4Url} posterUrl={posterUrl} posterFallbacks={posterFallbacks}
          lien={src.lien} airtable_id={src.airtable_id} />
        {/* Overlay vues haut-droite */}
        {src.vues > 0 && (
          <div className="absolute top-2 right-2 pointer-events-none z-10">
            <span className="inline-block px-1.5 py-0.5 rounded-md text-[10px] font-bold text-white font-mono"
              style={{ background: 'rgba(15,10,26,0.75)', backdropFilter: 'blur(8px)' }}>
              {fmtNum(src.vues)}
            </span>
          </div>
        )}
        {/* Badge format # + style de production haut-gauche : les 3 cards
            couvrent face cam / voix off / créatif pour MONTRER l'adaptation */}
        <div className="absolute top-2 left-2 pointer-events-none z-10 flex items-center gap-1">
          <span className="inline-block px-1.5 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-wider text-white"
            style={{ background: 'rgba(var(--merlin-accent-rgb, 254,130,4),0.90)', backdropFilter: 'blur(8px)' }}>
            #{String(idx + 1).padStart(2, '0')}
          </span>
          {src.style_production && (
            <span className="inline-block px-1.5 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-wider text-white"
              style={{ background: 'rgba(var(--merlin-violet-rgb, 124,58,237),0.88)', backdropFilter: 'blur(8px)' }}>
              {src.style_production === 'Face caméra' ? '🎤' : src.style_production === 'Voix off' ? '🎙' : '✨'} {src.style_production}
            </span>
          )}
        </div>
        {/* Badge "Facile à refaire" bas-gauche : LA promesse du produit */}
        {src.facile_a_refaire && (
          <div className="absolute bottom-2 left-2 pointer-events-none z-10">
            <span className="inline-block px-1.5 py-0.5 rounded-md text-[9px] font-bold text-white"
              style={{ background: 'rgba(16,185,129,0.92)', backdropFilter: 'blur(8px)' }}>
              ✓ Facile à refaire
            </span>
          </div>
        )}
      </div>

      {/* ═══ HEADER compact ═══ */}
      <div className="merlin-card-header-orange px-3 pt-2 pb-1.5">
        <h3 className="text-[14px] font-bold text-ink leading-tight">{src.format_label || src.format_nom || 'Format viral'}</h3>
        {src.createur && (
          <p className="text-[10px] text-ink-faint mt-0.5 truncate">@{src.createur}</p>
        )}
      </div>

      {/* ═══ Badges (tight) ═══ */}
      <div className="px-3 pt-1.5 pb-2 flex items-center gap-1 flex-wrap">
        {src.mecanisme_viral && <span className="merlin-badge-mecanique" style={{ fontSize: '9px', padding: '2px 6px' }}>{src.mecanisme_viral}</span>}
        {src.arc_narratif && <span className="merlin-badge-chassis" style={{ fontSize: '9px', padding: '2px 6px' }}>▣ {src.arc_narratif}</span>}
        {src.engagement > 0 && <span className="merlin-badge-easy" style={{ fontSize: '9px', padding: '2px 6px' }}>◆ {src.engagement}% eng</span>}
      </div>

      {/* ═══ HOOK ═══
           - free : hook_adapted (le résultat perso)
           - unlocked LP : suit l'onglet (original / template / placeholder) */}
      {/* HOOK — suit le tab actif (Original / Variables / Pour toi gated en Studio free) */}
      <div className="px-3 pt-1 pb-1.5">
        <p className="text-[9px] uppercase tracking-wider font-bold text-orange mb-1">
          {format.is_visual_primary ? 'Hook visuel' : 'Hook'}
        </p>
        <p className="text-[13px] font-bold text-ink leading-snug italic">
          {tab === 'source'   ? <>« {src.hook_original || renderText(format.hook_template)} »</>
            : tab === 'template' ? <>« {renderText(format.hook_template)} »</>
            : unlocked
              ? <span className="text-ink-muted not-italic font-normal text-[12px]">→ Lance ton scan pour voir le hook adapté à ta voix.</span>
              : <span className="flex items-center gap-1.5"><span style={{ fontSize: '11px' }}>🔒</span>
                  <span className="text-ink-muted not-italic font-normal text-[12px]">Hook adapté débloqué en Créateur</span>
                </span>}
        </p>
      </div>

      {/* Preview (adaptation non finie) : état de chargement clair sous la
          miniature — "Chargement des informations en cours" + barre animée,
          au lieu d'une carte vide ("pauvre vignette"). */}
      {isPreview && (
        <div className="px-3 pt-2 pb-3">
          <p className="text-[12px] font-bold mb-2 flex items-center gap-1.5" style={{ color: '#d97206' }}>
            <span className="inline-block w-3.5 h-3.5 border-2 border-orange/30 border-t-orange rounded-full animate-spin shrink-0" />
            Chargement des informations en cours…
          </p>
          <div className="h-2 rounded-full overflow-hidden" style={{ background: 'rgba(var(--merlin-accent-rgb, 254,130,4),0.12)' }}>
            <div className="h-full rounded-full animate-pulse" style={{ width: '66%', background: 'linear-gradient(90deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))' }} />
          </div>
        </div>
      )}

      {/* SCRIPT — 3 tabs : Original (verbatim) / Variables (template) / Trame.
           Le tab "Trame" remplace l'ancien "Pour toi" (script IA full) suite
           au feedback : un script IA fini fait cheap. On garde la structure
           (beats + fonction), l'user écrit ses mots à lui. Plus pro, garde
           le contrôle créatif, évite le rendu "tout-AI". */}
      <div className="px-3 pt-2 pb-2.5">
        <div className="bg-bg/70 rounded-lg border border-orange/25 overflow-hidden">
          <div className="flex gap-0.5 bg-ink/[0.04] p-0.5 m-1.5 rounded-md">
            {[
              { v: 'source',   l: 'Original' },
              { v: 'template', l: 'Variables' },
              { v: 'trame',    l: 'Ta trame' },
            ].map(opt => (
              <button key={opt.v}
                onClick={(e) => { e.preventDefault(); setTab(opt.v); studioEvents.tabClicked({ tab: opt.v, format_idx: idx, source_page: 'studio_ready' }); if (opt.v === 'trame' && !unlocked) { try { studioEvents.tramePaywallShown({ format_idx: idx }) } catch {} } }}
                className={`flex-1 text-[10px] px-2 py-1.5 rounded-md font-bold transition-colors flex items-center justify-center gap-1 ${tab === opt.v ? 'bg-white shadow-sm text-ink' : 'text-ink-muted hover:text-ink'}`}>
                {opt.l}
              </button>
            ))}
          </div>
          <div className="px-3 pb-3 pt-1">
            {tab === 'source' && (() => {
              const txt = src.script_original
              if (!txt) return <em className="text-ink-faint text-[11px]">Transcription source indisponible.</em>
              const wordCount = txt.split(/\s+/).filter(Boolean).length
              const isLong = txt.length > 280
              if (!isLong) {
                return (
                  <p className="text-[11px] text-ink whitespace-pre-line leading-snug">
                    {renderText(txt)}
                  </p>
                )
              }
              return (
                <details className="group">
                  <summary className="cursor-pointer list-none">
                    <p className="text-[11px] text-ink whitespace-pre-line leading-snug group-open:hidden">
                      {renderText(scriptPreview(txt))}<span className="text-ink-faint">…</span>
                    </p>
                    <p className="text-[10px] text-orange font-bold mt-1.5 group-open:hidden flex items-center gap-1">
                      <span>+</span> Voir le script complet ({wordCount} mots)
                    </p>
                    <p className="text-[10px] text-ink-faint font-bold mt-1.5 hidden group-open:flex items-center gap-1">
                      <span>−</span> Replier
                    </p>
                  </summary>
                  <p className="text-[11px] text-ink whitespace-pre-line leading-snug mt-1">
                    {renderText(txt)}
                  </p>
                </details>
              )
            })()}
            {tab === 'template' && (() => {
              let tmpl = cleanScriptTemplate(format.script_template || '')
              const hasVars = /\[[^\]]+\]/.test(tmpl)
              // Onglet Variables = SCRIPT PARLÉ avec mots à remplacer. Pour les
              // formats visuels, le backend met une recette visuelle ici (sans
              // [variables]) : on bascule alors sur le vrai script parlé du
              // format. S'il n'y a pas de script parlé, on renvoie vers la trame.
              const spoken = (format.script_fr || src.script_fr || src.script_original || '').trim()
              if (!hasVars && spoken) tmpl = spoken
              if (!hasVars && !spoken) {
                return <p className="text-[11px] text-ink-muted leading-snug">Ce format repose surtout sur le visuel : le détail étape par étape (plans, cadrage, à filmer) est dans l'onglet <strong className="text-ink">Ta trame</strong>.</p>
              }
              const varCount = (tmpl.match(/\[[^\]]+\]/g) || []).length
              const wordCount = tmpl.split(/\s+/).filter(Boolean).length
              const isLong = tmpl.length > 280
              return (
                <>
                  {varCount > 0 && (
                    <p className="text-[10px] font-bold mb-1.5 flex items-center gap-1.5"
                      style={{ color: '#d97206' }}>
                      <span style={{ fontSize: '11px' }}>⚙</span>
                      {varCount} variable{varCount > 1 ? 's' : ''} à remplacer pour adapter à ton compte
                    </p>
                  )}
                  {!isLong ? (
                    <p className="text-[11px] text-ink whitespace-pre-line leading-snug">
                      {renderText(tmpl)}
                    </p>
                  ) : (
                    <details className="group">
                      <summary className="cursor-pointer list-none">
                        <p className="text-[11px] text-ink whitespace-pre-line leading-snug group-open:hidden">
                          {renderText(scriptPreview(tmpl))}<span className="text-ink-faint">…</span>
                        </p>
                        <p className="text-[10px] text-orange font-bold mt-1.5 group-open:hidden flex items-center gap-1">
                          <span>+</span> Voir le script complet ({wordCount} mots)
                        </p>
                        <p className="text-[10px] text-ink-faint font-bold mt-1.5 hidden group-open:flex items-center gap-1">
                          <span>−</span> Replier
                        </p>
                      </summary>
                      <p className="text-[11px] text-ink whitespace-pre-line leading-snug mt-1">
                        {renderText(tmpl)}
                      </p>
                    </details>
                  )}
                </>
              )
            })()}
            {tab === 'trame' && (() => {
              const allBeats = format.beats_raw || format.structure || []
              // En Studio free (unlocked=false) : beat 1 visible + ghost
              // blurry des suivants pour donner envie + invite essai 7j.
              // En LP demo (unlocked=true) : tout visible.
              if (unlocked || allBeats.length === 0) {
                return <TrameContent beats={allBeats} mode={format.texte_ecran_mode || src.texte_ecran_mode} />
              }
              const remaining = allBeats.slice(1)
              return (
                <>
                  <TrameContent beats={allBeats.slice(0, 1)} mode={format.texte_ecran_mode || src.texte_ecran_mode} />
                  {remaining.length > 0 && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault()
                        studioEvents.tramePaywallClicked({ format_idx: idx, remaining_beats: remaining.length })
                        window.dispatchEvent(new CustomEvent('studio:open-upgrade', { detail: { trigger: 'trame_paywall' } }))
                      }}
                      className="relative mt-2 rounded-lg overflow-hidden block w-full text-left cursor-pointer group hover:scale-[1.005] transition-transform"
                      style={{ background: 'rgba(var(--merlin-violet-rgb, 124,58,237),0.04)', border: '1px dashed rgba(var(--merlin-violet-rgb, 124,58,237),0.30)' }}
                      aria-label="Débloquer la trame complète en Créateur">
                      {/* Ghost preview blurry : on devine à peine pour donner envie */}
                      <div className="p-2.5 select-none pointer-events-none"
                        style={{ filter: 'blur(5px)', opacity: 0.45 }}>
                        <TrameContent beats={remaining.slice(0, 2)} mode={format.texte_ecran_mode || src.texte_ecran_mode} />
                      </div>
                      {/* Overlay cadenas + invite essai */}
                      <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-3 pointer-events-none"
                        style={{ background: 'linear-gradient(180deg, rgba(255,251,245,0.50) 0%, rgba(255,251,245,0.88) 55%, rgba(255,251,245,0.96) 100%)' }}>
                        <p className="text-[11px] font-bold text-purple-700 mb-0.5 group-hover:underline">
                          🔒 {remaining.length} étape{remaining.length > 1 ? 's' : ''} restante{remaining.length > 1 ? 's' : ''}
                        </p>
                        <p className="text-[10px] text-ink-muted leading-snug max-w-[280px]">
                          {showsTrialUx() ? 'Débloque la trame complète en Créateur (essai 7 jours gratuit)' : 'Débloque la trame complète en Créateur'}
                        </p>
                      </div>
                    </button>
                  )}
                </>
              )
            })()}
          </div>
        </div>
      </div>

      {/* ═══ WHY — details collapsible ═══ */}
      {format.why_for_you && (
        <details className="group border-t border-orange/15">
          <summary className="cursor-pointer list-none flex items-center justify-between gap-2 px-3 py-2 hover:bg-orange/[0.06] transition-colors">
            <span className="text-[10px] font-bold uppercase tracking-wider text-orange">✨ Pourquoi pour toi</span>
            <Chevron accent="orange" />
          </summary>
          <p className="text-[11px] text-ink leading-relaxed italic px-3 pb-3">{format.why_for_you}</p>
        </details>
      )}

      {/* ═══ WHY-IT-WORKED — analyse synthétique du reel source ═══ */}
      {(() => {
        const bullets = buildWhyItWorked(src)
        if (bullets.length === 0) return null
        return (
          <details className="group border-t border-emerald-200/60">
            <summary className="cursor-pointer list-none flex items-center justify-between gap-2 px-3 py-2 hover:bg-emerald-50/60 transition-colors">
              <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">
                🔍 Pourquoi ce reel a marché · {bullets.length} points
              </span>
              <Chevron accent="emerald" />
            </summary>
            <ul className="px-3 pb-3 pt-1 space-y-2">
              {bullets.map((b, bi) => (
                <li key={bi} className="flex items-start gap-2 text-[11.5px] leading-snug">
                  <span className="text-[9px] uppercase tracking-wider font-bold shrink-0 mt-0.5 px-1.5 py-0.5 rounded"
                    style={{ background: 'rgba(16,185,129,0.10)', color: '#065f46', border: '1px solid rgba(16,185,129,0.25)' }}>
                    {b.tag}
                  </span>
                  <span className="text-ink">{b.text}</span>
                </li>
              ))}
            </ul>
          </details>
        )
      })()}

      {/* Note : ancien collapsible "Structure · X beats" retiré, info maintenant
           dans l'onglet "Ta trame" (pas de duplication). */}

      {/* ═══ CTA — paywall en free, signup en unlocked (LP) ═══ */}
      <div className="px-3 pt-2.5 pb-3 mt-auto border-t border-ink/[0.04]">
        <button
          onClick={() => goToStudioCheckout()}
          className="w-full py-2.5 rounded-xl font-bold text-[13px] text-white transition-all hover:scale-[1.01] active:scale-[0.99] flex items-center justify-center gap-1.5"
          style={{
            background: 'linear-gradient(135deg, var(--merlin-violet, #7c3aed), var(--merlin-violet-2, #5b21b6))',
            boxShadow: '0 4px 14px rgba(var(--merlin-violet-rgb, 124,58,237),0.30)',
          }}>
          {unlocked ? (
            <span>Générer mon script →</span>
          ) : (
            <>
              <span style={{ fontSize: '11px' }}>🔓</span>
              <span>Débloquer ce format</span>
            </>
          )}
        </button>
      </div>
    </article>
  )
}

// ═══════════════════════════════════════════════════════════════
// FORMAT CARD LOCKED
// ═══════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════
// SCRIPT PAYWALL — script "Pour toi" gated (blur + débloquer)
// ═══════════════════════════════════════════════════════════════
function ScriptPaywall({ script, renderText }) {
  // 2 lignes teaser claires → blur du reste avec fade. Pas de CTA dans
  // le bloc (le bouton 'Débloquer ce format' en bas de card couvre déjà).
  const teaserLines = (script || '').split('\n').filter(l => l.trim()).slice(0, 2).join('\n')

  return (
    <div className="relative bg-bg/70 rounded-lg border border-orange/25 overflow-hidden">
      <p className="text-[9px] uppercase tracking-wider font-bold text-ink-faint px-2.5 pt-2 pb-1">Script adapté</p>
      <div className="text-[11px] text-ink whitespace-pre-line leading-snug px-2.5 pb-1">
        {teaserLines ? renderText(teaserLines) : (
          <em className="text-ink-faint">Script personnalisé prêt</em>
        )}
      </div>
      <div className="relative h-[42px] overflow-hidden">
        <div className="absolute inset-x-2.5 top-0 text-[11px] text-ink whitespace-pre-line leading-snug blur-[4px] select-none pointer-events-none">
          {(script || '').split('\n').filter(l => l.trim()).slice(2, 5).join('\n') ||
            "Le format adapté à ton compte, étape par étape."}
        </div>
        <div className="absolute inset-0 pointer-events-none"
          style={{ background: 'linear-gradient(180deg, transparent 0%, rgba(255,251,245,0.92) 60%, rgba(255,251,245,1) 100%)' }} />
        <div className="absolute bottom-1 inset-x-0 flex items-center justify-center pointer-events-none">
          <span className="text-[10px] font-bold text-ink-faint flex items-center gap-1">
            <span style={{ fontSize: '8px' }}>🔒</span> Suite débloquée en Créateur
          </span>
        </div>
      </div>
    </div>
  )
}

function FormatCardLocked({ format, idx }) {
  const src = format.source || {}
  const fmtNum = (n) => {
    if (!n) return '0'
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
    if (n >= 1_000) return `${(n / 1_000).toFixed(0)}k`
    return String(n)
  }

  return (
    <article className="merlin-format-card flex flex-col opacity-95">

      {/* Video poster compact */}
      <div className="relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, var(--fmt-ph-1, #1e1145) 0%, var(--fmt-ph-2, #2d1b69) 100%)', aspectRatio: '16/10' }}>
        {src.lien ? (
          <a href={src.lien} target="_blank" rel="noopener noreferrer"
            className="relative w-full h-full flex items-center justify-center group">
            {src.poster_url ? (
              <img src={src.poster_url} alt=""
                className="absolute inset-0 w-full h-full object-cover opacity-55 group-hover:opacity-75 transition-opacity"
                loading="lazy" />
            ) : (
              <img src={merlinImg} alt="" className="absolute inset-0 w-1/4 h-auto m-auto opacity-15" />
            )}
            <div className="absolute inset-0"
              style={{ background: 'linear-gradient(180deg, rgba(15,10,26,0.45), rgba(26,16,64,0.70))' }} />
            <div className="relative z-10 w-11 h-11 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform"
              style={{ background: 'rgba(255,255,255,0.95)' }}>
              <span className="text-purple-900 text-base ml-0.5">▶</span>
            </div>
          </a>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-white/40 text-xs">
            Vidéo indisponible
          </div>
        )}
      </div>

      {/* Header */}
      <div className="merlin-card-header-orange px-3 sm:px-4 pt-2 sm:pt-3 pb-2">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
            style={{ background: 'rgba(var(--merlin-accent-rgb, 254,130,4),0.12)', color: 'var(--merlin-accent, #fe8204)' }}>
            Format {String(idx + 1).padStart(2, '0')}
          </span>
          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
            🔒 Pro
          </span>
        </div>
        <h3 className="text-sm font-bold text-ink leading-snug">{src.format_label || src.format_nom || 'Format viral'}</h3>
        {src.createur && (
          <p className="text-[11px] text-ink-faint mt-0.5">
            par <span className="font-bold">{src.createur}</span>
            <span className="mx-1.5 text-ink-faint">·</span>
            <span className="font-mono">{fmtNum(src.vues)} vues</span>
          </p>
        )}
      </div>

      {/* Badges */}
      <div className="px-3 sm:px-4 pt-2 flex items-center gap-1.5 flex-wrap">
        {src.mecanisme_viral && <span className="merlin-badge-mecanique">{src.mecanisme_viral}</span>}
        {src.arc_narratif && <span className="merlin-badge-chassis">▣ {src.arc_narratif}</span>}
      </div>

      {/* Hook teaser */}
      {format.hook_template && (
        <div className="px-3 sm:px-4 pt-2 sm:pt-3 flex-1">
          <p className="text-[10px] uppercase tracking-wider font-bold text-orange mb-1">Hook</p>
          <p className="text-sm font-bold text-ink leading-snug italic">
            « {format.hook_template} »
          </p>
          <p className="text-[11px] text-ink-faint italic mt-3 leading-relaxed">
            Format adapté à ton compte, structure narrative et CTA débloqués avec l'abonnement Pro.
          </p>
        </div>
      )}

      {/* CTA paywall */}
      <div className="px-3 sm:px-4 pt-3 sm:pt-4 pb-3 sm:pb-4 mt-auto">
        <button
          onClick={() => goToStudioCheckout()}
          className="merlin-cta-primary w-full text-center flex items-center justify-center gap-2">
          <span>🔒</span>
          <span>Débloquer ce script</span>
        </button>
      </div>
    </article>
  )
}
